import * as i0 from '@angular/core';
import { InjectionToken, Injectable, PLATFORM_ID, Inject, Optional, isDevMode, Component, NgModule, EventEmitter, Output, Input, SecurityContext, HostListener, ViewChild, ChangeDetectionStrategy, SkipSelf, Directive, HostBinding, Pipe, ContentChildren, forwardRef } from '@angular/core';
import * as i3$2 from '@angular/common';
import { isPlatformBrowser, isPlatformServer, DOCUMENT, CommonModule } from '@angular/common';
import { Cookie } from 'ng2-cookies';
import { Cookie as Cookie$1 } from 'ng2-cookies/ng2-cookies';
import * as i2 from 'ngx-webstorage';
import { encrypt, decrypt } from 'crypto-js/aes';
import utf8 from 'crypto-js/enc-utf8';
import * as i3 from '@angular/common/http';
import { HttpHeaders, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import * as i3$1 from '@angular/router';
import { NavigationEnd, NavigationStart, RouterModule } from '@angular/router';
import { timer, BehaviorSubject, filter, Subject, of, throwError, fromEvent } from 'rxjs';
import * as i1 from '@citi-gcg-173875/citi-common-ui-cds';
import { CdsIconModule, CdsModalDialogModule, CdsButtonModule } from '@citi-gcg-173875/citi-common-ui-cds';
import * as i1$1 from '@angular/platform-browser';
import { tap, catchError, finalize, share, take, takeUntil, debounceTime } from 'rxjs/operators';
import * as i1$2 from '@angular/cdk/a11y';
import { A11yModule } from '@angular/cdk/a11y';
import * as i4 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule, NgControl } from '@angular/forms';

const WINDOW = new InjectionToken('WindowToken');
/* Define abstract class for obtaining reference to the global window object. */
// @dynamic
class WindowRef {
    get nativeWindow() {
        // Had to make this function despite being an abstract class because teams are using it
        // if (isDevMode()) throw new Error(WindowRefError);
        return typeof window !== 'undefined' ? window : undefined;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: WindowRef, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: WindowRef, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: WindowRef, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
/* Define class that implements the abstract class and returns the native window object. */
// @dynamic
class BrowserWindowRef extends WindowRef {
    constructor(platformId) {
        super();
        this.platformId = platformId;
    }
    get nativeWindow() {
        if (isPlatformBrowser(this.platformId)) {
            return window;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BrowserWindowRef, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BrowserWindowRef, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BrowserWindowRef, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });
/* Create an factory function that returns the native window object. */
function windowFactory(browserWindowRef, platformId) {
    if (isPlatformBrowser(platformId)) {
        return browserWindowRef.nativeWindow;
    }
    return null;
}
const browserWindowProvider = {
    provide: WindowRef,
    useClass: BrowserWindowRef
};
/* Create an injectable provider that uses the windowFactory function for returning the native window object. */
const windowProvider = {
    provide: WINDOW,
    useFactory: windowFactory,
    deps: [WindowRef, PLATFORM_ID]
};
/* Create an array of providers. */
const WINDOW_PROVIDER = [browserWindowProvider, windowProvider];

class CookieService {
    constructor(_window) {
        this._window = _window;
        this.localCookies = new Map();
    }
    /**
     * Retrieves a value from cookie storage with a specified name.
     * @param name Name of a cookie to retrieve.
     * @returns Value of a specified name. Returns an empty string if not found.
     */
    getCookie(name) {
        if (this.localCookies.has(name)) {
            return this.localCookies.get(name);
        }
        else if (this._window) {
            return Cookie.get(name);
        }
        return '';
    }
    /**
     * Stores a cookie into the storage.
     * @param cookie Cookie object.
     */
    setCookie(cookie) {
        if (!cookie) {
            return;
        }
        if (typeof cookie === 'string') {
            this.parseCookieString(cookie);
        }
        else if (this.isCookieInstance(cookie)) {
            this.setCookieInLocal(cookie.name, cookie.value);
            this.setCookieInStorage(cookie);
        }
        else if (Array.isArray(cookie)) {
            cookie.filter(this.isCookieInstance).forEach(pair => {
                this.setCookieInLocal(pair.name, pair.value);
                this.setCookieInStorage(pair);
            });
        }
    }
    /**
     * Deletes a cookie with specified parameters.
     * @param name Name of the cookie.
     * @param path Path that the cookie is set to.
     * @param domain Domain that the cookie is set to.
     */
    deleteCookie(name, path, domain) {
        this.localCookies.delete(name);
        if (!this._window) {
            return;
        }
        if (path && domain) {
            Cookie.delete(name, path, domain);
        }
        else if (path) {
            Cookie.delete(name, path);
        }
        else if (domain) {
            Cookie.delete(name, null, domain);
        }
        else {
            Cookie.delete(name);
        }
    }
    /**
     * Converts the string into a cookie object.
     * @param strPairs String object delimited by ';' and '='
     */
    parseCookieString(strPairs) {
        strPairs.split(';')
            .map(pairs => pairs.split('='))
            .filter(pair => pair.length === 2)
            .filter(pair => pair[0].length > 0)
            .filter(pair => pair[1].length > 0)
            .forEach(pair => {
            const [name, val] = pair;
            this.setCookieInLocal(name, val);
            this.setCookieInStorage(name, val);
        });
    }
    /**
     * Stores the cookie into the Cookie storage object.
     * @param cookie Name or object of the cookie.
     * @param value Value that the specified cookie contains.
     */
    setCookieInStorage(cookie, value) {
        if (!this._window) {
            return;
        }
        if (typeof cookie === 'string') {
            Cookie.set(this.getTrimmedString(cookie), this.getTrimmedString(value));
        }
        else {
            Cookie.set(this.getTrimmedString(cookie.name), this.getTrimmedString(cookie.value), cookie.expires, cookie.path ? cookie.path : this._window.location.pathname, cookie.domain ? cookie.domain : this._window.location.hostname, false);
        }
    }
    /**
     * Stores the cookie locally.
     * @param name Name or object of the cookie.
     * @param value Value that the specified cookie contains.
     */
    setCookieInLocal(name, value) {
        if (name) {
            this.localCookies.set(this.getTrimmedString(name), this.getTrimmedString(value));
        }
    }
    /**
     * Returns a string that has leading and trailing whitespaces removed if exists.
     * @param str String value.
     */
    getTrimmedString(str) {
        if (str) {
            return str.trim();
        }
        return str;
    }
    /**
     * Returns whether or not argument is the cookie object.
     * @param arg Arguments to check.
     */
    isCookieInstance(arg) {
        return 'name' in arg;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CookieService, deps: [{ token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CookieService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CookieService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }] });

const IS_SSRENDERED_APP = new InjectionToken('IsSSRenderedApp');

// AppConfigService
/**
 * The AppConfigService provides access to the Application Config.
 */
class AppConfigService {
    constructor(_cookieService, _platformId, _document, _isSSRApp) {
        this._cookieService = _cookieService;
        this._platformId = _platformId;
        this._document = _document;
        this._isSSRApp = _isSSRApp;
        this.tenantId = 'CBOL';
        this.channelId = 'CBOL';
        let tenant = this._cookieService.getCookie('tenantId');
        this.isBrowser = isPlatformBrowser(this._platformId);
        this.setTenantId(!!tenant ? tenant : this.tenantId);
    }
    /**
     * The getLocale method gets the current locale.
     * method: getLocale
     */
    getLocale() {
        console.warn('THE LOCALE IS ' + this._appConfig.locale);
        return this._appConfig.locale;
    }
    /**
   * The getMessageConfig method gets the messaging config.
   * method: getMessageConfig
   */
    getMessageConfig() {
        return this._appConfig.messageConfig;
    }
    /**
  * The getAEMConfig method gets the AEM config.
  * method: getAEMConfig
  */
    getAEMConfig() {
        const aemConfig = this._appConfig.aemConfig;
        const { swdcAemConfig, gtdcAemConfig, ...genericConfig } = aemConfig;
        if (this._cookieService.getCookie('CITI_SITE') === 'swdc') {
            return aemConfig.swdcAemConfig ? aemConfig.swdcAemConfig : genericConfig;
        }
        else if (this._cookieService.getCookie('CITI_SITE') === 'gtdc') {
            return aemConfig.gtdcAemConfig ? aemConfig.gtdcAemConfig : genericConfig;
        }
        return genericConfig;
    }
    /**
     * The setLocale method sets the {locale}.
     * method: setLocale
     *
     * param: locale {string} The locale.
     */
    setLocale(locale) {
        this._appConfig.locale = locale;
    }
    /**
     * The getBusinessId method gets the current businessId.
     * method: getBusinessId
     */
    getBusinessId() {
        return this._appConfig.businessId;
    }
    /**
     * The setBusinessId method sets the {businessId}.
     * method setLocale
     *
     * param: businessId {string} The business id.
     */
    setBusinessId(businessId) {
        this._appConfig.businessId = businessId;
    }
    /**
     * The setacqHostName method sets the {acqHostName}.
     * method setacqHostName
     *
     * param: acqHostName {string} The ACQ CBOL hostname.
     */
    setacqHostName(acqHostName) {
        this._appConfig.acqHostName = acqHostName;
    }
    /**
     * The setcsrHostName method sets the {csrHostName}.
     * method setcsrHostName
     *
     * param: csrHostName {string} The CSR Angular CBOL hostname.
     */
    setcsrHostName(csrHostName) {
        this._appConfig.csrHostName = csrHostName;
    }
    /* adding for Cards Marketing pages*/
    setcardsmkrHostName(cardsmkrHostName) {
        this._appConfig.cardsmkrHostName = cardsmkrHostName;
    }
    /**
     * The setAppConfig method sets the {AppConfig} object containing the current configuration.
     * method: setAppConfig
     *
     * param: appConfig {AppConfig} The Application Configuration object.
     */
    setAppConfig(appConfig) {
        Object.assign(appConfig, {
            crossSiteScriptingRegex: new RegExp('\\00|<|\\+ADw-|>|\\+AD4-|%25|\\(\\s*[a-z]{2,}\\.[a-z]{2,}.*\\)', 'g')
        });
        this._appConfig = appConfig;
        this._appConfig.apiServerUrl = this.domainRealignedUrl(this._appConfig.apiServerUrl);
        if (this._appConfig.contentService &&
            this._appConfig.contentService.staticBaseUrl) {
            this._appConfig.contentService.staticBaseUrl = this.domainRealignedUrl(this._appConfig.contentService.staticBaseUrl);
        }
        this._appConfig.sessionService.sessionKeepAliveUrl = this.domainRealignedUrl(this._appConfig.sessionService.sessionKeepAliveUrl);
        if (this.isBrowser) {
            if (this._document.cookie.indexOf('partnerId') != -1) {
                this.setPartnerId(this._cookieService.getCookie('partnerId'));
            }
        }
    }
    /**
     * Set and Get methods for partnerId in appConfig object
     */
    setPartnerId(partnerId) {
        this._appConfig.partnerId = partnerId;
    }
    getPartnerId() {
        return this._appConfig.partnerId;
    }
    /**
     * The getEnvironment method gets the environment from the Application Configuration Object.
     * method: getEnvironment
     */
    getEnvironment() {
        return this._appConfig.environment;
    }
    /**
     * The getacqHostName method gets the hostname of ACQ CBOL from the Application Configuration Object.
     * method: getacqHostName
     */
    getacqHostName() {
        return this._appConfig.acqHostName;
    }
    /**
     * The getcsrHostName method gets the hostname of CSR Angular CBOL from the Application Configuration Object.
     * method: getcsrHostName
     */
    getcsrHostName() {
        return this._appConfig.csrHostName;
    }
    /* Adding for fetching the variables in the appconfig for DF1071 microapp */
    getDFMicroAppObj() {
        if (!!this._appConfig.df1071Config)
            return this._appConfig.df1071Config;
        else
            return {};
    }
    /* adding for cards marketing to get domain map*/
    getcardsmkrHostName() {
        if (!!this._appConfig.cardsmkrHostName)
            return this._appConfig.cardsmkrHostName;
        else
            return '';
    }
    /**
     * The getnewCitiHostName method gets the hostname for new citi.com domain from the Application Configuration Object.
     * method: getnewCitiHostName
     */
    getnewCitiHostName() {
        return this._appConfig.newCitiHostName;
    }
    /**
     * The getApiServerUrl method gets the apiServerUrl from the Application Configuration Object.
     * method: getApiServerUrl
     */
    getApiServerUrl() {
        if (this._isSSRApp) {
            return isPlatformServer(this._platformId)
                ? this._appConfig.apiServerUrlPlatformServer
                : this._appConfig.apiServerUrl;
        }
        return this._appConfig.apiServerUrl;
    }
    /**
     * The setApiServerUrl method sets the {apiServerUrl}.
     * method: setApiServerUrl
     *
     * param: apiServerUrl {string} The apiServerUrl.
     */
    setApiServerUrl(apiServerUrl) {
        this._appConfig.apiServerUrl = this.domainRealignedUrl(apiServerUrl);
    }
    /**
     * The crossSiteScriptingRegex method gets the apiServerUrl from the Application Configuration Object.
     * method: crossSiteScriptingRegex
     */
    crossSiteScriptingRegex() {
        return this._appConfig.crossSiteScriptingRegex;
    }
    /**
     * The getSessionCookieInformation method gets the sessioncookieInformation from the Application Configuration Object.
     * method: getSessionCookieInformation
     */
    getSessionCookieInformation() {
        return this._appConfig.sessionCookieInformation;
    }
    /**
     * The getSessionServiceConfig method gets the sessionService from the Application Configuration Object.
     * method: getSessionServiceConfig
     */
    getSessionServiceConfig() {
        return this._appConfig.sessionService;
    }
    /**
     * The getEUMAppConfig method gets the EUM Configuration from the Application Configuration Object.
     * method: getEUMAppConfig
     */
    /* getEUMAppConfig(): EUMAppConfig {
       return this._appConfig.EUMAppConfig;
     }
   */
    /**
     * The getHttpServiceConfig method gets the httpService from the Application Configuration Object.
     * method: getHttpServiceConfig
     */
    getHttpServiceConfig() {
        return this._appConfig.httpService;
    }
    getContentServiceConfig() {
        return this._appConfig.contentService;
    }
    getEntitlementFlagConfig() {
        if (!!this._appConfig.isFeatureEntitlementEnabled)
            return this._appConfig.isFeatureEntitlementEnabled;
        else
            return false;
    }
    getBrandingFlagConfig() {
        if (!!this._appConfig.newLogicBranding)
            return this._appConfig.newLogicBranding;
        else
            return false;
    }
    getMergeSSRCSR() {
        if (!!this._appConfig.mergeSSRCSR)
            return this._appConfig.mergeSSRCSR;
        else
            return false;
    }
    getSessionDeleteFlagConfig() {
        if (!!this._appConfig.newSessionDeleteLogic)
            return this._appConfig.newSessionDeleteLogic;
        else
            return false;
    }
    /**
     * The getConfig method gets the config object specified by param in the Application Configuration Object.
     * method: getConfig
     *
     * param: configObject {string} The name of the config object at root level we want to retrieve.
     */
    getConfig(configObject) {
        if (configObject) {
            return this._appConfig[configObject] || {};
        }
        else {
            return this._appConfig;
        }
    }
    getSessionIdentifiers() {
        return this._appConfig.sessionIdentifiers;
    }
    getTenantId() {
        return this.tenantId;
    }
    getChannelId() {
        return this.channelId;
    }
    setChannelId(newChannelId) {
        this.channelId = newChannelId;
        this._appConfig.httpService.defaultHeaders.channelId = this.channelId; // because http service is going to use channelId from default headers for requests
    }
    /**
     *
     * setTenantId modifies the default tenantId value in the environemnt config object
     * @param newTenantId is passed by the journey teams for identifying the mobile sessions
     */
    setTenantId(newTenantId) {
        this.tenantId = newTenantId;
        this._cookieService.setCookie({
            name: 'tenantId',
            value: newTenantId,
            path: '/',
            expires: 0.02,
            domain: 'citi.com'
        });
    }
    getApiServerUrlPlatformServer() {
        return this._appConfig.apiServerUrlPlatformServer;
    }
    setApiServerUrlPlatformServer(apiServerUrlPlatformServer) {
        this._appConfig.apiServerUrlPlatformServer = apiServerUrlPlatformServer;
    }
    domainRealignedUrl(url) {
        if (!url)
            return;
        if (!this._isSSRApp ||
            isPlatformServer(this._platformId) ||
            url.includes('api.citi.com') || //DEV environment URL
            location.origin.includes('localhost') ||
            (url.includes('online.citi.com') &&
                location.origin.includes('online.citi.com')) || //already include 'online.citi.com' in API URL
            (location.origin.includes('citi.com') &&
                !location.origin.includes('online.citi.com')) //citi.com domain
        ) {
            return url; //citi.com, //online.citi.com
        }
        url = url.replace('www.citi.com', 'citi.com');
        const index = url.indexOf('citi.com');
        const environment = url.substring(0, index).replace('0', '');
        return `${environment}online.${url.substring(index)}`;
    }
    /**
     * The getModFedThrottlingRoutes method gets the Mod Fed routes that have throttling configurations.
     * method: getModFedThrottlingRoutes
    **/
    getModFedThrottlingRoutes() {
        if (!!this._appConfig.modFedThrottlingRoutes)
            return this._appConfig.modFedThrottlingRoutes;
        else
            return {};
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: AppConfigService, deps: [{ token: CookieService }, { token: PLATFORM_ID }, { token: DOCUMENT }, { token: IS_SSRENDERED_APP, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: AppConfigService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: AppConfigService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: CookieService }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [IS_SSRENDERED_APP]
                }] }] });

class EcmCookieService {
    // Constructor
    constructor(cookieService, _platform) {
        this.cookieService = cookieService;
        this._platform = _platform;
        // This is the name value map, which maps names to indexes of the ECM cookie values
        this.cookieValueMap = {
            isCitigoldCookie: 1,
            alertEnrollnd: 2,
            autoPayEnrllnd: 3,
            BALCONId: 4,
            isBillPresentment: 5,
            isSPFMigrated: 6,
            isThankYouEnrolledinCC: 7,
            isVANEligible: 8,
            mob: 9,
            PID: 10,
            StatusCode: 11,
            isBPActive: 12,
            hasCD: 13,
            hasChecking: 14,
            hasCheckingPlus: 15,
            hasCreditCard: 16,
            hasMortgage: 17,
            hasSavings: 18,
            isBrokerage: 19,
            isCitiblue: 20,
            isCitigold: 21,
            isCPC: 22,
            isGEB: 23,
            isIPB: 24,
            isPaperless: 25,
            isPBG: 26,
            isThankYou: 27,
            isBusinessCust: 28,
            isBusinessOnly: 29,
            isCitiBizTier: 30,
            isRELOnly: 31,
            isCitiAlliance: 57,
            isCitiPriority: 32,
            isCitiGoldPrivateClientCookie: 53
        };
        // Kicks off the service by getting the ecm cookie
        this.init = () => {
            this.ecm = this.cookieService.getCookie('CBOLECM');
            // If we got an ECM cookie
            if (this.ecm) {
                // Split it on the '-' delimiter
                this.ecm = this.ecm.split('-');
            }
            // Set whether or not we got an ecm cookie
            this.hasEcmCookie = this.ecm ? true : false;
        };
        // Gets an ECM cookie value by name
        this.getValueByName = name => {
            if (isPlatformServer(this._platform)) {
                return false;
            }
            // If the service has an ECM cookie
            if (this.hasEcmCookie) {
                // If name is a string value and has length and the name matches a value in the cookieValueMap object
                if (typeof name === 'string' &&
                    name.length &&
                    typeof this.cookieValueMap[name] !== 'undefined') {
                    // Use the index provided by the cookieValueMap to return the correct ECM cookie value
                    return this.ecm[this.cookieValueMap[name] - 1];
                    // Otherwise if we are in dev mode
                }
                /* else if (isDevMode) {
                  // Warn that the value passed was not a string, or the name was invalid.  Also return a list of valid names.
                  console.warn(
                    'EcmCookeService - getValueByName: You provided a non-string name or the name you passed was not valid. Valid names are: ',
                    this.cookieValueMap
                  );
                } */
                return false;
                // Otherwise if we didn't find an ECM cookie
            }
            /*
            else if (isDevMode) {
              // Let the user know
              console.warn('The ECM cookie service did not find an ECM cookie.');
            } */
            return false;
        };
        // Get an ECM cookie value by index (if you know the index of the value in the cookie you are looking for)
        this.getValueByIndex = index => {
            if (isPlatformServer(this._platform)) {
                return false;
            }
            // If the service has an ECM cookie
            if (this.hasEcmCookie) {
                // If the index we got was a number, and there is a value in the ECM cookie at the index provided
                if (typeof index === 'number' && typeof this.ecm[index] !== 'undefined') {
                    return this.ecm[index + 1];
                }
                // Otherwise if we didn't find an ECM cookie
            }
            /* else if (isDevMode) {
              // Let the user know
             // console.warn('The ECM cookie service did not find an ECM cookie.');
            } */
        };
        // Kicks off the service
        this.init();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: EcmCookieService, deps: [{ token: CookieService }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: EcmCookieService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: EcmCookieService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: CookieService }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });

// FeatureDetection
class FeatureDetectionService {
    constructor() {
        this._navigator = {};
    }
    /*
    -----------------------------------------------------------------------------------
        [detectFeature(detectionFeature): any {}]
        checks if the particular features are available in the client's machine or not
        call this method as detectFeature('stringYouWantToDetect')
        Currently, we checks for the following features
        CANVAS, LOCALSTORAGE, SESSIONSTORAGE, GEOLOCATION, SVG, WEBSOCKET
    -----------------------------------------------------------------------------------
    */
    detectFeature(detectionFeature) {
        switch (detectionFeature.toLowerCase()) {
            case 'canvas': {
                return !!document.createElement('canvas').getContext ? true : false;
            }
            case 'localstorage': {
                return 'localStorage' in window && window['localStorage'] !== null
                    ? true
                    : false;
            }
            case 'sessionstorage': {
                return 'sessionStorage' in window && window['sessionStorage'] !== null
                    ? true
                    : false;
            }
            case 'geolocation': {
                return 'geolocation' in navigator ? true : false;
            }
            case 'svg': {
                return document.implementation.hasFeature('http://www.w3.org/TR/SVG11/feature#Image', '1.1')
                    ? true
                    : false;
            }
            case 'websocket': {
                return typeof WebSocket === 'function' ? true : false;
            }
            default: {
                return 'unknown';
            }
        }
    }
    // Returns either the client is on mobile device or not
    isMobile() {
        let isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        return isMobile ? true : false;
    }
    // This will return the iPhone models as an string
    getIPhoneModel() {
        if (this.isMobile()) {
            // iPhone 6 Plus
            if (window.screen.height / window.screen.width == 736 / 414 &&
                window.devicePixelRatio == 3) {
                return 'iPhone 6 Plus';
            }
            // iPhone 6
            else if (window.screen.height / window.screen.width == 667 / 375 &&
                window.devicePixelRatio == 2) {
                return 'iPhone 6';
            }
            // iPhone 5/5C/5S or 6 in zoom mode
            else if (window.screen.height / window.screen.width == 1.775 &&
                window.devicePixelRatio == 2) {
                return 'iPhone 5, 5C, 5S or 6 (display zoom)';
            }
            // iPhone 4/4S
            else if (window.screen.height / window.screen.width == 1.5 &&
                window.devicePixelRatio == 2) {
                return 'iPhone 4 or 4S';
            }
            // iPhone 1/3G/3GS
            else if (window.screen.height / window.screen.width == 1.5 &&
                window.devicePixelRatio == 1) {
                return 'iPhone 1, 3G or 3GS';
            }
            else {
                return 'Not an iPhone';
            }
        }
        else {
            return 'Not a mobile device';
        }
    }
    /*
    -----------------------------------------------------------------------------------
        If no parameters are passed, it will return the entire navigator object
        If called with parameters, it will return the value(s) of that key
        Example of calling with parameters:
        getNavigatorInfo('userAgent'); This will return the userAgent string something like this: 'Mozilla/5.0 (Windows NT 6.1; WOW64; rv:49.0) Gecko/20100101 Firefox/49.0'
    -----------------------------------------------------------------------------------
    */
    getNavigatorInfo(navigatorType) {
        // tslint:disable-next-line:forin
        for (let i in navigator) {
            this._navigator[i] = navigator[i];
        }
        if (navigatorType) {
            return this._navigator[navigatorType];
        }
        else {
            return JSON.stringify(this._navigator);
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: FeatureDetectionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: FeatureDetectionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: FeatureDetectionService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

/** Storage service **/
const preDefinedregex = /\x00|<|\+ADw-|>|\+AD4-|%25|System[.][a-z]|javascript\s*:|['"].*[+\-*/%=&|^~'"]|\?.*:|\(\s*[a-z]{2,}\.[a-z]{2,}.*\)/g;
class StorageService {
    static { this.COOKIE = 'cookie'; }
    static { this.LOCALSTORAGE = 'localstorage'; }
    static { this.SESSIONSTORAGE = 'sessionstorage'; }
    constructor(_appConfigService, storage, sessionStorage, featureDetectionService) {
        this._appConfigService = _appConfigService;
        this.storage = storage;
        this.sessionStorage = sessionStorage;
        this.featureDetectionService = featureDetectionService;
        this.SECERET_KEY = 'asdfgh0987654321';
        this.appConfigRegex = _appConfigService.crossSiteScriptingRegex();
    }
    /**
     * The set method returns a boolean after inserting the key,value inside the cookie or Storage.
     * method: set
     *
     * param: key: string - Accepts the key for storing in Cookie or Storage
     * param: Value: string - Accepts the value for the key for storing in Cookie or Storage
     * param: destination: string - Would accept any of these values(all in lower case)
     *          cookie
     *          localstorage
     *          sessionstorage
     * param: cipher: boolean -This is an optional paarameter that needs to be set to true if the value needs to be encrypted before setting . Default is false .
     * param: CookieExpires: number - This is an optional parameter that needs to be sent only if the destination is cookie.Cookie's expiration date in days from now. If it's undefined the cookie is a session Cookie
     * param: path: string - This is an optional parameter that needs to be sent only if the destination is cookie.Path relative to the domain where the cookie should be avaiable. Default '/'
     */
    set(key, value, destination, cipher, CookieExpires, path) {
        let destinationValue = destination != null && destination != ''
            ? destination.toLocaleLowerCase()
            : null;
        let path1 = path != null && path != '' ? path : '/';
        cipher =
            cipher == null || cipher == undefined || cipher == false ? false : true;
        let valueCheck = this.checkForUnexpectedPattern([value]);
        //Encrypting
        value = this.encrypt(value, cipher);
        let localStorageEnabled = this.featureDetectionService.detectFeature('localStorage');
        let sessionStorageEnabled = this.featureDetectionService.detectFeature('sessionStorage');
        if (valueCheck != false) {
            try {
                if (destination != null && destination != '') {
                    switch (destinationValue) {
                        case StorageService.COOKIE:
                            if (key != null && key != '' && value != null) {
                                Cookie$1.set(key, value, CookieExpires, path1);
                                return true;
                            }
                            else {
                                return false;
                            }
                        case StorageService.LOCALSTORAGE:
                            if (key != null &&
                                key != '' &&
                                value != null &&
                                localStorageEnabled) {
                                this.storage.store(key, value);
                                return true;
                            }
                            else {
                                return false;
                            }
                        case StorageService.SESSIONSTORAGE:
                            if (key != null &&
                                key != '' &&
                                value != null &&
                                sessionStorageEnabled) {
                                this.sessionStorage.store(key, value);
                                return true;
                            }
                            else {
                                return false;
                            }
                        default:
                            return false;
                    }
                }
                else {
                    switch (true) {
                        case sessionStorageEnabled &&
                            key != null &&
                            key != '' &&
                            value != null:
                            this.sessionStorage.store(key, value);
                            return true;
                        case localStorageEnabled &&
                            key != null &&
                            key != '' &&
                            value != null:
                            this.storage.store(key, value);
                            return true;
                        case key != null && key != '' && value != null:
                            Cookie$1.set(key, value, CookieExpires, path1);
                            return true;
                        default:
                            return false;
                    }
                }
            }
            catch (Exception) {
                return false;
            }
        }
        else {
            return false;
        }
    }
    /**
     * The get method returns string if key is available in cookie or local or session Storage. Cookie takes precedence.
     * method: get
     *
     * param: key: string - Accepts the key for storing in Cookie or Storage
     * param: destination: string - Would accept any of these values(all in lower case)
     *          cookie
     *          localstorage
     *          sessionstorage
     * param: cipher: boolean -This is an optional paarameter that needs to be set to true if the value needs to be encrypted before setting . Default is false .
     */
    get(key, destination, cipher) {
        cipher =
            cipher == null || cipher == undefined || cipher == false ? false : true;
        let arrayValues = this.getArray(key, null, destination, cipher);
        if (arrayValues != null && arrayValues.length > 0) {
            let val = arrayValues[0];
            if (key === 'locale') { // DRR-13706 temp fix to ignore locale values after 'en/es_US'
                val = val.includes('es_US') ? 'es_US' : 'en_US';
            }
            return val;
        }
        return null;
    }
    /**
     * The get method returns string[] if key is available in cookie or local or session Storage. Cookie takes precedence.
     * method: get
     *
     * param: key: string - Accepts the key for storing in Cookie or Storage
     * param: seperator: string - Accepts the value seperator if present
     * param: destination: string - Would accept any of these values(all in lower case)
     *          cookie
     *          localstorage
     *          sessionstorage
     * param: cipher: boolean -This is an optional paarameter that needs to be set to true if the value needs to be encrypted before setting . Default is false .
     */
    getArray(key, seperator, destination, cipher) {
        let outputStringArray = null;
        let localStorageEnabled = this.featureDetectionService.detectFeature('localStorage');
        let sessionStorageEnabled = this.featureDetectionService.detectFeature('sessionStorage');
        cipher =
            cipher == null || cipher == undefined || cipher == false ? false : true;
        if (destination != null && destination != '') {
            switch (destination) {
                case StorageService.COOKIE:
                    let tempValue = this.decrypt(Cookie$1.get(key), cipher);
                    if (seperator != null) {
                        outputStringArray = tempValue ? tempValue.split(seperator) : null;
                    }
                    else {
                        outputStringArray = tempValue ? [tempValue] : null;
                    }
                    break;
                case StorageService.LOCALSTORAGE:
                    if (localStorageEnabled) {
                        let tempValue = this.decrypt(this.storage.retrieve(key), cipher);
                        if (seperator != null) {
                            outputStringArray = tempValue ? tempValue.split(seperator) : null;
                        }
                        else {
                            outputStringArray = tempValue ? [tempValue] : null;
                        }
                    }
                    else {
                        outputStringArray = null;
                    }
                    break;
                case StorageService.SESSIONSTORAGE:
                    if (sessionStorageEnabled) {
                        let tempValue = this.decrypt(this.sessionStorage.retrieve(key), cipher);
                        if (seperator != null) {
                            outputStringArray = tempValue ? tempValue.split(seperator) : null;
                        }
                        else {
                            outputStringArray = tempValue ? [tempValue] : null;
                        }
                    }
                    else {
                        outputStringArray = null;
                    }
                    break;
                default:
                    return null;
            }
        }
        else {
            switch (false) {
                case !Cookie$1.get(key):
                    let retValue = this.decrypt(Cookie$1.get(key), cipher);
                    if (seperator != null) {
                        outputStringArray = retValue ? retValue.split(seperator) : null;
                    }
                    else {
                        outputStringArray = retValue ? [retValue] : null;
                    }
                    break;
                case !this.storage.retrieve(key):
                    if (localStorageEnabled) {
                        let tempValue = this.decrypt(this.storage.retrieve(key), cipher);
                        if (seperator != null) {
                            outputStringArray = tempValue ? tempValue.split(seperator) : null;
                        }
                        else {
                            outputStringArray = tempValue ? [tempValue] : null;
                        }
                    }
                    else {
                        outputStringArray = null;
                    }
                    break;
                case !this.sessionStorage.retrieve(key):
                    if (sessionStorageEnabled) {
                        let tempValue = this.decrypt(this.sessionStorage.retrieve(key), cipher);
                        if (seperator != null) {
                            outputStringArray = tempValue ? tempValue.split(seperator) : null;
                        }
                        else {
                            outputStringArray = tempValue ? [tempValue] : null;
                        }
                    }
                    else {
                        outputStringArray = null;
                    }
                    break;
                default:
                    break;
            }
        }
        if (null == outputStringArray) {
            return null;
        }
        else {
            return this.checkForUnexpectedPattern(outputStringArray)
                ? outputStringArray
                : null;
        }
    }
    /**
     * The delete method returns boolean if keyis available in cookie or local or session Storage is deleted. Cookie takes precedence.
     * method: deleteCookieOrStorageValue
     *
     * param: key: string - Accepts the key for storing in Cookie or Storage
     * param: destination: string - Would accept any of these values(all in lower case)
     *          cookie
     *          localstorage
     *          sessionstorage
     * param: path: string - Thsi optional parameter to be sent only for cookie if it is stored in a diff path
     */
    delete(key, destination, path) {
        let returnValue = false;
        let path1 = path != null && path != '' ? path : '/';
        let localStorageEnabled = this.featureDetectionService.detectFeature('localStorage');
        let sessionStorageEnabled = this.featureDetectionService.detectFeature('sessionStorage');
        if (destination != null && destination != '') {
            switch (destination) {
                case StorageService.COOKIE:
                    Cookie$1.delete(key, path1);
                    return true;
                case StorageService.LOCALSTORAGE:
                    if (localStorageEnabled)
                        this.storage.clear(key);
                    return localStorageEnabled;
                case StorageService.SESSIONSTORAGE:
                    if (sessionStorageEnabled)
                        this.sessionStorage.clear(key);
                    return sessionStorageEnabled;
                default:
                    return false;
            }
        }
        else {
            switch (false) {
                case !Cookie$1.get(key):
                    Cookie$1.delete(key);
                    return true;
                case !this.storage.retrieve(key):
                    if (localStorageEnabled)
                        this.storage.clear(key);
                    return localStorageEnabled;
                case !this.sessionStorage.retrieve(key):
                    if (sessionStorageEnabled)
                        this.sessionStorage.clear(key);
                    return sessionStorageEnabled;
                default:
                    break;
            }
        }
        return returnValue;
    }
    /**
     * The checkForUnexpectedPattern method returns boolean if the value contains unexpected pattern which might lead to CSRF threat
     * method: checkForUnexpectedPattern
     *
     * param: valueArray: string[] - Accepts the value that needs to be checked before inserting
     * It also has a regex which would be taken as an input from app-config else the default would be the below one(hardcoded in this file like below)
     * const preDefinedregex = /\x00|<|\+ADw-|>|\+AD4-|%25|System[.][a-z]|javascript\s*:|[''].*[+\-*\/%=&|^~'']|\?.*:|\(\s*[a-z]{2,}\.[a-z]{2,}.*\)/g;
     */
    checkForUnexpectedPattern(valueArray) {
        let m;
        let returnValue = true;
        const regex = this.appConfigRegex != null && this.appConfigRegex != ''
            ? this.appConfigRegex
            : preDefinedregex;
        // Please use for loop instead of forEach. 
        // forEach loop is returning true if catch block executed. Expected output should be false.
        // for loop will return false if catch block executed which is expected output.
        valueArray.forEach(value => {
            try {
                // tslint:disable-next-line:no-conditional-assignment
                while ((m = regex.exec(value)) !== null) {
                    // This is necessary to avoid infinite loops with zero-width matches
                    if (m.index === regex.lastIndex) {
                        regex.lastIndex++;
                    }
                    // The result can be accessed through the `m`-variable.
                    m.forEach((match, groupIndex) => {
                        returnValue = false;
                    });
                }
            }
            catch (Exception) {
                return false;
            }
        });
        return returnValue;
    }
    /**
     * The encrypt method returns the value with or without encypting based on input.It uses crypto-JS API
     * method: encrypt
     *
     * param: value: string - Accepts the value that needs to be encrypted
     * param: cipher: boolean - Accepts an boolean which determines whether the value needs to be encrypted or not. Private method . So default is false
     */
    encrypt(value, cipher) {
        let returnValue;
        try {
            if (cipher) {
                let bytes = encrypt(value, this.SECERET_KEY);
                returnValue = bytes != null ? bytes.toString() : null;
            }
            else {
                returnValue = value;
            }
        }
        catch (Exception) {
            returnValue = value;
        }
        return returnValue;
    }
    /**
     * The decrypt method returns the value with or without decypting based on input.It uses crypto-JS API
     * method: decrypt
     *
     * param: value: string - Accepts the value that needs to be decrypted
     * param: cipher: boolean - Accepts an boolean which determines whether the value needs to be decrypted or not. Private method . So default is false
     */
    decrypt(value, cipher) {
        let returnValue;
        try {
            if (cipher) {
                let bytes = decrypt(value, this.SECERET_KEY);
                returnValue = bytes != null ? bytes.toString(utf8) : null;
            }
            else {
                returnValue = value;
            }
        }
        catch (Exception) {
            returnValue = value;
        }
        return returnValue;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: StorageService, deps: [{ token: AppConfigService }, { token: i2.LocalStorageService }, { token: i2.SessionStorageService }, { token: FeatureDetectionService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: StorageService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: StorageService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AppConfigService }, { type: i2.LocalStorageService }, { type: i2.SessionStorageService }, { type: FeatureDetectionService }] });

// SessionService
class SessionService {
    constructor(_appConfigService, _storageService, _httpClient) {
        this._appConfigService = _appConfigService;
        this._storageService = _storageService;
        this._httpClient = _httpClient;
    }
    isPresentFriendKey() {
        let isFriendKey = this._appConfigService.getSessionCookieInformation()
            .isFriendKey;
        let isFriend = this._storageService.get(isFriendKey);
        return isFriend !== null && isFriend !== 'false'; // TODO:  Friend key is never === 'false' as its values are 'Y' and 'N'
    }
    /**
     * The isVisitor method returns true if the cookie for the session does not exist or it exists but it does not contain the session tokens.
     * method: isVisitor
     */
    isVisitor() {
        return this.getSessionType() == null ? true : false;
    }
    /**
     * The isCustomer method returns true if all the OUath token is present for the customer. If OuathToken is not present then it would return false.
     * method: isCustomer
     */
    isCustomer() {
        return !!this.getSessionType() && !this.isPresentFriendKey();
    }
    /**
     * The isFriend method returns true if all the OUath token is present and also if the key for isFriend is available in storage
     * method: isFriend
     */
    isFriend() {
        return !!this.getSessionType() && this.isPresentFriendKey();
    }
    /**
     * The sessionType method returns type of session the customer has - (nga or legacy)
     * If there is no session available, it returns null
     * method: sessionType
     */
    getSessionType() {
        return this._sessionType;
    }
    setSessionType(val) {
        this._sessionType = val;
    }
    /**
     * The setFriendKey method inserts the friend key in the storage.
     * method: setFriendKey
     *
     * param: isFriend {boolean} If true it inserts the friend key, if false it deletes the friend key.
     */
    setFriendKey(isFriend) {
        let isFriendKey = this._appConfigService.getSessionCookieInformation()
            .isFriendKey;
        if (isFriend) {
            this._storageService.set(isFriendKey, 'true');
        }
        else {
            this._storageService.delete(isFriendKey);
        }
    }
    revokeAccessToken() {
        let _this = this;
        return new Promise((response, reject) => {
            let jwtResponse = _this.getKoreData('jwtResponse');
            let sessionId = _this.getKoreData('convSid');
            let url = `${_this.getKoreData('revoke_token_api')}`;
            if (jwtResponse && sessionId && url) {
                let auth = jwtResponse.auth;
                let header = new HttpHeaders()
                    .set("Content-Type", "application/json")
                    .set("Auth", `bearer ${auth.accessToken}`)
                    .set("sessionId", sessionId);
                _this._httpClient.post(url, {}, { headers: header }).subscribe({
                    next: (res) => {
                        response(res);
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
            }
            else {
                reject("No Auth Token Found");
            }
        });
    }
    getKoreData(type) {
        try {
            let chatData;
            if (sessionStorage !== undefined && sessionStorage !== null) {
                chatData = JSON.parse(sessionStorage.getItem("chatData"));
                if (type == 'revoke_token_api') {
                    let kore_data = chatData['kore_data'];
                    let current_account_config = kore_data.messagingApi;
                    let service_url = current_account_config['revoke_token_api'];
                    return service_url;
                }
                else {
                    let koreData = chatData[type];
                    return koreData;
                }
            }
            else {
                return null;
            }
        }
        catch (e) {
            return null;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SessionService, deps: [{ token: AppConfigService }, { token: StorageService }, { token: i3.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SessionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SessionService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AppConfigService }, { type: StorageService }, { type: i3.HttpClient }] });

class Personalization {
    constructor() {
        // Below fields are extracted from ECM Cookie.
        this.isSPFMigrated = false;
        this.hasChecking = false;
        this.hasCheckingPlus = false;
        this.hasCreditCard = false;
        this.hasMortgage = false;
        this.hasSavings = false;
        this.isBrokerage = false;
        this.isCitiblue = true;
        this.isCitigold = false;
        this.isCitipriority = false;
        this.isRELOnlyUser = false;
        this.isPBG = false;
        // Below fields are extracted from session storage
        this.isMember = false;
        this.isFriend = false;
        this.isVisitor = true;
        this.isAACard = false;
        this.isHiltonCard = false;
        this.hasAAdvantageCard = false;
        this.language = 'en';
        this.isInvestor = false;
        this.isBanker = false;
        this.isATTCard = false;
        this.hasAAdvantageCardNew = false;
        this.isNewATnTPID = false;
        this.isCinlessUser = false;
        this.isBusinessOnly = false;
        this.customerType = '';
        this.isCitiPrivateBanking = false;
        this.packageInd = '0';
        this.residentialState = '';
        this.isGEB = false;
        this.isIPB = false;
        this.isCPC = false;
    }
}
class User {
    constructor() {
        this.personalization = new Personalization();
    }
}

class UserPersonalizationService extends EcmCookieService {
    constructor(_cookieService, _window, _platformId) {
        super(_cookieService, _platformId);
        this._cookieService = _cookieService;
        this._window = _window;
        this._platformId = _platformId;
        this.user = new User();
        this.initPersonlizationData();
    }
    initPersonlizationData() {
        let sessionParams = this._window
            ? JSON.parse(this._window.sessionStorage.getItem('sessionPostBrandingStorage'))
            : null;
        if (sessionParams == null) {
            this.getUserPrefFromCookie();
        }
        else {
            this.getUserPrefFromSessionStorage();
        }
    }
    getUserPrefFromCookie() {
        let propVal = '';
        for (const prop of Object.keys(this.user.personalization)) {
            propVal = this.getValueByName(prop);
            if (propVal) {
                if (propVal === 'Y') {
                    this.user.personalization[prop] = true;
                }
                else if (propVal === 'N') {
                    this.user.personalization[prop] = false;
                }
                else {
                    this.user.personalization[prop] = propVal;
                }
            }
        }
    }
    getUserPrefFromSessionStorage() {
        let sessionParams = this._window
            ? this._window.sessionStorage.getItem('sessionPostBrandingStorage')
            : null;
        let propVal = '';
        if (sessionParams !== null) {
            try {
                sessionParams = JSON.parse(sessionParams);
                for (const prop of Object.keys(this.user.personalization)) {
                    if (sessionParams !== null && sessionParams[prop]) {
                        propVal = sessionParams[prop];
                        if (propVal) {
                            if (propVal === 'Y') {
                                this.user.personalization[prop] = true;
                            }
                            else if (propVal === 'N' || propVal == 'null') {
                                this.user.personalization[prop] = false;
                            }
                            else {
                                this.user.personalization[prop] = propVal;
                            }
                        }
                    }
                }
            }
            catch (e) { }
        }
    }
    getPersonalizationData() {
        return this.user;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: UserPersonalizationService, deps: [{ token: CookieService }, { token: WINDOW }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: UserPersonalizationService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: UserPersonalizationService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: CookieService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });

class NavigationHistoryService {
    constructor(router) {
        this.router = router;
        this.previousUrl = null;
        this.currentUrl = null;
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.previousUrl = this.currentUrl;
                this.currentUrl = event.urlAfterRedirects;
            }
        });
    }
    /** Returns true on a fresh reload or back navigation, false otherwise */
    isFreshLoadOrBack() {
        return this.previousUrl === null;
    }
    detectNavigationType() {
        try {
            if (performance.getEntriesByType("navigation").length > 0) {
                const navType = performance.getEntriesByType("navigation")[0].type;
                if (navType === 'reload') {
                    return false;
                }
                else if (navType === 'back_forward' && this.isFreshLoadOrBack()) {
                    return true;
                }
            }
        }
        catch (err) {
            console.log("Unable to detect Navigation Type");
        }
        return false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: NavigationHistoryService, deps: [{ token: i3$1.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: NavigationHistoryService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: NavigationHistoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i3$1.Router }] });

const NAVIGATOR$1 = new InjectionToken('NavigatorToken');
/* Define abstract class for obtaining reference to the global Navigator object. */
class NavigatorRef {
    get nativeNavigator() {
        throw new Error('Not implemented.');
    }
}
/* Define class that implements the abstract class and returns the native Navigator object. */
class BrowserNavigatorRef extends NavigatorRef {
    constructor(platformId) {
        super();
        this.platformId = platformId;
    }
    get nativeNavigator() {
        if (isPlatformBrowser(this.platformId)) {
            return navigator;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BrowserNavigatorRef, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BrowserNavigatorRef, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BrowserNavigatorRef, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });
/* Create an factory function that returns the native Navigator object. */
function navigatorFactory$1(browserNavigatorRef, platformId) {
    if (isPlatformBrowser(platformId)) {
        return browserNavigatorRef.nativeNavigator;
    }
    return null;
}
const browserNavigatorProvider = {
    provide: NavigatorRef,
    useClass: BrowserNavigatorRef
};
/* Create an injectable provider that uses the NavigatorFactory function for returning the native Navigator object. */
const NavigatorProvider = {
    provide: NAVIGATOR$1,
    useFactory: navigatorFactory$1,
    deps: [NavigatorRef, PLATFORM_ID]
};
/* Create an array of providers. */
const NAVIGATOR_PROVIDER$1 = [browserNavigatorProvider, NavigatorProvider];

class PostSessionService {
    // Constructor
    constructor(_window) {
        this._window = _window;
        // Whether or not the sessionStorageNotFoundError has been emitted yet or not
        this.storageErrorEmitted = false;
        // Warning to show if the session storage is not found or errors out as invalid JSON
        this.storageNotFoundError = 'Post Session Service: No session storage object found or session storage object was invalid JSON. Please check that sessionPostBrandingStorage exists in session storage and is valid JSON.';
        // Warning to show if the item requested was not found in session storage
        this.itemNotFoundError = 'Post Session Service: The post session storage item you requested does not exist in the session storage object.';
        this.init();
    }
    init() {
        if (!(this._window && this._window.sessionStorage))
            return;
        // Check for a session post branding storage object in session storage
        let sessionStorage = this._window.sessionStorage.getItem('sessionPostBrandingStorage');
        // If we found a session storage object
        if (sessionStorage) {
            // See if we can eval the object into JSON
            try {
                // Try to parse the session storage object
                sessionStorage = JSON.parse(sessionStorage);
                // If we found a session storage object
                if (sessionStorage) {
                    // Set it on the service
                    this.sessionStorage = sessionStorage;
                }
            }
            catch (err) {
                // If we are in dev mode
                if (isDevMode && !this.storageErrorEmitted) {
                    // Warn that we did not find a session storage object
                    //   console.warn(this.storageNotFoundError);
                    this.storageErrorEmitted = true;
                }
            }
        }
    }
    // Gets the full session storage object
    getSessionStorageObject() {
        return this.sessionStorage;
    }
    // Gets an item from session storage by name
    getItem(itemName) {
        // Check if we have a session storage object and the item exists
        let item = this.sessionStorage &&
            typeof this.sessionStorage[itemName] !== 'undefined'
            ? this.sessionStorage[itemName]
            : false;
        // If the name request is defined in the session storage object
        if (this.sessionStorage &&
            typeof this.sessionStorage[itemName] !== 'undefined') {
            // Return the item
            return this.sessionStorage[itemName];
        }
        else {
            // If this is dev mode
            if (isDevMode) {
                // If there isnt a session storage item
                if (!this.sessionStorage && !this.storageErrorEmitted) {
                    //   console.warn(this.storageNotFoundError);
                    this.storageErrorEmitted = true;
                    // If the request item does not exist
                }
                else if (!item && !this.storageErrorEmitted) {
                    //  console.warn(this.itemNotFoundError, 'Item Requested:', itemName);
                }
            }
            // Return false
            return false;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PostSessionService, deps: [{ token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PostSessionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PostSessionService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }] });

class JSLoaderService {
    constructor(_document) {
        this._document = _document;
    }
    /* Dynamically load a JS file by creating a HTML element and appending it after the body */
    loadJS(url) {
        let node = this._document.createElement('script');
        node.src = url;
        node.type = 'text/javascript';
        node.async = true;
        node.charset = 'utf-8';
        this._document.getElementsByTagName('body')[0].appendChild(node);
    }
    /* Dynamically load a JS file and add a callback for after it is loaded */
    loadJSCallback(url, callback) {
        // Create the script tag and add it to the document
        const slib = this._document.createElement('script');
        slib.type = 'text/javascript';
        slib.async = true;
        slib.src = url;
        let cdone = false;
        const head = this._document.getElementsByTagName('head')[0];
        slib.onload = slib.onreadystatechange = function () {
            if (!cdone &&
                (!this.readyState ||
                    this.readyState === 'loaded' ||
                    this.readyState === 'complete')) {
                cdone = true;
                if (callback) {
                    callback();
                }
                // Handle memory leak in IE
                slib.onload = slib.onreadystatechange = null;
                if (head && slib.parentNode) {
                    head.removeChild(slib);
                }
            }
        };
        if (callback) {
            slib.onload = callback;
        }
        const s = this._document.getElementsByTagName('script')[0];
        s.parentNode.insertBefore(slib, s);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: JSLoaderService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: JSLoaderService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: JSLoaderService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class ScriptLoadService {
    // Constructor
    constructor(_document) {
        this._document = _document;
        // Variables
        this.scripts = [];
    }
    // Loads a script from a url
    loadScript(url) {
        // Return the promise we will resolve
        return new Promise((resolve, reject) => {
            // Check if the script has already been loaded by this service
            if (this.scripts.includes(url)) {
                // Resolve as already loaded if it has
                resolve({ script: name, loaded: true, status: 'Already Loaded' });
            }
            else {
                // Create the script element
                let script = this._document.createElement('script');
                script.type = 'text/javascript';
                script.src = url;
                // Onload for IE
                if (script.readyState) {
                    // On change of the ready state
                    script.onreadystatechange = () => {
                        // If the state was loaded or complete
                        if (script.readyState === 'loaded' ||
                            script.readyState === 'complete') {
                            // Remove the ready state change listener
                            script.onreadystatechange = null;
                            // Update the service so it knows this script has already been loaded
                            this.scripts.push(url);
                            // Resolve the promise to let the requester know their script was loaded
                            resolve({ script: url, loaded: true, status: 'Loaded' });
                        }
                    };
                }
                else {
                    // Listen for the onload event for the new script tag
                    script.onload = () => {
                        /// Update the service so it knows this script has already been loaded
                        this.scripts.push(url);
                        // Resolve the promise to let the requester know their script was loaded
                        resolve({ script: name, loaded: true, status: 'Loaded' });
                    };
                }
                // Catch any errors loading the script and report them back to the requester by resolving the promise
                script.onerror = (error) => resolve({ script: name, loaded: false, status: 'Load Failed' });
                // Append the new script to the head tag to kick everything off
                this._document.getElementsByTagName('head')[0].appendChild(script);
            }
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ScriptLoadService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ScriptLoadService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ScriptLoadService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class IdleTimeRouterComponent {
    constructor(_storageService, _appConfigService, _activatedRoute) {
        this._storageService = _storageService;
        this._appConfigService = _appConfigService;
        this._activatedRoute = _activatedRoute;
        this.userActive = false;
        this.routeToOrigin = null;
        // this._storageService.set('routeToOrigin','TY',StorageService.SESSIONSTORAGE);
    }
    ngOnInit() {
        // Get the routeToOrigin attribute from the sesion storage
        this.routeToOrigin = this._storageService.get('routeToOrigin', StorageService.SESSIONSTORAGE);
        // if routeToOrigin attribute is not available in the session storage fetch it from query param
        if (!this.routeToOrigin) {
            this.routeToOrigin = this._activatedRoute.snapshot.queryParams['routeToOrigin'];
        }
        if (this.routeToOrigin) {
            // Attaching click event listener
            document.addEventListener('click', () => {
                // flag is made to true if there are any click events on the screen
                this.userActive = true;
            });
            // Fetch the routeAfter time from the configuration file
            let idleTimeRouterConfig = this._appConfigService.getConfig('idleTimeRouterService');
            if (idleTimeRouterConfig) {
                let routeAfter = idleTimeRouterConfig.routeAfter * 1000;
                // observerable timer that observers  specified interval
                this._IdleTimeObserverable = timer(routeAfter, routeAfter);
                let self = this;
                this._IdleTimeSubscription = this._IdleTimeObserverable.subscribe(this._IdleTimeObserver);
                // Define oberver
                this._IdleTimeObserver = {
                    next: x => {
                        // if the user is not active route the user to the origin page
                        if (!this.userActive) {
                            self.routeToOriginApp();
                        }
                        // reset the active flag for next observation
                        this.userActive = false;
                    },
                    error: err => {
                        console.log('idle Time observer error');
                    },
                    complete: () => {
                        console.log('idle Time observer complete');
                    }
                };
                // subscribe to the idle time observable
            }
            else {
                console.log('idle Time observer not configured');
            }
        }
        else {
            console.log('idle Time observer not configured');
        }
    }
    /*
       This function routes the user to the origin application
    */
    routeToOriginApp() {
        // subscribe
        this._IdleTimeSubscription.unsubscribe();
        let routeUrl = null;
        // read the routing configuration from the app config
        let idleTimeRouterConfig = this._appConfigService.getConfig('idleTimeRouterService');
        let routingInfo = idleTimeRouterConfig.routingInfo;
        // Check if route to origin value is valid
        if (this.routeToOrigin) {
            let i = 0;
            // iterate through the routing info object to find the matching app name to get the route url
            if (routingInfo) {
                for (i = 0; i < routingInfo.length; i++) {
                    if (routingInfo[i]) {
                        if (this.routeToOrigin == routingInfo[i].appName) {
                            routeUrl = routingInfo[i].routeUrl;
                        }
                    }
                }
            }
            // if no route url matches, redirect to default route
            if (!routeUrl) {
                routeUrl = idleTimeRouterConfig.defaultRoute;
            }
            window.location.href = routeUrl;
        }
        else {
            console.log('No route to Origin is present');
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: IdleTimeRouterComponent, deps: [{ token: StorageService }, { token: AppConfigService }, { token: i3$1.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: IdleTimeRouterComponent, isStandalone: false, selector: "idleTime-router", ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: IdleTimeRouterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'idleTime-router',
                    standalone: false,
                    template: ``
                }]
        }], ctorParameters: () => [{ type: StorageService }, { type: AppConfigService }, { type: i3$1.ActivatedRoute }] });

class IdleTimeRouterModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: IdleTimeRouterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: IdleTimeRouterModule, declarations: [IdleTimeRouterComponent], exports: [IdleTimeRouterComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: IdleTimeRouterModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: IdleTimeRouterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    declarations: [IdleTimeRouterComponent],
                    exports: [IdleTimeRouterComponent]
                }]
        }] });

class RouterRedirectGuard {
    constructor(platformId, _appConfig) {
        this.platformId = platformId;
        this._appConfig = _appConfig;
    }
    canActivate(childRoute, state) {
        const isBrowser = isPlatformBrowser(this.platformId);
        if (!isBrowser)
            return true;
        window.location.href = window.location.href.includes('/US/ag')
            ? window.location.href.replace('/US/ag', '')
            : this._appConfig.getcsrHostName() + `/US/ag${state.url.toString()}`;
        return false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: RouterRedirectGuard, deps: [{ token: PLATFORM_ID }, { token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: RouterRedirectGuard }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: RouterRedirectGuard, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: AppConfigService }] });

class CitiAppConfigService {
    constructor(_injector) {
        this._injector = _injector;
    }
    getConfiguration(className) {
        // tslint:disable-next-line: deprecation
        const oIConfigService = this._injector.get(className);
        return oIConfigService.getConfig();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CitiAppConfigService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CitiAppConfigService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CitiAppConfigService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.Injector }] });

class CitiAppConfig {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CitiAppConfig, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: CitiAppConfig }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CitiAppConfig, providers: [CitiAppConfigService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CitiAppConfig, decorators: [{
            type: NgModule,
            args: [{
                    providers: [CitiAppConfigService]
                }]
        }] });

class LoggingService {
    constructor(appConfig) {
        this.appConfig = appConfig;
        this.loggingEnabled = this.loggingEligible();
    }
    log(...args) {
        if (this.loggingEnabled) {
            console.log(args);
        }
    }
    loggingEligible() {
        if (this.appConfig.getEnvironment() != 'PROD' && !!console) {
            return true;
        }
        return false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: LoggingService, deps: [{ token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: LoggingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: LoggingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: AppConfigService }] });

// cbol-core DataCollectorService can able to share the data through out the applciation
class DataCollectorService {
    constructor() {
        this.dataCollector = {};
    }
    // add data if its type is an object
    addDataCollector(data) {
        if (data instanceof Object) {
            Object.assign(this.dataCollector, data);
        }
    }
    // get data Object
    getDataCollector() {
        return this.dataCollector;
    }
    // reset data Object
    resetDataCollector() {
        this.dataCollector = {};
    }
    // remove data based on the key string
    removeDataCollector(key) {
        if (!key) {
            return undefined;
        }
        if (this.dataCollector.hasOwnProperty(key)) {
            delete this.dataCollector[key];
            return true;
        }
        return false; // Return false if the key does not exist
    }
    // get data based on the key string
    getDataForKey(key) {
        if (key !== null && key !== undefined) {
            return this.dataCollector[key];
        }
        return undefined;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: DataCollectorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: DataCollectorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: DataCollectorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

const TAGGING_CONSTANT = {
    "544cTopNavNavigationItemClicked": {
        "test_id": "544 c Top Nav - Navigation Item Clicked",
        "action_type": "event",
        "app_hierarchy": "enable|top nav|navigation item clicked",
        "event": "navigation_item_clicked"
    },
    "324ad0SearchPanelHowCanWeHelpShow": {
        "test_id": "324 ad0 Search Panel - How Can We Help - Show",
        "action_type": "event",
        "app_hierarchy": "public|search panel|how can we help|show"
    },
    "324ad1SearchPanelHowCanWeHelpHide": {
        "test_id": "324 ad1 Search Panel - How Can We Help - Hide",
        "action_type": "event",
        "app_hierarchy": "public|search panel|how can we help|show"
    },
    "61aCreditCardServicesLanding": {
        "action_type": "page view",
        "app_hierarchy": "services|credit card services|landing",
        "test_id": "61 a Credit Card Services Landing",
        "event": "navigation_item_displayed"
    },
    "61a2CreditCardServiceLanding2ndLevelNavigationLinkClicked": {
        "action_type": "event",
        "app_hierarchy": "services|credit card services|landing:2nd level navigation link clicked",
        "test_id": "61 a2 Credit Card Services Landing - 2nd Level Navigation Link Clicked",
        "event": "navigation_item_clicked"
    },
    "62aBankingServicesLanding": {
        "action_type": "page view",
        "app_hierarchy": "services|banking services|landing",
        "test_id": "62 a Banking Services Landing",
        "event": "navigation_item_displayed"
    },
    "62a2BankingServicesLanding2ndLevelNavigationLinkClicked": {
        "action_type": "event",
        "app_hierarchy": "services|banking services|landing:2nd level navigation link clicked",
        "test_id": "62 a2 Banking Services Landing - 2nd Level Navigation Link Clicked",
        "event": "navigation_item_clicked"
    },
    "543aFooterLinkClicked": {
        "action_type": "event",
        "test_id": "543 a Footer - Link Clicked",
        "event": "footer_link_click",
    },
    // Start Citi Mobile Modal Tagging
    /**** Modal Displayed *****/
    "381hDashboard2Modal": {
        "action_type": 'page view',
        "app_hierarchy": 'enable|custom smart banner|modal',
        "form_type": 'Get it Done with Citi Mobile|Over 15 million',
        "test_id": '648 b Enable - Custom Smart Banner - Modal'
    },
    /**** Modal Close Icon *****/
    "381h2Dashboard2ModalInteractionClick": {
        "action_type": 'event',
        "app_hierarchy": 'enable|custom smart banner|modal:cta clicked',
        "event": 'custom_link_click',
        "page_inter": 'Close',
        "form_type": 'Citi Mobile|Get it Done with Citi Mobile',
        "test_id": '648 b2 Enable - Custom Smart Banner - Modal - CTA Clicked'
    },
    /**** Modal Use The App Button *****/
    "381h3Dashboard2ModalCTAClick": {
        "action_type": 'event',
        "app_hierarchy": 'enable|custom smart banner|modal:cta clicked',
        "event": 'custom_link_click',
        "link_name": 'Use the App',
        "form_type": 'Citi Mobile|Get it Done with Citi Mobile',
        "test_id": '648 b2 Enable - Custom Smart Banner - Modal - CTA Clicked'
    },
    /**** Modal Not Now Button *****/
    "381h3Dashboard2ModalCTABtnlick": {
        "action_type": 'event',
        "app_hierarchy": 'enable|custom smart banner|modal:cta clicked',
        "event": 'custom_link_click',
        "link_name": 'Not Now',
        "form_type": 'Citi Mobile|Get it Done with Citi Mobile',
        "test_id": '648 b2 Enable - Custom Smart Banner - Modal - CTA Clicked'
    },
    // End Citi Mobile Modal Tagging
    // Start Smart Banner Tagging
    /**** Smart Banner Displayed *****/
    "648aEnableCustomSmartBannerDisplayed": {
        "action_type": 'event',
        "event": 'dynamic_content_view',
        "app_hierarchy": 'enable|custom smart banner|displayed',
        "form_type": 'Citi Mobile',
        "test_id": '648 a Enable - Custom Smart Banner - Displayed'
    },
    /**** Smart Banner Use CTA Clicked *****/
    "648a2EnableCustomSmartBannerCTAClicked": {
        "action_type": 'event',
        "event": 'custom_link_click',
        "app_hierarchy": 'enable|custom smart banner|cta clicked',
        "link_name": 'Go',
        "form_type": 'Citi Mobile',
        "test_id": '648 a2 Enable - Custom Smart Banner - CTA Clicked'
    },
    /**** Smart Banner Close Icon CTA Clicked *****/
    "648a2EnableCustomSmartBannerCTACloseIconClicked": {
        "action_type": 'event',
        "event": 'custom_link_click',
        "app_hierarchy": 'enable|custom smart banner|cta clicked',
        "link_name": 'Close',
        "form_type": 'Citi Mobile',
        "test_id": '648 a2 Enable - Custom Smart Banner - CTA Clicked'
    },
    // End Smart Banner Tagging Tagging
    /**** Global Error Handler *****/
    "546aEnableGlobalError": {
        "action_type": "event",
        "test_id": "546 a Enable - Global Error",
        "event": "custom_error",
        "app_hierarchy": 'public|enable|global|error'
    },
    // End Global Error Handler
    /**** Browser support and troubleshooting page Tagging *****/
    "655BrowserWarningPage": {
        "action_type": "page view",
        "test_id": "655 Browser Warning Page",
        "app_hierarchy": "software|browser warning|landing"
    },
    /**** Browser support and troubleshooting CTA click *****/
    "655BrowserWarningCTAClicks": {
        "action_type": "event",
        "test_id": "655 Browser Warning CTA Clicks",
        "event": "custom_link_click",
        "app_hierarchy": "software|browser warning|landing:cta click"
    },
    /**** Session timeout Modal *****/
    "654SessionTimeoutPage": {
        "action_type": "page view",
        "test_id": "654 Session Timeout Page",
        "event": "custom_error",
        "app_hierarchy": "signout|session timeout|landing"
    },
    /**** Session timeout Modal Close/Continue/End *****/
    "654_bSessionTimeoutCTAClick": {
        "action_type": "event",
        "test_id": "654_b  Session Timeout CTA Click",
        "event": "custom_link_click",
        "app_hierarchy": "signout|session timeout|landing:cta click"
    },
    /**** Flex Pay T&C page Tagging *****/
    "366_4aFlexPayPartnerTermsandConditionsLanding": {
        "action_type": "page view",
        "test_id": "366_4 a Flex Pay - Partner Terms and Conditions - Landing",
        "app_hierarchy": "citi flex pay|partner terms and conditions|landing"
    },
    /**** Error Page  Tagging *****/
    "656aGerenralErrorPageSecure": {
        "action_type": "page view",
        "test_id": "656 a Gerenral Error Page Secure",
        "app_hierarchy": "account|general error|landing",
        "event": "cust_error"
    },
    /**** Error Page  Tagging *****/
    "656bGerenralErrorPageSecureCTAClicks": {
        "action_type": "event",
        "test_id": "656 b Gerenral Error Page Secure CTA Clicks"
    },
    /**** Error Page  Tagging *****/
    "656cGerenralErrorPagePublic": {
        "action_type": "page view",
        "test_id": "656 c Gerenral Error Page Public",
        "app_hierarchy": "account|general error|landing",
        "event": "cust_error"
    },
    /**** Error Page  Tagging *****/
    "656dGerenralErrorPagePublicCTAClicks": {
        "action_type": "event",
        "test_id": "656 d Gerenral Error Page Public CTA Clicks"
    },
    "674aCareersHomePage": {
        "action_type": "page view",
        "app_hierarchy": "careers|careerhome|landing",
        "test_id": "674 a Careers Home Page"
    },
    "674bCareersHomePageCTAClicks": {
        "action_type": "event",
        "app_hierarchy": "public|careers|careerhome|landing:cta click",
        "event": "custom_link_click",
        "test_id": "674 b Careers Home Page:CTA CLICK"
    },
    /**** Error Page Tagging - Bad Accounts *****/
    "656_1BadAccountsErrorPage": {
        "action_type": "page view",
        "test_id": "656_1 Bad Accounts Error Page",
        "app_hierarchy": "bad account|general error|landing",
        "event": "cust_error"
    },
    /**** Unauthorized Page Tagging ****/
    "673UnauthorizedLandingPage": {
        "action_type": "page view",
        "test_id": "673 Unauthorized Landing Page",
        "app_hierarchy": "unauthorized|redirect|landing"
    }
};

class TaggingService {
    triggerPageTagging(nonIcmsId, isPostLogin, staticParam, options) {
        try {
            if (window) {
                let taggingDataLayer = window.taggingDataLayer;
                if (taggingDataLayer) {
                    let app_hierarchy_prefix = isPostLogin ? "secure|" : "public|";
                    let app_hierarchy = app_hierarchy_prefix + TAGGING_CONSTANT[staticParam]["app_hierarchy"];
                    let topNavObject = {
                        app_hierarchy: app_hierarchy
                    };
                    if (options?.dynamicContent.navigation_item_click_attribution) {
                        options.dynamicContent.navigation_item_click_attribution = app_hierarchy + '~' + options.dynamicContent.navigation_items;
                    }
                    const contentParam = options && options.dynamicContent ?
                        { ...TAGGING_CONSTANT[staticParam], ...options.dynamicContent, ...topNavObject } : { ...TAGGING_CONSTANT[staticParam], ...topNavObject };
                    staticParam ? taggingDataLayer.applicationData(nonIcmsId, contentParam) : taggingDataLayer.applicationData(TAGGING_CONSTANT[nonIcmsId]);
                }
            }
        }
        catch (e) {
            console.log('Error in Tagging');
        }
    }
    triggerEventTagging(nonIcmsId, staticParam, options) {
        try {
            if (window) {
                let taggingDataLayer = window.taggingDataLayer;
                if (taggingDataLayer) {
                    const contentParam = options && options.dynamicContent ?
                        { ...TAGGING_CONSTANT[staticParam], ...options.dynamicContent } : { ...TAGGING_CONSTANT[staticParam] };
                    staticParam ? taggingDataLayer.updateEventData(nonIcmsId, contentParam) : taggingDataLayer.updateEventData(TAGGING_CONSTANT[nonIcmsId]);
                }
            }
        }
        catch (e) {
            console.log('Error in Tagging');
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TaggingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TaggingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TaggingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

// MessagingService
class MessagingService {
    constructor(_appConfigService) {
        this._appConfigService = _appConfigService;
        this.messageEvent = {};
        this.getMessage = new BehaviorSubject('');
    }
    /**
     * The sendMessage method is called for sending and receiving data.
     * method: sendMessage
     */
    sendMessage(eventName, type, source) {
        let config = this._appConfigService.getMessageConfig();
        let origin = config ? config['messageOrigin'] : source;
        let appId = config ? config['messageAppId'] : '';
        this.messageEvent.eventName = eventName + '_Init';
        this.messageEvent.appId = appId;
        //receive message listener
        window.addEventListener('message', event => {
            if (typeof event.data === 'string' && event.data.includes(eventName)) {
                const parsedData = JSON.parse(event.data);
                if (parsedData.eventName === eventName + '_Response' && event.origin === origin) {
                    console.log('## CONTAINER MESSAGING = ', event);
                    //emitting the message
                    this.getMessage.next(event.data);
                }
            }
        });
        //postMessage
        let windowFrameStyle = (type == 'iframe') ? window.top : (type == 'opener') ? window.opener : (type == 'parent') ? window.parent : window.top;
        windowFrameStyle.postMessage(JSON.stringify(this.messageEvent), origin);
    }
    /**
     * The receiveMessage method is called alone for receiving data.
     * method: receiveMessage
     */
    receiveMessage(data, origin) {
        window.addEventListener('message', event => {
            if ((typeof event.data === 'string' && event.data.includes(data)) || event.origin === origin) {
                const parsedData = JSON.parse(event.data);
                console.log('## CONTAINER MESSAGING = ', parsedData);
                //emitting the message
                this.getMessage.next(parsedData);
            }
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MessagingService, deps: [{ token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MessagingService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MessagingService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AppConfigService }] });

// GlobalErrorhandler
class GlobalErrorHandlerService {
    constructor(injector, _taggingService, _appConfigService, _router) {
        this.injector = injector;
        this._taggingService = _taggingService;
        this._appConfigService = _appConfigService;
        this._router = _router;
        _router.events
            .pipe(filter(event => event instanceof NavigationStart))
            .subscribe(event => {
            console.log('Accessed Route', event['url']);
            this.accessedRoute = event['url'];
        });
    }
    /**
     * The handleError method is used for capturing different errors globally and perform set of actions as needed.
     * method: handleError
     */
    handleError(error) {
        console.error(error);
        if (error && error.message && (error.message.includes('Loading chunk') || error.message.includes('Failed to fetch dynamically imported module'))) {
            // Capture the specific chunk failed to load (if available)
            const regex = /error:\s*([^\s)]+)/;
            const chunkFailedMessage = error.message.match(regex);
            const httpUrl = error.message.match(/https?:\/\/[^\s)"']+/i);
            const chunkFailed = httpUrl?.[0] || (chunkFailedMessage
                ? chunkFailedMessage[1]
                : error.message);
            // Determine the type of error (network, missing file, etc.)
            let errorType = error.message.includes() ? 'TypeError' : 'ChunkLoadError';
            if (error.message.includes('missing')) {
                errorType = 'Missing file';
            }
            else if (error.name === 'NetworkError') {
                errorType = 'Network error';
            }
            const titleString = `${errorType}, Chunk: ${chunkFailed}`;
            this.errorHandlerTagging(titleString);
            window.location.href = this._appConfigService.getcsrHostName() + '/US/ag' + this.accessedRoute;
        }
    }
    /**
     * @Service: Global Error Handler Service
     * @Section: error handler tagging
     * @Description: This method is used for calling tagging service on chunk load error as per specifications
     */
    errorHandlerTagging(custError) {
        this._taggingService.triggerEventTagging('nonicms_content', '546aEnableGlobalError', {
            dynamicContent: {
                cust_error: custError
            }
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: GlobalErrorHandlerService, deps: [{ token: i0.Injector }, { token: TaggingService }, { token: AppConfigService }, { token: i3$1.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: GlobalErrorHandlerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: GlobalErrorHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.Injector }, { type: TaggingService }, { type: AppConfigService }, { type: i3$1.Router }] });

// PostSignoffService
class PostSignoffService {
    constructor(_window, _appConfigService) {
        this._window = _window;
        this._appConfigService = _appConfigService;
    }
    clearCookiesAndSessionStorage(type) {
        this.postNgaCoExist = Cookie.get('NGACoExistenceCookie');
        if (this.postNgaCoExist) {
            this.isNgaLogin = false;
        }
        else {
            this.isNgaLogin = true;
        }
        sessionStorage.setItem('isNgaLogin', JSON.stringify(this.isNgaLogin));
        const cookiesToDelete = [
            'SessionRecoveryCookie',
            'isSecondaryLogin',
            'DVAModePersist',
            'zeroPredictionToggle',
            'NGACoExistenceCookie',
            'NGAKeepAOAlive',
            'client_id',
            'apimBaseUrl',
            'appVersion',
            'businessCode',
            'channelId',
            'countryCode',
            'tenantId',
            'bcsid',
            'qrCodeLogin',
            'ngaDeepdrop',
            'interstitialSeen',
            'ts',
            'citi_authorization',
            'cbol_timeout',
            'timeout_modal_state'
        ];
        const sessionStorageToDelete = [
            'NgaKATimeInterVal',
            'wealthDropDownSelection',
            'sessionPostBrandingStorage',
            'sessionJFP_TOKEN',
            'sessionJFP_ID',
            'siteCatUserProp',
            'helpCenterFlags',
            'searchPanelOpen',
            'wealthWhatsNextTileDismissed',
            'partnerId',
            'deepDropDLP',
            'OFFER_interstitial',
            'KYC_SUBMISSION_STATUS',
            'KYC_interstitial',
            'SETTLE_AGENT',
            'firstDisplay',
            'suspectedCards',
            'currentCardIndex',
            'selectedTrans',
            'completedDetectedCardIndex',
            'visitedOffer',
            'nextBestActions',
            'popupVisited',
            'chatData',
            'iraData',
            'iraDefaultContent',
            'mopData',
            'alreadyRedirected',
            'citi_authorization',
            'searchPanelSettings',
            'hideConsolidationTile',
            'quickactionprefillvalue',
            'setAuthCode',
            'isTierAccelerationEnrolled',
            'postInterstitialTargetUri',
            'partnerLogin',
            'cccSSOUniqueID',
            'cccHRTSet',
            'cccUpcomingMeet',
            'conversionOverlayShown',
            'nextRoute',
            'smartBanner',
            'rmfaBio',
            'getAllCardData',
            'sharedAccount',
            'userSessionActive'
        ];
        if (!type || type === 'cookies') {
            for (const cookieKey of cookiesToDelete) {
                console.log(Cookie.get(cookieKey));
                Cookie.delete(cookieKey, '/');
                Cookie.delete(cookieKey, '/', '.citi.com');
            }
        }
        if (!type || type === 'session') {
            for (const sessionStorageKey of sessionStorageToDelete) {
                console.log(sessionStorage.getItem(sessionStorageKey));
                sessionStorage.removeItem(sessionStorageKey);
                console.log(sessionStorage.getItem(sessionStorageKey));
            }
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PostSignoffService, deps: [{ token: WINDOW }, { token: AppConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PostSignoffService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PostSignoffService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: AppConfigService }] });

class ModalSharedService {
    constructor() {
        this.dialogStateSource = new Subject();
        this.dialogState$ = this.dialogStateSource.asObservable();
    }
    openDialog() {
        this.dialogStateSource.next(true);
    }
    closeDialog() {
        this.dialogStateSource.next(false);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ModalSharedService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ModalSharedService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ModalSharedService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class QaCitiMobileModal {
    constructor() {
        // #region // ---------- Properties ---------- //
        /**
         * @property showCitiMobile
         * @description Whether cds Modal open or not
         */
        this.showCitiMobile = false;
        /**
         * @property useAppNavigate
         * @description emits when the use app button is clicked
         * @twoway false.
         */
        this.useAppNavigate = new EventEmitter();
        /**
         * @property notNowCloseModal
         * @description emits when the launchQR Code Mobile button is clicked
         * @twoway false.
         */
        this.notNowCloseModal = new EventEmitter();
        /**
         * @property emitTaggingForQRcodeCTA
         * @description emits when the QRcodeCTA button is clicked
         * @twoway false.
         */
        this.emitTaggingForQRcodeCTA = new EventEmitter();
        /**
       * @property closeCitiModalIcon
       * @description emits when the closeCitiModal Icon is clicked
       * @twoway false.
       */
        this.closeCitiModalIcon = new EventEmitter();
    }
    // #endregion
    // #region // ------------ Methods ------------ //
    /**
     * @Method: onUseAppClick
     * @description Opens new tab for provided link.
     * @return Void
     */
    onUseAppClick(url, qaObjCopy) {
        this.useAppNavigate.emit();
        this.triggerTagging(qaObjCopy);
    }
    /**
     * @Method: onCloseIcon
     * @description Close Citi Modal.
     * @return Void
     */
    onCloseIcon(qaObjCopy) {
        this.showCitiMobile = false;
        this.closeCitiModalIcon.emit();
        this.triggerTagging(qaObjCopy);
    }
    /**
     * @Method: onNotNowBtn
     * @description Opens/close new QrCode Modal.
     * @return Void
     */
    onNotNowBtn(qaObjCopy) {
        this.notNowCloseModal.emit();
        this.triggerTagging(qaObjCopy);
    }
    triggerTagging(qaObjCopy) {
        this.emitTaggingForQRcodeCTA.emit(qaObjCopy);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: QaCitiMobileModal, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: QaCitiMobileModal, isStandalone: false, selector: "qa-citi-mobile-modal", inputs: { showCitiMobile: "showCitiMobile", quickHref: "quickHref", qaCitiMobileContent: "qaCitiMobileContent", selectedView: "selectedView", productName: "productName" }, outputs: { useAppNavigate: "useAppNavigate", notNowCloseModal: "notNowCloseModal", emitTaggingForQRcodeCTA: "emitTaggingForQRcodeCTA", closeCitiModalIcon: "closeCitiModalIcon" }, ngImport: i0, template: "<cds-modal-dialog [(isOpen)]=\"showCitiMobile\" [isClosedOnBackdropClick]=\"false\">\n  <div class=\"cds-modal-dialog-centered\">\n    <div\n      class=\"cds-modal2-content\"\n      cdsModalInitialFocus\n      role=\"dialog\"\n      tabindex=\"0\"\n    >\n      <div class=\"close-button-wrap\">\n        <button\n          class=\"cds-modal2-close icon-btn\"\n          cdsButton=\"secondary\"\n          buttonIcon=\"icon\"\n          size=\"small\"\n          type=\"button\"\n          aria-label=\"close\"\n          (click)=\"onCloseIcon()\"\n        >\n          <!-- replace cds-icon due to style issue -->\n          <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\">\n            <path\n              fill-rule=\"evenodd\"\n              clip-rule=\"evenodd\"\n              d=\"m12.787 12 9.05-9.05a.557.557 0 0 0-.787-.787L12 11.213l-9.05-9.05a.557.557 0 0 0-.787.787l9.05 9.05-9.05 9.05a.557.557 0 0 0 .787.787l9.05-9.05 9.05 9.05a.557.557 0 0 0 .787-.787L12.787 12Z\"\n              fill=\"#056DAE\"\n            ></path>\n          </svg>\n        </button>\n      </div>\n      <div class=\"mobile-image\">\n        <img\n          src=\"/CBOL/IA/Angular/assets/mobileResponsiveCitiModal.svg\"\n          alt=\"mobile image\"\n        />\n      </div>\n      <div class=\"cds-modal2-header top-margin padding-left-right\">\n        <div class=\"cds-modal-title\" role=\"heading\" aria-level=\"1\">\n          {{qaCitiMobileContent?.quickTitle}}\n        </div>\n      </div>\n      <div class=\"cds-modal2-body padding-left-right\">\n        <p class=\"first-p\">\n          <span class=\"sub-title-first\"\n            >{{qaCitiMobileContent?.quickContent1Subheader}}</span\n          >\n          {{qaCitiMobileContent?.quickContent1}}\n        </p>\n\n        <span class=\"info-content\"\n          ><span class=\"sub-title-second\"\n            >{{qaCitiMobileContent?.quickContent2Subheader}}</span\n          >\n          <p>\n            If you tap \u201CUse the App\u201D you\u2019ll be leaving\n            <a target=\"_blank\" class=\"qa-link\" href=\"https://www.citi.com\"\n              >Citi.com</a\n            >. If you haven\u2019t downloaded Citi Mobile yet, you\u2019ll be taken to a\n            third-party site to download it. That site may have a different\n            privacy and security policy and may provide you less privacy and\n            security than Citi. Citi and its affiliates are not responsible for\n            the products, services and content on the third-party site.\n          </p></span\n        >\n      </div>\n      <div class=\"cds-modal2-footer padding-left-right top-padding\">\n        <button\n          role=\"link\"\n          class=\"ml-left\"\n          cdsButton=\"primary\"\n          (click)=\"onUseAppClick(quickHref, qaCitiMobileContent.quickPrimaryBtn)\"\n        >\n          {{qaCitiMobileContent?.quickPrimaryBtn}}\n        </button>\n\n        <button\n          cdsButton=\"secondary\"\n          (click)=\"onNotNowBtn(qaCitiMobileContent?.quickSecondryBtn)\"\n        >\n          {{qaCitiMobileContent?.quickSecondryBtn}}\n        </button>\n      </div>\n    </div>\n  </div>\n</cds-modal-dialog>\n", styles: [".cds-modal-fade{transition:opacity .15s linear}body.cds-modal-open{overflow:hidden}body.cds-modal-open .cds-modal{overflow-x:hidden;overflow-y:auto}.cds-modal-backdrop{position:fixed;top:0;left:0;background-color:#000;width:100vw;height:100vh;z-index:10000;opacity:.6}.cds-modal-backdrop.cds-modal-fade{opacity:0}.cds-modal-backdrop.cds-modal-show{opacity:.6}.cds-modal{position:fixed;top:0;left:0;z-index:10001;width:100%;height:100%;overflow:hidden;display:none}.cds-modal .cds-modal-dialog{transform:translateY(-2rem);opacity:0}.cds-modal.cds-modal-fade .cds-modal-dialog{transition:transform .3s ease-out,opacity .3s linear;transform:translateY(-2rem);opacity:0}.cds-modal.cds-modal-show .cds-modal-dialog{transform:none;opacity:1}.cds-modal-dialog{display:flow-root;pointer-events:none;height:100%}.cds-modal2-content{display:flex;flex-direction:column;width:calc(100% - 1rem);background-color:#fff;border-radius:8px;pointer-events:auto;margin:.5rem;position:relative}.cds-modal2-content h4{font-weight:700;font-size:36px;margin-bottom:0}.cds-modal2-content .cds-button-secondary,.cds-modal2-content .cds-button-tertiary{margin-left:16px}@media (min-width: 576px){.cds-modal2-content{margin:1.5rem auto;max-width:684px}}@media (min-width: 576px){.cds-modal2-sm{max-width:448px}}.cds-modal2-sm h4{font-size:28px}.cds-modal2-sm h5{font-size:21px}@media (min-width: 992px){.cds-modal2-lg,.cds-modal2-xl{max-width:856px}}@media (min-width: 1200px){.cds-modal2-xl{max-width:1156px}}.cds-modal2-header,.cds-modal2-body,.cds-modal2-footer{padding-left:40px;padding-right:40px}.cds-modal2-close{display:flex;position:absolute;right:0;padding:0;margin:12px 12px 0}.cds-modal2-header{display:flex;margin-top:48px;padding-bottom:24px;flex-direction:column}.cds-modal2-header .cds-modal2-close{margin:-1rem -1rem -1rem auto}.cds-modal2-content .centered{text-align:center;align-content:center;justify-content:center}.cds-modal2-content .steps h6{display:flex;margin-top:40px;padding-left:40px;padding-right:40px;font-size:16px;margin-bottom:8px}.cds-modal2-content .steps .cds-modal2-header{margin-top:0}.cds-modal2-content .steps h6.centered{padding-left:40px}.cds-modal2-content .icons{display:flex;flex-direction:column;margin-top:40px}.cds-modal2-content .icons cds-icon{padding-left:40px;padding-bottom:1em}.cds-modal2-content .icons .cds-modal2-header{margin-top:0}.cds-modal2-content .icons.centered cds-icon{padding-left:0}.cds-modal2-content .secondary-button{margin-left:10px}.cds-modal2-sm .secondary-button{margin-right:0;margin-top:16px}.cds-modal2-sm .cds-button-tertiary{margin-top:16px;margin-left:0}.cds-modal2-content .subtitle{margin-bottom:0}.cds-modal2-content h5{margin-top:10px;font-size:28px;margin-bottom:0}.cds-modal2-content .imgs-left{display:flex;flex-direction:row}.cds-modal2-content .imgs-left .cds-modal2-header,.cds-modal2-content .imgs-left h5,.cds-modal2-content .imgs-left .cds-modal2-body,.cds-modal2-content .imgs-left .cds-modal2-footer{padding-left:40px;padding-right:40px}.cds-modal2-content .imgs-left img{padding-left:40px;padding-top:70px;padding-bottom:70px;object-fit:cover;align-self:center}.cds-modal2-content .imgs-right{display:flex;flex-direction:row}.cds-modal2-content .imgs-right .cds-modal2-header,.cds-modal2-content .imgs-right h5,.cds-modal2-content .imgs-right .cds-modal2-body,.cds-modal2-content .imgs-right .cds-modal2-footer{padding-right:40px}.cds-modal2-content .imgs-right img{padding-right:40px;padding-top:70px;padding-bottom:70px;object-fit:cover;align-self:center}.cds-modal2-content .secondary-buttons{display:flex;flex-direction:column}.cds-modal2-content .secondary-buttons .button-wrap-1{padding-left:40px;padding-bottom:1em;padding-right:2em}.cds-modal2-content .secondary-buttons .button-wrap-2{margin-bottom:40px;padding-left:40px}.cds-modal2-content .secondary-buttons .button-wrap-2 button{margin-left:3px;border:none}.cds-modal2-content .secondary-buttons button.image-text cds-icon{padding-right:5px}.cds-modal2-content .secondary-buttons.centered .button-wrap-1{padding-left:40px}.cds-modal2-content .secondary-buttons.centered .button-wrap-2{padding-left:0}.cds-modal2-footer.secondary-buttons.centered{padding-bottom:0}@media only screen and (min-width: 990px){.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text){flex-direction:row}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{display:flex;flex:1 1 auto;padding-bottom:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{flex:1 1 auto;display:flex;padding-right:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .button-wrap-1,.cds-modal2-xl .button-wrap-1{padding-left:0!important;padding-right:0;padding-bottom:0!important}.cds-modal2-lg .button-wrap-2,.cds-modal2-xl .button-wrap-2{padding-bottom:0;padding-top:3px;padding-right:0!important;margin-bottom:0!important}.cds-modal2-lg .secondary-buttons,.cds-modal2-xl .secondary-buttons{flex-direction:row}.cds-modal2-lg .secondary-buttons .button-wrap-1,.cds-modal2-xl .secondary-buttons .button-wrap-1{display:flex;flex:1 1 auto}.cds-modal2-lg .secondary-buttons .button-wrap-2,.cds-modal2-xl .secondary-buttons .button-wrap-2{display:flex;flex:1 1 auto;margin-bottom:0}.cds-modal2-lg .secondary-buttons .button-wrap-2 .first,.cds-modal2-xl .secondary-buttons .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .secondary-buttons.centered,.cds-modal2-xl .secondary-buttons.centered{padding-bottom:40px!important}.cds-modal2-lg .secondary-buttons.centered .button-wrap-1,.cds-modal2-xl .secondary-buttons.centered .button-wrap-1{padding-left:0}}.cds-modal2-sm .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1{display:flex;flex-direction:row;padding-right:40px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .secondary-button,.cds-modal2-sm .secondary-buttons .button-wrap-1 .cds-button-tertiary{margin-left:16px}.cds-modal2-sm .secondary-buttons .button-wrap-1 button{margin-top:0}.cds-modal2-sm .secondary-buttons .button-wrap-2{padding-left:0;margin-left:40px}.cds-modal2-sm .secondary-buttons.centered .button-wrap-1 button{flex:1 1 auto}.cds-modal2-sm .secondary-buttons.centered .button-wrap-2{margin-left:0}.cds-modal2-sm .cds-modal2-footer:not(.vertical-buttons) .cds-button-tertiary{margin-top:0;margin-left:16px}.cds-modal2-sm .cds-modal2-footer.vertical-buttons{display:flex;flex-direction:column}.cds-modal2-sm .cds-modal2-footer.vertical-buttons .secondary-button{margin-left:0}.cds-modal2-sm .cds-modal2-footer.vertical-buttons button{align-self:center;width:100%}.cds-modal2-content .display-top{display:none}.cds-modal2-content .display-bottom{display:block}.cds-modal2-dialog-scrollable{display:flex;flex-direction:column}.cds-modal2-dialog-scrollable .cds-modal2-body{overflow-y:auto;max-height:40vh}.cds-modal2-dialog-scrollable .cds-modal2-footer,.cds-modal2-dialog-scrollable .secondary-buttons{padding-top:40px}@media only screen and (min-width: 645px) and (max-width: 990px){.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text){flex-direction:row}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{display:flex;flex:1 1 auto;padding-bottom:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{flex:1 1 auto;display:flex;padding-right:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1,.cds-modal2-xl .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{padding-bottom:0!important;padding-left:0}.cds-modal2-lg .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2,.cds-modal2-xl .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{padding-right:0!important;margin-bottom:0}.cds-modal2-lg .secondary-buttons.triple-button-secondary-text .button-wrap-1,.cds-modal2-xl .secondary-buttons.triple-button-secondary-text .button-wrap-1{padding-left:0}.cds-modal2-lg .secondary-buttons.triple-button-secondary-text .button-wrap-2,.cds-modal2-xl .secondary-buttons.triple-button-secondary-text .button-wrap-2{margin-bottom:0;padding-left:0}.cds-modal2-dialog-scrollable .cds-modal2-body{max-height:25vh}.cds-modal2-dialog-scrollable:has(.cds-modal-border-top) .cds-modal2-body{max-height:10vh}}.cds-modal2-content .imgs-left h5,.cds-modal2-content .imgs-right h5{padding-left:0}@media only screen and (max-width: 645px){.cds-modal2-content .cds-button-tertiary{margin-top:16px;margin-left:0}.cds-modal2-content .cds-button-secondary{margin-right:0;margin-top:16px;margin-left:0}.cds-modal2-dialog-scrollable .cds-modal2-body{height:20vh}.cds-modal2-content .display-top{display:block}.cds-modal2-content .display-bottom{display:none}.cds-modal2-content .imgs-left,.cds-modal2-content .imgs-right{flex-direction:column}.cds-modal2-content .imgs-left img,.cds-modal2-content .imgs-right img{padding-right:0;padding-bottom:0;padding-left:0}.cds-modal2-content .secondary-buttons.centered .button-wrap-2{display:flex;flex-direction:row;margin-bottom:40px}.cds-modal2-sm .cds-button-tertiary{margin-top:16px!important;margin-left:0!important}.cds-modal2-sm .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1{display:flex;flex-direction:column;padding-right:40px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .cds-button-tertiary{margin-top:16px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .secondary-button{margin-top:16px;margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1 button{align-self:center;width:100%}.cds-modal2-sm .secondary-buttons .button-wrap-2{align-self:center;padding-left:0;margin-left:0}.cds-modal2-dialog-scrollable .cds-modal2-sm .cds-button-tertiary{margin-top:16px!important;margin-left:0!important}.cds-modal2-lg .button-wrap-1,.cds-modal2-xl .button-wrap-1{padding-right:0!important;padding-left:0!important}.cds-modal2-lg .button-wrap-2,.cds-modal2-xl .button-wrap-2{display:flex;flex-direction:row;margin-bottom:0}.cds-modal2-content .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-content .secondary-buttons .button-wrap-1{display:flex;flex-direction:column;padding-right:40px}.cds-modal2-content .secondary-buttons .button-wrap-1 button{align-self:center;width:100%}.cds-modal2-content .secondary-buttons .button-wrap-2{align-self:center;padding-left:0}.cds-modal2-content .secondary-buttons .secondary-text{margin-left:0}.cds-modal2-footer{display:flex;flex-direction:column}.cds-modal2-footer .secondary-button{margin-left:0}.cds-modal2-footer button{align-self:center;width:100%}}.cds-modal2-content .no-button{margin-bottom:40px;padding-bottom:0}.cds-modal2-content .close-btn{outline:none;border:none;background-color:transparent}.cds-modal2-content .close-btn:focus{outline:auto}.cds-modal2-body{font-size:15px;padding-left:40px}.cds-modal2-body p,.cds-modal-title{margin-bottom:0}.cds-modal2-footer{display:flex;padding-top:40px;padding-bottom:40px}.cds-modal2-content .secondary-buttons{padding-top:40px}.cds-modal2-content .cds-modal-border-top{border-top:1px solid #cccccc;padding-top:40px;margin-top:20px}.cds-modal2-content .cds-modal-border-bottom{border-bottom:1px solid rgba(0,0,0,.2509803922)}.cds-modal-dialog-centered{display:flex;align-items:center;min-height:100%}.cds-modal-dialog-centered.cds-modal2-dialog-scrollable{justify-content:center}body.cds-modal-open citi-header div.primary-header{z-index:0!important}.cds-button{outline:none}.cds-button-inlineLink{font-family:Interstate_Light,sans-serif;text-decoration:underline;border-radius:6px;border-color:transparent;height:auto;margin:auto 0}.cds-button-inlineLink:focus{box-shadow:0 0 0 1px #002a54 inset}.cds-button-inlineLink:hover,.cds-button-inlineLink:active{text-decoration:underline}.cds-button-inlineLink:disabled{background-color:transparent;border-color:transparent}.cds-button-inlineLink.cds-button-icon,.cds-button-inlineLink.cds-button-icon-round{padding:0!important}.cds-button-inlineLink.cds-button-chevron-right,.cds-button-inlineLink.cds-button-chevron-left{text-decoration:none}.cds-button-tertiary{text-decoration:underline;border-color:transparent}.cds-button-tertiary:hover,.cds-button-tertiary:active{text-decoration:underline}.cds-button-tertiary.cds-button-chevron-right,.cds-button-tertiary.cds-button-chevron-left{text-decoration:none}.cds-button-secondaryInverse cds-icon path{fill:#fff!important}.cds-button-secondaryInverse:focus{box-shadow:0 0 0 1px #002a54,0 0 0 2px #056dae}.cds-modal-title{font-family:Interstate_Regular,sans-serif;font-weight:500;font-size:24px;line-height:32px}.cds-modal-title .copy-right{position:relative;bottom:4;right:8px}.sub-title-first{font-family:Interstate;font-size:16px;font-weight:700;line-height:24px;letter-spacing:0px}.first-p{font-size:16px;line-height:24px}.sub-title-second{font-family:Interstate;font-size:12px;font-weight:700;line-height:18px;letter-spacing:0px;margin-bottom:8px;display:block}.info-content{margin-top:24px;display:block}.mobile-image{width:100%;height:100%;margin-top:5px}.mobile-image img{width:100%;height:100%;padding:0 24px}.qa-link{color:#056dae;text-decoration-line:underline}.close-button-wrap{display:flex;align-items:center;margin-top:16px;height:48px}.close-button-wrap .icon-btn{border:none;margin:0 17px 0 0;padding:16px}.close-button-wrap .icon-btn svg{width:24px;height:24px}.top-margin{margin-top:24px}.padding-left-right{padding-right:24px;padding-left:24px}.cds-modal2-body{text-align:justify}.top-padding{padding-top:24px}\n"], dependencies: [{ kind: "component", type: i1.CdsModalDialogComponent, selector: "cds-modal-dialog", inputs: ["isOpen", "isClosedOnBackdropClick", "isClosedOnEscape", "hasFade", "ariaRole", "isRestoreFocusDisabled", "ariaLabel", "ariaLabelledby", "fadeTime"], outputs: ["isOpenChange", "openStart", "openEnd", "closeStart", "closeEnd"] }, { kind: "directive", type: i1.CdsButton, selector: "button[cdsButton],button[cds-button],a[cdsButton],a[cds-button]", inputs: ["cdsButton", "size", "buttonIcon", "arrowRight", "arrowLeft", "JFPToken"], exportAs: ["cdsButton"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: QaCitiMobileModal, decorators: [{
            type: Component,
            args: [{ selector: 'qa-citi-mobile-modal', standalone: false, template: "<cds-modal-dialog [(isOpen)]=\"showCitiMobile\" [isClosedOnBackdropClick]=\"false\">\n  <div class=\"cds-modal-dialog-centered\">\n    <div\n      class=\"cds-modal2-content\"\n      cdsModalInitialFocus\n      role=\"dialog\"\n      tabindex=\"0\"\n    >\n      <div class=\"close-button-wrap\">\n        <button\n          class=\"cds-modal2-close icon-btn\"\n          cdsButton=\"secondary\"\n          buttonIcon=\"icon\"\n          size=\"small\"\n          type=\"button\"\n          aria-label=\"close\"\n          (click)=\"onCloseIcon()\"\n        >\n          <!-- replace cds-icon due to style issue -->\n          <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\">\n            <path\n              fill-rule=\"evenodd\"\n              clip-rule=\"evenodd\"\n              d=\"m12.787 12 9.05-9.05a.557.557 0 0 0-.787-.787L12 11.213l-9.05-9.05a.557.557 0 0 0-.787.787l9.05 9.05-9.05 9.05a.557.557 0 0 0 .787.787l9.05-9.05 9.05 9.05a.557.557 0 0 0 .787-.787L12.787 12Z\"\n              fill=\"#056DAE\"\n            ></path>\n          </svg>\n        </button>\n      </div>\n      <div class=\"mobile-image\">\n        <img\n          src=\"/CBOL/IA/Angular/assets/mobileResponsiveCitiModal.svg\"\n          alt=\"mobile image\"\n        />\n      </div>\n      <div class=\"cds-modal2-header top-margin padding-left-right\">\n        <div class=\"cds-modal-title\" role=\"heading\" aria-level=\"1\">\n          {{qaCitiMobileContent?.quickTitle}}\n        </div>\n      </div>\n      <div class=\"cds-modal2-body padding-left-right\">\n        <p class=\"first-p\">\n          <span class=\"sub-title-first\"\n            >{{qaCitiMobileContent?.quickContent1Subheader}}</span\n          >\n          {{qaCitiMobileContent?.quickContent1}}\n        </p>\n\n        <span class=\"info-content\"\n          ><span class=\"sub-title-second\"\n            >{{qaCitiMobileContent?.quickContent2Subheader}}</span\n          >\n          <p>\n            If you tap \u201CUse the App\u201D you\u2019ll be leaving\n            <a target=\"_blank\" class=\"qa-link\" href=\"https://www.citi.com\"\n              >Citi.com</a\n            >. If you haven\u2019t downloaded Citi Mobile yet, you\u2019ll be taken to a\n            third-party site to download it. That site may have a different\n            privacy and security policy and may provide you less privacy and\n            security than Citi. Citi and its affiliates are not responsible for\n            the products, services and content on the third-party site.\n          </p></span\n        >\n      </div>\n      <div class=\"cds-modal2-footer padding-left-right top-padding\">\n        <button\n          role=\"link\"\n          class=\"ml-left\"\n          cdsButton=\"primary\"\n          (click)=\"onUseAppClick(quickHref, qaCitiMobileContent.quickPrimaryBtn)\"\n        >\n          {{qaCitiMobileContent?.quickPrimaryBtn}}\n        </button>\n\n        <button\n          cdsButton=\"secondary\"\n          (click)=\"onNotNowBtn(qaCitiMobileContent?.quickSecondryBtn)\"\n        >\n          {{qaCitiMobileContent?.quickSecondryBtn}}\n        </button>\n      </div>\n    </div>\n  </div>\n</cds-modal-dialog>\n", styles: [".cds-modal-fade{transition:opacity .15s linear}body.cds-modal-open{overflow:hidden}body.cds-modal-open .cds-modal{overflow-x:hidden;overflow-y:auto}.cds-modal-backdrop{position:fixed;top:0;left:0;background-color:#000;width:100vw;height:100vh;z-index:10000;opacity:.6}.cds-modal-backdrop.cds-modal-fade{opacity:0}.cds-modal-backdrop.cds-modal-show{opacity:.6}.cds-modal{position:fixed;top:0;left:0;z-index:10001;width:100%;height:100%;overflow:hidden;display:none}.cds-modal .cds-modal-dialog{transform:translateY(-2rem);opacity:0}.cds-modal.cds-modal-fade .cds-modal-dialog{transition:transform .3s ease-out,opacity .3s linear;transform:translateY(-2rem);opacity:0}.cds-modal.cds-modal-show .cds-modal-dialog{transform:none;opacity:1}.cds-modal-dialog{display:flow-root;pointer-events:none;height:100%}.cds-modal2-content{display:flex;flex-direction:column;width:calc(100% - 1rem);background-color:#fff;border-radius:8px;pointer-events:auto;margin:.5rem;position:relative}.cds-modal2-content h4{font-weight:700;font-size:36px;margin-bottom:0}.cds-modal2-content .cds-button-secondary,.cds-modal2-content .cds-button-tertiary{margin-left:16px}@media (min-width: 576px){.cds-modal2-content{margin:1.5rem auto;max-width:684px}}@media (min-width: 576px){.cds-modal2-sm{max-width:448px}}.cds-modal2-sm h4{font-size:28px}.cds-modal2-sm h5{font-size:21px}@media (min-width: 992px){.cds-modal2-lg,.cds-modal2-xl{max-width:856px}}@media (min-width: 1200px){.cds-modal2-xl{max-width:1156px}}.cds-modal2-header,.cds-modal2-body,.cds-modal2-footer{padding-left:40px;padding-right:40px}.cds-modal2-close{display:flex;position:absolute;right:0;padding:0;margin:12px 12px 0}.cds-modal2-header{display:flex;margin-top:48px;padding-bottom:24px;flex-direction:column}.cds-modal2-header .cds-modal2-close{margin:-1rem -1rem -1rem auto}.cds-modal2-content .centered{text-align:center;align-content:center;justify-content:center}.cds-modal2-content .steps h6{display:flex;margin-top:40px;padding-left:40px;padding-right:40px;font-size:16px;margin-bottom:8px}.cds-modal2-content .steps .cds-modal2-header{margin-top:0}.cds-modal2-content .steps h6.centered{padding-left:40px}.cds-modal2-content .icons{display:flex;flex-direction:column;margin-top:40px}.cds-modal2-content .icons cds-icon{padding-left:40px;padding-bottom:1em}.cds-modal2-content .icons .cds-modal2-header{margin-top:0}.cds-modal2-content .icons.centered cds-icon{padding-left:0}.cds-modal2-content .secondary-button{margin-left:10px}.cds-modal2-sm .secondary-button{margin-right:0;margin-top:16px}.cds-modal2-sm .cds-button-tertiary{margin-top:16px;margin-left:0}.cds-modal2-content .subtitle{margin-bottom:0}.cds-modal2-content h5{margin-top:10px;font-size:28px;margin-bottom:0}.cds-modal2-content .imgs-left{display:flex;flex-direction:row}.cds-modal2-content .imgs-left .cds-modal2-header,.cds-modal2-content .imgs-left h5,.cds-modal2-content .imgs-left .cds-modal2-body,.cds-modal2-content .imgs-left .cds-modal2-footer{padding-left:40px;padding-right:40px}.cds-modal2-content .imgs-left img{padding-left:40px;padding-top:70px;padding-bottom:70px;object-fit:cover;align-self:center}.cds-modal2-content .imgs-right{display:flex;flex-direction:row}.cds-modal2-content .imgs-right .cds-modal2-header,.cds-modal2-content .imgs-right h5,.cds-modal2-content .imgs-right .cds-modal2-body,.cds-modal2-content .imgs-right .cds-modal2-footer{padding-right:40px}.cds-modal2-content .imgs-right img{padding-right:40px;padding-top:70px;padding-bottom:70px;object-fit:cover;align-self:center}.cds-modal2-content .secondary-buttons{display:flex;flex-direction:column}.cds-modal2-content .secondary-buttons .button-wrap-1{padding-left:40px;padding-bottom:1em;padding-right:2em}.cds-modal2-content .secondary-buttons .button-wrap-2{margin-bottom:40px;padding-left:40px}.cds-modal2-content .secondary-buttons .button-wrap-2 button{margin-left:3px;border:none}.cds-modal2-content .secondary-buttons button.image-text cds-icon{padding-right:5px}.cds-modal2-content .secondary-buttons.centered .button-wrap-1{padding-left:40px}.cds-modal2-content .secondary-buttons.centered .button-wrap-2{padding-left:0}.cds-modal2-footer.secondary-buttons.centered{padding-bottom:0}@media only screen and (min-width: 990px){.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text){flex-direction:row}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{display:flex;flex:1 1 auto;padding-bottom:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{flex:1 1 auto;display:flex;padding-right:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .button-wrap-1,.cds-modal2-xl .button-wrap-1{padding-left:0!important;padding-right:0;padding-bottom:0!important}.cds-modal2-lg .button-wrap-2,.cds-modal2-xl .button-wrap-2{padding-bottom:0;padding-top:3px;padding-right:0!important;margin-bottom:0!important}.cds-modal2-lg .secondary-buttons,.cds-modal2-xl .secondary-buttons{flex-direction:row}.cds-modal2-lg .secondary-buttons .button-wrap-1,.cds-modal2-xl .secondary-buttons .button-wrap-1{display:flex;flex:1 1 auto}.cds-modal2-lg .secondary-buttons .button-wrap-2,.cds-modal2-xl .secondary-buttons .button-wrap-2{display:flex;flex:1 1 auto;margin-bottom:0}.cds-modal2-lg .secondary-buttons .button-wrap-2 .first,.cds-modal2-xl .secondary-buttons .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .secondary-buttons.centered,.cds-modal2-xl .secondary-buttons.centered{padding-bottom:40px!important}.cds-modal2-lg .secondary-buttons.centered .button-wrap-1,.cds-modal2-xl .secondary-buttons.centered .button-wrap-1{padding-left:0}}.cds-modal2-sm .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1{display:flex;flex-direction:row;padding-right:40px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .secondary-button,.cds-modal2-sm .secondary-buttons .button-wrap-1 .cds-button-tertiary{margin-left:16px}.cds-modal2-sm .secondary-buttons .button-wrap-1 button{margin-top:0}.cds-modal2-sm .secondary-buttons .button-wrap-2{padding-left:0;margin-left:40px}.cds-modal2-sm .secondary-buttons.centered .button-wrap-1 button{flex:1 1 auto}.cds-modal2-sm .secondary-buttons.centered .button-wrap-2{margin-left:0}.cds-modal2-sm .cds-modal2-footer:not(.vertical-buttons) .cds-button-tertiary{margin-top:0;margin-left:16px}.cds-modal2-sm .cds-modal2-footer.vertical-buttons{display:flex;flex-direction:column}.cds-modal2-sm .cds-modal2-footer.vertical-buttons .secondary-button{margin-left:0}.cds-modal2-sm .cds-modal2-footer.vertical-buttons button{align-self:center;width:100%}.cds-modal2-content .display-top{display:none}.cds-modal2-content .display-bottom{display:block}.cds-modal2-dialog-scrollable{display:flex;flex-direction:column}.cds-modal2-dialog-scrollable .cds-modal2-body{overflow-y:auto;max-height:40vh}.cds-modal2-dialog-scrollable .cds-modal2-footer,.cds-modal2-dialog-scrollable .secondary-buttons{padding-top:40px}@media only screen and (min-width: 645px) and (max-width: 990px){.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text){flex-direction:row}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{display:flex;flex:1 1 auto;padding-bottom:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{flex:1 1 auto;display:flex;padding-right:40px}.cds-modal2-content:not(.cds-modal2-sm) .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2 .first{margin-left:auto}.cds-modal2-lg .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1,.cds-modal2-xl .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-1{padding-bottom:0!important;padding-left:0}.cds-modal2-lg .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2,.cds-modal2-xl .secondary-buttons:not(.centered):not(.triple-button-secondary-text) .button-wrap-2{padding-right:0!important;margin-bottom:0}.cds-modal2-lg .secondary-buttons.triple-button-secondary-text .button-wrap-1,.cds-modal2-xl .secondary-buttons.triple-button-secondary-text .button-wrap-1{padding-left:0}.cds-modal2-lg .secondary-buttons.triple-button-secondary-text .button-wrap-2,.cds-modal2-xl .secondary-buttons.triple-button-secondary-text .button-wrap-2{margin-bottom:0;padding-left:0}.cds-modal2-dialog-scrollable .cds-modal2-body{max-height:25vh}.cds-modal2-dialog-scrollable:has(.cds-modal-border-top) .cds-modal2-body{max-height:10vh}}.cds-modal2-content .imgs-left h5,.cds-modal2-content .imgs-right h5{padding-left:0}@media only screen and (max-width: 645px){.cds-modal2-content .cds-button-tertiary{margin-top:16px;margin-left:0}.cds-modal2-content .cds-button-secondary{margin-right:0;margin-top:16px;margin-left:0}.cds-modal2-dialog-scrollable .cds-modal2-body{height:20vh}.cds-modal2-content .display-top{display:block}.cds-modal2-content .display-bottom{display:none}.cds-modal2-content .imgs-left,.cds-modal2-content .imgs-right{flex-direction:column}.cds-modal2-content .imgs-left img,.cds-modal2-content .imgs-right img{padding-right:0;padding-bottom:0;padding-left:0}.cds-modal2-content .secondary-buttons.centered .button-wrap-2{display:flex;flex-direction:row;margin-bottom:40px}.cds-modal2-sm .cds-button-tertiary{margin-top:16px!important;margin-left:0!important}.cds-modal2-sm .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1{display:flex;flex-direction:column;padding-right:40px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .cds-button-tertiary{margin-top:16px}.cds-modal2-sm .secondary-buttons .button-wrap-1 .secondary-button{margin-top:16px;margin-left:0}.cds-modal2-sm .secondary-buttons .button-wrap-1 button{align-self:center;width:100%}.cds-modal2-sm .secondary-buttons .button-wrap-2{align-self:center;padding-left:0;margin-left:0}.cds-modal2-dialog-scrollable .cds-modal2-sm .cds-button-tertiary{margin-top:16px!important;margin-left:0!important}.cds-modal2-lg .button-wrap-1,.cds-modal2-xl .button-wrap-1{padding-right:0!important;padding-left:0!important}.cds-modal2-lg .button-wrap-2,.cds-modal2-xl .button-wrap-2{display:flex;flex-direction:row;margin-bottom:0}.cds-modal2-content .secondary-buttons .secondary-button{margin-left:0}.cds-modal2-content .secondary-buttons .button-wrap-1{display:flex;flex-direction:column;padding-right:40px}.cds-modal2-content .secondary-buttons .button-wrap-1 button{align-self:center;width:100%}.cds-modal2-content .secondary-buttons .button-wrap-2{align-self:center;padding-left:0}.cds-modal2-content .secondary-buttons .secondary-text{margin-left:0}.cds-modal2-footer{display:flex;flex-direction:column}.cds-modal2-footer .secondary-button{margin-left:0}.cds-modal2-footer button{align-self:center;width:100%}}.cds-modal2-content .no-button{margin-bottom:40px;padding-bottom:0}.cds-modal2-content .close-btn{outline:none;border:none;background-color:transparent}.cds-modal2-content .close-btn:focus{outline:auto}.cds-modal2-body{font-size:15px;padding-left:40px}.cds-modal2-body p,.cds-modal-title{margin-bottom:0}.cds-modal2-footer{display:flex;padding-top:40px;padding-bottom:40px}.cds-modal2-content .secondary-buttons{padding-top:40px}.cds-modal2-content .cds-modal-border-top{border-top:1px solid #cccccc;padding-top:40px;margin-top:20px}.cds-modal2-content .cds-modal-border-bottom{border-bottom:1px solid rgba(0,0,0,.2509803922)}.cds-modal-dialog-centered{display:flex;align-items:center;min-height:100%}.cds-modal-dialog-centered.cds-modal2-dialog-scrollable{justify-content:center}body.cds-modal-open citi-header div.primary-header{z-index:0!important}.cds-button{outline:none}.cds-button-inlineLink{font-family:Interstate_Light,sans-serif;text-decoration:underline;border-radius:6px;border-color:transparent;height:auto;margin:auto 0}.cds-button-inlineLink:focus{box-shadow:0 0 0 1px #002a54 inset}.cds-button-inlineLink:hover,.cds-button-inlineLink:active{text-decoration:underline}.cds-button-inlineLink:disabled{background-color:transparent;border-color:transparent}.cds-button-inlineLink.cds-button-icon,.cds-button-inlineLink.cds-button-icon-round{padding:0!important}.cds-button-inlineLink.cds-button-chevron-right,.cds-button-inlineLink.cds-button-chevron-left{text-decoration:none}.cds-button-tertiary{text-decoration:underline;border-color:transparent}.cds-button-tertiary:hover,.cds-button-tertiary:active{text-decoration:underline}.cds-button-tertiary.cds-button-chevron-right,.cds-button-tertiary.cds-button-chevron-left{text-decoration:none}.cds-button-secondaryInverse cds-icon path{fill:#fff!important}.cds-button-secondaryInverse:focus{box-shadow:0 0 0 1px #002a54,0 0 0 2px #056dae}.cds-modal-title{font-family:Interstate_Regular,sans-serif;font-weight:500;font-size:24px;line-height:32px}.cds-modal-title .copy-right{position:relative;bottom:4;right:8px}.sub-title-first{font-family:Interstate;font-size:16px;font-weight:700;line-height:24px;letter-spacing:0px}.first-p{font-size:16px;line-height:24px}.sub-title-second{font-family:Interstate;font-size:12px;font-weight:700;line-height:18px;letter-spacing:0px;margin-bottom:8px;display:block}.info-content{margin-top:24px;display:block}.mobile-image{width:100%;height:100%;margin-top:5px}.mobile-image img{width:100%;height:100%;padding:0 24px}.qa-link{color:#056dae;text-decoration-line:underline}.close-button-wrap{display:flex;align-items:center;margin-top:16px;height:48px}.close-button-wrap .icon-btn{border:none;margin:0 17px 0 0;padding:16px}.close-button-wrap .icon-btn svg{width:24px;height:24px}.top-margin{margin-top:24px}.padding-left-right{padding-right:24px;padding-left:24px}.cds-modal2-body{text-align:justify}.top-padding{padding-top:24px}\n"] }]
        }], propDecorators: { showCitiMobile: [{
                type: Input
            }], quickHref: [{
                type: Input
            }], qaCitiMobileContent: [{
                type: Input
            }], selectedView: [{
                type: Input
            }], productName: [{
                type: Input
            }], useAppNavigate: [{
                type: Output
            }], notNowCloseModal: [{
                type: Output
            }], emitTaggingForQRcodeCTA: [{
                type: Output
            }], closeCitiModalIcon: [{
                type: Output
            }] } });

const MODULES$c = [
    CommonModule,
    CdsIconModule,
    CdsModalDialogModule,
    CdsButtonModule
];
class QaCitiMobileModalModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: QaCitiMobileModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: QaCitiMobileModalModule, declarations: [QaCitiMobileModal], imports: [CommonModule,
            CdsIconModule,
            CdsModalDialogModule,
            CdsButtonModule], exports: [QaCitiMobileModal] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: QaCitiMobileModalModule, imports: [MODULES$c] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: QaCitiMobileModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$c],
                    declarations: [QaCitiMobileModal],
                    exports: [QaCitiMobileModal],
                }]
        }] });

/**
 * Represents the SpeedBumpComponent.
 */
class SpeedBumpComponent {
    constructor() {
        this._speedBumpContent = null;
        this.currentData = { head: '', body: '' };
        /**
         * Opens the SpeedBump modal when the Use button is clicked.
         * @twoway false.
         */
        this.showModal = false;
        /**
        * Emits the modalClosed value.
        */
        this.modalClosed = new EventEmitter;
    }
    /**
     * Initializes the component.
     */
    ngOnInit() {
        this.updateCurrentData();
        console.log('SpeedBumpComponent initialized');
    }
    /**
     * Receives speedBump content to display on Modal.
     * @param res The speedBump content.
     */
    set speedBumpContent(res) {
        this._speedBumpContent = res;
        this.updateCurrentData();
    }
    /**
     * Updates the current data based on the current index.
     */
    updateCurrentData() {
        this.currentData.head = this._speedBumpContent?.header || '';
        this.currentData.body = this._speedBumpContent?.body || '';
    }
    /**
     * Handles the "Continue" and "Close" button click event.
     */
    onContinue() {
        this.closeModal();
        this.modalClosed.emit(true);
    }
    /**
     * Closes the modal and resets the current index and data.
     */
    closeModal() {
        this.showModal = false;
        this.modalClosed.emit(false);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SpeedBumpComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: SpeedBumpComponent, isStandalone: true, selector: "speed-bump", inputs: { showModal: "showModal", speedBumpContent: "speedBumpContent" }, outputs: { modalClosed: "modalClosed" }, ngImport: i0, template: `
  <cds-modal-dialog [isOpen]="showModal">
  <div class="cds-modal-dialog-centered">
    <div
      cdsModalInitialFocus
      class="cds-modal2-content cds-modal2-lg"
      aria-modal="true"
      role="dialog"
      aria-label="Important Information"
      tabindex="0"
    >
      <div class="close-button-wrap">
        <button
          class="cds-modal2-close close-btn"
          cdsButton="secondary"
          buttonIcon="icon"
          size="small"
          (click)="closeModal()"
          aria-label="Close Modal"
        >
          <cds-icon
            class="close-icon"
            type="2021"
            name="044-close"
            [svgStyle]="{'width.px': 20,'height.px': 20}"
            aria-hidden="true"
          ></cds-icon>
        </button>
      </div>
      <div class="cds-modal2-header">
        <h4 class="cds-modal-title">
          {{this.currentData.head}}
        </h4>
      </div>
      <div class="cds-modal2-body">
        {{this.currentData.body}}
      </div>
      <div class="cds-modal2-footer">
        <button
          class="ml-left"
          cdsButton="primary"
          (click)="onContinue()"
          aria-label = "Continue"
        >
          Continue
        </button>
      </div>
    </div>
  </div>
</cds-modal-dialog>
  `, isInline: true, dependencies: [{ kind: "ngmodule", type: CdsModalDialogModule }, { kind: "component", type: i1.CdsModalDialogComponent, selector: "cds-modal-dialog", inputs: ["isOpen", "isClosedOnBackdropClick", "isClosedOnEscape", "hasFade", "ariaRole", "isRestoreFocusDisabled", "ariaLabel", "ariaLabelledby", "fadeTime"], outputs: ["isOpenChange", "openStart", "openEnd", "closeStart", "closeEnd"] }, { kind: "ngmodule", type: CdsIconModule }, { kind: "component", type: i1.CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }, { kind: "ngmodule", type: CdsButtonModule }, { kind: "directive", type: i1.CdsButton, selector: "button[cdsButton],button[cds-button],a[cdsButton],a[cds-button]", inputs: ["cdsButton", "size", "buttonIcon", "arrowRight", "arrowLeft", "JFPToken"], exportAs: ["cdsButton"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SpeedBumpComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'speed-bump',
                    template: `
  <cds-modal-dialog [isOpen]="showModal">
  <div class="cds-modal-dialog-centered">
    <div
      cdsModalInitialFocus
      class="cds-modal2-content cds-modal2-lg"
      aria-modal="true"
      role="dialog"
      aria-label="Important Information"
      tabindex="0"
    >
      <div class="close-button-wrap">
        <button
          class="cds-modal2-close close-btn"
          cdsButton="secondary"
          buttonIcon="icon"
          size="small"
          (click)="closeModal()"
          aria-label="Close Modal"
        >
          <cds-icon
            class="close-icon"
            type="2021"
            name="044-close"
            [svgStyle]="{'width.px': 20,'height.px': 20}"
            aria-hidden="true"
          ></cds-icon>
        </button>
      </div>
      <div class="cds-modal2-header">
        <h4 class="cds-modal-title">
          {{this.currentData.head}}
        </h4>
      </div>
      <div class="cds-modal2-body">
        {{this.currentData.body}}
      </div>
      <div class="cds-modal2-footer">
        <button
          class="ml-left"
          cdsButton="primary"
          (click)="onContinue()"
          aria-label = "Continue"
        >
          Continue
        </button>
      </div>
    </div>
  </div>
</cds-modal-dialog>
  `,
                    standalone: true,
                    imports: [CdsModalDialogModule, CdsIconModule, CdsButtonModule]
                }]
        }], propDecorators: { showModal: [{
                type: Input
            }], speedBumpContent: [{
                type: Input
            }], modalClosed: [{
                type: Output
            }] } });

function addJfpToken(href) {
    // If the target is set to _blank
    if (!href.match('JFP_TOKEN')) {
        // Check if there is a JFP Token in session storage
        // The casting is to avoid any compilcation error because of strict type checking of Typescript
        const jfpToken = sessionStorage.sessionJFP_TOKEN;
        if (jfpToken) {
            // Append the correct prefix
            href += href.match(/\?/g) ? '&' : '?';
            // Append the JFP_TOKEN
            href += 'JFP_TOKEN=' + jfpToken;
        }
    }
    return href;
}

const boolCheck = (val) => {
    if (val === 'true' || val === 'false' || val === true || val === false)
        return true;
    else
        return false;
};
const CTA_CLASSES = {
    primary: 'btn btn-primary',
    secondary: 'btn btn-link chevron-link secondary',
    tertiary: 'btn btn-link',
    icon: 'btn-link btn-icon',
    arrowRight: 'arrow-btn next',
    arrowLeft: 'arrow-btn prev',
    large: 'large',
    small: 'small',
    bold: 'bold',
    disabled: 'disabled',
    noMargin: 'noMargin',
    leftArrow: 'left link',
    btn_white: 'btn btn-primary btn_white',
    btn_white_on_blue: 'btn btn-primary btn_white_on_blue',
    'btn-white-disabled': 'btn btn-primary btn-white-disabled disabled',
    'btn-dark-disabled': 'btn btn-primary btn-dark-disabled disabled',
    'btn-blue-arrow-right': 'btn btn-primary arrow-btn next',
    'btn-blue-arrow-left': 'btn btn-primary arrow-btn prev',
    'btn-white-arrow-right': 'btn btn-primary btn_white_arrow_right arrow-btn next',
    'btn-white-arrow-left': 'btn btn-primary btn_white_arrow_left arrow-btn prev',
    'btn-dark-arrow-right': 'btn btn-primary btn_dark_arrow_right arrow-btn next',
    'btn-dark-arrow-left': 'btn btn-primary btn_dark_arrow_left arrow-btn prev',
    'btn-blue-hover-arrow-right': 'btn btn-primary btn_blue_hover_arrow_right arrow-btn next',
    'btn-blue-hover-arrow-left': 'btn btn-primary btn_blue_hover_arrow_left arrow-btn prev',
    btn_white_priority_arrow_right: 'btn btn-primary btn_white_priority_arrow_right arrow-btn next',
    btn_white_priority_arrow_left: 'btn btn-primary btn_white_priority_arrow_left arrow-btn prev',
    btn_dark_priority: 'btn btn-primary btn_dark_priority',
    btn_light_priority: 'btn btn-primary btn_light_priority',
    btn_gold: 'btn btn-primary btn_gold',
    gold_arrow_right: 'btn btn-primary btn_gold gold_arrow_right arrow-btn next',
    gold_arrow_left: 'btn btn-primary btn_gold gold_arrow_left arrow-btn prev',
    btn_gold_dark: 'btn btn-primary dark btn_gold',
    gold_dark_arrow_right: 'btn btn-primary dark btn_gold gold_arrow_right arrow-btn next',
    gold_dark_arrow_left: 'btn btn-primary dark btn_gold gold_arrow_left arrow-btn prev'
};
/**
 * @Component: Click to Action
 * @Section: ButtonsLinksLists
 * @description This component generates citi related click to actions like links, buttons, and their
 * various states.
 */
class CTAComponent {
    /**
     * @property bold
     * @description Makes the CTA bold.
     * @Type: Empty Attribute
     */
    set bold(bold) {
        if (bold !== false)
            this._bold = 'bold';
    }
    /**
     * @property noMargin
     * @description Supplies the aria-labeledby value
     * @return string
     */
    set noMargin(notMargin) {
        if (typeof notMargin !== 'undefined' && notMargin !== false) {
            this._noMargin = 'noMargin';
        }
    }
    /**
     * @property tabIndex
     * @description Supplies the tabindex value
     * @return string
     */
    set tabIndex(tabindex) {
        if (typeof tabindex !== 'undefined') {
            this._tabIndex = tabindex;
        }
    }
    /**
     * @property ariaDescribedBy
     * @description Supplies the aria-describedby value
     * @return string
     */
    set ariaDescription(description) {
        if (typeof description !== 'undefined') {
            this._ariaDescription = description;
        }
    }
    /**
     * @property ariaLabeledBy
     * @description Supplies the aria-labeledby value
     * @return string
     */
    set ariaLabeledBy(labeledBy) {
        if (typeof labeledBy !== 'undefined') {
            this._ariaLabeledBy = labeledBy;
        }
    }
    /**
     * @property ariaHasPopup
     * @description Supplies the value for the aria-haspopup attribute
     * @return string
     */
    set ariaHasPopup(hasPopup) {
        if (typeof hasPopup !== 'undefined') {
            this._ariaHasPopup = hasPopup;
        }
    }
    /**
     * @property ariaRole
     * @description Supplies the value for the role attribute
     * @return string
     */
    set ariaRole(role) {
        if (typeof role !== 'undefined') {
            this._role = role;
        }
    }
    /**
     * @property ariaLabel
     * @description Supplies the aria-label value
     * @return string
     */
    set ariaLabel(label) {
        if (typeof label !== 'undefined') {
            this._ariaLabel = label;
        }
    }
    /**
     * @property ariaControls
     * @description Supplies the aria-controls value
     * @return string
     */
    set ariaControls(controls) {
        if (typeof controls !== 'undefined') {
            this._ariaControls = controls;
        }
    }
    /**
     * @property ariaExpanded
     * @description Supplies the aria-controls value
     * @return string
     */
    set ariaExpanded(expanded) {
        if (typeof expanded !== 'undefined') {
            this._ariaExpanded = expanded;
        }
    }
    /**
     * @property dataTarget
     * @description Sets value to the data-target attribute for a button
     * @return string
     */
    set dataTarget(value) {
        this._dataTarget = value;
    }
    /**
     * @property addJFPToken
     * @description Whether the JFP Token should be appended regardless of what the target attribute is set to.
     * NOTE: This is only to be used in cases where the target attribute would otherwise prevent the JFP token from being appended.
     * @Type: boolean
     */
    set addJFPToken(add) {
        this._addJFPToken = add;
        this.href = this._href;
    }
    /**
     * @property doNotAddJFPToken
     * @description Overrides the addition of adding the JFP token (could not use the addJFPToken attribute since it defaults to false already)
     */
    set doNotAddJFPToken(doNotAdd) {
        this._doNotAddJFPToken = doNotAdd;
        this.href = this._href;
    }
    /**
     * @property buttonType
     * @description This is the type property for the button
     * @return string
     */
    set buttonType(type) {
        if (typeof type !== 'undefined') {
            this._buttonType = type;
        }
    }
    /**
     * @property target
     * @description The value of the target attribute for the CTA.
     * @return string
     */
    set target(target) {
        if (typeof target !== 'undefined') {
            this._guid = this.guid();
            this._target = target;
            if (target === '_blank' && !this._ariaDescription) {
                this._ariaDescription = 'Opens in new window';
            }
            this.href = this._href;
        }
    }
    /**
     * @property href
     * @description The value of the href attribute for the CTA.
     * @return string
     */
    set href(href) {
        if (typeof href === 'string' && href.length) {
            if ((this._target !== '_blank' || this._addJFPToken) &&
                !href.match('JFP_TOKEN') &&
                !this._doNotAddJFPToken) {
                if (isPlatformBrowser(this.platformId))
                    href = addJfpToken(href);
            }
            this._href = this._domSanitizer.sanitize(SecurityContext.URL, href);
        }
    }
    /**
     * @property value
     * @description The text value of the CTA.
     * @return string
     */
    set value(value) {
        if (typeof value !== 'undefined') {
            this._guid = this.guid();
            this._value = value;
        }
    }
    /**
     * @property disabled
     * @description Shows the disabled state of the CTA
     * @Type: boolean
     * @twoway true
     */
    set disabled(value) {
        if (typeof value !== 'undefined') {
            // Prevent error when trying to JSON.parse empty string
            if (value === '')
                value = 'false';
            if (boolCheck(value)) {
                this._isDisabled = JSON.parse(value);
                this._guid = this.guid();
                JSON.parse(value) === true
                    ? (this._disabled = 'disabled')
                    : (this._disabled = '');
                setTimeout(() => {
                    this.disabledChange.emit(value);
                }, 0);
            }
        }
    }
    /**
     * @property type
     * @description Determines the type of CTA to be displayed.  Valid options include
     * primary, secondary, tertiary, or icon.
     * @return string
     */
    set type(type) {
        if (typeof type !== 'undefined') {
            this._guid = this.guid();
            const typeKeys = Object.keys(this.buttonClasses);
            if (!typeKeys.includes(type)) {
                console.error('Valid button types are ', ...typeKeys);
            }
            else {
                for (const k of typeKeys) {
                    if (type === k) {
                        this._type = this.buttonClasses[k];
                        break;
                    }
                }
            }
        }
    }
    /**
     * @property size
     * @description Determines the size of CTA to be displayed.  Valid options include
     * large and small.  Can only be applied to primary buttons.
     * @return string
     */
    set size(size) {
        if (typeof size !== 'undefined') {
            this._guid = this.guid();
            const typeKeys = Object.keys(this.buttonClasses);
            if (typeKeys.includes('primary')) {
                if (!this._arrow || (this._arrow && size !== 'small')) {
                    this._size = size;
                }
            }
            else {
                console.error('Button sizes cannot be applied to tertiary or secondary buttons.');
            }
        }
    }
    /**
     * @property arrow
     * @description Determines whether the CTA should have an arrow or not.  Valid options
     * include left or right. Arrows can only be given to primary buttons and buttons that are not small.
     * @return string
     */
    set arrow(arrow) {
        if (typeof arrow !== 'undefined') {
            this._hasArrow = true;
            this._guid = this.guid();
            if (!this._type.match('secondary') || !this._type.match('tertiary')) {
                if (this._size !== 'small') {
                    if (arrow === 'left') {
                        this._arrow = this.buttonClasses.arrowLeft;
                    }
                    else if (arrow === 'right') {
                        this._arrow = this.buttonClasses.arrowRight;
                    }
                }
                else {
                    if (isDevMode) {
                        console.warn('Arrows cannot be added to a small size button');
                    }
                }
            }
            else {
                if (isDevMode) {
                    console.warn('Arrows can only be added to primary buttons');
                }
            }
        }
    }
    /**
     * @property leftArrow
     * @description Determines whether the Secondary CTA should have a left arrow or not.  Valid options
     * include left.
     * @return string
     */
    set leftArrow(leftArrow) {
        if (typeof leftArrow !== 'undefined') {
            this._guid = this.guid();
            if (this._type.match('chevron-link')) {
                if (leftArrow === 'left') {
                    this._leftArrow = this.buttonClasses.leftArrow;
                }
            }
            else {
                if (isDevMode) {
                    console.warn('leftArrow cannot be added to a primary button');
                }
            }
        }
    }
    // #endregion
    // #region // ----------------------- Methods ----------------------- //
    constructor(platformId, cdr, _domSanitizer) {
        this.platformId = platformId;
        this.cdr = cdr;
        this._domSanitizer = _domSanitizer;
        // #region // ----------------------- Properties ----------------------- //
        /**
         * @description CTASettings to hold the settings for the CTA
         */
        this._arrow = '';
        this._bold = '';
        this._button = '';
        this._buttonType = 'button';
        this._disabled = '';
        this._href = null;
        this._size = '';
        this._target = '_self';
        this._type = 'btn btn-primary';
        this._value = '';
        this._dataTarget = null;
        this._addJFPToken = false;
        this._doNotAddJFPToken = false;
        this._noMargin = '';
        this._leftArrow = '';
        /**
         * @property buttonClasses
         * @description Classes for buttons
         */
        this.buttonClasses = CTA_CLASSES;
        // #endregion
        // #region // ----------------------- Input/Output ----------------------- //
        /**
         * @property textAlign
         * @description Gives ability to align the text. By default is center.
         * @return string
         */
        this.textAlign = 'center';
        /**
         * @Event: ctaClick
         * @description CTA click event for detecting 'disabled' state
         */
        this.ctaClick = new EventEmitter();
        /**
         * @Event: disabledChange
         * @description Disabled property is changed
         */
        this.disabledChange = new EventEmitter();
        /**
         * @Event: ctaFocus
         * @description CTA is focused
         */
        this.ctaFocus = new EventEmitter();
        /**
         * @Event: ctaBlur
         * @description CTA is blurred
         */
        this.ctaBlur = new EventEmitter();
        this.idStr = this.guid();
    }
    onFocus(event) {
        // Sets the focus to the inner button which is the real element need to be focus
        this.button.nativeElement.focus();
    }
    onButtonFocus(event) {
        this.ctaFocus.emit(event);
    }
    onButtonBlur(event) {
        this.ctaBlur.emit(event);
    }
    guid() {
        function s4() {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        }
        return (s4() +
            s4() +
            '-' +
            s4() +
            '-' +
            s4() +
            '-' +
            s4() +
            '-' +
            s4() +
            s4() +
            s4());
    }
    onCTAClick() {
        if (this._disabled !== 'disabled') {
            this.ctaClick.emit();
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CTAComponent, deps: [{ token: PLATFORM_ID }, { token: i0.ChangeDetectorRef }, { token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: CTAComponent, isStandalone: false, selector: "citi-cta", inputs: { textAlign: "textAlign", bold: "bold", noMargin: "noMargin", tabIndex: "tabIndex", ariaDescription: "ariaDescription", ariaLabeledBy: "ariaLabeledBy", ariaHasPopup: "ariaHasPopup", ariaRole: "ariaRole", ariaLabel: "ariaLabel", ariaControls: "ariaControls", ariaExpanded: "ariaExpanded", router: "router", queryParams: "queryParams", idStr: "idStr", dataTarget: "dataTarget", addJFPToken: "addJFPToken", doNotAddJFPToken: "doNotAddJFPToken", buttonType: "buttonType", target: "target", href: "href", value: "value", disabled: "disabled", type: "type", size: "size", arrow: "arrow", leftArrow: "leftArrow" }, outputs: { ctaClick: "ctaClick", disabledChange: "disabledChange", ctaFocus: "ctaFocus", ctaBlur: "ctaBlur" }, host: { listeners: { "focus": "onFocus($event)" } }, viewQueries: [{ propertyName: "button", first: true, predicate: ["button"], descendants: true }], ngImport: i0, template: `
    <button
      #button
      *ngIf="!_href && !router"
      [type]="_buttonType"
      [attr.id]="idStr"
      [attr.tabindex]="_tabIndex ? _tabIndex : null"
      [attr.disabled]="_disabled === 'disabled' ? true : null"
      [attr.aria-haspopup]="_ariaHasPopup ? _ariaHasPopup : null"
      [attr.aria-controls]="_ariaControls ? _ariaControls : null"
      [attr.aria-expanded]="_ariaExpanded"
      [attr.role]="_role ? _role : null"
      [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
      [attr.aria-labelledby]="_ariaLabeledBy ? _ariaLabeledBy : null"
      [attr.aria-describedby]="
        _ariaDescription ? _guid + '_ariadescription' : null
      "
      [ngClass]="[
        _button,
        _type,
        _disabled,
        _arrow,
        _leftArrow,
        _size,
        _bold,
        _noMargin
      ]"
      [attr.data-target]="_dataTarget"
      [innerHtml]="_value"
      (click)="onCTAClick()"
      [style.text-align]="textAlign"
      (focus)="onButtonFocus($event)"
      (blur)="onButtonBlur($event)"
    ></button>
    <a
      #button
      *ngIf="_href"
      [attr.target]="_target"
      [attr.id]="idStr"
      [attr.tabindex]="_tabIndex ? _tabIndex : null "
      [attr.disabled]="_disabled === 'disabled' ? true : null"
      [attr.aria-disabled]="_disabled === 'disabled' ? true : null"
      [attr.href]="_href ? _href : null"
      [attr.role]="_role ? _role : null"
      [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
      [attr.aria-labelledby]="_ariaLabeledBy ? _ariaLabeledBy : null"
      [attr.aria-describedby]="
        _ariaDescription ? _guid + '_ariadescription' : null
      "
      [ngClass]="[
        _button,
        _type,
        _disabled,
        _leftArrow,
        _bold,
        _noMargin
      ]"
      class="cta-link"
      [innerHtml]="_value"
      [attr.data-target]="_dataTarget"
      (click)="onCTAClick()"
      (focus)="onButtonFocus($event)"
      (blur)="onButtonBlur($event)"
    >
    </a>
    <a
      #button
      *ngIf="router"
      [routerLink]="router"
      [queryParams]="queryParams"
      [attr.id]="idStr"
      [attr.tabindex]="_tabIndex ? _tabIndex : null"
      [attr.disabled]="_disabled === 'disabled' ? true : null"
      [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
      [attr.aria-labelledby]="_ariaLabeledBy ? _ariaLabeledBy : null"
      [attr.aria-describedby]="
        _ariaDescription ? _guid + '_ariadescription' : null
      "
      [innerHtml]="_value"
      [attr.data-target]="_dataTarget"
      [ngClass]="[
        _button,
        _type,
        _disabled,
        _leftArrow,
        _bold,
        _noMargin
      ]"
      class="cta-link-router"
      (focus)="onButtonFocus($event)"
      (blur)="onButtonBlur($event)"
    ></a>

    <span
      *ngIf="_ariaDescription"
      [id]="_guid + '_ariadescription'"
      class="sr-only"
      aria-hidden="true"
      [innerHtml]="_ariaDescription"
    ></span>
  `, isInline: true, styles: ["@charset \"UTF-8\";.btn_white,.btn-primary.btn_white{background-color:#fff;color:#056dae;border-color:#fff}.btn_white:hover,.btn_white:focus,.btn-primary.btn_white:hover,.btn-primary.btn_white:focus{background-color:#056dae;color:#fff;border-color:#056dae}.btn_white_on_blue,.btn-primary.btn_white_on_blue{background-color:#fff;color:#056dae;border-color:#fff}.btn_white_on_blue:hover,.btn_white_on_blue:focus,.btn-primary.btn_white_on_blue:hover,.btn-primary.btn_white_on_blue:focus{background-color:#002a54;color:#fff;border-color:#002a54}.btn_dark_priority{background-color:#0e2a48;color:#fff;border-color:#0e2a48}.btn_dark_priority:hover,.btn_dark_priority:focus{background-color:#091022;color:#fff;border-color:#091022}.btn-primary.btn_dark_priority:active{background-color:#091022;color:#fff;border-color:#091022}.btn_light_priority,.btn-primary.btn_light_priority{background-color:#fff;color:#000;border-color:#fff}.btn_light_priority:hover,.btn_light_priority:focus,.btn-primary.btn_light_priority:hover,.btn-primary.btn_light_priority:focus{background-color:#65778a;color:#fff;border-color:#65778a}.btn.btn-white-disabled{border-color:#fff;color:#fff;background-color:transparent;opacity:1}.btn.btn-white-disabled:hover{border-color:#fff;color:#fff}.btn.btn-dark-disabled{border-color:#666;opacity:.5;color:#666;background-color:transparent}.btn.btn-dark-disabled:hover{border-color:#666;color:#666}.btn_dark_arrow_right{background-color:#0e2a48;color:#fff;border-color:#0e2a48}.btn_dark_arrow_right:hover,.btn_dark_arrow_right:focus{background-color:#091022;border-color:#091022}.btn_dark_arrow_right[disabled],.btn_dark_arrow_right.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.btn_dark_arrow_right:active{background-color:#091022;color:#fff;border-color:#091022}.btn_dark_arrow_left{background-color:#0e2a48;color:#fff;border-color:#0e2a48;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn_dark_arrow_left:hover,.btn_dark_arrow_left:focus{background-color:#091022;color:#fff;border-color:#091022;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn_dark_arrow_left[disabled],.btn_dark_arrow_left.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.btn_dark_arrow_left:active{background-color:#091022;color:#fff;border-color:#091022}.btn_white_arrow_left{background-color:#fff;color:#056dae;border-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_arrow_left:hover,.btn_white_arrow_left:focus{background-color:#056dae;color:#fff;border-color:#056dae;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_arrow_right{background-color:#fff;color:#056dae;border-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_arrow_right:hover,.btn_white_arrow_right:focus{background-color:#056dae;color:#fff;border-color:#056dae;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_blue_hover_arrow_left{background-color:#fff;color:#056dae;border-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_blue_hover_arrow_left:hover,.btn_blue_hover_arrow_left:focus{background-color:#65778a;color:#fff;border-color:#65778a;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_blue_hover_arrow_right{background-color:#fff;color:#056dae;border-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_blue_hover_arrow_right:hover,.btn_blue_hover_arrow_right:focus{background-color:#65778a;color:#fff;border-color:#65778a;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_priority_arrow_left{background-color:#fff;color:#0e2a48;border-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%230e2a48;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_priority_arrow_left:hover,.btn_white_priority_arrow_left:focus{background-color:#65778a;color:#fff;border-color:#65778a;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_priority_arrow_right{background-color:#fff!important;color:#0e2a48!important;border-color:#fff!important;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%230e2a48;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_priority_arrow_right:hover,.btn_white_priority_arrow_right:focus{background-color:#65778a!important;color:#fff!important;border-color:#65778a!important;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_gold{background-color:#8e6f32;color:#fff;border-color:#8e6f32}.btn_gold:hover,.btn_gold:focus{background-color:#745b2d;color:#fff;border-color:#745b2d}.btn_gold[disabled],.btn_gold[disabled]:hover,.btn_gold[disabled]:focus,.btn_gold.disabled,.btn_gold.disabled:hover,.btn_gold.disabled:focus{color:#00000080;border-color:#00000080}.btn-primary.btn_gold:active{background-color:#745b2d;color:#fff;border-color:#745b2d}.gold_arrow_right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.gold_arrow_right:hover,.gold_arrow_right:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.gold_arrow_right[disabled],.gold_arrow_right.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.gold_arrow_right:active{background-color:#745b2d;color:#fff;border-color:#745b2d}.gold_arrow_left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.gold_arrow_left:hover,.gold_arrow_left:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.gold_arrow_left[disabled],.gold_arrow_left.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.gold_arrow_left:active{background-color:#745b2d;color:#fff;border-color:#745b2d}:host-context(.theme-dark,.cta-bg-dark) .btn_gold,.dark.btn_gold{background-color:#fff;color:#8e6f32;border-color:#fff}:host-context(.theme-dark,.cta-bg-dark) .btn_gold:hover,.dark.btn_gold:hover,:host-context(.theme-dark,.cta-bg-dark) .btn_gold:focus,.dark.btn_gold:focus{background-color:#8e6f32;color:#fff;border-color:#8e6f32}:host-context(.theme-dark,.cta-bg-dark) [disabled].btn_gold,[disabled].dark.btn_gold,:host-context(.theme-dark,.cta-bg-dark) .disabled.btn_gold,.disabled.dark.btn_gold{background-color:transparent;color:#fff;border-color:#fff}:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_right,.dark.gold_arrow_right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%238e6f32;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_right:hover,.dark.gold_arrow_right:hover,:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_right:focus,.dark.gold_arrow_right:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark,.cta-bg-dark) [disabled].gold_arrow_right,[disabled].dark.gold_arrow_right,:host-context(.theme-dark,.cta-bg-dark) .disabled.gold_arrow_right,.disabled.dark.gold_arrow_right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_left,.dark.gold_arrow_left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%238e6f32;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_left:hover,.dark.gold_arrow_left:hover,:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_left:focus,.dark.gold_arrow_left:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark,.cta-bg-dark) [disabled].gold_arrow_left,[disabled].dark.gold_arrow_left,:host-context(.theme-dark,.cta-bg-dark) .disabled.gold_arrow_left,.disabled.dark.gold_arrow_left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.arrow-btn.next[disabled],.btn-primary.arrow-btn.next.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.arrow-btn.prev[disabled],.btn-primary.arrow-btn.prev.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.secondary{text-decoration:none;white-space:normal}a.secondary.cta-link,a.cta-link-router{text-decoration:underline}a.cta-link-router.btn-primary{text-decoration:none}.btn-link.btn-icon:before{content:\"\";left:-20px;top:4px;height:12px;width:12px;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Cpath d='M26,19H21V14a1,1,0,0,0-2,0v5H14a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V21h5a1,1,0,0,0,0-2' transform='translate(-13 -13)' style='fill:%23056DAE;fill-rule:evenodd'/%3E%3C/svg%3E\")}.btn-link.btn-icon:hover,.btn-link.btn-icon:focus{text-decoration:underline}.btn-link.btn-icon:hover:before,.btn-link.btn-icon:focus:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Cpath d='M26,19H21V14a1,1,0,0,0-2,0v5H14a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V21h5a1,1,0,0,0,0-2' transform='translate(-13 -13)' style='fill:%23002a54;fill-rule:evenodd'/%3E%3C/svg%3E\")}.btn-link.btn-icon[disabled],.btn-link.btn-icon.disabled{color:#00000080;cursor:default;opacity:.65}.btn-link.btn-icon[disabled]:before,.btn-link.btn-icon.disabled:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 14 14%27%3E%3Cpath d=%27M26,19H21V14a1,1,0,0,0-2,0v5H14a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V21h5a1,1,0,0,0,0-2%27 transform=%27translate(-13 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-link.btn-icon[disabled]:hover,.btn-link.btn-icon[disabled]:focus,.btn-link.btn-icon.disabled:hover,.btn-link.btn-icon.disabled:focus{text-decoration:none}a.btn-icon.cta-link,a.cta-link-router.btn-link.btn-icon{text-decoration:underline}:host-context(.theme-dark) .secondary:hover,:host-context(.theme-dark) .secondary:focus{text-decoration:underline}.noMargin{margin:0}.chevron-link.chevron-link,.chevron-link.chevron-link:hover,.chevron-link.chevron-link:focus,.chevron-link.chevron-link.bold,.chevron-link.chevron-link.bold:hover,.chevron-link.chevron-link.bold:focus{background-image:none;margin-right:0}.chevron-link.chevron-link:after{content:\"\\a0\";background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\");background-repeat:no-repeat;background-color:transparent;background-position:top 50% right 0;display:inline-block;margin-left:6px}.chevron-link.chevron-link:hover:after,.chevron-link.chevron-link:focus:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23002a54;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.bold:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%276.25px%27 height=%2711.25px%27 viewBox=%270 0 5 9%27%3E%3Cg transform=%27translate(-1274.000000, -1120.000000)%27 fill=%27%23056dae%27 %3E%3Cpath d=%27M1278.93387,1124.67494 L1278.93387,1123.48506 L1274.744,1120.08 L1274.744,1122.26987 L1277.11109,1124.09266 L1274.744,1125.90278 L1274.744,1128.08 L1278.93387,1124.67494 Z%27 /%3E%3C/g%3E%3C/svg%3E\")}.chevron-link.chevron-link.bold:hover:after,.chevron-link.chevron-link.bold:focus:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%276.25px%27 height=%2711.25px%27 viewBox=%270 0 5 9%27%3E%3Cg transform=%27translate(-1274.000000, -1120.000000)%27 fill=%27%23002a54%27 %3E%3Cpath d=%27M1278.93387,1124.67494 L1278.93387,1123.48506 L1274.744,1120.08 L1274.744,1122.26987 L1277.11109,1124.09266 L1274.744,1125.90278 L1274.744,1128.08 L1278.93387,1124.67494 Z%27 /%3E%3C/g%3E%3C/svg%3E\")}.chevron-link.chevron-link.disabled:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.disabled:hover:after,.chevron-link.chevron-link.disabled:focus:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.disabled.bold:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%276.25px%27 height=%2711.25px%27 viewBox=%270 0 5 9%27%3E%3Cg transform=%27translate(-1274.000000, -1120.000000)%27 fill=%27rgba(0, 0, 0, 0.5)%27 %3E%3Cpath d=%27M1278.93387,1124.67494 L1278.93387,1123.48506 L1274.744,1120.08 L1274.744,1122.26987 L1277.11109,1124.09266 L1274.744,1125.90278 L1274.744,1128.08 L1278.93387,1124.67494 Z%27 /%3E%3C/g%3E%3C/svg%3E\")}.chevron-link.chevron-link.disabled.bold:hover:after,.chevron-link.chevron-link.disabled.bold:focus:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%276.25px%27 height=%2711.25px%27 viewBox=%270 0 5 9%27%3E%3Cg transform=%27translate(-1274.000000, -1120.000000)%27 fill=%27rgba(0, 0, 0, 0.5)%27 %3E%3Cpath d=%27M1278.93387,1124.67494 L1278.93387,1123.48506 L1274.744,1120.08 L1274.744,1122.26987 L1277.11109,1124.09266 L1274.744,1125.90278 L1274.744,1128.08 L1278.93387,1124.67494 Z%27 /%3E%3C/g%3E%3C/svg%3E\")}.chevron-link.chevron-link.left:after{display:none}.chevron-link.chevron-link.left:before{content:\"\\a0\";background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\");background-repeat:no-repeat;background-color:transparent;background-position:top 50% left 0;display:inline-block;margin-right:6px}.chevron-link.chevron-link.left:hover:before,.chevron-link.chevron-link.left:focus:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23002a54;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.left.bold:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.left.bold:hover:before,.chevron-link.chevron-link.left.bold:focus:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23002a54;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.left.disabled:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.left.disabled:hover:before,.chevron-link.chevron-link.left.disabled:focus:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark) .chevron-link,:host-context(.theme-dark) .chevron-link:hover,:host-context(.theme-dark) .chevron-link:focus,:host-context(.theme-dark) .chevron-link.bold,:host-context(.theme-dark) .chevron-link.bold:hover,:host-context(.theme-dark) .chevron-link.bold:focus{background-image:none;margin-right:0}:host-context(.theme-dark) .chevron-link:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark) .chevron-link.bold:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%276.25px%27 height=%2711.25px%27 viewBox=%270 0 5 9%27%3E%3Cg transform=%27translate(-1274.000000, -1120.000000)%27 fill=%27%23ffffff%27 %3E%3Cpath d=%27M1278.93387,1124.67494 L1278.93387,1123.48506 L1274.744,1120.08 L1274.744,1122.26987 L1277.11109,1124.09266 L1274.744,1125.90278 L1274.744,1128.08 L1278.93387,1124.67494 Z%27 /%3E%3C/g%3E%3C/svg%3E\")}:host-context(.theme-dark) .chevron-link.left:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark) .chevron-link.left.bold:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn{vertical-align:middle;margin-right:20px;text-align:center;font-size:1rem;display:inline-block;margin-bottom:0;-ms-touch-action:manipulation;touch-action:manipulation;cursor:pointer;background-image:none;border:1px solid transparent;white-space:nowrap;padding:6px 20px;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.btn-primary{color:#fff;border-color:#056dae;background:#056dae;line-height:46px;border-width:2px;border-style:solid;margin:20px 20px 20px 0;min-width:220px;position:relative;padding-top:0;padding-bottom:0;font-family:Interstate_Bold,sans-serif;font-weight:300;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;border-radius:6px}.btn-primary:active,.btn-primary:hover,.btn-primary:focus{color:#fff;background-color:#002a54;border-color:#002a54;text-decoration:none}@media screen and (max-width: 991px){.btn-primary{float:none!important;width:100%}}.btn-default,.btn-default:active,.btn-default:focus,.btn-default:hover,.btn-default-light,.btn-default-light:active,.btn-default-light:focus,.btn-default-light:hover,.btn-info-svg,.btn-info-svg:active,.btn-info-svg:focus,.btn-info-svg:hover,.btn-info,.btn-info:active,.btn-info:focus,.btn-info:hover,.btn-link,.btn-link:active,.btn-link:focus,.btn-link:hover{border-color:transparent;background-color:transparent}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CTAComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-cta', standalone: false, template: `
    <button
      #button
      *ngIf="!_href && !router"
      [type]="_buttonType"
      [attr.id]="idStr"
      [attr.tabindex]="_tabIndex ? _tabIndex : null"
      [attr.disabled]="_disabled === 'disabled' ? true : null"
      [attr.aria-haspopup]="_ariaHasPopup ? _ariaHasPopup : null"
      [attr.aria-controls]="_ariaControls ? _ariaControls : null"
      [attr.aria-expanded]="_ariaExpanded"
      [attr.role]="_role ? _role : null"
      [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
      [attr.aria-labelledby]="_ariaLabeledBy ? _ariaLabeledBy : null"
      [attr.aria-describedby]="
        _ariaDescription ? _guid + '_ariadescription' : null
      "
      [ngClass]="[
        _button,
        _type,
        _disabled,
        _arrow,
        _leftArrow,
        _size,
        _bold,
        _noMargin
      ]"
      [attr.data-target]="_dataTarget"
      [innerHtml]="_value"
      (click)="onCTAClick()"
      [style.text-align]="textAlign"
      (focus)="onButtonFocus($event)"
      (blur)="onButtonBlur($event)"
    ></button>
    <a
      #button
      *ngIf="_href"
      [attr.target]="_target"
      [attr.id]="idStr"
      [attr.tabindex]="_tabIndex ? _tabIndex : null "
      [attr.disabled]="_disabled === 'disabled' ? true : null"
      [attr.aria-disabled]="_disabled === 'disabled' ? true : null"
      [attr.href]="_href ? _href : null"
      [attr.role]="_role ? _role : null"
      [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
      [attr.aria-labelledby]="_ariaLabeledBy ? _ariaLabeledBy : null"
      [attr.aria-describedby]="
        _ariaDescription ? _guid + '_ariadescription' : null
      "
      [ngClass]="[
        _button,
        _type,
        _disabled,
        _leftArrow,
        _bold,
        _noMargin
      ]"
      class="cta-link"
      [innerHtml]="_value"
      [attr.data-target]="_dataTarget"
      (click)="onCTAClick()"
      (focus)="onButtonFocus($event)"
      (blur)="onButtonBlur($event)"
    >
    </a>
    <a
      #button
      *ngIf="router"
      [routerLink]="router"
      [queryParams]="queryParams"
      [attr.id]="idStr"
      [attr.tabindex]="_tabIndex ? _tabIndex : null"
      [attr.disabled]="_disabled === 'disabled' ? true : null"
      [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
      [attr.aria-labelledby]="_ariaLabeledBy ? _ariaLabeledBy : null"
      [attr.aria-describedby]="
        _ariaDescription ? _guid + '_ariadescription' : null
      "
      [innerHtml]="_value"
      [attr.data-target]="_dataTarget"
      [ngClass]="[
        _button,
        _type,
        _disabled,
        _leftArrow,
        _bold,
        _noMargin
      ]"
      class="cta-link-router"
      (focus)="onButtonFocus($event)"
      (blur)="onButtonBlur($event)"
    ></a>

    <span
      *ngIf="_ariaDescription"
      [id]="_guid + '_ariadescription'"
      class="sr-only"
      aria-hidden="true"
      [innerHtml]="_ariaDescription"
    ></span>
  `, changeDetection: ChangeDetectionStrategy.OnPush, styles: ["@charset \"UTF-8\";.btn_white,.btn-primary.btn_white{background-color:#fff;color:#056dae;border-color:#fff}.btn_white:hover,.btn_white:focus,.btn-primary.btn_white:hover,.btn-primary.btn_white:focus{background-color:#056dae;color:#fff;border-color:#056dae}.btn_white_on_blue,.btn-primary.btn_white_on_blue{background-color:#fff;color:#056dae;border-color:#fff}.btn_white_on_blue:hover,.btn_white_on_blue:focus,.btn-primary.btn_white_on_blue:hover,.btn-primary.btn_white_on_blue:focus{background-color:#002a54;color:#fff;border-color:#002a54}.btn_dark_priority{background-color:#0e2a48;color:#fff;border-color:#0e2a48}.btn_dark_priority:hover,.btn_dark_priority:focus{background-color:#091022;color:#fff;border-color:#091022}.btn-primary.btn_dark_priority:active{background-color:#091022;color:#fff;border-color:#091022}.btn_light_priority,.btn-primary.btn_light_priority{background-color:#fff;color:#000;border-color:#fff}.btn_light_priority:hover,.btn_light_priority:focus,.btn-primary.btn_light_priority:hover,.btn-primary.btn_light_priority:focus{background-color:#65778a;color:#fff;border-color:#65778a}.btn.btn-white-disabled{border-color:#fff;color:#fff;background-color:transparent;opacity:1}.btn.btn-white-disabled:hover{border-color:#fff;color:#fff}.btn.btn-dark-disabled{border-color:#666;opacity:.5;color:#666;background-color:transparent}.btn.btn-dark-disabled:hover{border-color:#666;color:#666}.btn_dark_arrow_right{background-color:#0e2a48;color:#fff;border-color:#0e2a48}.btn_dark_arrow_right:hover,.btn_dark_arrow_right:focus{background-color:#091022;border-color:#091022}.btn_dark_arrow_right[disabled],.btn_dark_arrow_right.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.btn_dark_arrow_right:active{background-color:#091022;color:#fff;border-color:#091022}.btn_dark_arrow_left{background-color:#0e2a48;color:#fff;border-color:#0e2a48;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn_dark_arrow_left:hover,.btn_dark_arrow_left:focus{background-color:#091022;color:#fff;border-color:#091022;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn_dark_arrow_left[disabled],.btn_dark_arrow_left.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.btn_dark_arrow_left:active{background-color:#091022;color:#fff;border-color:#091022}.btn_white_arrow_left{background-color:#fff;color:#056dae;border-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_arrow_left:hover,.btn_white_arrow_left:focus{background-color:#056dae;color:#fff;border-color:#056dae;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_arrow_right{background-color:#fff;color:#056dae;border-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_arrow_right:hover,.btn_white_arrow_right:focus{background-color:#056dae;color:#fff;border-color:#056dae;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_blue_hover_arrow_left{background-color:#fff;color:#056dae;border-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_blue_hover_arrow_left:hover,.btn_blue_hover_arrow_left:focus{background-color:#65778a;color:#fff;border-color:#65778a;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_blue_hover_arrow_right{background-color:#fff;color:#056dae;border-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_blue_hover_arrow_right:hover,.btn_blue_hover_arrow_right:focus{background-color:#65778a;color:#fff;border-color:#65778a;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_priority_arrow_left{background-color:#fff;color:#0e2a48;border-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%230e2a48;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_priority_arrow_left:hover,.btn_white_priority_arrow_left:focus{background-color:#65778a;color:#fff;border-color:#65778a;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_priority_arrow_right{background-color:#fff!important;color:#0e2a48!important;border-color:#fff!important;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%230e2a48;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_white_priority_arrow_right:hover,.btn_white_priority_arrow_right:focus{background-color:#65778a!important;color:#fff!important;border-color:#65778a!important;background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")!important}.btn_gold{background-color:#8e6f32;color:#fff;border-color:#8e6f32}.btn_gold:hover,.btn_gold:focus{background-color:#745b2d;color:#fff;border-color:#745b2d}.btn_gold[disabled],.btn_gold[disabled]:hover,.btn_gold[disabled]:focus,.btn_gold.disabled,.btn_gold.disabled:hover,.btn_gold.disabled:focus{color:#00000080;border-color:#00000080}.btn-primary.btn_gold:active{background-color:#745b2d;color:#fff;border-color:#745b2d}.gold_arrow_right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.gold_arrow_right:hover,.gold_arrow_right:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.gold_arrow_right[disabled],.gold_arrow_right.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.gold_arrow_right:active{background-color:#745b2d;color:#fff;border-color:#745b2d}.gold_arrow_left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.gold_arrow_left:hover,.gold_arrow_left:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.gold_arrow_left[disabled],.gold_arrow_left.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.gold_arrow_left:active{background-color:#745b2d;color:#fff;border-color:#745b2d}:host-context(.theme-dark,.cta-bg-dark) .btn_gold,.dark.btn_gold{background-color:#fff;color:#8e6f32;border-color:#fff}:host-context(.theme-dark,.cta-bg-dark) .btn_gold:hover,.dark.btn_gold:hover,:host-context(.theme-dark,.cta-bg-dark) .btn_gold:focus,.dark.btn_gold:focus{background-color:#8e6f32;color:#fff;border-color:#8e6f32}:host-context(.theme-dark,.cta-bg-dark) [disabled].btn_gold,[disabled].dark.btn_gold,:host-context(.theme-dark,.cta-bg-dark) .disabled.btn_gold,.disabled.dark.btn_gold{background-color:transparent;color:#fff;border-color:#fff}:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_right,.dark.gold_arrow_right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%238e6f32;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_right:hover,.dark.gold_arrow_right:hover,:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_right:focus,.dark.gold_arrow_right:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark,.cta-bg-dark) [disabled].gold_arrow_right,[disabled].dark.gold_arrow_right,:host-context(.theme-dark,.cta-bg-dark) .disabled.gold_arrow_right,.disabled.dark.gold_arrow_right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_left,.dark.gold_arrow_left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%238e6f32;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_left:hover,.dark.gold_arrow_left:hover,:host-context(.theme-dark,.cta-bg-dark) .gold_arrow_left:focus,.dark.gold_arrow_left:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark,.cta-bg-dark) [disabled].gold_arrow_left,[disabled].dark.gold_arrow_left,:host-context(.theme-dark,.cta-bg-dark) .disabled.gold_arrow_left,.disabled.dark.gold_arrow_left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.arrow-btn.next[disabled],.btn-primary.arrow-btn.next.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-primary.arrow-btn.prev[disabled],.btn-primary.arrow-btn.prev.disabled{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.secondary{text-decoration:none;white-space:normal}a.secondary.cta-link,a.cta-link-router{text-decoration:underline}a.cta-link-router.btn-primary{text-decoration:none}.btn-link.btn-icon:before{content:\"\";left:-20px;top:4px;height:12px;width:12px;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Cpath d='M26,19H21V14a1,1,0,0,0-2,0v5H14a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V21h5a1,1,0,0,0,0-2' transform='translate(-13 -13)' style='fill:%23056DAE;fill-rule:evenodd'/%3E%3C/svg%3E\")}.btn-link.btn-icon:hover,.btn-link.btn-icon:focus{text-decoration:underline}.btn-link.btn-icon:hover:before,.btn-link.btn-icon:focus:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Cpath d='M26,19H21V14a1,1,0,0,0-2,0v5H14a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V21h5a1,1,0,0,0,0-2' transform='translate(-13 -13)' style='fill:%23002a54;fill-rule:evenodd'/%3E%3C/svg%3E\")}.btn-link.btn-icon[disabled],.btn-link.btn-icon.disabled{color:#00000080;cursor:default;opacity:.65}.btn-link.btn-icon[disabled]:before,.btn-link.btn-icon.disabled:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 14 14%27%3E%3Cpath d=%27M26,19H21V14a1,1,0,0,0-2,0v5H14a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V21h5a1,1,0,0,0,0-2%27 transform=%27translate(-13 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn-link.btn-icon[disabled]:hover,.btn-link.btn-icon[disabled]:focus,.btn-link.btn-icon.disabled:hover,.btn-link.btn-icon.disabled:focus{text-decoration:none}a.btn-icon.cta-link,a.cta-link-router.btn-link.btn-icon{text-decoration:underline}:host-context(.theme-dark) .secondary:hover,:host-context(.theme-dark) .secondary:focus{text-decoration:underline}.noMargin{margin:0}.chevron-link.chevron-link,.chevron-link.chevron-link:hover,.chevron-link.chevron-link:focus,.chevron-link.chevron-link.bold,.chevron-link.chevron-link.bold:hover,.chevron-link.chevron-link.bold:focus{background-image:none;margin-right:0}.chevron-link.chevron-link:after{content:\"\\a0\";background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\");background-repeat:no-repeat;background-color:transparent;background-position:top 50% right 0;display:inline-block;margin-left:6px}.chevron-link.chevron-link:hover:after,.chevron-link.chevron-link:focus:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23002a54;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.bold:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%276.25px%27 height=%2711.25px%27 viewBox=%270 0 5 9%27%3E%3Cg transform=%27translate(-1274.000000, -1120.000000)%27 fill=%27%23056dae%27 %3E%3Cpath d=%27M1278.93387,1124.67494 L1278.93387,1123.48506 L1274.744,1120.08 L1274.744,1122.26987 L1277.11109,1124.09266 L1274.744,1125.90278 L1274.744,1128.08 L1278.93387,1124.67494 Z%27 /%3E%3C/g%3E%3C/svg%3E\")}.chevron-link.chevron-link.bold:hover:after,.chevron-link.chevron-link.bold:focus:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%276.25px%27 height=%2711.25px%27 viewBox=%270 0 5 9%27%3E%3Cg transform=%27translate(-1274.000000, -1120.000000)%27 fill=%27%23002a54%27 %3E%3Cpath d=%27M1278.93387,1124.67494 L1278.93387,1123.48506 L1274.744,1120.08 L1274.744,1122.26987 L1277.11109,1124.09266 L1274.744,1125.90278 L1274.744,1128.08 L1278.93387,1124.67494 Z%27 /%3E%3C/g%3E%3C/svg%3E\")}.chevron-link.chevron-link.disabled:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.disabled:hover:after,.chevron-link.chevron-link.disabled:focus:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.disabled.bold:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%276.25px%27 height=%2711.25px%27 viewBox=%270 0 5 9%27%3E%3Cg transform=%27translate(-1274.000000, -1120.000000)%27 fill=%27rgba(0, 0, 0, 0.5)%27 %3E%3Cpath d=%27M1278.93387,1124.67494 L1278.93387,1123.48506 L1274.744,1120.08 L1274.744,1122.26987 L1277.11109,1124.09266 L1274.744,1125.90278 L1274.744,1128.08 L1278.93387,1124.67494 Z%27 /%3E%3C/g%3E%3C/svg%3E\")}.chevron-link.chevron-link.disabled.bold:hover:after,.chevron-link.chevron-link.disabled.bold:focus:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%276.25px%27 height=%2711.25px%27 viewBox=%270 0 5 9%27%3E%3Cg transform=%27translate(-1274.000000, -1120.000000)%27 fill=%27rgba(0, 0, 0, 0.5)%27 %3E%3Cpath d=%27M1278.93387,1124.67494 L1278.93387,1123.48506 L1274.744,1120.08 L1274.744,1122.26987 L1277.11109,1124.09266 L1274.744,1125.90278 L1274.744,1128.08 L1278.93387,1124.67494 Z%27 /%3E%3C/g%3E%3C/svg%3E\")}.chevron-link.chevron-link.left:after{display:none}.chevron-link.chevron-link.left:before{content:\"\\a0\";background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\");background-repeat:no-repeat;background-color:transparent;background-position:top 50% left 0;display:inline-block;margin-right:6px}.chevron-link.chevron-link.left:hover:before,.chevron-link.chevron-link.left:focus:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23002a54;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.left.bold:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23056dae;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.left.bold:hover:before,.chevron-link.chevron-link.left.bold:focus:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23002a54;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.left.disabled:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}.chevron-link.chevron-link.left.disabled:hover:before,.chevron-link.chevron-link.left.disabled:focus:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:rgba(0, 0, 0, 0.5);fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark) .chevron-link,:host-context(.theme-dark) .chevron-link:hover,:host-context(.theme-dark) .chevron-link:focus,:host-context(.theme-dark) .chevron-link.bold,:host-context(.theme-dark) .chevron-link.bold:hover,:host-context(.theme-dark) .chevron-link.bold:focus{background-image:none;margin-right:0}:host-context(.theme-dark) .chevron-link:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E04-glyph-right-white%3C/title%3E%3Cpath d=%27M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark) .chevron-link.bold:after{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%276.25px%27 height=%2711.25px%27 viewBox=%270 0 5 9%27%3E%3Cg transform=%27translate(-1274.000000, -1120.000000)%27 fill=%27%23ffffff%27 %3E%3Cpath d=%27M1278.93387,1124.67494 L1278.93387,1123.48506 L1274.744,1120.08 L1274.744,1122.26987 L1277.11109,1124.09266 L1274.744,1125.90278 L1274.744,1128.08 L1278.93387,1124.67494 Z%27 /%3E%3C/g%3E%3C/svg%3E\")}:host-context(.theme-dark) .chevron-link.left:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}:host-context(.theme-dark) .chevron-link.left.bold:before{background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 8.02 14%27%3E%3Ctitle%3E03-glyph-left-white%3C/title%3E%3Cpath d=%27M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27%27 transform=%27translate(-15.99 -13)%27 style=%27fill:%23ffffff;fill-rule:evenodd%27/%3E%3C/svg%3E\")}.btn{vertical-align:middle;margin-right:20px;text-align:center;font-size:1rem;display:inline-block;margin-bottom:0;-ms-touch-action:manipulation;touch-action:manipulation;cursor:pointer;background-image:none;border:1px solid transparent;white-space:nowrap;padding:6px 20px;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.btn-primary{color:#fff;border-color:#056dae;background:#056dae;line-height:46px;border-width:2px;border-style:solid;margin:20px 20px 20px 0;min-width:220px;position:relative;padding-top:0;padding-bottom:0;font-family:Interstate_Bold,sans-serif;font-weight:300;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;border-radius:6px}.btn-primary:active,.btn-primary:hover,.btn-primary:focus{color:#fff;background-color:#002a54;border-color:#002a54;text-decoration:none}@media screen and (max-width: 991px){.btn-primary{float:none!important;width:100%}}.btn-default,.btn-default:active,.btn-default:focus,.btn-default:hover,.btn-default-light,.btn-default-light:active,.btn-default-light:focus,.btn-default-light:hover,.btn-info-svg,.btn-info-svg:active,.btn-info-svg:focus,.btn-info-svg:hover,.btn-info,.btn-info:active,.btn-info:focus,.btn-info:hover,.btn-link,.btn-link:active,.btn-link:focus,.btn-link:hover{border-color:transparent;background-color:transparent}\n"] }]
        }], ctorParameters: () => [{ type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i0.ChangeDetectorRef }, { type: i1$1.DomSanitizer }], propDecorators: { button: [{
                type: ViewChild,
                args: ['button']
            }], textAlign: [{
                type: Input
            }], bold: [{
                type: Input
            }], noMargin: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabeledBy: [{
                type: Input
            }], ariaHasPopup: [{
                type: Input
            }], ariaRole: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaControls: [{
                type: Input
            }], ariaExpanded: [{
                type: Input
            }], router: [{
                type: Input
            }], queryParams: [{
                type: Input
            }], idStr: [{
                type: Input
            }], dataTarget: [{
                type: Input
            }], addJFPToken: [{
                type: Input
            }], doNotAddJFPToken: [{
                type: Input
            }], buttonType: [{
                type: Input
            }], target: [{
                type: Input
            }], href: [{
                type: Input
            }], value: [{
                type: Input
            }], disabled: [{
                type: Input
            }], type: [{
                type: Input
            }], size: [{
                type: Input
            }], arrow: [{
                type: Input
            }], leftArrow: [{
                type: Input
            }], ctaClick: [{
                type: Output
            }], disabledChange: [{
                type: Output
            }], ctaFocus: [{
                type: Output
            }], ctaBlur: [{
                type: Output
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }] } });

const COMPONENTS$a = [CTAComponent];
const MODULES$b = [CommonModule, RouterModule];
class CTA {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CTA, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: CTA, declarations: [CTAComponent], imports: [CommonModule, RouterModule], exports: [CTAComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CTA, imports: [MODULES$b] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CTA, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$b],
                    declarations: [...COMPONENTS$a],
                    exports: [...COMPONENTS$a]
                }]
        }] });

const SERVER_URL = new InjectionToken('SERVER_URL');
class Icon2RegistryService {
    constructor(http, platformId, serverUrl) {
        this.http = http;
        this.platformId = platformId;
        this.serverUrl = serverUrl;
        this.cachedIconByUrl = new Map();
        this.iconsFetchingByUrl = new Map();
    }
    /** Add a SVG to the registry by passing a name and the SVG data. */
    addSvgByData(data, name) {
        if (!this.cachedIconByUrl.has(name)) {
            this.cachedIconByUrl.set(name, data);
        }
    }
    /** Add a SVG to the registry by passing a name and the SVG source url. */
    addSvgByUrl(url, name = url) {
        if (!this.cachedIconByUrl.has(name)) {
            const data = this._fetchSvgFromUrl(url);
            this.iconsFetchingByUrl.set(name, data);
        }
    }
    /** Load a SVG to the registry from a URL. */
    loadSvg(name) {
        const svgByName = this.getSvgByName(name);
        if (svgByName)
            return svgByName;
        const shade = name.includes('b1') ? 'b1' : 'white';
        const filePath = `./commonui-assets/svgs/icons/${shade}/${name}.svg`;
        const svgData = this._fetchSvgFromUrl(filePath, name);
        return svgData;
    }
    /** Get loaded SVG from registry by name. */
    getSvgByName(name) {
        if (this.cachedIconByUrl.has(name)) {
            return of(this.cachedIconByUrl.get(name));
        }
        else if (this.iconsFetchingByUrl.has(name)) {
            return this.iconsFetchingByUrl.get(name);
        }
    }
    /** Remove a SVG from the registry by URL (or name). */
    unloadSvg(name) {
        return this.cachedIconByUrl.delete(name);
    }
    /**fetch SVG data from given URL */
    _fetchSvgFromUrl(url, name = url) {
        if (!url)
            throw Error('URL is empty or undefined');
        const fetchingInProgress = this.iconsFetchingByUrl.get(url);
        if (fetchingInProgress)
            return fetchingInProgress;
        const svgRequest = this.http.get(url, { responseType: 'text' }).pipe(tap(req => this.cachedIconByUrl.set(name, req)), catchError(err => {
            return throwError(err);
        }), finalize(() => this.iconsFetchingByUrl.delete(name)), share());
        this.iconsFetchingByUrl.set(name, svgRequest);
        return svgRequest;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Icon2RegistryService, deps: [{ token: i3.HttpClient }, { token: PLATFORM_ID }, { token: SERVER_URL, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Icon2RegistryService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Icon2RegistryService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i3.HttpClient }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SERVER_URL]
                }] }] });
function ICON2_REGISTRY_PROVIDER_FACTORY(parentRegistry, httpClient, platformId, serverUrl) {
    return parentRegistry || new Icon2RegistryService(httpClient, platformId, serverUrl);
}
const ICON2_REGISTRY_PROVIDER = {
    provide: Icon2RegistryService,
    deps: [[new Optional(), new SkipSelf(), Icon2RegistryService], HttpClient, [PLATFORM_ID],
        [new Optional(), SERVER_URL]
    ],
    useFactory: ICON2_REGISTRY_PROVIDER_FACTORY
};

class Icon2Component {
    set shade(style) {
        this._shade = style === 'dark' ? 'b1' : style === 'light' ? 'white' : style;
    }
    /**
     * SVG Icons are name in this manner: 000-name-shade(-circle)
     *000: 3 digits code for 'icon', 2 digits code for 'sfi'
     *name: name of the icon
     *shade: 'b1' for dark, 'white' for light
     *(-circle): include -circle in file name if includeCircle is true
     */
    set name(name) {
        this._name = name;
        this.type = name.split('-')[0].length === 2 ? 'sfi' : 'icon';
    }
    constructor(iconRegistry, cdr) {
        this.iconRegistry = iconRegistry;
        this.cdr = cdr;
        this.role = 'img';
        this.idStr = '';
        this.ariaHidden = false;
        this.size = 'medium';
        this.isHoverSfi = false;
        this.includeCircle = false;
        this.type = 'sfi';
        this.tabIndex = null;
        this._shade = '';
    }
    ngOnInit() {
        this.init();
    }
    ngOnDestroy() {
        this.destroy();
    }
    ngOnChanges() {
        if (this._svgElement)
            this.destroy();
        this.init();
    }
    get circleString() {
        return (this.type === 'sfi' && this.isHoverSfi) || this.includeCircle
            ? '-circle'
            : '';
    }
    get svgName() {
        const shade = this._shade ? `-${this._shade}` : '';
        return `${this._name}${shade}${this.circleString}`;
    }
    get smallFormatIconName() {
        const alt = this.isHoverSfi ? '-hover' : this.circleString;
        return `${this._shade}-${this._name}${alt}`;
    }
    destroy() {
        this.svgDivElementRef.nativeElement.innerHTML = '';
        this._svgElement = undefined;
        if (this._iconSubscription)
            this._iconSubscription.unsubscribe();
    }
    init() {
        this.type === 'sfi' ? this.getSmallFormatIconClass() : this.getSVGIcon();
    }
    getSVGIcon() {
        this.iconClass = `${this.size}${this.circleString}`;
        this._iconSubscription = this.iconRegistry
            .loadSvg(this.svgName)
            .subscribe(data => this.initSvg(data));
    }
    getSmallFormatIconClass() {
        this.iconClass = `sfi ${this.smallFormatIconName} ${this.size}${this.circleString}`;
    }
    initSvg(data) {
        if (!data)
            return;
        setTimeout(() => {
            const divElem = this.svgDivElementRef.nativeElement;
            divElem.innerHTML = data;
            this._svgElement = divElem.firstChild;
            this.cdr.markForCheck();
        }, 0);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Icon2Component, deps: [{ token: Icon2RegistryService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: Icon2Component, isStandalone: false, selector: "citi-icon2", inputs: { role: "role", idStr: "idStr", ariaLabel: "ariaLabel", ariaHidden: "ariaHidden", size: "size", isHoverSfi: "isHoverSfi", includeCircle: "includeCircle", type: "type", svgStyle: "svgStyle", tabIndex: "tabIndex", shade: "shade", name: "name" }, viewQueries: [{ propertyName: "svgDivElementRef", first: true, predicate: ["svgContainer"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <div
      #svgContainer
      [attr.tabindex]="tabIndex"
      [ngStyle]="type === 'glyph' ? '' : svgStyle"
      [ngClass]="[iconClass]"
      [attr.id]="idStr"
      [attr.role]="role"
      [attr.aria-label]="ariaLabel"
      [attr.aria-hidden]="ariaHidden ? true : null"
    ></div>
  `, isInline: true, styles: [":host{line-height:0}:host div{display:inline-block;line-height:0px}.small{width:12px;height:12px}.medium{width:14px;height:14px}.large{width:16px;height:16px}.small-circle{width:18px;height:18px}.medium-circle{width:20px;height:20px}.large-circle{width:22px;height:22px}.sfi{background-repeat:no-repeat}.sfi.small{width:12px;height:12px;margin:3px}.sfi.medium{width:14px;height:14px;margin:3px}.sfi.large{width:16px;height:16px;margin:4px}.sfi.small-circle{width:18px;height:18px;margin:5px}.sfi.medium-circle{width:20px;height:20px;margin:5px}.sfi.large-circle{width:22px;height:22px;margin:5px}.sfi.white-01-up{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7.99'%3E%3Ctitle%3E01-up-b1%3C/title%3E%3Cpath d='M18,15.45a1,1,0,0,1-.73-.32L12,9.47,6.73,15.13a1,1,0,0,1-1.46-1.36l5.32-5.72a2,2,0,0,1,2.82,0l5.32,5.72A1,1,0,0,1,18,15.45Z' transform='translate(-5 -7.46)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-01-up-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E01-up-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M13.41,8.05a2,2,0,0,0-2.82,0L5.27,13.77a1,1,0,0,0,1.46,1.36L12,9.47l5.27,5.66a1,1,0,1,0,1.46-1.36Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-01-up-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E01-up-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.68,14.18a1,1,0,0,1-1.41,0L12,9.47,6.73,15.13a1,1,0,0,1-1.46-1.36l5.32-5.72a2,2,0,0,1,2.82,0l5.32,5.72A1,1,0,0,1,18.68,15.18Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-02-down{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-b1%3C/title%3E%3Cpath d='M12,16.52a1.94,1.94,0,0,1-1.41-.62L5.27,10.18A1,1,0,0,1,6.73,8.82L12,14.48l5.27-5.66a1,1,0,1,1,1.46,1.36L13.41,15.9A1.94,1.94,0,0,1,12,16.52Z' transform='translate(-5 -8.5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-02-down-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E02-down-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M17.27,8.82,12,14.48,6.73,8.82a1,1,0,0,0-1.46,1.36l5.32,5.72a1.91,1.91,0,0,0,2.82,0l5.32-5.72a1,1,0,1,0-1.46-1.36Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-02-down-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E02-down-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.73,9.18L13.41,15.9a1.91,1.91,0,0,1-2.82,0L5.27,10.18A1,1,0,0,1,6.73,8.82L12,14.48l5.27-5.66a1,1,0,1,1,1.46,1.36Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-03-left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Cpath d='M14.45,19a1,1,0,0,1-.68-.27L8.05,13.41A2,2,0,0,1,7.43,12a2,2,0,0,1,.62-1.42l5.72-5.32a1,1,0,0,1,1.36,1.47L9.47,12l5.66,5.26a1,1,0,0,1,0,1.42A1,1,0,0,1,14.45,19Z' transform='translate(-7.43 -5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-03-left-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E03-left-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M15.18,5.32a1,1,0,0,0-1.41-.06L8.05,10.58A2,2,0,0,0,7.43,12a2,2,0,0,0,.62,1.41l5.72,5.32a1,1,0,0,0,1.36-1.47L9.47,12l5.66-5.27A1,1,0,0,0,15.18,5.32Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-03-left-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E03-left-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm3.13,16.26a1,1,0,0,1,0,1.42,1,1,0,0,1-1.41.05L8.05,13.41A2,2,0,0,1,7.43,12a2,2,0,0,1,.62-1.42l5.72-5.32a1,1,0,0,1,1.36,1.47L9.47,12Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-04-right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Ctitle%3E04-right-b1%3C/title%3E%3Cpath d='M10,19a1,1,0,0,1-.73-.32,1,1,0,0,1,.05-1.42L15,12,9.32,6.73a1,1,0,0,1,1.36-1.47l5.72,5.32A2,2,0,0,1,17,12a2,2,0,0,1-.62,1.41l-5.72,5.32A1,1,0,0,1,10,19Z' transform='translate(-9 -5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-04-right-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E04-right-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M10.68,5.26A1,1,0,0,0,9.32,6.73L15,12,9.32,17.26a1,1,0,0,0-.05,1.42,1,1,0,0,0,1.41.05l5.72-5.32A2,2,0,0,0,17,12a2,2,0,0,0-.62-1.42Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-04-right-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E04-right-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm4.4,12.41-5.72,5.32a1,1,0,0,1-1.36-1.47L15,12,9.32,6.73a1,1,0,0,1,1.36-1.47l5.72,5.32A2,2,0,0,1,17,12,2,2,0,0,1,16.4,13.41Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-05-check{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 9.99'%3E%3Ctitle%3E05-check-b1%3C/title%3E%3Cpath d='M9.54,17a1.51,1.51,0,0,1-1-.42L5.3,13.48A1,1,0,0,1,6.7,12L9.54,14.8,17.3,7.28a1,1,0,1,1,1.4,1.43l-8.1,7.86A1.53,1.53,0,0,1,9.54,17Z' transform='translate(-5 -7)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-05-check-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E05-check-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M17.3,7.28,9.54,14.8,6.7,12a1,1,0,0,0-1.4,1.44l3.19,3.09a1.51,1.51,0,0,0,1,.42,1.53,1.53,0,0,0,1.06-.42l8.1-7.86a1,1,0,1,0-1.4-1.43Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-05-check-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E05-check-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.7,7.71-8.1,7.86A1.53,1.53,0,0,1,9.54,17a1.51,1.51,0,0,1-1-.42L5.3,13.48A1,1,0,0,1,6.7,12L9.54,14.8,17.3,7.28a1,1,0,1,1,1.4,1.43Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-06-close{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-b1%3C/title%3E%3Cpath d='M13.41,12l3.8-3.79a1,1,0,1,0-1.42-1.42L12,10.59,8.21,6.79A1,1,0,1,0,6.79,8.21L10.59,12l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-6.5 -6.5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-06-close-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M17.21,6.79a1,1,0,0,0-1.42,0L12,10.59,8.21,6.79A1,1,0,1,0,6.79,8.21L10.59,12l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l3.8-3.79A1,1,0,0,0,17.21,6.79Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-06-close-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm5.21,14.79a1,1,0,0,1,0,1.42,1,1,0,0,1-1.42,0L12,13.41l-3.79,3.8a1,1,0,0,1-1.42,0,1,1,0,0,1,0-1.42L10.59,12,6.79,8.21A1,1,0,1,1,8.21,6.79L12,10.59l3.79-3.8a1,1,0,1,1,1.42,1.42L13.41,12Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-07-equal{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7'%3E%3Ctitle%3E07-equal-b1%3C/title%3E%3Cpath d='M18,10.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -8.5)' style='fill:%23fff'/%3E%3Cpath d='M18,15.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -8.5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-07-equal-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E07-equal-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,8.5H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,13.5H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-07-equal-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E07-equal-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,14.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-08-plus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Ctitle%3E08-plus-b1%3C/title%3E%3Cpath d='M18,11H13V6a1,1,0,0,0-2,0v5H6a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V13h5a1,1,0,0,0,0-2Z' transform='translate(-5 -5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-08-plus-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-08-plus-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,12H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-09-minus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 2'%3E%3Ctitle%3E09-minus-b1%3C/title%3E%3Cpath d='M18,13H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -11)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-09-minus-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-09-minus-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,12H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-10-navigation{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 10'%3E%3Ctitle%3E10-navigation-b1%3C/title%3E%3Cpath d='M6,9H18a1,1,0,0,0,0-2H6A1,1,0,0,0,6,9Z' transform='translate(-5 -7)' style='fill:%23fff'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-5 -7)' style='fill:%23fff'/%3E%3Cpath d='M18,15H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-5 -7)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-10-navigation-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E10-navigation-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,7H6A1,1,0,0,0,6,9H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,15H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-10-navigation-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E10-navigation-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,16H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-4H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-4H6A1,1,0,0,1,6,7H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-11-favorite{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Ctitle%3E11-favorite-b1%3C/title%3E%3Cpath d='M12,7.33l.71,2.26L13,10.64h3.31l-1.85,1.43-.81.63.3,1L14.63,16l-1.7-1.3L12,14l-.91.69L9.4,16l.69-2.28.29-1-.82-.63L7.71,10.64h3.07l.35-1L12,7.33M12,4.5a.88.88,0,0,0-.83.58L9.72,9.14H5.88a.91.91,0,0,0-.7.34.88.88,0,0,0,.16,1.23l3.31,2.55L7.41,17.37a.87.87,0,0,0,.15.78.86.86,0,0,0,.7.35.89.89,0,0,0,.53-.18L12,15.86l3.23,2.45a.87.87,0,0,0,.53.18,1.15,1.15,0,0,0,.26,0,.88.88,0,0,0,.58-1.1l-1.24-4.11,3.28-2.54A.85.85,0,0,0,19,10a.87.87,0,0,0-.88-.88h-4l-1.27-4a.9.9,0,0,0-.55-.56.93.93,0,0,0-.29,0Z' transform='translate(-5 -4.5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-11-favorite-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E11-favorite-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18.12,9.14h-4l-1.27-4a.9.9,0,0,0-.55-.56.93.93,0,0,0-.29,0,.88.88,0,0,0-.83.58L9.72,9.14H5.88a.91.91,0,0,0-.7.34.88.88,0,0,0,.16,1.23l3.31,2.55L7.41,17.37a.87.87,0,0,0,.15.78.86.86,0,0,0,.7.35.89.89,0,0,0,.53-.18L12,15.86l3.23,2.45a.87.87,0,0,0,.53.18,1.15,1.15,0,0,0,.26,0,.88.88,0,0,0,.58-1.1l-1.24-4.11,3.28-2.54A.85.85,0,0,0,19,10,.87.87,0,0,0,18.12,9.14Zm-3.66,2.93-.81.63.3,1L14.63,16l-1.7-1.3L12,14l-.91.69L9.4,16l.69-2.28.29-1-.82-.63L7.71,10.64h3.07l.35-1L12,7.33l.71,2.26L13,10.64h3.31Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-11-favorite-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E11-favorite-b1-hover%3C/title%3E%3Cpolygon points='12 9.64 11.66 8.59 10.95 6.33 10.13 8.64 9.78 9.64 8.72 9.64 6.71 9.64 8.56 11.07 9.38 11.7 9.09 12.69 8.4 14.97 10.11 13.66 11.02 12.97 11.93 13.66 13.63 14.96 12.95 12.69 12.65 11.7 13.46 11.07 15.31 9.64 13.1 9.64 12 9.64' style='fill:%23fff'/%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.66,9.71-3.28,2.54,1.24,4.11a.88.88,0,0,1-.58,1.1,1.15,1.15,0,0,1-.26,0,.87.87,0,0,1-.53-.18L12,15.86,8.79,18.32a.89.89,0,0,1-.53.18.86.86,0,0,1-.7-.35.87.87,0,0,1-.15-.78l1.24-4.11L5.34,10.71a.88.88,0,0,1-.16-1.23.91.91,0,0,1,.7-.34H9.72l1.44-4.06A.88.88,0,0,1,12,4.5a.93.93,0,0,1,.29,0,.9.9,0,0,1,.55.56l1.27,4h4A.87.87,0,0,1,19,10,.85.85,0,0,1,18.66,10.71Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-12-info{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E12-info-b1%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff;fill-rule:evenodd'/%3E%3Cpath d='M12,9a1,1,0,0,0-1,1v8a1,1,0,0,0,2,0V10A1,1,0,0,0,12,9Z' transform='translate(-1 -1)' style='fill:%23fff;fill-rule:evenodd'/%3E%3Ccircle cx='11' cy='5' r='1' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-12-info-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12 1C5.925 1 1 5.926 1 12c0 6.075 4.925 11 11 11s11-4.925 11-11c0-6.074-4.925-11-11-11m0 1c5.514 0 10 4.486 10 10 0 5.515-4.486 10-10 10S2 17.515 2 12C2 6.485 6.486 2 12 2m0 17.149a1 1 0 0 1-1-1v-8a1 1 0 1 1 2 0v8a1 1 0 0 1-1 1m1-13a1 1 0 1 1-2 0 1 1 0 0 1 2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='%23fff'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='%23FFF'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.sfi.white-12-info-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12 1C5.925 1 1 5.926 1 12c0 6.075 4.925 11 11 11s11-4.925 11-11c0-6.074-4.925-11-11-11m0 18.149a1 1 0 0 1-1-1v-8a1 1 0 1 1 2 0v8a1 1 0 0 1-1 1m1-13a1 1 0 1 1-2 0 1 1 0 0 1 2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='%23fff'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='%23FFF'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.sfi.white-13-help{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12 1C5.925 1 1 5.926 1 12c0 6.075 4.925 11 11 11s11-4.925 11-11c0-6.074-4.925-11-11-11m0 1c5.514 0 10 4.486 10 10 0 5.515-4.486 10-10 10S2 17.515 2 12C2 6.485 6.486 2 12 2m1 16.155a1 1 0 1 1-2 0 1 1 0 0 1 2 0M12.053 7.15c.344 0 .67.04.944.082.61.094 1.036.593 1.036 1.215 0 .457-.223.739-.417.934l-1.336 1.276a3.97 3.97 0 0 0-1.268 2.919v.572c0 .554.45 1.005 1.003 1.005.553 0 1.002-.451 1.002-1.005v-.572c0-.361.05-.892.49-1.348l1.66-1.547c.56-.593.872-1.387.872-2.234 0-1.598-1.113-2.934-2.646-3.177-.696-.155-1.901-.15-2.533.01a3.112 3.112 0 0 0-1.865 1.092 4.701 4.701 0 0 0-.117.167c-.154.212-.223.397-.223.588 0 .552.45 1.002 1.003 1.002.317 0 .495-.086.68-.33.052-.07.336-.427.892-.56.258-.06.534-.09.823-.09' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='%23fff'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='%23FFF'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.sfi.white-13-help-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E12-info-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm1,17a1,1,0,0,1-2,0V10a1,1,0,0,1,2,0ZM12,7a1,1,0,1,1,1-1A1,1,0,0,1,12,7Z' transform='translate(-1 -1)' style='fill:%23fff;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.white-14-warning{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12.001 1c-.511 0-1.003.3-1.245.816L1.144 20.882a1.535 1.535 0 0 0-.144.651C1 22.343 1.621 23 2.388 23h19.224c.213 0 .424-.052.616-.152.686-.36.967-1.24.627-1.966L13.244 1.816a1.432 1.432 0 0 0-.629-.664A1.321 1.321 0 0 0 12.001 1m0 1a.32.32 0 0 1 .151.039c.078.04.144.112.199.228l9.599 19.039c.112.239.025.546-.186.656a.33.33 0 0 1-.152.038H2.388c-.21 0-.388-.214-.388-.467 0-.079.017-.157.037-.201L11.662 2.24c.083-.177.23-.24.339-.24M12 15.996a1 1 0 0 1-1-1v-7a1 1 0 1 1 2 0v7a1 1 0 0 1-1 1m-1 3a1 1 0 1 1 2 0 1 1 0 0 1-2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='%23fff'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='%23FFF'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.sfi.white-14-warning-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12.001 1c-.511 0-1.003.3-1.245.816L1.144 20.882a1.535 1.535 0 0 0-.144.651C1 22.343 1.621 23 2.388 23h19.224c.213 0 .424-.052.616-.152.686-.36.967-1.24.627-1.966L13.244 1.816a1.432 1.432 0 0 0-.629-.664A1.321 1.321 0 0 0 12.001 1M12 15.996a1 1 0 0 1-1-1v-7a1 1 0 1 1 2 0v7a1 1 0 0 1-1 1m-1 3a1 1 0 1 1 2 0 1 1 0 0 1-2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='%23fff'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='%23FFF'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.sfi.b1-01-up{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7.99'%3E%3Ctitle%3E01-up-b1%3C/title%3E%3Cpath d='M18,15.45a1,1,0,0,1-.73-.32L12,9.47,6.73,15.13a1,1,0,0,1-1.46-1.36l5.32-5.72a2,2,0,0,1,2.82,0l5.32,5.72A1,1,0,0,1,18,15.45Z' transform='translate(-5 -7.46)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-01-up-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E01-up-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M13.41,8.05a2,2,0,0,0-2.82,0L5.27,13.77a1,1,0,0,0,1.46,1.36L12,9.47l5.27,5.66a1,1,0,1,0,1.46-1.36Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-01-up-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E01-up-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.68,14.18a1,1,0,0,1-1.41,0L12,9.47,6.73,15.13a1,1,0,0,1-1.46-1.36l5.32-5.72a2,2,0,0,1,2.82,0l5.32,5.72A1,1,0,0,1,18.68,15.18Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-02-down{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-b1%3C/title%3E%3Cpath d='M12,16.52a1.94,1.94,0,0,1-1.41-.62L5.27,10.18A1,1,0,0,1,6.73,8.82L12,14.48l5.27-5.66a1,1,0,1,1,1.46,1.36L13.41,15.9A1.94,1.94,0,0,1,12,16.52Z' transform='translate(-5 -8.5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-02-down-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E02-down-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M17.27,8.82,12,14.48,6.73,8.82a1,1,0,0,0-1.46,1.36l5.32,5.72a1.91,1.91,0,0,0,2.82,0l5.32-5.72a1,1,0,1,0-1.46-1.36Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-02-down-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E02-down-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.73,9.18L13.41,15.9a1.91,1.91,0,0,1-2.82,0L5.27,10.18A1,1,0,0,1,6.73,8.82L12,14.48l5.27-5.66a1,1,0,1,1,1.46,1.36Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-03-left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Cpath d='M14.45,19a1,1,0,0,1-.68-.27L8.05,13.41A2,2,0,0,1,7.43,12a2,2,0,0,1,.62-1.42l5.72-5.32a1,1,0,0,1,1.36,1.47L9.47,12l5.66,5.26a1,1,0,0,1,0,1.42A1,1,0,0,1,14.45,19Z' transform='translate(-7.43 -5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-03-left-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E03-left-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M15.18,5.32a1,1,0,0,0-1.41-.06L8.05,10.58A2,2,0,0,0,7.43,12a2,2,0,0,0,.62,1.41l5.72,5.32a1,1,0,0,0,1.36-1.47L9.47,12l5.66-5.27A1,1,0,0,0,15.18,5.32Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-03-left-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E03-left-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm3.13,16.26a1,1,0,0,1,0,1.42,1,1,0,0,1-1.41.05L8.05,13.41A2,2,0,0,1,7.43,12a2,2,0,0,1,.62-1.42l5.72-5.32a1,1,0,0,1,1.36,1.47L9.47,12Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-04-right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Ctitle%3E04-right-b1%3C/title%3E%3Cpath d='M10,19a1,1,0,0,1-.73-.32,1,1,0,0,1,.05-1.42L15,12,9.32,6.73a1,1,0,0,1,1.36-1.47l5.72,5.32A2,2,0,0,1,17,12a2,2,0,0,1-.62,1.41l-5.72,5.32A1,1,0,0,1,10,19Z' transform='translate(-9 -5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-04-right-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E04-right-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M10.68,5.26A1,1,0,0,0,9.32,6.73L15,12,9.32,17.26a1,1,0,0,0-.05,1.42,1,1,0,0,0,1.41.05l5.72-5.32A2,2,0,0,0,17,12a2,2,0,0,0-.62-1.42Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-04-right-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E04-right-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm4.4,12.41-5.72,5.32a1,1,0,0,1-1.36-1.47L15,12,9.32,6.73a1,1,0,0,1,1.36-1.47l5.72,5.32A2,2,0,0,1,17,12,2,2,0,0,1,16.4,13.41Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-05-check{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 9.99'%3E%3Ctitle%3E05-check-b1%3C/title%3E%3Cpath d='M9.54,17a1.51,1.51,0,0,1-1-.42L5.3,13.48A1,1,0,0,1,6.7,12L9.54,14.8,17.3,7.28a1,1,0,1,1,1.4,1.43l-8.1,7.86A1.53,1.53,0,0,1,9.54,17Z' transform='translate(-5 -7)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-05-check-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E05-check-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M17.3,7.28,9.54,14.8,6.7,12a1,1,0,0,0-1.4,1.44l3.19,3.09a1.51,1.51,0,0,0,1,.42,1.53,1.53,0,0,0,1.06-.42l8.1-7.86a1,1,0,1,0-1.4-1.43Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-05-check-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E05-check-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.7,7.71-8.1,7.86A1.53,1.53,0,0,1,9.54,17a1.51,1.51,0,0,1-1-.42L5.3,13.48A1,1,0,0,1,6.7,12L9.54,14.8,17.3,7.28a1,1,0,1,1,1.4,1.43Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-06-close{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-b1%3C/title%3E%3Cpath d='M13.41,12l3.8-3.79a1,1,0,1,0-1.42-1.42L12,10.59,8.21,6.79A1,1,0,1,0,6.79,8.21L10.59,12l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-6.5 -6.5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-06-close-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M17.21,6.79a1,1,0,0,0-1.42,0L12,10.59,8.21,6.79A1,1,0,1,0,6.79,8.21L10.59,12l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l3.8-3.79A1,1,0,0,0,17.21,6.79Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-06-close-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm5.21,14.79a1,1,0,0,1,0,1.42,1,1,0,0,1-1.42,0L12,13.41l-3.79,3.8a1,1,0,0,1-1.42,0,1,1,0,0,1,0-1.42L10.59,12,6.79,8.21A1,1,0,1,1,8.21,6.79L12,10.59l3.79-3.8a1,1,0,1,1,1.42,1.42L13.41,12Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-07-equal{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7'%3E%3Ctitle%3E07-equal-b1%3C/title%3E%3Cpath d='M18,10.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -8.5)' style='fill:%23002a54'/%3E%3Cpath d='M18,15.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -8.5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-07-equal-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E07-equal-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,8.5H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,13.5H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-07-equal-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E07-equal-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,14.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-08-plus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Ctitle%3E08-plus-b1%3C/title%3E%3Cpath d='M18,11H13V6a1,1,0,0,0-2,0v5H6a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V13h5a1,1,0,0,0,0-2Z' transform='translate(-5 -5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-08-plus-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-08-plus-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E08-plus-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,12H13v5a1,1,0,0,1-2,0V13H6a1,1,0,0,1,0-2h5V6a1,1,0,0,1,2,0v5h5a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-09-minus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 2'%3E%3Ctitle%3E09-minus-b1%3C/title%3E%3Cpath d='M18,13H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -11)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-09-minus-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0' y='0' viewBox='0 0 24 24' xml:space='preserve'%3E%3Cstyle%3E .st0%7Bfill:%23002a54%7D %3C/style%3E%3Cpath class='st0' d='M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 21C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z'/%3E%3Cpath class='st0' d='M18 11H6a1 1 0 1 0 0 2h12a1 1 0 1 0 0-2z'/%3E%3C/svg%3E%0A\")}.sfi.b1-09-minus-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,12H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-10-navigation{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 10'%3E%3Ctitle%3E10-navigation-b1%3C/title%3E%3Cpath d='M6,9H18a1,1,0,0,0,0-2H6A1,1,0,0,0,6,9Z' transform='translate(-5 -7)' style='fill:%23002a54'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-5 -7)' style='fill:%23002a54'/%3E%3Cpath d='M18,15H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-5 -7)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-10-navigation-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E10-navigation-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,7H6A1,1,0,0,0,6,9H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,15H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-10-navigation-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E10-navigation-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,16H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-4H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-4H6A1,1,0,0,1,6,7H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-11-favorite{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Ctitle%3E11-favorite-b1%3C/title%3E%3Cpath d='M12,7.33l.71,2.26L13,10.64h3.31l-1.85,1.43-.81.63.3,1L14.63,16l-1.7-1.3L12,14l-.91.69L9.4,16l.69-2.28.29-1-.82-.63L7.71,10.64h3.07l.35-1L12,7.33M12,4.5a.88.88,0,0,0-.83.58L9.72,9.14H5.88a.91.91,0,0,0-.7.34.88.88,0,0,0,.16,1.23l3.31,2.55L7.41,17.37a.87.87,0,0,0,.15.78.86.86,0,0,0,.7.35.89.89,0,0,0,.53-.18L12,15.86l3.23,2.45a.87.87,0,0,0,.53.18,1.15,1.15,0,0,0,.26,0,.88.88,0,0,0,.58-1.1l-1.24-4.11,3.28-2.54A.85.85,0,0,0,19,10a.87.87,0,0,0-.88-.88h-4l-1.27-4a.9.9,0,0,0-.55-.56.93.93,0,0,0-.29,0Z' transform='translate(-5 -4.5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-11-favorite-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E11-favorite-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18.12,9.14h-4l-1.27-4a.9.9,0,0,0-.55-.56.93.93,0,0,0-.29,0,.88.88,0,0,0-.83.58L9.72,9.14H5.88a.91.91,0,0,0-.7.34.88.88,0,0,0,.16,1.23l3.31,2.55L7.41,17.37a.87.87,0,0,0,.15.78.86.86,0,0,0,.7.35.89.89,0,0,0,.53-.18L12,15.86l3.23,2.45a.87.87,0,0,0,.53.18,1.15,1.15,0,0,0,.26,0,.88.88,0,0,0,.58-1.1l-1.24-4.11,3.28-2.54A.85.85,0,0,0,19,10,.87.87,0,0,0,18.12,9.14Zm-3.66,2.93-.81.63.3,1L14.63,16l-1.7-1.3L12,14l-.91.69L9.4,16l.69-2.28.29-1-.82-.63L7.71,10.64h3.07l.35-1L12,7.33l.71,2.26L13,10.64h3.31Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-11-favorite-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E11-favorite-b1-hover%3C/title%3E%3Cpolygon points='12 9.64 11.66 8.59 10.95 6.33 10.13 8.64 9.78 9.64 8.72 9.64 6.71 9.64 8.56 11.07 9.38 11.7 9.09 12.69 8.4 14.97 10.11 13.66 11.02 12.97 11.93 13.66 13.63 14.96 12.95 12.69 12.65 11.7 13.46 11.07 15.31 9.64 13.1 9.64 12 9.64' style='fill:%23002a54'/%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.66,9.71-3.28,2.54,1.24,4.11a.88.88,0,0,1-.58,1.1,1.15,1.15,0,0,1-.26,0,.87.87,0,0,1-.53-.18L12,15.86,8.79,18.32a.89.89,0,0,1-.53.18.86.86,0,0,1-.7-.35.87.87,0,0,1-.15-.78l1.24-4.11L5.34,10.71a.88.88,0,0,1-.16-1.23.91.91,0,0,1,.7-.34H9.72l1.44-4.06A.88.88,0,0,1,12,4.5a.93.93,0,0,1,.29,0,.9.9,0,0,1,.55.56l1.27,4h4A.87.87,0,0,1,19,10,.85.85,0,0,1,18.66,10.71Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-12-info{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E12-info-b1%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54;fill-rule:evenodd'/%3E%3Cpath d='M12,9a1,1,0,0,0-1,1v8a1,1,0,0,0,2,0V10A1,1,0,0,0,12,9Z' transform='translate(-1 -1)' style='fill:%23002a54;fill-rule:evenodd'/%3E%3Ccircle cx='11' cy='5' r='1' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-12-info-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0' y='0' viewBox='0 0 24 24' xml:space='preserve'%3E%3Cstyle%3E .st0%7Bfill-rule:evenodd;clip-rule:evenodd;fill:%23002a54%7D %3C/style%3E%3Cpath class='st0' d='M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 21C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z'/%3E%3Cpath class='st0' d='M12 8.996a1 1 0 0 0-1 1v8a1 1 0 1 0 2 0v-8a1 1 0 0 0-1-1z'/%3E%3Ccircle class='st0' cx='12' cy='5.997' r='1'/%3E%3C/svg%3E%0A\")}.sfi.b1-12-info-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E12-info-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm1,17a1,1,0,0,1-2,0V10a1,1,0,0,1,2,0ZM12,7a1,1,0,1,1,1-1A1,1,0,0,1,12,7Z' transform='translate(-1 -1)' style='fill:%23002a54;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.b1-13-help{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0' y='0' viewBox='0 0 24 24' xml:space='preserve'%3E%3Cstyle%3E .st0%7Bfill-rule:evenodd;clip-rule:evenodd;fill:%23002a54%7D %3C/style%3E%3Cpath class='st0' d='M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 21C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z'/%3E%3Cpath class='st0' d='M13.393 5.118c-.697-.156-1.9-.15-2.534.01A3.108 3.108 0 0 0 8.995 6.22a7.317 7.317 0 0 0-.118.167c-.153.212-.223.398-.223.588 0 .553.45 1.003 1.004 1.003.317 0 .495-.087.68-.33.052-.07.336-.428.891-.56.259-.06.535-.09.824-.09.344 0 .67.042.944.083.61.093 1.036.592 1.036 1.214 0 .457-.223.74-.417.935l-1.337 1.275a3.973 3.973 0 0 0-1.267 2.92v.571c0 .554.45 1.005 1.003 1.005s1.002-.451 1.002-1.005v-.572c0-.36.05-.891.49-1.347l1.661-1.547a3.24 3.24 0 0 0 .871-2.235c0-1.597-1.113-2.933-2.646-3.176z'/%3E%3Ccircle class='st0' cx='12' cy='18.002' r='1'/%3E%3C/svg%3E%0A\")}.sfi.b1-13-help-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 18.002a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm3.168-8.473l-1.66 1.547c-.44.456-.49.987-.49 1.347v.572c0 .554-.45 1.005-1.003 1.005s-1.003-.451-1.003-1.005v-.572c0-1.113.45-2.15 1.267-2.919l1.337-1.275c.194-.195.417-.478.417-.935 0-.622-.426-1.12-1.036-1.214a6.37 6.37 0 0 0-.944-.082c-.289 0-.565.029-.823.088-.556.133-.84.49-.891.56-.186.244-.364.331-.681.331-.553 0-1.004-.45-1.004-1.003 0-.19.07-.376.223-.588.017-.024.097-.14.118-.167a3.108 3.108 0 0 1 1.864-1.09c.633-.162 1.837-.167 2.534-.011 1.533.243 2.646 1.579 2.646 3.176a3.24 3.24 0 0 1-.871 2.235z' fill='%23002a54'/%3E%3C/svg%3E%0A\")}.sfi.b1-14-warning{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0' y='0' viewBox='0 0 24 24' xml:space='preserve'%3E%3Cstyle%3E .st1%7Bfill:%23002a54%7D %3C/style%3E%3Ccircle cx='12' cy='18.997' r='1' fill-rule='evenodd' clip-rule='evenodd' fill='%23002a54'/%3E%3Cpath class='st1' d='M12 6.996a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1z'/%3E%3Cpath class='st1' d='M22.856 20.882L13.244 1.817a1.43 1.43 0 0 0-.629-.664A1.321 1.321 0 0 0 12.001 1c-.511 0-1.003.3-1.245.817L1.144 20.882a1.536 1.536 0 0 0-.144.65C1 22.344 1.621 23 2.388 23h19.224c.213 0 .424-.052.615-.152.687-.36.969-1.24.629-1.966zm-1.092 1.08a.325.325 0 0 1-.152.038H2.388c-.21 0-.388-.214-.388-.467 0-.08.017-.158.037-.2L11.662 2.24c.083-.178.23-.241.339-.241.051 0 .102.013.15.039.08.04.145.112.2.228l9.599 19.04c.112.238.025.544-.186.655z'/%3E%3C/svg%3E%0A\")}.sfi.b1-14-warning-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M22.856 20.882L13.244 1.817a1.43 1.43 0 0 0-.629-.664A1.321 1.321 0 0 0 12.001 1c-.511 0-1.003.3-1.245.817L1.144 20.882a1.536 1.536 0 0 0-.144.65C1 22.344 1.621 23 2.388 23h19.224c.213 0 .424-.052.615-.152.687-.36.969-1.24.629-1.966zM12 19.997a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm1-5a1 1 0 1 1-2 0v-7a1 1 0 1 1 2 0v7z' fill='%23002a54'/%3E%3C/svg%3E%0A\")}.sfi.b2-01-up{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7.99'%3E%3Ctitle%3E01-up-white%3C/title%3E%3Cpath d='M26,24a1,1,0,0,1-.73-.32L20,18l-5.27,5.67a1,1,0,1,1-1.46-1.37l5.32-5.71a2,2,0,0,1,2.82,0l5.32,5.71A1,1,0,0,1,26,24' transform='translate(-13 -16)' style='fill:%23056DAE;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.b2-02-down{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-white%3C/title%3E%3Cpath d='M20,24a1.94,1.94,0,0,1-1.41-.62l-5.32-5.72a1,1,0,0,1,1.46-1.36L20,22l5.27-5.66a1,1,0,0,1,1.46,1.36l-5.32,5.72A1.94,1.94,0,0,1,20,24' transform='translate(-13 -15.99)' style='fill:%23056DAE;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.b2-03-left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Ctitle%3E03-left-white%3C/title%3E%3Cpath d='M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27' transform='translate(-15.99 -13)' style='fill:%23056DAE;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.b2-04-right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Ctitle%3E04-right-white%3C/title%3E%3Cpath d='M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27' transform='translate(-15.99 -13)' style='fill:%23056DAE;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.g1-06-close{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-white%3C/title%3E%3Cpath d='M21.41,20l3.8-3.79a1,1,0,0,0-1.42-1.42L20,18.59l-3.79-3.8a1,1,0,0,0-1.42,1.42L18.59,20l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L20,21.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-14.5 -14.5)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.g1-06-close:hover,.sfi.g1-06-close:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-white-hover%3C/title%3E%3Cpath d='M21.41,20.15l3.8-3.79a1,1,0,0,0,0-1.41,1,1,0,0,0-1.42,0L20,18.74,16.21,15a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l3.8,3.79L14.79,24a1,1,0,0,0,0,1.41,1,1,0,0,0,1.42,0L20,21.57l3.79,3.79a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.41ZM20,9A11,11,0,1,0,31,20,11,11,0,0,0,20,9' transform='translate(-9 -9)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.g1-02-down{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-white%3C/title%3E%3Cpath d='M20,24a1.94,1.94,0,0,1-1.41-.62l-5.32-5.72a1,1,0,0,1,1.46-1.36L20,22l5.27-5.66a1,1,0,0,1,1.46,1.36l-5.32,5.72A1.94,1.94,0,0,1,20,24' transform='translate(-13 -15.99)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Icon2Component, decorators: [{
            type: Component,
            args: [{ selector: 'citi-icon2', standalone: false, template: `
    <div
      #svgContainer
      [attr.tabindex]="tabIndex"
      [ngStyle]="type === 'glyph' ? '' : svgStyle"
      [ngClass]="[iconClass]"
      [attr.id]="idStr"
      [attr.role]="role"
      [attr.aria-label]="ariaLabel"
      [attr.aria-hidden]="ariaHidden ? true : null"
    ></div>
  `, styles: [":host{line-height:0}:host div{display:inline-block;line-height:0px}.small{width:12px;height:12px}.medium{width:14px;height:14px}.large{width:16px;height:16px}.small-circle{width:18px;height:18px}.medium-circle{width:20px;height:20px}.large-circle{width:22px;height:22px}.sfi{background-repeat:no-repeat}.sfi.small{width:12px;height:12px;margin:3px}.sfi.medium{width:14px;height:14px;margin:3px}.sfi.large{width:16px;height:16px;margin:4px}.sfi.small-circle{width:18px;height:18px;margin:5px}.sfi.medium-circle{width:20px;height:20px;margin:5px}.sfi.large-circle{width:22px;height:22px;margin:5px}.sfi.white-01-up{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7.99'%3E%3Ctitle%3E01-up-b1%3C/title%3E%3Cpath d='M18,15.45a1,1,0,0,1-.73-.32L12,9.47,6.73,15.13a1,1,0,0,1-1.46-1.36l5.32-5.72a2,2,0,0,1,2.82,0l5.32,5.72A1,1,0,0,1,18,15.45Z' transform='translate(-5 -7.46)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-01-up-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E01-up-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M13.41,8.05a2,2,0,0,0-2.82,0L5.27,13.77a1,1,0,0,0,1.46,1.36L12,9.47l5.27,5.66a1,1,0,1,0,1.46-1.36Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-01-up-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E01-up-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.68,14.18a1,1,0,0,1-1.41,0L12,9.47,6.73,15.13a1,1,0,0,1-1.46-1.36l5.32-5.72a2,2,0,0,1,2.82,0l5.32,5.72A1,1,0,0,1,18.68,15.18Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-02-down{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-b1%3C/title%3E%3Cpath d='M12,16.52a1.94,1.94,0,0,1-1.41-.62L5.27,10.18A1,1,0,0,1,6.73,8.82L12,14.48l5.27-5.66a1,1,0,1,1,1.46,1.36L13.41,15.9A1.94,1.94,0,0,1,12,16.52Z' transform='translate(-5 -8.5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-02-down-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E02-down-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M17.27,8.82,12,14.48,6.73,8.82a1,1,0,0,0-1.46,1.36l5.32,5.72a1.91,1.91,0,0,0,2.82,0l5.32-5.72a1,1,0,1,0-1.46-1.36Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-02-down-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E02-down-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.73,9.18L13.41,15.9a1.91,1.91,0,0,1-2.82,0L5.27,10.18A1,1,0,0,1,6.73,8.82L12,14.48l5.27-5.66a1,1,0,1,1,1.46,1.36Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-03-left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Cpath d='M14.45,19a1,1,0,0,1-.68-.27L8.05,13.41A2,2,0,0,1,7.43,12a2,2,0,0,1,.62-1.42l5.72-5.32a1,1,0,0,1,1.36,1.47L9.47,12l5.66,5.26a1,1,0,0,1,0,1.42A1,1,0,0,1,14.45,19Z' transform='translate(-7.43 -5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-03-left-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E03-left-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M15.18,5.32a1,1,0,0,0-1.41-.06L8.05,10.58A2,2,0,0,0,7.43,12a2,2,0,0,0,.62,1.41l5.72,5.32a1,1,0,0,0,1.36-1.47L9.47,12l5.66-5.27A1,1,0,0,0,15.18,5.32Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-03-left-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E03-left-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm3.13,16.26a1,1,0,0,1,0,1.42,1,1,0,0,1-1.41.05L8.05,13.41A2,2,0,0,1,7.43,12a2,2,0,0,1,.62-1.42l5.72-5.32a1,1,0,0,1,1.36,1.47L9.47,12Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-04-right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Ctitle%3E04-right-b1%3C/title%3E%3Cpath d='M10,19a1,1,0,0,1-.73-.32,1,1,0,0,1,.05-1.42L15,12,9.32,6.73a1,1,0,0,1,1.36-1.47l5.72,5.32A2,2,0,0,1,17,12a2,2,0,0,1-.62,1.41l-5.72,5.32A1,1,0,0,1,10,19Z' transform='translate(-9 -5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-04-right-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E04-right-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M10.68,5.26A1,1,0,0,0,9.32,6.73L15,12,9.32,17.26a1,1,0,0,0-.05,1.42,1,1,0,0,0,1.41.05l5.72-5.32A2,2,0,0,0,17,12a2,2,0,0,0-.62-1.42Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-04-right-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E04-right-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm4.4,12.41-5.72,5.32a1,1,0,0,1-1.36-1.47L15,12,9.32,6.73a1,1,0,0,1,1.36-1.47l5.72,5.32A2,2,0,0,1,17,12,2,2,0,0,1,16.4,13.41Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-05-check{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 9.99'%3E%3Ctitle%3E05-check-b1%3C/title%3E%3Cpath d='M9.54,17a1.51,1.51,0,0,1-1-.42L5.3,13.48A1,1,0,0,1,6.7,12L9.54,14.8,17.3,7.28a1,1,0,1,1,1.4,1.43l-8.1,7.86A1.53,1.53,0,0,1,9.54,17Z' transform='translate(-5 -7)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-05-check-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E05-check-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M17.3,7.28,9.54,14.8,6.7,12a1,1,0,0,0-1.4,1.44l3.19,3.09a1.51,1.51,0,0,0,1,.42,1.53,1.53,0,0,0,1.06-.42l8.1-7.86a1,1,0,1,0-1.4-1.43Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-05-check-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E05-check-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.7,7.71-8.1,7.86A1.53,1.53,0,0,1,9.54,17a1.51,1.51,0,0,1-1-.42L5.3,13.48A1,1,0,0,1,6.7,12L9.54,14.8,17.3,7.28a1,1,0,1,1,1.4,1.43Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-06-close{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-b1%3C/title%3E%3Cpath d='M13.41,12l3.8-3.79a1,1,0,1,0-1.42-1.42L12,10.59,8.21,6.79A1,1,0,1,0,6.79,8.21L10.59,12l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-6.5 -6.5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-06-close-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M17.21,6.79a1,1,0,0,0-1.42,0L12,10.59,8.21,6.79A1,1,0,1,0,6.79,8.21L10.59,12l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l3.8-3.79A1,1,0,0,0,17.21,6.79Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-06-close-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm5.21,14.79a1,1,0,0,1,0,1.42,1,1,0,0,1-1.42,0L12,13.41l-3.79,3.8a1,1,0,0,1-1.42,0,1,1,0,0,1,0-1.42L10.59,12,6.79,8.21A1,1,0,1,1,8.21,6.79L12,10.59l3.79-3.8a1,1,0,1,1,1.42,1.42L13.41,12Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-07-equal{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7'%3E%3Ctitle%3E07-equal-b1%3C/title%3E%3Cpath d='M18,10.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -8.5)' style='fill:%23fff'/%3E%3Cpath d='M18,15.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -8.5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-07-equal-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E07-equal-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,8.5H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,13.5H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-07-equal-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E07-equal-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,14.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-08-plus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Ctitle%3E08-plus-b1%3C/title%3E%3Cpath d='M18,11H13V6a1,1,0,0,0-2,0v5H6a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V13h5a1,1,0,0,0,0-2Z' transform='translate(-5 -5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-08-plus-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-08-plus-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,12H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-09-minus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 2'%3E%3Ctitle%3E09-minus-b1%3C/title%3E%3Cpath d='M18,13H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -11)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-09-minus-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-09-minus-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,12H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-10-navigation{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 10'%3E%3Ctitle%3E10-navigation-b1%3C/title%3E%3Cpath d='M6,9H18a1,1,0,0,0,0-2H6A1,1,0,0,0,6,9Z' transform='translate(-5 -7)' style='fill:%23fff'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-5 -7)' style='fill:%23fff'/%3E%3Cpath d='M18,15H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-5 -7)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-10-navigation-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E10-navigation-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,7H6A1,1,0,0,0,6,9H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18,15H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-10-navigation-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E10-navigation-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,16H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-4H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-4H6A1,1,0,0,1,6,7H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-11-favorite{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Ctitle%3E11-favorite-b1%3C/title%3E%3Cpath d='M12,7.33l.71,2.26L13,10.64h3.31l-1.85,1.43-.81.63.3,1L14.63,16l-1.7-1.3L12,14l-.91.69L9.4,16l.69-2.28.29-1-.82-.63L7.71,10.64h3.07l.35-1L12,7.33M12,4.5a.88.88,0,0,0-.83.58L9.72,9.14H5.88a.91.91,0,0,0-.7.34.88.88,0,0,0,.16,1.23l3.31,2.55L7.41,17.37a.87.87,0,0,0,.15.78.86.86,0,0,0,.7.35.89.89,0,0,0,.53-.18L12,15.86l3.23,2.45a.87.87,0,0,0,.53.18,1.15,1.15,0,0,0,.26,0,.88.88,0,0,0,.58-1.1l-1.24-4.11,3.28-2.54A.85.85,0,0,0,19,10a.87.87,0,0,0-.88-.88h-4l-1.27-4a.9.9,0,0,0-.55-.56.93.93,0,0,0-.29,0Z' transform='translate(-5 -4.5)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-11-favorite-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E11-favorite-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3Cpath d='M18.12,9.14h-4l-1.27-4a.9.9,0,0,0-.55-.56.93.93,0,0,0-.29,0,.88.88,0,0,0-.83.58L9.72,9.14H5.88a.91.91,0,0,0-.7.34.88.88,0,0,0,.16,1.23l3.31,2.55L7.41,17.37a.87.87,0,0,0,.15.78.86.86,0,0,0,.7.35.89.89,0,0,0,.53-.18L12,15.86l3.23,2.45a.87.87,0,0,0,.53.18,1.15,1.15,0,0,0,.26,0,.88.88,0,0,0,.58-1.1l-1.24-4.11,3.28-2.54A.85.85,0,0,0,19,10,.87.87,0,0,0,18.12,9.14Zm-3.66,2.93-.81.63.3,1L14.63,16l-1.7-1.3L12,14l-.91.69L9.4,16l.69-2.28.29-1-.82-.63L7.71,10.64h3.07l.35-1L12,7.33l.71,2.26L13,10.64h3.31Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-11-favorite-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E11-favorite-b1-hover%3C/title%3E%3Cpolygon points='12 9.64 11.66 8.59 10.95 6.33 10.13 8.64 9.78 9.64 8.72 9.64 6.71 9.64 8.56 11.07 9.38 11.7 9.09 12.69 8.4 14.97 10.11 13.66 11.02 12.97 11.93 13.66 13.63 14.96 12.95 12.69 12.65 11.7 13.46 11.07 15.31 9.64 13.1 9.64 12 9.64' style='fill:%23fff'/%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.66,9.71-3.28,2.54,1.24,4.11a.88.88,0,0,1-.58,1.1,1.15,1.15,0,0,1-.26,0,.87.87,0,0,1-.53-.18L12,15.86,8.79,18.32a.89.89,0,0,1-.53.18.86.86,0,0,1-.7-.35.87.87,0,0,1-.15-.78l1.24-4.11L5.34,10.71a.88.88,0,0,1-.16-1.23.91.91,0,0,1,.7-.34H9.72l1.44-4.06A.88.88,0,0,1,12,4.5a.93.93,0,0,1,.29,0,.9.9,0,0,1,.55.56l1.27,4h4A.87.87,0,0,1,19,10,.85.85,0,0,1,18.66,10.71Z' transform='translate(-1 -1)' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-12-info{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E12-info-b1%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23fff;fill-rule:evenodd'/%3E%3Cpath d='M12,9a1,1,0,0,0-1,1v8a1,1,0,0,0,2,0V10A1,1,0,0,0,12,9Z' transform='translate(-1 -1)' style='fill:%23fff;fill-rule:evenodd'/%3E%3Ccircle cx='11' cy='5' r='1' style='fill:%23fff'/%3E%3C/svg%3E\")}.sfi.white-12-info-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12 1C5.925 1 1 5.926 1 12c0 6.075 4.925 11 11 11s11-4.925 11-11c0-6.074-4.925-11-11-11m0 1c5.514 0 10 4.486 10 10 0 5.515-4.486 10-10 10S2 17.515 2 12C2 6.485 6.486 2 12 2m0 17.149a1 1 0 0 1-1-1v-8a1 1 0 1 1 2 0v8a1 1 0 0 1-1 1m1-13a1 1 0 1 1-2 0 1 1 0 0 1 2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='%23fff'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='%23FFF'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.sfi.white-12-info-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12 1C5.925 1 1 5.926 1 12c0 6.075 4.925 11 11 11s11-4.925 11-11c0-6.074-4.925-11-11-11m0 18.149a1 1 0 0 1-1-1v-8a1 1 0 1 1 2 0v8a1 1 0 0 1-1 1m1-13a1 1 0 1 1-2 0 1 1 0 0 1 2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='%23fff'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='%23FFF'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.sfi.white-13-help{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12 1C5.925 1 1 5.926 1 12c0 6.075 4.925 11 11 11s11-4.925 11-11c0-6.074-4.925-11-11-11m0 1c5.514 0 10 4.486 10 10 0 5.515-4.486 10-10 10S2 17.515 2 12C2 6.485 6.486 2 12 2m1 16.155a1 1 0 1 1-2 0 1 1 0 0 1 2 0M12.053 7.15c.344 0 .67.04.944.082.61.094 1.036.593 1.036 1.215 0 .457-.223.739-.417.934l-1.336 1.276a3.97 3.97 0 0 0-1.268 2.919v.572c0 .554.45 1.005 1.003 1.005.553 0 1.002-.451 1.002-1.005v-.572c0-.361.05-.892.49-1.348l1.66-1.547c.56-.593.872-1.387.872-2.234 0-1.598-1.113-2.934-2.646-3.177-.696-.155-1.901-.15-2.533.01a3.112 3.112 0 0 0-1.865 1.092 4.701 4.701 0 0 0-.117.167c-.154.212-.223.397-.223.588 0 .552.45 1.002 1.003 1.002.317 0 .495-.086.68-.33.052-.07.336-.427.892-.56.258-.06.534-.09.823-.09' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='%23fff'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='%23FFF'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.sfi.white-13-help-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E12-info-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm1,17a1,1,0,0,1-2,0V10a1,1,0,0,1,2,0ZM12,7a1,1,0,1,1,1-1A1,1,0,0,1,12,7Z' transform='translate(-1 -1)' style='fill:%23fff;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.white-14-warning{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12.001 1c-.511 0-1.003.3-1.245.816L1.144 20.882a1.535 1.535 0 0 0-.144.651C1 22.343 1.621 23 2.388 23h19.224c.213 0 .424-.052.616-.152.686-.36.967-1.24.627-1.966L13.244 1.816a1.432 1.432 0 0 0-.629-.664A1.321 1.321 0 0 0 12.001 1m0 1a.32.32 0 0 1 .151.039c.078.04.144.112.199.228l9.599 19.039c.112.239.025.546-.186.656a.33.33 0 0 1-.152.038H2.388c-.21 0-.388-.214-.388-.467 0-.079.017-.157.037-.201L11.662 2.24c.083-.177.23-.24.339-.24M12 15.996a1 1 0 0 1-1-1v-7a1 1 0 1 1 2 0v7a1 1 0 0 1-1 1m-1 3a1 1 0 1 1 2 0 1 1 0 0 1-2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='%23fff'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='%23FFF'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.sfi.white-14-warning-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12.001 1c-.511 0-1.003.3-1.245.816L1.144 20.882a1.535 1.535 0 0 0-.144.651C1 22.343 1.621 23 2.388 23h19.224c.213 0 .424-.052.616-.152.686-.36.967-1.24.627-1.966L13.244 1.816a1.432 1.432 0 0 0-.629-.664A1.321 1.321 0 0 0 12.001 1M12 15.996a1 1 0 0 1-1-1v-7a1 1 0 1 1 2 0v7a1 1 0 0 1-1 1m-1 3a1 1 0 1 1 2 0 1 1 0 0 1-2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='%23fff'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='%23FFF'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.sfi.b1-01-up{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7.99'%3E%3Ctitle%3E01-up-b1%3C/title%3E%3Cpath d='M18,15.45a1,1,0,0,1-.73-.32L12,9.47,6.73,15.13a1,1,0,0,1-1.46-1.36l5.32-5.72a2,2,0,0,1,2.82,0l5.32,5.72A1,1,0,0,1,18,15.45Z' transform='translate(-5 -7.46)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-01-up-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E01-up-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M13.41,8.05a2,2,0,0,0-2.82,0L5.27,13.77a1,1,0,0,0,1.46,1.36L12,9.47l5.27,5.66a1,1,0,1,0,1.46-1.36Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-01-up-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E01-up-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.68,14.18a1,1,0,0,1-1.41,0L12,9.47,6.73,15.13a1,1,0,0,1-1.46-1.36l5.32-5.72a2,2,0,0,1,2.82,0l5.32,5.72A1,1,0,0,1,18.68,15.18Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-02-down{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-b1%3C/title%3E%3Cpath d='M12,16.52a1.94,1.94,0,0,1-1.41-.62L5.27,10.18A1,1,0,0,1,6.73,8.82L12,14.48l5.27-5.66a1,1,0,1,1,1.46,1.36L13.41,15.9A1.94,1.94,0,0,1,12,16.52Z' transform='translate(-5 -8.5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-02-down-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E02-down-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M17.27,8.82,12,14.48,6.73,8.82a1,1,0,0,0-1.46,1.36l5.32,5.72a1.91,1.91,0,0,0,2.82,0l5.32-5.72a1,1,0,1,0-1.46-1.36Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-02-down-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E02-down-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.73,9.18L13.41,15.9a1.91,1.91,0,0,1-2.82,0L5.27,10.18A1,1,0,0,1,6.73,8.82L12,14.48l5.27-5.66a1,1,0,1,1,1.46,1.36Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-03-left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Cpath d='M14.45,19a1,1,0,0,1-.68-.27L8.05,13.41A2,2,0,0,1,7.43,12a2,2,0,0,1,.62-1.42l5.72-5.32a1,1,0,0,1,1.36,1.47L9.47,12l5.66,5.26a1,1,0,0,1,0,1.42A1,1,0,0,1,14.45,19Z' transform='translate(-7.43 -5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-03-left-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E03-left-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M15.18,5.32a1,1,0,0,0-1.41-.06L8.05,10.58A2,2,0,0,0,7.43,12a2,2,0,0,0,.62,1.41l5.72,5.32a1,1,0,0,0,1.36-1.47L9.47,12l5.66-5.27A1,1,0,0,0,15.18,5.32Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-03-left-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E03-left-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm3.13,16.26a1,1,0,0,1,0,1.42,1,1,0,0,1-1.41.05L8.05,13.41A2,2,0,0,1,7.43,12a2,2,0,0,1,.62-1.42l5.72-5.32a1,1,0,0,1,1.36,1.47L9.47,12Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-04-right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Ctitle%3E04-right-b1%3C/title%3E%3Cpath d='M10,19a1,1,0,0,1-.73-.32,1,1,0,0,1,.05-1.42L15,12,9.32,6.73a1,1,0,0,1,1.36-1.47l5.72,5.32A2,2,0,0,1,17,12a2,2,0,0,1-.62,1.41l-5.72,5.32A1,1,0,0,1,10,19Z' transform='translate(-9 -5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-04-right-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E04-right-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M10.68,5.26A1,1,0,0,0,9.32,6.73L15,12,9.32,17.26a1,1,0,0,0-.05,1.42,1,1,0,0,0,1.41.05l5.72-5.32A2,2,0,0,0,17,12a2,2,0,0,0-.62-1.42Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-04-right-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E04-right-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm4.4,12.41-5.72,5.32a1,1,0,0,1-1.36-1.47L15,12,9.32,6.73a1,1,0,0,1,1.36-1.47l5.72,5.32A2,2,0,0,1,17,12,2,2,0,0,1,16.4,13.41Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-05-check{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 9.99'%3E%3Ctitle%3E05-check-b1%3C/title%3E%3Cpath d='M9.54,17a1.51,1.51,0,0,1-1-.42L5.3,13.48A1,1,0,0,1,6.7,12L9.54,14.8,17.3,7.28a1,1,0,1,1,1.4,1.43l-8.1,7.86A1.53,1.53,0,0,1,9.54,17Z' transform='translate(-5 -7)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-05-check-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E05-check-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M17.3,7.28,9.54,14.8,6.7,12a1,1,0,0,0-1.4,1.44l3.19,3.09a1.51,1.51,0,0,0,1,.42,1.53,1.53,0,0,0,1.06-.42l8.1-7.86a1,1,0,1,0-1.4-1.43Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-05-check-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E05-check-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.7,7.71-8.1,7.86A1.53,1.53,0,0,1,9.54,17a1.51,1.51,0,0,1-1-.42L5.3,13.48A1,1,0,0,1,6.7,12L9.54,14.8,17.3,7.28a1,1,0,1,1,1.4,1.43Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-06-close{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-b1%3C/title%3E%3Cpath d='M13.41,12l3.8-3.79a1,1,0,1,0-1.42-1.42L12,10.59,8.21,6.79A1,1,0,1,0,6.79,8.21L10.59,12l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-6.5 -6.5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-06-close-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M17.21,6.79a1,1,0,0,0-1.42,0L12,10.59,8.21,6.79A1,1,0,1,0,6.79,8.21L10.59,12l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l3.8-3.79A1,1,0,0,0,17.21,6.79Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-06-close-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm5.21,14.79a1,1,0,0,1,0,1.42,1,1,0,0,1-1.42,0L12,13.41l-3.79,3.8a1,1,0,0,1-1.42,0,1,1,0,0,1,0-1.42L10.59,12,6.79,8.21A1,1,0,1,1,8.21,6.79L12,10.59l3.79-3.8a1,1,0,1,1,1.42,1.42L13.41,12Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-07-equal{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7'%3E%3Ctitle%3E07-equal-b1%3C/title%3E%3Cpath d='M18,10.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -8.5)' style='fill:%23002a54'/%3E%3Cpath d='M18,15.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -8.5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-07-equal-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E07-equal-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,8.5H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,13.5H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-07-equal-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E07-equal-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,14.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-08-plus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Ctitle%3E08-plus-b1%3C/title%3E%3Cpath d='M18,11H13V6a1,1,0,0,0-2,0v5H6a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V13h5a1,1,0,0,0,0-2Z' transform='translate(-5 -5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-08-plus-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-08-plus-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E08-plus-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,12H13v5a1,1,0,0,1-2,0V13H6a1,1,0,0,1,0-2h5V6a1,1,0,0,1,2,0v5h5a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-09-minus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 2'%3E%3Ctitle%3E09-minus-b1%3C/title%3E%3Cpath d='M18,13H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -11)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-09-minus-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0' y='0' viewBox='0 0 24 24' xml:space='preserve'%3E%3Cstyle%3E .st0%7Bfill:%23002a54%7D %3C/style%3E%3Cpath class='st0' d='M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 21C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z'/%3E%3Cpath class='st0' d='M18 11H6a1 1 0 1 0 0 2h12a1 1 0 1 0 0-2z'/%3E%3C/svg%3E%0A\")}.sfi.b1-09-minus-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,12H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-10-navigation{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 10'%3E%3Ctitle%3E10-navigation-b1%3C/title%3E%3Cpath d='M6,9H18a1,1,0,0,0,0-2H6A1,1,0,0,0,6,9Z' transform='translate(-5 -7)' style='fill:%23002a54'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-5 -7)' style='fill:%23002a54'/%3E%3Cpath d='M18,15H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-5 -7)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-10-navigation-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E10-navigation-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,7H6A1,1,0,0,0,6,9H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18,15H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-10-navigation-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E10-navigation-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,16H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-4H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-4H6A1,1,0,0,1,6,7H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-11-favorite{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Ctitle%3E11-favorite-b1%3C/title%3E%3Cpath d='M12,7.33l.71,2.26L13,10.64h3.31l-1.85,1.43-.81.63.3,1L14.63,16l-1.7-1.3L12,14l-.91.69L9.4,16l.69-2.28.29-1-.82-.63L7.71,10.64h3.07l.35-1L12,7.33M12,4.5a.88.88,0,0,0-.83.58L9.72,9.14H5.88a.91.91,0,0,0-.7.34.88.88,0,0,0,.16,1.23l3.31,2.55L7.41,17.37a.87.87,0,0,0,.15.78.86.86,0,0,0,.7.35.89.89,0,0,0,.53-.18L12,15.86l3.23,2.45a.87.87,0,0,0,.53.18,1.15,1.15,0,0,0,.26,0,.88.88,0,0,0,.58-1.1l-1.24-4.11,3.28-2.54A.85.85,0,0,0,19,10a.87.87,0,0,0-.88-.88h-4l-1.27-4a.9.9,0,0,0-.55-.56.93.93,0,0,0-.29,0Z' transform='translate(-5 -4.5)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-11-favorite-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E11-favorite-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3Cpath d='M18.12,9.14h-4l-1.27-4a.9.9,0,0,0-.55-.56.93.93,0,0,0-.29,0,.88.88,0,0,0-.83.58L9.72,9.14H5.88a.91.91,0,0,0-.7.34.88.88,0,0,0,.16,1.23l3.31,2.55L7.41,17.37a.87.87,0,0,0,.15.78.86.86,0,0,0,.7.35.89.89,0,0,0,.53-.18L12,15.86l3.23,2.45a.87.87,0,0,0,.53.18,1.15,1.15,0,0,0,.26,0,.88.88,0,0,0,.58-1.1l-1.24-4.11,3.28-2.54A.85.85,0,0,0,19,10,.87.87,0,0,0,18.12,9.14Zm-3.66,2.93-.81.63.3,1L14.63,16l-1.7-1.3L12,14l-.91.69L9.4,16l.69-2.28.29-1-.82-.63L7.71,10.64h3.07l.35-1L12,7.33l.71,2.26L13,10.64h3.31Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-11-favorite-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E11-favorite-b1-hover%3C/title%3E%3Cpolygon points='12 9.64 11.66 8.59 10.95 6.33 10.13 8.64 9.78 9.64 8.72 9.64 6.71 9.64 8.56 11.07 9.38 11.7 9.09 12.69 8.4 14.97 10.11 13.66 11.02 12.97 11.93 13.66 13.63 14.96 12.95 12.69 12.65 11.7 13.46 11.07 15.31 9.64 13.1 9.64 12 9.64' style='fill:%23002a54'/%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.66,9.71-3.28,2.54,1.24,4.11a.88.88,0,0,1-.58,1.1,1.15,1.15,0,0,1-.26,0,.87.87,0,0,1-.53-.18L12,15.86,8.79,18.32a.89.89,0,0,1-.53.18.86.86,0,0,1-.7-.35.87.87,0,0,1-.15-.78l1.24-4.11L5.34,10.71a.88.88,0,0,1-.16-1.23.91.91,0,0,1,.7-.34H9.72l1.44-4.06A.88.88,0,0,1,12,4.5a.93.93,0,0,1,.29,0,.9.9,0,0,1,.55.56l1.27,4h4A.87.87,0,0,1,19,10,.85.85,0,0,1,18.66,10.71Z' transform='translate(-1 -1)' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-12-info{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E12-info-b1%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:%23002a54;fill-rule:evenodd'/%3E%3Cpath d='M12,9a1,1,0,0,0-1,1v8a1,1,0,0,0,2,0V10A1,1,0,0,0,12,9Z' transform='translate(-1 -1)' style='fill:%23002a54;fill-rule:evenodd'/%3E%3Ccircle cx='11' cy='5' r='1' style='fill:%23002a54'/%3E%3C/svg%3E\")}.sfi.b1-12-info-circle{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0' y='0' viewBox='0 0 24 24' xml:space='preserve'%3E%3Cstyle%3E .st0%7Bfill-rule:evenodd;clip-rule:evenodd;fill:%23002a54%7D %3C/style%3E%3Cpath class='st0' d='M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 21C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z'/%3E%3Cpath class='st0' d='M12 8.996a1 1 0 0 0-1 1v8a1 1 0 1 0 2 0v-8a1 1 0 0 0-1-1z'/%3E%3Ccircle class='st0' cx='12' cy='5.997' r='1'/%3E%3C/svg%3E%0A\")}.sfi.b1-12-info-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E12-info-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm1,17a1,1,0,0,1-2,0V10a1,1,0,0,1,2,0ZM12,7a1,1,0,1,1,1-1A1,1,0,0,1,12,7Z' transform='translate(-1 -1)' style='fill:%23002a54;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.b1-13-help{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0' y='0' viewBox='0 0 24 24' xml:space='preserve'%3E%3Cstyle%3E .st0%7Bfill-rule:evenodd;clip-rule:evenodd;fill:%23002a54%7D %3C/style%3E%3Cpath class='st0' d='M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 21C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z'/%3E%3Cpath class='st0' d='M13.393 5.118c-.697-.156-1.9-.15-2.534.01A3.108 3.108 0 0 0 8.995 6.22a7.317 7.317 0 0 0-.118.167c-.153.212-.223.398-.223.588 0 .553.45 1.003 1.004 1.003.317 0 .495-.087.68-.33.052-.07.336-.428.891-.56.259-.06.535-.09.824-.09.344 0 .67.042.944.083.61.093 1.036.592 1.036 1.214 0 .457-.223.74-.417.935l-1.337 1.275a3.973 3.973 0 0 0-1.267 2.92v.571c0 .554.45 1.005 1.003 1.005s1.002-.451 1.002-1.005v-.572c0-.36.05-.891.49-1.347l1.661-1.547a3.24 3.24 0 0 0 .871-2.235c0-1.597-1.113-2.933-2.646-3.176z'/%3E%3Ccircle class='st0' cx='12' cy='18.002' r='1'/%3E%3C/svg%3E%0A\")}.sfi.b1-13-help-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 18.002a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm3.168-8.473l-1.66 1.547c-.44.456-.49.987-.49 1.347v.572c0 .554-.45 1.005-1.003 1.005s-1.003-.451-1.003-1.005v-.572c0-1.113.45-2.15 1.267-2.919l1.337-1.275c.194-.195.417-.478.417-.935 0-.622-.426-1.12-1.036-1.214a6.37 6.37 0 0 0-.944-.082c-.289 0-.565.029-.823.088-.556.133-.84.49-.891.56-.186.244-.364.331-.681.331-.553 0-1.004-.45-1.004-1.003 0-.19.07-.376.223-.588.017-.024.097-.14.118-.167a3.108 3.108 0 0 1 1.864-1.09c.633-.162 1.837-.167 2.534-.011 1.533.243 2.646 1.579 2.646 3.176a3.24 3.24 0 0 1-.871 2.235z' fill='%23002a54'/%3E%3C/svg%3E%0A\")}.sfi.b1-14-warning{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0' y='0' viewBox='0 0 24 24' xml:space='preserve'%3E%3Cstyle%3E .st1%7Bfill:%23002a54%7D %3C/style%3E%3Ccircle cx='12' cy='18.997' r='1' fill-rule='evenodd' clip-rule='evenodd' fill='%23002a54'/%3E%3Cpath class='st1' d='M12 6.996a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1z'/%3E%3Cpath class='st1' d='M22.856 20.882L13.244 1.817a1.43 1.43 0 0 0-.629-.664A1.321 1.321 0 0 0 12.001 1c-.511 0-1.003.3-1.245.817L1.144 20.882a1.536 1.536 0 0 0-.144.65C1 22.344 1.621 23 2.388 23h19.224c.213 0 .424-.052.615-.152.687-.36.969-1.24.629-1.966zm-1.092 1.08a.325.325 0 0 1-.152.038H2.388c-.21 0-.388-.214-.388-.467 0-.08.017-.158.037-.2L11.662 2.24c.083-.178.23-.241.339-.241.051 0 .102.013.15.039.08.04.145.112.2.228l9.599 19.04c.112.238.025.544-.186.655z'/%3E%3C/svg%3E%0A\")}.sfi.b1-14-warning-hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M22.856 20.882L13.244 1.817a1.43 1.43 0 0 0-.629-.664A1.321 1.321 0 0 0 12.001 1c-.511 0-1.003.3-1.245.817L1.144 20.882a1.536 1.536 0 0 0-.144.65C1 22.344 1.621 23 2.388 23h19.224c.213 0 .424-.052.615-.152.687-.36.969-1.24.629-1.966zM12 19.997a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm1-5a1 1 0 1 1-2 0v-7a1 1 0 1 1 2 0v7z' fill='%23002a54'/%3E%3C/svg%3E%0A\")}.sfi.b2-01-up{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7.99'%3E%3Ctitle%3E01-up-white%3C/title%3E%3Cpath d='M26,24a1,1,0,0,1-.73-.32L20,18l-5.27,5.67a1,1,0,1,1-1.46-1.37l5.32-5.71a2,2,0,0,1,2.82,0l5.32,5.71A1,1,0,0,1,26,24' transform='translate(-13 -16)' style='fill:%23056DAE;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.b2-02-down{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-white%3C/title%3E%3Cpath d='M20,24a1.94,1.94,0,0,1-1.41-.62l-5.32-5.72a1,1,0,0,1,1.46-1.36L20,22l5.27-5.66a1,1,0,0,1,1.46,1.36l-5.32,5.72A1.94,1.94,0,0,1,20,24' transform='translate(-13 -15.99)' style='fill:%23056DAE;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.b2-03-left{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Ctitle%3E03-left-white%3C/title%3E%3Cpath d='M23,27a1,1,0,0,1-.68-.27l-5.72-5.32a1.91,1.91,0,0,1,0-2.82l5.72-5.32a1,1,0,1,1,1.36,1.46L18,20l5.66,5.27a1,1,0,0,1,0,1.41A1,1,0,0,1,23,27' transform='translate(-15.99 -13)' style='fill:%23056DAE;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.b2-04-right{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Ctitle%3E04-right-white%3C/title%3E%3Cpath d='M17,27a1,1,0,0,1-.68-1.73L22,20l-5.66-5.27a1,1,0,0,1,1.36-1.46l5.72,5.32a1.91,1.91,0,0,1,0,2.82l-5.72,5.32A1,1,0,0,1,17,27' transform='translate(-15.99 -13)' style='fill:%23056DAE;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.g1-06-close{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-white%3C/title%3E%3Cpath d='M21.41,20l3.8-3.79a1,1,0,0,0-1.42-1.42L20,18.59l-3.79-3.8a1,1,0,0,0-1.42,1.42L18.59,20l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L20,21.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-14.5 -14.5)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.g1-06-close:hover,.sfi.g1-06-close:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-white-hover%3C/title%3E%3Cpath d='M21.41,20.15l3.8-3.79a1,1,0,0,0,0-1.41,1,1,0,0,0-1.42,0L20,18.74,16.21,15a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l3.8,3.79L14.79,24a1,1,0,0,0,0,1.41,1,1,0,0,0,1.42,0L20,21.57l3.79,3.79a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.41ZM20,9A11,11,0,1,0,31,20,11,11,0,0,0,20,9' transform='translate(-9 -9)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.g1-02-down{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-white%3C/title%3E%3Cpath d='M20,24a1.94,1.94,0,0,1-1.41-.62l-5.32-5.72a1,1,0,0,1,1.46-1.36L20,22l5.27-5.66a1,1,0,0,1,1.46,1.36l-5.32,5.72A1.94,1.94,0,0,1,20,24' transform='translate(-13 -15.99)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}\n"] }]
        }], ctorParameters: () => [{ type: Icon2RegistryService }, { type: i0.ChangeDetectorRef }], propDecorators: { role: [{
                type: Input
            }], idStr: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaHidden: [{
                type: Input
            }], size: [{
                type: Input
            }], isHoverSfi: [{
                type: Input
            }], includeCircle: [{
                type: Input
            }], type: [{
                type: Input
            }], svgStyle: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], shade: [{
                type: Input
            }], name: [{
                type: Input
            }], svgDivElementRef: [{
                type: ViewChild,
                args: ['svgContainer']
            }] } });

class Icon2WrapperComponent {
    constructor() {
        this.flexDirection = 'row';
        this.flexWrap = 'wrap';
        this.padding = '0px';
        this.alignment = 'center';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Icon2WrapperComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: Icon2WrapperComponent, isStandalone: false, selector: "citi-icon2-wrapper, [citi-icon2-wrapper]", inputs: { flexDirection: "flexDirection", flexWrap: "flexWrap", padding: "padding", alignment: "alignment" }, host: { classAttribute: "citi-icon2-wrapper" }, ngImport: i0, template: `
      <div class="flex-wrapper wrapper-text"
        [ngStyle]="{
          'flex-direction': flexDirection,
          'padding': padding,
          'align-items': alignment,
          'flex-wrap': flexWrap
        }">
          <ng-content></ng-content>
      </div>
    `, isInline: true, styles: [".flex-wrapper{display:inline-flex;position:relative}.wrapper-text{font-size:16px}\n"], dependencies: [{ kind: "directive", type: i3$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Icon2WrapperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-icon2-wrapper, [citi-icon2-wrapper]', standalone: false, host: { class: 'citi-icon2-wrapper' }, template: `
      <div class="flex-wrapper wrapper-text"
        [ngStyle]="{
          'flex-direction': flexDirection,
          'padding': padding,
          'align-items': alignment,
          'flex-wrap': flexWrap
        }">
          <ng-content></ng-content>
      </div>
    `, styles: [".flex-wrapper{display:inline-flex;position:relative}.wrapper-text{font-size:16px}\n"] }]
        }], propDecorators: { flexDirection: [{
                type: Input
            }], flexWrap: [{
                type: Input
            }], padding: [{
                type: Input
            }], alignment: [{
                type: Input
            }] } });

const COMPONENTS$9 = [
    Icon2Component,
    Icon2WrapperComponent
];
const MODULES$a = [
    CommonModule,
];
class Icons2 {
    static forRoot() {
        return {
            ngModule: Icons2,
            providers: [ICON2_REGISTRY_PROVIDER, provideHttpClient(withInterceptorsFromDi())]
        };
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Icons2, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: Icons2, declarations: [Icon2Component,
            Icon2WrapperComponent], imports: [CommonModule], exports: [Icon2Component,
            Icon2WrapperComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Icons2, providers: [provideHttpClient(withInterceptorsFromDi())], imports: [MODULES$a] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Icons2, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$a],
                    declarations: [...COMPONENTS$9],
                    exports: [...COMPONENTS$9],
                    providers: [provideHttpClient(withInterceptorsFromDi())]
                }]
        }] });

class ContentHtmlDirective {
    constructor(_el) {
        this._el = _el;
    }
    ngOnInit() {
        if (this.html) {
            this._el.nativeElement.innerHTML = this.html;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ContentHtmlDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.14", type: ContentHtmlDirective, isStandalone: false, selector: "[contentHtml]", inputs: { html: ["contentHtml", "html"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ContentHtmlDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[contentHtml]',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { html: [{
                type: Input,
                args: ['contentHtml']
            }] } });

class ContentHtml {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ContentHtml, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: ContentHtml, declarations: [ContentHtmlDirective], imports: [CommonModule], exports: [ContentHtmlDirective] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ContentHtml, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ContentHtml, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [ContentHtmlDirective],
                    exports: [ContentHtmlDirective]
                }]
        }] });

function coerceBool(val) {
    if (typeof val === 'boolean')
        return val;
    else if (!val || val == 'false')
        return false;
    else if (val == 'true')
        return true;
    else
        console.error(`Expected a boolean but received ${val}`);
}

class DdlAriaHideOuterDomDirective {
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(el, _renderer) {
        this.el = el;
        this._renderer = _renderer;
        // #region ========== Properties ========== //
        this._parentalSiblings = [];
        /**
         * Array of CSS selectors that should be excluded from having aria-hidden applied
         * @default '.cdk-live-announcer-element' live announcer should not be hidden from screen readers
         */
        this.outerDomExceptions = ['.cdk-live-announcer-element', '.ddl-live-announcer-element'];
    }
    ngOnChanges(simpleChanges) {
        for (const propName in simpleChanges) {
            if (simpleChanges.hasOwnProperty(propName)) {
                const change = simpleChanges[propName];
                if (propName === 'isOuterDomAriaHidden' && !change.isFirstChange()) {
                    if (this.isOuterDomAriaHidden) {
                        this._ariaHideParentalSiblings();
                    }
                    else {
                        this._ariaRestoreParentalSiblings();
                    }
                }
            }
        }
    }
    ngAfterViewInit() {
        if (this.isOuterDomAriaHidden) {
            this._ariaHideParentalSiblings();
        }
    }
    ngOnDestroy() {
        this._ariaRestoreParentalSiblings();
    }
    // #endregion
    // #region ========== Public Methods ========== //
    show() {
        this._ariaRestoreParentalSiblings();
    }
    hide() {
        this._ariaHideParentalSiblings();
    }
    // #endregion
    // #region ========== Private Methods ========== //
    _ariaHideParentalSiblings() {
        // This method should not be run again when _parentalSiblings array is not empty.
        // However, in case it is, we need to clear it first before running again.
        if (this._parentalSiblings.length) {
            this._ariaRestoreParentalSiblings();
        }
        this.getElementSiblings(this.el.nativeElement).forEach(sibling => {
            if (!this._isElementExcluded(sibling)) {
                this._applyAriaHidden(sibling);
            }
        });
        this.getElementParentsUntil(this.el.nativeElement, 'body').forEach(parent => {
            this.getElementSiblings(parent).forEach(sibling => {
                if (!this._isElementExcluded(sibling)) {
                    this._applyAriaHidden(sibling);
                }
            });
        });
    }
    _applyAriaHidden(el) {
        const currentAriaHiddenAttribute = el.getAttribute('aria-hidden');
        // first store element's attribute value for aria-hidden
        this._parentalSiblings.push({
            element: el,
            attributeName: 'aria-hidden',
            previousAttributeValue: currentAriaHiddenAttribute
        });
        // now set aria-hidden to false
        this._renderer.setAttribute(el, 'aria-hidden', 'true');
    }
    _ariaRestoreParentalSiblings() {
        this._parentalSiblings.forEach(parentSibling => {
            if (parentSibling.previousAttributeValue) {
                this._renderer.setAttribute(parentSibling.element, parentSibling.attributeName, parentSibling.previousAttributeValue);
            }
            else {
                this._renderer.removeAttribute(parentSibling.element, parentSibling.attributeName);
            }
        });
        // after resetting values, clear array
        this._parentalSiblings.length = 0;
    }
    _isElementExcluded(el) {
        return el.matches(this.outerDomExceptions.join(','));
    }
    // #endregion
    /**
     * Gets an array of predecessor elements until you reach a specific element
     * @param node pass in the node to find its predecessors
     * @param [selector] limits the array of predecessors to the closest element matching the selector
     * @returns array of predecessor elements of the provided node, until it reaches the element matching the selector
     */
    getElementParentsUntil(node, selector) {
        const parentElements = [];
        let parent = node.parentElement;
        while (parent) {
            if (selector &&
                parent?.closest &&
                parent?.closest(selector) === parent) {
                break;
            }
            if (parent.nodeType === 1) {
                parentElements.unshift(parent);
            }
            parent = parent.parentElement;
        }
        return parentElements;
    }
    /**
     * Gets an array of all predecessor elements
     * @param node pass in the node to find its predecessors
     * @returns array of predecessor elements of the provided node
     */
    getElementParents(node) {
        return this.getElementParentsUntil(node);
    }
    /**
     * Gets element siblings
     * @param node pass in the node to find it's siblings
     * @returns an array of the sibling elements
     */
    getElementSiblings(node) {
        return this.getElementChildren(node.parentNode, [node]);
    }
    /**
     * Gets element children
     * @param parentNode pass in the node to get it's children
     * @param [skipChildNodes] the child node (or array of node children) you want to skip (optional)
     * @returns an array of the child elements of the supplied node
     */
    getElementChildren(parentNode, skipChildNodes) {
        const returnChildren = [];
        if (!parentNode) {
            return returnChildren;
        }
        let child = parentNode.firstChild;
        while (child) {
            const isSkipped = Array.isArray(skipChildNodes)
                ? skipChildNodes.includes(child)
                : skipChildNodes === child;
            // check for Element type and if skipped
            if (child.nodeType === 1 && !isSkipped) {
                returnChildren.push(child);
            }
            child = child.nextSibling;
        }
        return returnChildren;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: DdlAriaHideOuterDomDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.14", type: DdlAriaHideOuterDomDirective, isStandalone: false, selector: "[ddlAriaHideOuterDom]", inputs: { isOuterDomAriaHidden: "isOuterDomAriaHidden", outerDomExceptions: "outerDomExceptions" }, exportAs: ["ddlAriaHideOuterDom"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: DdlAriaHideOuterDomDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ddlAriaHideOuterDom]',
                    standalone: false,
                    exportAs: 'ddlAriaHideOuterDom'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { isOuterDomAriaHidden: [{
                type: Input
            }], outerDomExceptions: [{
                type: Input
            }] } });

/**
* @Component: JAMP
* @description This component generates a JAMP (Just A Moment Please) element that can be shown during loading
*/
class JampComponent {
    set useGif(value) {
        this._useGif = value;
        this.loadingClass = this._useGif ? '' : 'loading';
    }
    ;
    get useGif() { return this._useGif; }
    ;
    set visible(show) {
        this._visible = coerceBool(show);
        if (!!this._visible !== !this.isHidden) {
            if (this._visible) {
                this.show();
            }
            else {
                this.hide();
            }
        }
    }
    get visible() { return this._visible; }
    ;
    /**
    * @property pagelevel
    * @description Whether JAMP fills the entire window instead of just its immediate container
    * @Type: boolean
    */
    set pagelevel(value) {
        this.isPageLevel = value;
    }
    ;
    get pagelevel() { return this.isPageLevel; }
    ;
    /**
    * @property additionalCssClasses
    * @description Adds the given string as additional classes to the class attribute of the jamp. This will replaces
    * any CSS classes not in DDL standard with the string provided in the parameter.
    * @return string
    * @twoway false
    */
    set additionalCssClasses(value) {
        this._additionalCssClasses = value;
        this.updateCssClasses();
    }
    get additionalCssClasses() {
        return this._additionalCssClasses;
    }
    // #endregion
    // #region ========== Constructor / LifeCycle Hooks ========== //
    constructor(announcer, _doc, _renderer) {
        this.announcer = announcer;
        this._doc = _doc;
        this._renderer = _renderer;
        // #region ========== Properties ========== //
        this.jampCssClass = '';
        this.loadingClass = '';
        this._additionalCssClasses = '';
        // #endregion
        // #region ========== HostBinding ========== //
        // Set classes on host <citi-jamp> tag
        this.isHidden = true;
        this.isPageLevel = false;
        this._document = this._doc;
    }
    ngOnInit() {
        // if both are set to true on init, then this makes sure it shows up correctly
        if (this.pagelevel && this.visible) {
            this.isHidden = true;
            this.show();
        }
        this.updateCssClasses();
    }
    ngOnDestroy() {
        this._setBodyAttributes(false);
    }
    // #endregion
    // #region ========== Methods ========== //
    updateCssClasses() {
        const classSet = this._additionalCssClasses.split(' ');
        classSet.push('jamp-css');
        if (this.isCenterAligned)
            classSet.push('jamp-center-css');
        this.jampCssClass = classSet.join(' ');
    }
    /**
    * @name show
    * @description Makes the JAMP visible.
    * @return Void
    */
    show() {
        if (this.isHidden) {
            this.isHidden = false;
            if (this.isPageLevel) {
                this._previousFocusedElement = this._document.activeElement;
                this._isPageLevelJampVisible = true;
                this._setBodyAttributes(this._isPageLevelJampVisible);
                setTimeout(() => {
                    this.jampPageLevelContent?.nativeElement?.focus();
                    this._isDomAriaHidden = true;
                }, 100);
            }
            else {
                this.announcer.announce(this.waitMessage, 200);
            }
        }
    }
    /**
    * @name show
    * @description Makes the JAMP hidden.
    * @return Void
    */
    hide() {
        if (!this.isHidden) {
            this.announcer.announce(this.readyMessage, 200);
            this.isHidden = true;
            this._isDomAriaHidden = false;
            if (this.isPageLevel) {
                this._setBodyAttributes(!this.isHidden);
                setTimeout(() => {
                    this._previousFocusedElement?.focus();
                    this._isPageLevelJampVisible = false;
                }, 10);
            }
        }
    }
    _setBodyAttributes(isJampVisible) {
        if (isJampVisible && this.isPageLevel) {
            this._renderer.addClass(this._document.body, 'ddl-jamp-open');
        }
        else {
            this._renderer.removeClass(this._document.body, 'ddl-jamp-open');
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: JampComponent, deps: [{ token: i1$2.LiveAnnouncer }, { token: DOCUMENT }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: JampComponent, isStandalone: false, selector: "citi-jamp", inputs: { useGif: "useGif", visible: "visible", pagelevel: "pagelevel", htmlContent: "htmlContent", additionalCssClasses: "additionalCssClasses", waitMessage: "waitMessage", readyMessage: "readyMessage", isCenterAligned: "isCenterAligned" }, host: { properties: { "class.hidden": "this.isHidden", "class.jamp-page-level": "this.isPageLevel" } }, viewQueries: [{ propertyName: "jampPageLevelContent", first: true, predicate: ["jampPageLevelContent"], descendants: true }], ngImport: i0, template: `
    <div class="fillHeight" *ngIf="!isPageLevel; else pageLevelJamp">
      <div class="alignmentEl fillHeight" [ngClass]="(isCenterAligned ? 'position-initial' : '')">
        <div class="jamp {{jampCssClass}}">
          <div class="img {{loadingClass}}" role="img" [attr.aria-label]="waitMessage"></div>
          <div class="message" [contentHtml]="htmlContent">
            <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
          </div>
        </div>
      </div>
    </div>
    <ng-template #pageLevelJamp>
      <div class="fillHeight" *ngIf="_isPageLevelJampVisible" ddlAriaHideOuterDom [isOuterDomAriaHidden]="_isDomAriaHidden">
        <div class="alignmentEl fillHeight" [ngClass]="(isCenterAligned ? 'position-initial' : '')" cdkTrapFocus>
          <div #jampPageLevelContent class="jamp {{jampCssClass}}" tabindex="0" role="dialog">
            <div class="img {{loadingClass}}" role="img" [attr.aria-label]="waitMessage"></div>
            <div class="message" [contentHtml]="htmlContent">
              <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
            </div>
          </div>
        </div>
      </div>
    </ng-template>
    <ng-template #contentTemplate><ng-content></ng-content></ng-template>
    `, isInline: true, styles: [".jamp.white .message{color:#fff}.jamp .img{background-position:0 0;background-repeat:no-repeat;background-size:contain;padding-top:5px;margin-right:3px;width:30px;height:30px;display:inline-block}.jamp .message{position:relative;top:-9px}.jamp.jamp-css .loading{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.1.0%2C%20SVG%20Export%20Plug-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220px%22%0A%09%20viewBox%3D%220%200%2030%2030%22%20style%3D%22enable-background%3Anew%200%200%2030%2030%3B%22%20xml%3Aspace%3D%22preserve%22%3E%0A%3Cstyle%20type%3D%22text/css%22%3E%0A%09.Drop_x0020_Shadow%7Bfill%3Anone%3B%7D%0A%09.Round_x0020_Corners_x0020_2_x0020_pt%7Bfill%3A%23FFFFFF%3Bstroke%3A%23000000%3Bstroke-miterlimit%3A10%3B%7D%0A%09.Live_x0020_Reflect_x0020_X%7Bfill%3Anone%3B%7D%0A%09.Bevel_x0020_Soft%7Bfill%3Aurl%28%23SVGID_1_%29%3B%7D%0A%09.Dusk%7Bfill%3A%23FFFFFF%3B%7D%0A%09.Foliage_GS%7Bfill%3A%23FFDD00%3B%7D%0A%09.Pompadour_GS%7Bfill-rule%3Aevenodd%3Bclip-rule%3Aevenodd%3Bfill%3A%2344ADE2%3B%7D%0A%09.st0%7Bfill%3Anone%3B%7D%0A%09.st1%7Bfill%3A%23056EAE%3B%7D%0A%3C/style%3E%0A%3ClinearGradient%20id%3D%22SVGID_1_%22%20gradientUnits%3D%22userSpaceOnUse%22%20x1%3D%220%22%20y1%3D%220%22%20x2%3D%220.7071%22%20y2%3D%220.7071%22%3E%0A%09%3Cstop%20%20offset%3D%220%22%20style%3D%22stop-color%3A%23DEDFE3%22/%3E%0A%09%3Cstop%20%20offset%3D%220.1738%22%20style%3D%22stop-color%3A%23D8D9DD%22/%3E%0A%09%3Cstop%20%20offset%3D%220.352%22%20style%3D%22stop-color%3A%23C9CACD%22/%3E%0A%09%3Cstop%20%20offset%3D%220.5323%22%20style%3D%22stop-color%3A%23B4B5B8%22/%3E%0A%09%3Cstop%20%20offset%3D%220.7139%22%20style%3D%22stop-color%3A%23989A9C%22/%3E%0A%09%3Cstop%20%20offset%3D%220.8949%22%20style%3D%22stop-color%3A%23797C7E%22/%3E%0A%09%3Cstop%20%20offset%3D%221%22%20style%3D%22stop-color%3A%23656B6C%22/%3E%0A%3C/linearGradient%3E%0A%3Cg%3E%0A%09%3Crect%20x%3D%220%22%20class%3D%22st0%22%20width%3D%2230%22%20height%3D%2230%22/%3E%0A%09%3Cpath%20class%3D%22st1%22%20d%3D%22M2.2%2C17.5C2.1%2C16.7%2C2%2C15.8%2C2%2C15C2%2C7.8%2C7.8%2C2%2C15%2C2s13%2C5.8%2C13%2C13c0%2C0.8-0.1%2C1.7-0.2%2C2.5h2%0A%09%09c0.1-0.8%2C0.2-1.6%2C0.2-2.5c0-8.3-6.7-15-15-15S0%2C6.7%2C0%2C15c0%2C0.8%2C0.1%2C1.7%2C0.2%2C2.5H2.2z%22/%3E%0A%3C/g%3E%0A%3C/svg%3E%0A);background-repeat:no-repeat;-webkit-animation:spin 1.16s infinite linear;animation:spin 1.16s infinite linear}.jamp.jamp-css.white .loading{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.2.1%2C%20SVG%20Export%20Plug-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220px%22%0A%09%20viewBox%3D%220%200%2030%2030%22%20style%3D%22enable-background%3Anew%200%200%2030%2030%3B%22%20xml%3Aspace%3D%22preserve%22%3E%0A%3Cstyle%20type%3D%22text/css%22%3E%0A%09.st0%7Bfill%3Anone%3B%7D%0A%09.st1%7Bfill%3A%23FFFFFF%3B%7D%0A%3C/style%3E%0A%3Cg%3E%0A%09%3Crect%20class%3D%22st0%22%20width%3D%2230%22%20height%3D%2230%22/%3E%0A%09%3Cpath%20class%3D%22st1%22%20d%3D%22M2.2%2C17.5C2.1%2C16.7%2C2%2C15.8%2C2%2C15C2%2C7.8%2C7.8%2C2%2C15%2C2s13%2C5.8%2C13%2C13c0%2C0.8-0.1%2C1.7-0.2%2C2.5h2%0A%09%09c0.1-0.8%2C0.2-1.6%2C0.2-2.5c0-8.3-6.7-15-15-15S0%2C6.7%2C0%2C15c0%2C0.8%2C0.1%2C1.7%2C0.2%2C2.5H2.2z%22/%3E%0A%3C/g%3E%0A%3C/svg%3E%0A);background-repeat:no-repeat}@-webkit-keyframes spin{0%{-webkit-transform:rotate(0deg);transform:rotate(0)}to{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}@keyframes spin{0%{-webkit-transform:rotate(0deg);transform:rotate(0)}to{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}:host.jamp-page-level{background:#ffffffe6;position:fixed;z-index:100;inset:0}:host.jamp-page-level .row{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}:host.jamp-page-level .jamp:focus-visible{outline:none}.jamp-center-css{width:calc(100% - 60px);max-height:calc(100% - 60px);position:absolute!important;top:50%!important;left:50%!important;transform:translate(-50%,-50%)}.jamp{position:relative!important;top:50%!important;left:0%!important;text-align:center!important;-webkit-transform:translateY(-50%)!important;-moz-transform:translateY(-50%)!important;-ms-transform:translateY(-50%)!important;-o-transform:translateY(-50%)!important;transform:translateY(-50%)!important;margin:0}.fillHeight{min-height:100%;height:100%}.position-initial{position:initial}.citi-modal :host .jamp{margin:30px 0 10px}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: ContentHtmlDirective, selector: "[contentHtml]", inputs: ["contentHtml"] }, { kind: "directive", type: i1$2.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "directive", type: DdlAriaHideOuterDomDirective, selector: "[ddlAriaHideOuterDom]", inputs: ["isOuterDomAriaHidden", "outerDomExceptions"], exportAs: ["ddlAriaHideOuterDom"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: JampComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-jamp', standalone: false, template: `
    <div class="fillHeight" *ngIf="!isPageLevel; else pageLevelJamp">
      <div class="alignmentEl fillHeight" [ngClass]="(isCenterAligned ? 'position-initial' : '')">
        <div class="jamp {{jampCssClass}}">
          <div class="img {{loadingClass}}" role="img" [attr.aria-label]="waitMessage"></div>
          <div class="message" [contentHtml]="htmlContent">
            <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
          </div>
        </div>
      </div>
    </div>
    <ng-template #pageLevelJamp>
      <div class="fillHeight" *ngIf="_isPageLevelJampVisible" ddlAriaHideOuterDom [isOuterDomAriaHidden]="_isDomAriaHidden">
        <div class="alignmentEl fillHeight" [ngClass]="(isCenterAligned ? 'position-initial' : '')" cdkTrapFocus>
          <div #jampPageLevelContent class="jamp {{jampCssClass}}" tabindex="0" role="dialog">
            <div class="img {{loadingClass}}" role="img" [attr.aria-label]="waitMessage"></div>
            <div class="message" [contentHtml]="htmlContent">
              <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
            </div>
          </div>
        </div>
      </div>
    </ng-template>
    <ng-template #contentTemplate><ng-content></ng-content></ng-template>
    `, styles: [".jamp.white .message{color:#fff}.jamp .img{background-position:0 0;background-repeat:no-repeat;background-size:contain;padding-top:5px;margin-right:3px;width:30px;height:30px;display:inline-block}.jamp .message{position:relative;top:-9px}.jamp.jamp-css .loading{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.1.0%2C%20SVG%20Export%20Plug-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220px%22%0A%09%20viewBox%3D%220%200%2030%2030%22%20style%3D%22enable-background%3Anew%200%200%2030%2030%3B%22%20xml%3Aspace%3D%22preserve%22%3E%0A%3Cstyle%20type%3D%22text/css%22%3E%0A%09.Drop_x0020_Shadow%7Bfill%3Anone%3B%7D%0A%09.Round_x0020_Corners_x0020_2_x0020_pt%7Bfill%3A%23FFFFFF%3Bstroke%3A%23000000%3Bstroke-miterlimit%3A10%3B%7D%0A%09.Live_x0020_Reflect_x0020_X%7Bfill%3Anone%3B%7D%0A%09.Bevel_x0020_Soft%7Bfill%3Aurl%28%23SVGID_1_%29%3B%7D%0A%09.Dusk%7Bfill%3A%23FFFFFF%3B%7D%0A%09.Foliage_GS%7Bfill%3A%23FFDD00%3B%7D%0A%09.Pompadour_GS%7Bfill-rule%3Aevenodd%3Bclip-rule%3Aevenodd%3Bfill%3A%2344ADE2%3B%7D%0A%09.st0%7Bfill%3Anone%3B%7D%0A%09.st1%7Bfill%3A%23056EAE%3B%7D%0A%3C/style%3E%0A%3ClinearGradient%20id%3D%22SVGID_1_%22%20gradientUnits%3D%22userSpaceOnUse%22%20x1%3D%220%22%20y1%3D%220%22%20x2%3D%220.7071%22%20y2%3D%220.7071%22%3E%0A%09%3Cstop%20%20offset%3D%220%22%20style%3D%22stop-color%3A%23DEDFE3%22/%3E%0A%09%3Cstop%20%20offset%3D%220.1738%22%20style%3D%22stop-color%3A%23D8D9DD%22/%3E%0A%09%3Cstop%20%20offset%3D%220.352%22%20style%3D%22stop-color%3A%23C9CACD%22/%3E%0A%09%3Cstop%20%20offset%3D%220.5323%22%20style%3D%22stop-color%3A%23B4B5B8%22/%3E%0A%09%3Cstop%20%20offset%3D%220.7139%22%20style%3D%22stop-color%3A%23989A9C%22/%3E%0A%09%3Cstop%20%20offset%3D%220.8949%22%20style%3D%22stop-color%3A%23797C7E%22/%3E%0A%09%3Cstop%20%20offset%3D%221%22%20style%3D%22stop-color%3A%23656B6C%22/%3E%0A%3C/linearGradient%3E%0A%3Cg%3E%0A%09%3Crect%20x%3D%220%22%20class%3D%22st0%22%20width%3D%2230%22%20height%3D%2230%22/%3E%0A%09%3Cpath%20class%3D%22st1%22%20d%3D%22M2.2%2C17.5C2.1%2C16.7%2C2%2C15.8%2C2%2C15C2%2C7.8%2C7.8%2C2%2C15%2C2s13%2C5.8%2C13%2C13c0%2C0.8-0.1%2C1.7-0.2%2C2.5h2%0A%09%09c0.1-0.8%2C0.2-1.6%2C0.2-2.5c0-8.3-6.7-15-15-15S0%2C6.7%2C0%2C15c0%2C0.8%2C0.1%2C1.7%2C0.2%2C2.5H2.2z%22/%3E%0A%3C/g%3E%0A%3C/svg%3E%0A);background-repeat:no-repeat;-webkit-animation:spin 1.16s infinite linear;animation:spin 1.16s infinite linear}.jamp.jamp-css.white .loading{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.2.1%2C%20SVG%20Export%20Plug-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220px%22%0A%09%20viewBox%3D%220%200%2030%2030%22%20style%3D%22enable-background%3Anew%200%200%2030%2030%3B%22%20xml%3Aspace%3D%22preserve%22%3E%0A%3Cstyle%20type%3D%22text/css%22%3E%0A%09.st0%7Bfill%3Anone%3B%7D%0A%09.st1%7Bfill%3A%23FFFFFF%3B%7D%0A%3C/style%3E%0A%3Cg%3E%0A%09%3Crect%20class%3D%22st0%22%20width%3D%2230%22%20height%3D%2230%22/%3E%0A%09%3Cpath%20class%3D%22st1%22%20d%3D%22M2.2%2C17.5C2.1%2C16.7%2C2%2C15.8%2C2%2C15C2%2C7.8%2C7.8%2C2%2C15%2C2s13%2C5.8%2C13%2C13c0%2C0.8-0.1%2C1.7-0.2%2C2.5h2%0A%09%09c0.1-0.8%2C0.2-1.6%2C0.2-2.5c0-8.3-6.7-15-15-15S0%2C6.7%2C0%2C15c0%2C0.8%2C0.1%2C1.7%2C0.2%2C2.5H2.2z%22/%3E%0A%3C/g%3E%0A%3C/svg%3E%0A);background-repeat:no-repeat}@-webkit-keyframes spin{0%{-webkit-transform:rotate(0deg);transform:rotate(0)}to{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}@keyframes spin{0%{-webkit-transform:rotate(0deg);transform:rotate(0)}to{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}:host.jamp-page-level{background:#ffffffe6;position:fixed;z-index:100;inset:0}:host.jamp-page-level .row{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}:host.jamp-page-level .jamp:focus-visible{outline:none}.jamp-center-css{width:calc(100% - 60px);max-height:calc(100% - 60px);position:absolute!important;top:50%!important;left:50%!important;transform:translate(-50%,-50%)}.jamp{position:relative!important;top:50%!important;left:0%!important;text-align:center!important;-webkit-transform:translateY(-50%)!important;-moz-transform:translateY(-50%)!important;-ms-transform:translateY(-50%)!important;-o-transform:translateY(-50%)!important;transform:translateY(-50%)!important;margin:0}.fillHeight{min-height:100%;height:100%}.position-initial{position:initial}.citi-modal :host .jamp{margin:30px 0 10px}\n"] }]
        }], ctorParameters: () => [{ type: i1$2.LiveAnnouncer }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.Renderer2 }], propDecorators: { isHidden: [{
                type: HostBinding,
                args: ['class.hidden']
            }], isPageLevel: [{
                type: HostBinding,
                args: ['class.jamp-page-level']
            }], jampPageLevelContent: [{
                type: ViewChild,
                args: ['jampPageLevelContent']
            }], useGif: [{
                type: Input
            }], visible: [{
                type: Input
            }], pagelevel: [{
                type: Input
            }], htmlContent: [{
                type: Input
            }], additionalCssClasses: [{
                type: Input
            }], waitMessage: [{
                type: Input
            }], readyMessage: [{
                type: Input
            }], isCenterAligned: [{
                type: Input
            }] } });

class DdlAriaHideOuterDomModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: DdlAriaHideOuterDomModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: DdlAriaHideOuterDomModule, declarations: [DdlAriaHideOuterDomDirective], imports: [CommonModule], exports: [DdlAriaHideOuterDomDirective] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: DdlAriaHideOuterDomModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: DdlAriaHideOuterDomModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DdlAriaHideOuterDomDirective],
                    imports: [CommonModule],
                    exports: [DdlAriaHideOuterDomDirective]
                }]
        }] });

const COMPONENTS$8 = [
    JampComponent
];
const MODULES$9 = [
    CommonModule,
    ContentHtml,
    A11yModule,
    DdlAriaHideOuterDomModule
];
class JAMP {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: JAMP, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: JAMP, declarations: [JampComponent], imports: [CommonModule,
            ContentHtml,
            A11yModule,
            DdlAriaHideOuterDomModule], exports: [JampComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: JAMP, imports: [MODULES$9] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: JAMP, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$9],
                    declarations: [...COMPONENTS$8],
                    exports: [JampComponent]
                }]
        }] });

let nextId = 0;
/**
 * @Component: Modal
 * @description This component generates a modal
 */
class ModalComponent {
    get ariaTitle() {
        return this.title ? this.title.replace(/<\/?[^>]+(>|$)/g, '') : '';
    }
    /**
     * @property idStr
     * @description Sets the id attribute of the modal
     * @return string
     */
    get idStr() {
        return this._id || this._uid;
    }
    set idStr(value) {
        this._id = value || this._uid;
    }
    /**
     * @property show
     * @description Whether the modal should be open or not
     * @twoway true
     * @Type: boolean
     */
    set show(show) {
        // Only run if changing.
        // You don't need to run code to show if already shown & vise versa
        // Also, keeps showChange emit from causing this to run twice
        if (this.show !== show) {
            this._show = show;
            // Should not run until after AfterViewInit.
            // Keeps from running on the first ngOnChanges
            if (this.isDoneWithViewInit) {
                if (this._show) {
                    this.showModal();
                }
                else {
                    this.hideModal();
                }
            }
        }
    }
    get show() {
        return this._show;
    }
    /**
     * @property closeButtonIdStr
     * @description Sets the id attribute of the modal close button
     * @return string
     */
    set closeButtonIdStr(idStr) {
        if (typeof idStr !== 'undefined') {
            this._closeButtonIdStr = idStr;
        }
    }
    get closeButtonIdStr() {
        return this._closeButtonIdStr;
    }
    // #endregion
    // #region // -------------- Constructor -------------- //
    constructor(renderer, _ngZone, cdr, _document, _navigator, _window) {
        this.renderer = renderer;
        this._ngZone = _ngZone;
        this.cdr = cdr;
        this._document = _document;
        this._window = _window;
        /**
         * @property ariaRole
         * @description the ariaRole to define a role for the modal
         * @return string
         */
        this.ariaRole = 'dialog';
        this._uid = `citi-modal-${nextId++}`;
        /**
         * @property primaryButtonTarget
         * @description The target for the primary button
         * @return string
         */
        this.primaryButtonTarget = null;
        /**
         * @property closeOnBgClick
         * @description whather or not close on bg click
         * @Type: boolean
         */
        this.closeOnBgClick = false;
        // #endregion
        // #region // -------------- @Output -------------- //
        /**
         * @Event: primaryButtonClick
         * @description This event that is fired when the primary button of the modal changes.
         * Can be bound to with (primaryButtonClick)='myFunction($event)'
         * @return Object
         */
        this.primaryButtonClick = new EventEmitter();
        /**
         * @Event: secondaryButtonClick
         * @description This event that is fired when the secondary button of the modal changes.
         * Can be bound to with (secondaryButtonClick)='myFunction($event)'
         * @return Object
         */
        this.secondaryButtonClick = new EventEmitter();
        /**
         * @Event: closeButtonClick
         * @description This event that is fired when the close button of the modal changes.
         * Can be bound to with (closeButtonClick)='myFunction($event)'
         * @return Object
         */
        this.closeButtonClick = new EventEmitter();
        /**
         * @Event: showChange
         * @description This event that is fired when the modal is hidden/displayed.
         * Can be bound to with (showChange)='myFunction($event)'.
         * Value 'true' in the parameter means the modal is displayed
         * Value 'false' in the parameter means the modal is hidden
         * @return boolean
         */
        this.showChange = new EventEmitter();
        /**
         * @Event: onHide
         * @description This event that is fired when the modal is hidden
         * @return Object
         */
        this.onHide = new EventEmitter();
        // This line has to be in the constructor.
        this.bodyEl = this._document.getElementsByTagName('body')[0];
        this.doc = this._document.documentElement;
        this.initialPosition = 0;
        this.isIOS =
            _navigator &&
                !!_navigator.platform &&
                /iPad|iPhone|iPod/.test(_navigator.platform);
        this._closeButtonIdStr = `modalCloseButton-${nextId++}`;
    }
    // #endregion
    // #region // -------------- Lifecycle Hooks -------------- //
    ngAfterViewInit() {
        this.isDoneWithViewInit = true;
        // If modal is set to show initially, then this will show the modal once the view is ready
        if (this.show) {
            this.showModal();
        }
    }
    ngOnDestroy() {
        // Make sure to remove classes/styles from body element
        this.toggleBodyScrollable(false);
        this.IOSFixBodyToScrollPos(false);
    }
    // #endregion
    // #region // -------------- HostListener -------------- //
    onEscape(event) {
        if (this.show && event.key === 'Escape') {
            if ((event.target.classList[0] === 'dropdown' || event.target.classList[0] === 'citi-tooltip-close' || event.target.classList[0] === 'tooltip-trigger') &&
                event.defaultPrevented === true) {
                this.show = true;
            }
            else {
                this.show = false;
            }
        }
    }
    // #endregion
    // #region // -------------- Methods -------------- //
    onClick(event) {
        //if we click outside close the modal
        if (this.isInClassActive === true &&
            !this.modal.nativeElement.contains(event.target) &&
            this.closeOnBgClick) {
            this.closeButtonClicked(event);
        }
    }
    // Function to open/close modal
    toggleModal(show) {
        if (typeof show !== 'boolean')
            this.show = !this.show;
        else
            this.show = show;
    }
    /** This function is being called from close button in Modal, which will fire toggle modal event and also
      close button event.
    */
    closeButtonClicked(e) {
        this.show = false;
        this.closeButtonClick.emit(e);
    }
    /** This function is being called on keyup event from close button in Modal, which will set role attribute to none when ctrl is pressed.
     */
    onKeyUpCloseButton(e) {
        if (e.keyCode == 17)
            this.ariaRole = 'none';
    }
    /** This function is being called from primary button in Modal, fires the event to to eventEmitter object,
      and passing parameter 'e' for the use on consuming side
    */
    primaryButtonClicked(e) {
        this.primaryButtonClick.emit(e);
    }
    /** This function is being called from secondary button in Modal, which will fire toggle
     modal event and also end user session event.
    */
    secondaryButtonClicked(e) {
        this.show = false;
        this.secondaryButtonClick.emit(e);
    }
    closeAndNavigate() {
        this.show = false;
    }
    executeOnStable(func) {
        this._ngZone.runOutsideAngular(() => {
            this._ngZone.onStable
                .asObservable()
                .pipe(take(1))
                .subscribe(() => func());
        });
    }
    showModal() {
        // We have to add the modal-open class to the body tag when the modal is open
        this.toggleBodyScrollable(true);
        this.isDisplayBlock = true;
        this.startShowTransition();
        this.showChange.emit(true);
        setTimeout(() => {
            this.openButton = this.getOpenerElement();
            this.finishShowTransition();
            // added because there are use-cases where change detection is not triggered
            this.cdr.markForCheck();
        }, 0);
    }
    hideModal() {
        this.IOSFixBodyToScrollPos(false);
        this.startHideTransition();
        this.showChange.emit(false);
        this.isDisplayBlock = false;
        // Focus the button that originally opened the modal
        setTimeout(() => {
            if (this.openButton) {
                this.openButton.focus();
            }
        }, 150);
        // Wait for the modal to fade out
        // The delay should match the CSS transition speed
        setTimeout(() => {
            // We have to remove the modal-open class when the modal is closed
            this.toggleBodyScrollable(false);
            this.finishHideTransition();
            // Emits event when a modal is hidden
            this.onHide.emit();
            // added because there are use-cases where change detection is not triggered
            this.cdr.markForCheck();
        }, 150);
    }
    startShowTransition() {
        // IOS Scroll fix
        this.IOSFixBodyToScrollPos(true);
        //expression has changed issue fixed by addign setTimeout
        setTimeout(() => {
            this.isDisplayBlock = true;
        });
    }
    finishShowTransition() {
        // Fade/transition in from top
        this.isInClassActive = true;
        // Scroll to top of modal
        this.modalBody.nativeElement.scrollTop = 0;
        this.modal.nativeElement.scrollTop = 0;
        // Focus modal container
        this.modal.nativeElement.focus();
        // Update to focus on the modal header (CUC-6346)
        if (!this.disableCloseButton)
            this.closeButton.nativeElement.focus();
        else if (this.title)
            this.modalHeader.nativeElement.focus();
        else if (this.modalHeaderAlt)
            this.modalHeaderAlt.nativeElement.focus();
        else
            this.content.nativeElement.focus();
    }
    startHideTransition() {
        this.isInClassActive = false;
    }
    finishHideTransition() {
        this.isDisplayBlock = false;
    }
    toggleBodyScrollable(isScrollable = true) {
        if (isScrollable) {
            // We have to add the 'modal-open' class to disable the scrolling capability for the body element
            this.renderer.addClass(this.bodyEl, 'modal-open');
            this.renderer.setStyle(this.bodyEl, 'padding-right', '17px');
        }
        else {
            // We have to remove the 'modal-open' class to reactivate the scrolling capability for the body element
            this.renderer.removeClass(this.bodyEl, 'modal-open');
            this.renderer.removeStyle(this.bodyEl, 'padding-right');
        }
    }
    IOSFixBodyToScrollPos(modalOpen = false) {
        if (this.isIOS) {
            if (modalOpen) {
                this.initialPosition =
                    ((this._window && this._window.pageYOffset) || this.doc.scrollTop) -
                        (this.doc.clientTop || 0);
                this.renderer.setStyle(this.bodyEl, 'overflow', 'hidden');
                this.renderer.setStyle(this.bodyEl, 'position', 'fixed');
                this.renderer.setStyle(this.bodyEl, 'width', '100%');
                this.renderer.setStyle(this.bodyEl, 'top', `-${this.initialPosition}px`);
            }
            else {
                this.initialPosition = this.initialPosition || 0;
                this.renderer.removeStyle(this.bodyEl, 'overflow');
                this.renderer.removeStyle(this.bodyEl, 'position');
                this.renderer.removeStyle(this.bodyEl, 'width');
                this.renderer.removeStyle(this.bodyEl, 'top');
                if (this._window)
                    this._window.scrollTo(0, this.initialPosition);
            }
        }
    }
    // Create a reference to the button that opened the modal
    getOpenerElement() {
        let openerElement;
        if (this.openerSelector)
            openerElement = (this._document.querySelector(this.openerSelector));
        else
            openerElement = (this._document.querySelector('[data-target="#' + this.idStr + '"]'));
        return openerElement ? openerElement : null;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ModalComponent, deps: [{ token: i0.Renderer2 }, { token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: DOCUMENT }, { token: NAVIGATOR$1 }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: ModalComponent, isStandalone: false, selector: "citi-modal", inputs: { scrollable: "scrollable", htmlContent: "htmlContent", ariaRole: "ariaRole", footerContent: "footerContent", idStr: "idStr", title: "title", primaryButtonUrl: "primaryButtonUrl", primaryButtonTarget: "primaryButtonTarget", primaryButtonText: "primaryButtonText", showCancelButton: "showCancelButton", footerProjection: "footerProjection", disablePrimaryButton: "disablePrimaryButton", disableCloseButton: "disableCloseButton", closeOnBgClick: "closeOnBgClick", cancelButtonText: "cancelButtonText", openerSelector: "openerSelector", show: "show", closeButtonIdStr: "closeButtonIdStr" }, outputs: { primaryButtonClick: "primaryButtonClick", secondaryButtonClick: "secondaryButtonClick", closeButtonClick: "closeButtonClick", showChange: "showChange", onHide: "onHide" }, host: { listeners: { "window:keydown": "onEscape($event)", "window:touchstart": "onClick($event)", "window:click": "onClick($event)" } }, viewQueries: [{ propertyName: "modalHeader", first: true, predicate: ["modalHeader"], descendants: true }, { propertyName: "modalHeaderAlt", first: true, predicate: ["modalHeaderAlt"], descendants: true }, { propertyName: "modal", first: true, predicate: ["modal"], descendants: true }, { propertyName: "modalBg", first: true, predicate: ["modalBg"], descendants: true }, { propertyName: "modalBody", first: true, predicate: ["modalBody"], descendants: true }, { propertyName: "closeButton", first: true, predicate: ["closeButton"], descendants: true }, { propertyName: "primaryButton", first: true, predicate: ["primaryButton"], descendants: true }, { propertyName: "secondaryButton", first: true, predicate: ["secondaryButton"], descendants: true }, { propertyName: "wrapDiv", first: true, predicate: ["wrapDiv"], descendants: true }, { propertyName: "content", first: true, predicate: ["content"], descendants: true }], ngImport: i0, template: `
    <div ddlAriaHideOuterDom [isOuterDomAriaHidden]="isDisplayBlock">
      <div
        #wrapDiv
        tabindex="-1"
        class="modal citi-modal fade"
        [class.scrollable]="scrollable"
        [class.in]="isInClassActive"
        [style.display]="isDisplayBlock ? 'block' : 'none'"
        [attr.id]="idStr"
      >
        <div #modal class="modal-dialog">
          <div class="modal-content" tabindex="-1" [attr.role]="ariaRole" cdkTrapFocus>
            <div class="modal-header"></div>
            <div>
              <button
                #closeButton
                *ngIf="!disableCloseButton"
                type="button"
                class="close-button"
                aria-label="Close modal"
                (click)="closeButtonClicked($event)"
                (keyup)="onKeyUpCloseButton($event)"
              >
                <span class="sr-only">Close</span>
              </button>
              <div
                #modalBody
                class="modal-body"
                [attr.tabindex]="scrollable ? 0 : null"
              >
                <div
                  #modalHeader
                  class="border-outline"
                  tabindex="-1"
                  *ngIf="title; else altHeader"
                >
                  <h1
                    level="2"
                    [style]="2"
                    [innerHtml]="title"
                  ></h1>
                </div>
                <ng-template #altHeader>
                  <div class="header" #modalHeaderAlt tabindex="-1">
                    <ng-content select="[title]"></ng-content>
                  </div>
                </ng-template>
                <div
                  class="modal-body-content"
                  [contentHtml]="htmlContent"
                  #content
                  tabindex="-1"
                >
                  <ng-content></ng-content>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <div class="text-right" *ngIf="!footerProjection">
                <div
                  *ngIf="
                    (showCancelButton && cancelButtonText) || primaryButtonText
                  "
                  class="citi-modal-controls"
                  [hidden]="!primaryButtonText && !showCancelButton"
                >
                  <citi-cta
                    [tabIndex]="disablePrimaryButton ? -1 : 0"
                    #primaryButton
                    *ngIf="primaryButtonText"
                    [ariaRole]="!primaryButtonUrl ? 'button' : null"
                    class="modal-primary-btn btnclassTest"
                    [target]="primaryButtonTarget"
                    [href]="primaryButtonUrl ? !disablePrimaryButton ? primaryButtonUrl : 'javascript:;' : null"
                    [value]="primaryButtonText"
                    [disabled]="disablePrimaryButton"
                    (ctaClick)="primaryButtonClicked($event)"
                  >
                  </citi-cta>
                  <span aria-hidden="false" class="sr-only"></span>
                  <citi-cta
                    #secondaryButton
                    *ngIf="showCancelButton && cancelButtonText"
                    class="modal-secondary-btn marginBetweenBtns"
                    ariaRole="button"
                    type="tertiary"
                    [value]="cancelButtonText"
                    (ctaClick)="secondaryButtonClicked($event)"
                  >
                  </citi-cta>
                </div>
              </div>
              <div *ngIf="footerProjection" class="footer-projection">
                <div [contentHtml]="footerContent">
                  <ng-content select="[footerContent]"></ng-content>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        #modalBg
        class="modal-backdrop citi-modal-backdrop fade"
        [class.in]="isInClassActive"
        [style.display]="isDisplayBlock ? 'block' : 'none'"
      ></div>
    </div>
  `, isInline: true, styles: [".border-outline{outline:none}.modal{display:none;overflow:hidden;position:fixed;inset:0;-webkit-overflow-scrolling:touch;outline:0}.modal.fade .modal-dialog{-webkit-transform:translate(0,-25%);transform:translateY(-25%);transition:-webkit-transform .3s ease-out;transition:transform .3s ease-out;transition:transform .3s ease-out,-webkit-transform .3s ease-out}.modal.in .modal-dialog{-webkit-transform:none;transform:none}.fade{opacity:0;transition:opacity .15s linear}.fade.in{opacity:1}.modal-dialog{position:relative;padding:10px;margin:30px auto}@media (min-width: 699.9px){.modal-dialog{padding:0}}.modal-content{position:relative;background-color:#fff;text-align:left;border:none;border-radius:0;box-shadow:none;background-clip:padding-box;outline:0}.modal-header{position:relative;padding:30px 40px;border-bottom:0 none;min-height:16.5px}@media (min-width: 480px){.modal .modal-body,.modal .modal-footer,.modal .modal-header{padding-right:40px;padding-left:40px}}.modal-body{position:relative;padding:0 20px;border:none}.modal-footer{padding:40px 20px;text-align:right;border:none}.modal-backdrop{z-index:9999;inset:0;position:fixed;background-color:#000}.modal-backdrop.fade{opacity:0;filter:alpha(opacity=0)}.modal-backdrop.in{opacity:.6;filter:alpha(opacity=50)}::ng-deep .text-right .btn:last-child{margin-right:0}::ng-deep .modal-secondary-btn button{color:#056dae;text-decoration:underline;padding:0!important}.citi-modal{z-index:10000}.citi-modal .modal-dialog{width:55%;max-width:1200px}@media (max-width: 480px){.citi-modal .modal-dialog{width:100%;max-height:100%;overflow:auto}}@media (min-width: 480px){.citi-modal .modal-dialog{width:100%}.citi-modal .modal-dialog .modal-footer citi-cta{display:inline-block}}@media (min-width: 768px){.citi-modal .modal-dialog{width:65%}}@media (min-width: 1200px){.citi-modal .modal-dialog{width:60%}}@media (min-width: 1440px){.citi-modal .modal-dialog{width:55%}}.citi-modal.scrollable .modal-body{max-height:360px;overflow-x:hidden;overflow-y:auto}.citi-modal .modal-footer ::ng-deep button{padding-top:0;padding-bottom:0;vertical-align:middle;font-size:1rem;-webkit-font-smoothing:antialiased;font-weight:700;border-radius:6px}@media (max-width: 480px){.citi-modal .modal-footer ::ng-deep button{width:100%}}@media (min-width: 480px){.citi-modal .modal-footer ::ng-deep button{width:auto}}.citi-modal .footer-projection ::ng-deep .secondary.chevron-link.chevron-link:after{background-image:none}.citi-modal button.close-button{position:absolute;top:30px;right:35px;padding:0;border:0;width:15px;height:15px;margin-top:4px;margin-right:4px;background-repeat:no-repeat;background-color:transparent;-webkit-appearance:none;opacity:1;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-white%3C/title%3E%3Cpath d='M21.41,20l3.8-3.79a1,1,0,0,0-1.42-1.42L20,18.59l-3.79-3.8a1,1,0,0,0-1.42,1.42L18.59,20l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L20,21.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-14.5 -14.5)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.citi-modal button.close-button:focus,.citi-modal button.close-button:hover{opacity:1;margin-top:0;margin-right:0;height:25px;width:25px;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-white-hover%3C/title%3E%3Cpath d='M21.41,20.15l3.8-3.79a1,1,0,0,0,0-1.41,1,1,0,0,0-1.42,0L20,18.74,16.21,15a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l3.8,3.79L14.79,24a1,1,0,0,0,0,1.41,1,1,0,0,0,1.42,0L20,21.57l3.79,3.79a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.41ZM20,9A11,11,0,1,0,31,20,11,11,0,0,0,20,9' transform='translate(-9 -9)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.citi-modal button.close-button :focus-visible{outline:none}.modal.in{overflow-x:hidden;overflow-y:auto}.botPadding{padding-bottom:20px!important}.marginBetweenBtns{margin-left:20px}@media (max-width: 480px){.marginBetweenBtns{margin-left:0}}.header{display:block;box-sizing:border-box;margin-top:0;margin-bottom:20px;font-family:Interstate_Light,sans-serif;font-size:42px;font-weight:200;line-height:3.125rem;text-align:left;letter-spacing:normal}\n"], dependencies: [{ kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "directive", type: ContentHtmlDirective, selector: "[contentHtml]", inputs: ["contentHtml"] }, { kind: "component", type: CTAComponent, selector: "citi-cta", inputs: ["textAlign", "bold", "noMargin", "tabIndex", "ariaDescription", "ariaLabeledBy", "ariaHasPopup", "ariaRole", "ariaLabel", "ariaControls", "ariaExpanded", "router", "queryParams", "idStr", "dataTarget", "addJFPToken", "doNotAddJFPToken", "buttonType", "target", "href", "value", "disabled", "type", "size", "arrow", "leftArrow"], outputs: ["ctaClick", "disabledChange", "ctaFocus", "ctaBlur"] }, { kind: "directive", type: DdlAriaHideOuterDomDirective, selector: "[ddlAriaHideOuterDom]", inputs: ["isOuterDomAriaHidden", "outerDomExceptions"], exportAs: ["ddlAriaHideOuterDom"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-modal', standalone: false, template: `
    <div ddlAriaHideOuterDom [isOuterDomAriaHidden]="isDisplayBlock">
      <div
        #wrapDiv
        tabindex="-1"
        class="modal citi-modal fade"
        [class.scrollable]="scrollable"
        [class.in]="isInClassActive"
        [style.display]="isDisplayBlock ? 'block' : 'none'"
        [attr.id]="idStr"
      >
        <div #modal class="modal-dialog">
          <div class="modal-content" tabindex="-1" [attr.role]="ariaRole" cdkTrapFocus>
            <div class="modal-header"></div>
            <div>
              <button
                #closeButton
                *ngIf="!disableCloseButton"
                type="button"
                class="close-button"
                aria-label="Close modal"
                (click)="closeButtonClicked($event)"
                (keyup)="onKeyUpCloseButton($event)"
              >
                <span class="sr-only">Close</span>
              </button>
              <div
                #modalBody
                class="modal-body"
                [attr.tabindex]="scrollable ? 0 : null"
              >
                <div
                  #modalHeader
                  class="border-outline"
                  tabindex="-1"
                  *ngIf="title; else altHeader"
                >
                  <h1
                    level="2"
                    [style]="2"
                    [innerHtml]="title"
                  ></h1>
                </div>
                <ng-template #altHeader>
                  <div class="header" #modalHeaderAlt tabindex="-1">
                    <ng-content select="[title]"></ng-content>
                  </div>
                </ng-template>
                <div
                  class="modal-body-content"
                  [contentHtml]="htmlContent"
                  #content
                  tabindex="-1"
                >
                  <ng-content></ng-content>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <div class="text-right" *ngIf="!footerProjection">
                <div
                  *ngIf="
                    (showCancelButton && cancelButtonText) || primaryButtonText
                  "
                  class="citi-modal-controls"
                  [hidden]="!primaryButtonText && !showCancelButton"
                >
                  <citi-cta
                    [tabIndex]="disablePrimaryButton ? -1 : 0"
                    #primaryButton
                    *ngIf="primaryButtonText"
                    [ariaRole]="!primaryButtonUrl ? 'button' : null"
                    class="modal-primary-btn btnclassTest"
                    [target]="primaryButtonTarget"
                    [href]="primaryButtonUrl ? !disablePrimaryButton ? primaryButtonUrl : 'javascript:;' : null"
                    [value]="primaryButtonText"
                    [disabled]="disablePrimaryButton"
                    (ctaClick)="primaryButtonClicked($event)"
                  >
                  </citi-cta>
                  <span aria-hidden="false" class="sr-only"></span>
                  <citi-cta
                    #secondaryButton
                    *ngIf="showCancelButton && cancelButtonText"
                    class="modal-secondary-btn marginBetweenBtns"
                    ariaRole="button"
                    type="tertiary"
                    [value]="cancelButtonText"
                    (ctaClick)="secondaryButtonClicked($event)"
                  >
                  </citi-cta>
                </div>
              </div>
              <div *ngIf="footerProjection" class="footer-projection">
                <div [contentHtml]="footerContent">
                  <ng-content select="[footerContent]"></ng-content>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        #modalBg
        class="modal-backdrop citi-modal-backdrop fade"
        [class.in]="isInClassActive"
        [style.display]="isDisplayBlock ? 'block' : 'none'"
      ></div>
    </div>
  `, styles: [".border-outline{outline:none}.modal{display:none;overflow:hidden;position:fixed;inset:0;-webkit-overflow-scrolling:touch;outline:0}.modal.fade .modal-dialog{-webkit-transform:translate(0,-25%);transform:translateY(-25%);transition:-webkit-transform .3s ease-out;transition:transform .3s ease-out;transition:transform .3s ease-out,-webkit-transform .3s ease-out}.modal.in .modal-dialog{-webkit-transform:none;transform:none}.fade{opacity:0;transition:opacity .15s linear}.fade.in{opacity:1}.modal-dialog{position:relative;padding:10px;margin:30px auto}@media (min-width: 699.9px){.modal-dialog{padding:0}}.modal-content{position:relative;background-color:#fff;text-align:left;border:none;border-radius:0;box-shadow:none;background-clip:padding-box;outline:0}.modal-header{position:relative;padding:30px 40px;border-bottom:0 none;min-height:16.5px}@media (min-width: 480px){.modal .modal-body,.modal .modal-footer,.modal .modal-header{padding-right:40px;padding-left:40px}}.modal-body{position:relative;padding:0 20px;border:none}.modal-footer{padding:40px 20px;text-align:right;border:none}.modal-backdrop{z-index:9999;inset:0;position:fixed;background-color:#000}.modal-backdrop.fade{opacity:0;filter:alpha(opacity=0)}.modal-backdrop.in{opacity:.6;filter:alpha(opacity=50)}::ng-deep .text-right .btn:last-child{margin-right:0}::ng-deep .modal-secondary-btn button{color:#056dae;text-decoration:underline;padding:0!important}.citi-modal{z-index:10000}.citi-modal .modal-dialog{width:55%;max-width:1200px}@media (max-width: 480px){.citi-modal .modal-dialog{width:100%;max-height:100%;overflow:auto}}@media (min-width: 480px){.citi-modal .modal-dialog{width:100%}.citi-modal .modal-dialog .modal-footer citi-cta{display:inline-block}}@media (min-width: 768px){.citi-modal .modal-dialog{width:65%}}@media (min-width: 1200px){.citi-modal .modal-dialog{width:60%}}@media (min-width: 1440px){.citi-modal .modal-dialog{width:55%}}.citi-modal.scrollable .modal-body{max-height:360px;overflow-x:hidden;overflow-y:auto}.citi-modal .modal-footer ::ng-deep button{padding-top:0;padding-bottom:0;vertical-align:middle;font-size:1rem;-webkit-font-smoothing:antialiased;font-weight:700;border-radius:6px}@media (max-width: 480px){.citi-modal .modal-footer ::ng-deep button{width:100%}}@media (min-width: 480px){.citi-modal .modal-footer ::ng-deep button{width:auto}}.citi-modal .footer-projection ::ng-deep .secondary.chevron-link.chevron-link:after{background-image:none}.citi-modal button.close-button{position:absolute;top:30px;right:35px;padding:0;border:0;width:15px;height:15px;margin-top:4px;margin-right:4px;background-repeat:no-repeat;background-color:transparent;-webkit-appearance:none;opacity:1;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-white%3C/title%3E%3Cpath d='M21.41,20l3.8-3.79a1,1,0,0,0-1.42-1.42L20,18.59l-3.79-3.8a1,1,0,0,0-1.42,1.42L18.59,20l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L20,21.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-14.5 -14.5)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.citi-modal button.close-button:focus,.citi-modal button.close-button:hover{opacity:1;margin-top:0;margin-right:0;height:25px;width:25px;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-white-hover%3C/title%3E%3Cpath d='M21.41,20.15l3.8-3.79a1,1,0,0,0,0-1.41,1,1,0,0,0-1.42,0L20,18.74,16.21,15a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l3.8,3.79L14.79,24a1,1,0,0,0,0,1.41,1,1,0,0,0,1.42,0L20,21.57l3.79,3.79a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.41ZM20,9A11,11,0,1,0,31,20,11,11,0,0,0,20,9' transform='translate(-9 -9)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.citi-modal button.close-button :focus-visible{outline:none}.modal.in{overflow-x:hidden;overflow-y:auto}.botPadding{padding-bottom:20px!important}.marginBetweenBtns{margin-left:20px}@media (max-width: 480px){.marginBetweenBtns{margin-left:0}}.header{display:block;box-sizing:border-box;margin-top:0;margin-bottom:20px;font-family:Interstate_Light,sans-serif;font-size:42px;font-weight:200;line-height:3.125rem;text-align:left;letter-spacing:normal}\n"] }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NAVIGATOR$1]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }], propDecorators: { modalHeader: [{
                type: ViewChild,
                args: ['modalHeader']
            }], modalHeaderAlt: [{
                type: ViewChild,
                args: ['modalHeaderAlt']
            }], modal: [{
                type: ViewChild,
                args: ['modal']
            }], modalBg: [{
                type: ViewChild,
                args: ['modalBg']
            }], modalBody: [{
                type: ViewChild,
                args: ['modalBody']
            }], closeButton: [{
                type: ViewChild,
                args: ['closeButton']
            }], primaryButton: [{
                type: ViewChild,
                args: ['primaryButton']
            }], secondaryButton: [{
                type: ViewChild,
                args: ['secondaryButton']
            }], wrapDiv: [{
                type: ViewChild,
                args: ['wrapDiv']
            }], content: [{
                type: ViewChild,
                args: ['content']
            }], scrollable: [{
                type: Input
            }], htmlContent: [{
                type: Input
            }], ariaRole: [{
                type: Input
            }], footerContent: [{
                type: Input
            }], idStr: [{
                type: Input
            }], title: [{
                type: Input
            }], primaryButtonUrl: [{
                type: Input
            }], primaryButtonTarget: [{
                type: Input
            }], primaryButtonText: [{
                type: Input
            }], showCancelButton: [{
                type: Input
            }], footerProjection: [{
                type: Input
            }], disablePrimaryButton: [{
                type: Input
            }], disableCloseButton: [{
                type: Input
            }], closeOnBgClick: [{
                type: Input
            }], cancelButtonText: [{
                type: Input
            }], openerSelector: [{
                type: Input
            }], show: [{
                type: Input
            }], closeButtonIdStr: [{
                type: Input
            }], primaryButtonClick: [{
                type: Output
            }], secondaryButtonClick: [{
                type: Output
            }], closeButtonClick: [{
                type: Output
            }], showChange: [{
                type: Output
            }], onHide: [{
                type: Output
            }], onEscape: [{
                type: HostListener,
                args: ['window:keydown', ['$event']]
            }], onClick: [{
                type: HostListener,
                args: ['window:touchstart', ['$event']]
            }, {
                type: HostListener,
                args: ['window:click', ['$event']]
            }] } });

const COMPONENTS$7 = [ModalComponent];
const MODULES$8 = [
    CommonModule,
    A11yModule,
    ContentHtml,
    CTA,
    DdlAriaHideOuterDomModule
];
class Modal {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Modal, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: Modal, declarations: [ModalComponent], imports: [CommonModule,
            A11yModule,
            ContentHtml,
            CTA,
            DdlAriaHideOuterDomModule], exports: [ModalComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Modal, providers: [WINDOW_PROVIDER, NAVIGATOR_PROVIDER$1], imports: [MODULES$8] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Modal, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$8],
                    declarations: [...COMPONENTS$7],
                    providers: [WINDOW_PROVIDER, NAVIGATOR_PROVIDER$1],
                    exports: [...COMPONENTS$7]
                }]
        }] });

const LIVE_ANNOUNCER_ELEMENT_TOKEN = new InjectionToken('liveAnnouncerElement');
class CitiLiveAnnouncer {
    constructor(elementToken, _document) {
        this._document = _document;
        this.multiAnnounceMessage = '';
        // We inject the live element as `any` because the constructor signature cannot reference
        // browser globals (HTMLElement) on non-browser environments, since having a class decorator
        // causes TypeScript to preserve the constructor signature types.
        this._liveElement = elementToken || this._createLiveElement();
    }
    /**
     * Announces a message to screenreaders.
     * @param message Message to be announced to the screenreader
     * @param politeness The politeness of the announcer element
     * @returns Promise that will be resolved when the message is added to the DOM.
     */
    announce(message, politeness = 'polite', preventClear = false) {
        if (!message)
            return Promise.resolve();
        this._liveElement.textContent = '';
        this._liveElement.setAttribute('aria-live', politeness);
        // This 100ms timeout is necessary for some browser + screen-reader combinations:
        // - Both JAWS and NVDA over IE11 will not announce anything without a non-zero timeout.
        // - With Chrome and IE11 with NVDA or JAWS, a repeated (identical) message won't be read a
        //   second time without clearing and then using a non-zero delay.
        // (using JAWS 17 at time of this writing).
        return new Promise(resolve => {
            setTimeout(() => {
                this._liveElement.textContent = this.cleanHTML(message);
                resolve();
                if (!preventClear)
                    setTimeout(() => this.clear(), 10000);
            }, 100);
        });
    }
    // this is a less comprehensive solution, but may allow for multipilt
    multiAnnounce(msg) {
        if (!msg)
            return;
        this.multiAnnounceMessage += ` ${this.cleanHTML(msg)}`;
        this._liveElement.textContent = this.multiAnnounceMessage;
        setTimeout(() => this.multiAnnounceMessage = '', 0);
    }
    cleanHTML(message) {
        return message
            .replace(/<(?:.|\n)*?>/gm, '')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>');
    }
    /**
     * Clears the current text from the announcer element. Can be used to prevent
     * screen readers from reading the text out again while the user is going
     * through the page landmarks.
     */
    clear() {
        if (this._liveElement) {
            this._liveElement.textContent = '';
        }
    }
    ngOnDestroy() {
        if (this._liveElement && this._liveElement.parentNode) {
            this._liveElement.parentNode.removeChild(this._liveElement);
        }
    }
    _createLiveElement() {
        const liveEl = this._document.createElement('div');
        liveEl.classList.add('sr-only', 'ddl-live-announcer-element');
        liveEl.setAttribute('aria-atomic', 'true');
        liveEl.setAttribute('aria-live', 'polite');
        this._document.body.appendChild(liveEl);
        return liveEl;
    }
    changePageTitleAndSetFocus(title, refocusBody = true, announce = false) {
        // Set the new title if there is one
        if (typeof title === 'string' && title.length) {
            const titleTag = this._document.getElementsByTagName('title')[0];
            if (titleTag) {
                titleTag.innerHTML = title;
            }
            if (announce)
                this.announce(title);
        }
        // Add tabindex and aria-live to body tag, and then set focus on the body tag\
        if (refocusBody) {
            const bodyTag = this._document.getElementsByTagName('body')[0];
            if (bodyTag) {
                bodyTag.tabIndex = -1;
                bodyTag.setAttribute('aria-live', 'assertive');
                bodyTag.focus();
            }
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CitiLiveAnnouncer, deps: [{ token: LIVE_ANNOUNCER_ELEMENT_TOKEN, optional: true }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CitiLiveAnnouncer, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CitiLiveAnnouncer, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [LIVE_ANNOUNCER_ELEMENT_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });
/** @docs-private */
function LIVE_ANNOUNCER_PROVIDER_FACTORY(parentDispatcher, liveElement, _document) {
    return parentDispatcher || new CitiLiveAnnouncer(liveElement, _document);
}
/** @docs-private */
const LIVE_ANNOUNCER_PROVIDER = {
    // If there is already a LiveAnnouncer available, use that. Otherwise, provide a new one.
    provide: CitiLiveAnnouncer,
    deps: [
        [new Optional(), new SkipSelf(), CitiLiveAnnouncer],
        [new Optional(), new Inject(LIVE_ANNOUNCER_ELEMENT_TOKEN)],
        DOCUMENT,
    ],
    useFactory: LIVE_ANNOUNCER_PROVIDER_FACTORY
};

class ValidationService {
    constructor() {
        this.errors = [];
        this.inputs = [];
        this.hasErrors = function () {
            return this.errors && this.errors.length ? true : false;
        };
        this.addError = function (error) {
            if (this.errors.indexOf(error) === -1) {
                this.errors.push(error);
                this.errorAdded.emit(error);
            }
        };
        this.removeError = function (error) {
            let index = this.errors.indexOf(error);
            if (index !== -1) {
                this.errors.splice(index, 1);
                this.errorRemoved.emit(error);
            }
        };
        this.addInput = function (input) {
            this.inputs.push(input);
        };
        this.errorAdded = new EventEmitter();
        this.errorRemoved = new EventEmitter();
        this.validateNow = new EventEmitter();
    }
    ;
    validate() {
        this.validateNow.emit();
    }
    ;
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ValidationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ValidationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ValidationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

class CustomPipe {
    constructor(injector) {
        this.injector = injector;
    }
    transform(value, pipeToken) {
        if (!pipeToken) {
            return value;
        }
        else {
            let pipe = this.injector.get(pipeToken);
            return pipe.transform(value);
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CustomPipe, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: CustomPipe, isStandalone: false, name: "customPipe" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CustomPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'customPipe',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: i0.Injector }] });

class MaskingPipe {
    transform(value) {
        if (!value)
            return value;
        return value.replace(/\w\S*/g, function (txt) {
            return txt.charAt(0) + 'x'.repeat(txt.length - 2) + txt.charAt(txt.length - 1);
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MaskingPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: MaskingPipe, isStandalone: false, name: "masking" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: MaskingPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'masking', standalone: false }]
        }] });

class OptionsComponent {
    set value(value) {
        if (typeof value === 'number')
            value = value.toString();
        this._value = value;
    }
    get value() {
        return this._value;
    }
    // #endregion
    // #region // ----------------------- Constructor / Lifecycle Hooks ----------------------- //
    constructor() {
        // #region // ----------------------- Properties ----------------------- //
        //displayed will be used for filtering
        this.displayed = true;
        //whether or not the option is a group child
        this.optionGroupChild = false;
        //used to reset highlight styles
        this.resetOption = false;
        // #endregion
        // #region // ----------------------- @Input ----------------------- //
        this.isSelected = false;
        this.disabled = false;
        // #endregion
        // #region // ----------------------- @Output ----------------------- //
        // NOTE: the followwing @Outputs are subscribed to in the dropdown2 component
        this.ddResetStyles = new EventEmitter();
        this.ddClose = new EventEmitter();
        this.ddDisplayText = new EventEmitter();
        this.ddValueName = new EventEmitter();
        this.ddValue = new EventEmitter();
        this.ddHasOptionSelected = new EventEmitter();
        this.ddAnnounce = new EventEmitter();
    }
    ngAfterViewInit() {
        setTimeout(() => {
            if (this.value === this.initDropdownValue)
                this.alreadySelected();
            if (this.isSelected)
                this.setValue();
        }, 0);
    }
    // #endregion
    // #region // ----------------------- Methods ----------------------- //
    resetStyles() {
        /*on hover, we want to get rid of the background on the selected option...
          so we will call a method in dropdown to search for the selected option and
          set that option's resetOption property to true*/
        this.ddResetStyles.emit(this.optionGroupChild);
    }
    setValueOnClick(event) {
        if (this.disabled)
            return;
        this.setValue();
        this.ddClose.emit(event);
    }
    setValueSilent(postAnnounceValue) {
        this.optionGroupChild
            ? this.announceValueWithGroup(postAnnounceValue)
            : this.announceValue(postAnnounceValue);
    }
    alreadySelected() {
        this.isSelected = true;
        this.ddDisplayText.emit(this.label);
    }
    setValue(postAnnounceValue) {
        if (this.disabled)
            return;
        this.ddHasOptionSelected.emit(true);
        this.ddValueName.emit(this.label);
        this.ddDisplayText.emit(this.label);
        this.ddValue.emit(this.value);
        this.setValueSilent(postAnnounceValue);
    }
    announceValue(postAnnounceValue) {
        let announceStr = `${this.valueText.nativeElement.innerHTML}`;
        announceStr += !!postAnnounceValue ? `. ${postAnnounceValue}` : '';
        this.ddAnnounce.emit(announceStr);
    }
    announceValueWithGroup(postAnnounceValue) {
        let announceStr = `${this.optionGroupName} grouping ${this.valueText.nativeElement.innerHTML}`;
        announceStr += !!postAnnounceValue ? `. ${postAnnounceValue}` : '';
        this.ddAnnounce.emit(announceStr);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: OptionsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: OptionsComponent, isStandalone: false, selector: "citi-options2", inputs: { isSelected: "isSelected", value: "value", label: "label", disabled: "disabled" }, outputs: { ddResetStyles: "ddResetStyles", ddClose: "ddClose", ddDisplayText: "ddDisplayText", ddValueName: "ddValueName", ddValue: "ddValue", ddHasOptionSelected: "ddHasOptionSelected", ddAnnounce: "ddAnnounce" }, host: { classAttribute: "citi-options2" }, viewQueries: [{ propertyName: "optionEl", first: true, predicate: ["optionEl"], descendants: true, static: true }, { propertyName: "valueText", first: true, predicate: ["valueText"], descendants: true, static: true }], ngImport: i0, template: `
    <div
      class="dd2-option"
      #optionEl
      role="option"
      (click)="setValueOnClick($event)"
      (mouseenter)="resetStyles()"
      [ngClass]="{
        selectedOption: isSelected && !resetOption,
        disabled: disabled,
        displayNone: !displayed
      }"
    >
      <span
        #valueText
        class="dd2-option-text"
        [ngClass]="{ optionGroupOption: optionGroupChild }"
        [innerHTML]="label"
      >
      </span>
    </div>
  `, isInline: true, styles: [".selectedOption{color:#056dae;text-decoration:underline}div{cursor:pointer;color:#333;font-size:16px;line-height:24px 1.5;padding:10px 20px;z-index:999}div:hover{color:#056dae;text-decoration:underline}.disabled{cursor:default;color:#666}.disabled:hover{color:#666;text-decoration:none}.displayNone{display:none}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: OptionsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-options2', standalone: false, template: `
    <div
      class="dd2-option"
      #optionEl
      role="option"
      (click)="setValueOnClick($event)"
      (mouseenter)="resetStyles()"
      [ngClass]="{
        selectedOption: isSelected && !resetOption,
        disabled: disabled,
        displayNone: !displayed
      }"
    >
      <span
        #valueText
        class="dd2-option-text"
        [ngClass]="{ optionGroupOption: optionGroupChild }"
        [innerHTML]="label"
      >
      </span>
    </div>
  `, host: { class: 'citi-options2' }, styles: [".selectedOption{color:#056dae;text-decoration:underline}div{cursor:pointer;color:#333;font-size:16px;line-height:24px 1.5;padding:10px 20px;z-index:999}div:hover{color:#056dae;text-decoration:underline}.disabled{cursor:default;color:#666}.disabled:hover{color:#666;text-decoration:none}.displayNone{display:none}\n"] }]
        }], ctorParameters: () => [], propDecorators: { optionEl: [{
                type: ViewChild,
                args: ['optionEl', { static: true }]
            }], valueText: [{
                type: ViewChild,
                args: ['valueText', { static: true }]
            }], isSelected: [{
                type: Input
            }], value: [{
                type: Input
            }], label: [{
                type: Input
            }], disabled: [{
                type: Input
            }], ddResetStyles: [{
                type: Output
            }], ddClose: [{
                type: Output
            }], ddDisplayText: [{
                type: Output
            }], ddValueName: [{
                type: Output
            }], ddValue: [{
                type: Output
            }], ddHasOptionSelected: [{
                type: Output
            }], ddAnnounce: [{
                type: Output
            }] } });

class OptionsGroupComponent {
    constructor() {
        this.isDisplayed = true;
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.options.forEach(option => {
                option.optionGroupChild = true;
                option.optionGroupName = this.groupName;
            });
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: OptionsGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: OptionsGroupComponent, isStandalone: false, selector: "citi-options-group2", inputs: { groupName: "groupName" }, host: { classAttribute: "citi-options-group2" }, queries: [{ propertyName: "options", predicate: OptionsComponent }], ngImport: i0, template: `
    <div role="group" [ngClass]="{ displayNone: !isDisplayed }">
      <div class="optionsGroupText" [innerHTML]="groupName"></div>
      <div><ng-content></ng-content></div>
    </div>
  `, isInline: true, styles: [".optionsGroupText{font-weight:700;padding:10px 20px}.displayNone{display:none}:host ::ng-deep .dd2-option-text{margin-left:20px}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: OptionsGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-options-group2', standalone: false, template: `
    <div role="group" [ngClass]="{ displayNone: !isDisplayed }">
      <div class="optionsGroupText" [innerHTML]="groupName"></div>
      <div><ng-content></ng-content></div>
    </div>
  `, host: { class: 'citi-options-group2' }, styles: [".optionsGroupText{font-weight:700;padding:10px 20px}.displayNone{display:none}:host ::ng-deep .dd2-option-text{margin-left:20px}\n"] }]
        }], ctorParameters: () => [], propDecorators: { options: [{
                type: ContentChildren,
                args: [OptionsComponent]
            }], groupName: [{
                type: Input
            }] } });

const DD2_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    // tslint:disable-next-line: no-use-before-declare
    useExisting: forwardRef(() => Dropdown2Component),
    multi: true
};
let nextUniqueId$4 = 0;
/* AfterContentChecked, */
class Dropdown2Component {
    /**
     * This defines the id of the input.
     */
    get idStr() {
        return this._id;
    }
    set idStr(value) {
        this._id = value || this._uid;
    }
    /**
     * label id
     */
    set labelId(id) {
        this._labelId = id;
    }
    get labelId() {
        return this._labelId || this._uLabelId;
    }
    /**
     * size for the dropdown (fullwidth, large, medium, small, xsmall)
     */
    set size(size) {
        if (size === 'large') {
            this.hostRowClass = true;
            this.sizeClasses = 'col-md-8';
        }
        else if (size === 'medium') {
            this.hostRowClass = true;
            this.sizeClasses = 'col-md-6 col-sm-9';
        }
        else if (size === 'small') {
            this.hostRowClass = true;
            this.sizeClasses = 'col-md-4 col-sm-6';
        }
        else if (size === 'xsmall') {
            this.hostRowClass = true;
            this.sizeClasses = 'col-md-2 col-sm-3 col-xs-6';
        }
        else {
            this.hostRowClass = false;
            this.sizeClasses = '';
        }
        this._size = size;
    }
    get size() {
        return this._size;
    }
    set error(isErrored) {
        if (isErrored === this._error)
            return;
        if (isErrored)
            this.errorMessage = this.customErrorMessage;
        this._error = isErrored;
    }
    get error() {
        return this._error;
    }
    get value() {
        return this._value;
    }
    set value(newVal) {
        if (typeof newVal === 'number')
            newVal = newVal.toString();
        if (newVal !== this._value) {
            this.selectOption(newVal);
            this._value = newVal;
        }
    }
    /**
     * AriaLabelledby of scrollable menu
     * @Two-way: true
     */
    set getScrollableMenuAriaLabelledby(labeledbyId) {
        this._getScrollableMenuAriaLabelledby = labeledbyId;
    }
    get getScrollableMenuAriaLabelledby() {
        if (!this._getScrollableMenuAriaLabelledby && this.labelId) {
            this._getScrollableMenuAriaLabelledby = this.labelId;
        }
        return this._getScrollableMenuAriaLabelledby;
    }
    // #endregion
    // #region // ----------------------- Getter Properties ----------------------- //
    get labelOpacity() {
        return this.visibleLabel
            ? 1
            : !!this.placeholder && (this._value || this.showOptionsBox)
                ? 1
                : 0;
    }
    get currentText() {
        return this.dropdownValueText.nativeElement.innerHTML;
    }
    get isAndroid() {
        return /(android)/i.test(navigator.userAgent);
    }
    get isIOS() {
        return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    }
    get isFirefox() {
        return /(firefox)/i.test(navigator.userAgent);
    }
    get isSafari() {
        return /(safari)/i.test(navigator.userAgent);
    }
    // #endregion
    // #region // ----------------------- Constructor / Lifecycle Hooks ----------------------- //
    constructor(validationService, announcer, _ngZone) {
        this.validationService = validationService;
        this.announcer = announcer;
        this._ngZone = _ngZone;
        this.unsub$ = new Subject();
        // #region // ----------------------- Properties ----------------------- //
        // whether the options box is displayed or not
        this.showOptionsBox = false;
        // whether or not we have a selected option
        this.hasOptionSelected = false;
        // error message to be displayed when there is an error
        this.errorMessage = 'Error';
        // need this to stop value from being set in writeValue
        this.allowSetValueInWriteValue = true;
        // sets to true if scrollable option box should scroll to top when selecting with arrow keys
        this.scrollTop = false;
        // set to true if scrollable options box should scroll to bottom when selecting with arrow keys
        this.scrollBottom = false;
        /* SEARCHING PROPERTIES */
        // whether or not the input search box has focus
        this.inputHasFocus = false;
        // array which will be used for predictive searching
        this.filteredList = [];
        // string used to store the values entered for searching without input box
        this.searchingInput = '';
        // whether or not there are any options displayed when searching with input box
        this.noOps = false;
        // placeholder for predictive search box
        this.searchBoxPlaceholder = 'Begin typing to filter options...';
        // whether the dropdown is being validated via form container submit
        this.errorFromSubmitCheck = false;
        this.sizeClasses = '';
        // search value
        this.searchValue = '';
        this.subscriptionArray = [];
        // #endregion
        // #region // -------------- HostBinding / Children -------------- //
        this.hostRowClass = false;
        /**
         * masking for selected drropdown
         */
        this.mask = false;
        /**
         * placeholder text for dropdown
         */
        this.placeholder = '';
        /**
         * label text to be displayed above dropdown
         */
        this.label = '';
        this._uid = `citi-dropdown2-${nextUniqueId$4++}`;
        this._uLabelId = `${this._uid}-label`;
        /**
         * name associated with the hidden input which will binf to the <form>
         */
        this.name = 'dropdown';
        this._size = '';
        /**
         * custom error message to be displayed when there is an error
         */
        this.customErrorMessage = 'Error';
        /**
         * This defines the error state on the input
         */
        this._error = false;
        /**
         * Text to be displayed when the input is required and no value was entered
         */
        this.requiredError = 'This field is required';
        /*
         * whether or not the predictive search box will be present
         */
        this.predictiveSearch = false;
        /**
         * The dropdown is not clickable
         */
        this.isDisabled = false;
        /**
         * optional 'dark' or 'light' theme for manually setting dropdown background color
         */
        this.dropdownTheme = 'dark';
        /**
         * This defines visableLabel for label.
         */
        this.visibleLabel = false;
        /* OUTPUTS */
        /**
         * The event is emitted when the error status changed
         * The event is returned.
         */
        this.errorChange = new EventEmitter();
        /**
         * The event is emitted when the value is changed
         * The event is returned.
         */
        this.valueChange = new EventEmitter();
        /**
         * Emits the value typed in the search box
         */
        this.searchValueChange = new EventEmitter();
        this.displayedValueChange = new EventEmitter();
        // #region // ----------------------- Form Binding Methods ----------------------- //
        // All methods below are required for the Value Control Accessor interface to work with [(ngModel)]
        this.onChange = (value) => { };
        this.onTouched = () => { };
    }
    ngOnInit() {
        // ensures that an id is assigned
        this.idStr = this.idStr;
        if (!!this.placeholder)
            this.displayedText = this.placeholder;
        this.validationService.validateNow
            .pipe(takeUntil(this.unsub$))
            .subscribe(() => {
            this.errorFromSubmitCheck = true;
            this.validate();
        });
        this.valueChange.pipe(takeUntil(this.unsub$)).subscribe(() => {
            this.displayedText = this.valueName || this.placeholder;
        });
        /*   fromEvent(this.selectEl.nativeElement, 'touchstart')
            .pipe(takeUntil(this.unsub$))
            .subscribe((touchEvent: Event) => {
              if (this.isAndroid && !this.showOptionsBox && !this.predictiveSearch) {
                this.announcer.announce(
                  `expanded.\n ${this.required ? 'required.\n' : ''} ${
                    this.currentText
                  }`
                );
              }
            });
    
          if (!this.predictiveSearch) {
            fromEvent(this.selectEl.nativeElement, 'keypress')
              .pipe(takeUntil(this.unsub$))
              .subscribe((e: KeyboardEvent) => {
                this.searchWithoutInputBox(e);
              });
    
            // clear our string we are using to search with after not typing for 1 second
            fromEvent(this.selectEl.nativeElement, 'keypress')
              .pipe(
                debounceTime(1000),
                takeUntil(this.unsub$)
              )
              .subscribe((e: KeyboardEvent) => (this.searchingInput = ''));
          }
       */
        // Tell the valdation service that an option was added
        this.validationService.addInput(this);
    }
    ngAfterViewInit() {
        // queries that are available after ngafterviewinit lifecycle
        fromEvent(this.selectEl.nativeElement, 'touchstart')
            .pipe(takeUntil(this.unsub$))
            .subscribe((touchEvent) => {
            if (this.isAndroid && !this.showOptionsBox && !this.predictiveSearch) {
                this.announcer.announce(`expanded.\n ${this.required ? 'required.\n' : ''} ${this.currentText}`);
            }
        });
        if (!this.predictiveSearch) {
            fromEvent(this.selectEl.nativeElement, 'keypress')
                .pipe(takeUntil(this.unsub$))
                .subscribe((e) => {
                this.searchWithoutInputBox(e);
            });
            // clear our string we are using to search with after not typing for 1 second
            fromEvent(this.selectEl.nativeElement, 'keypress')
                .pipe(debounceTime(1000), takeUntil(this.unsub$))
                .subscribe((e) => (this.searchingInput = ''));
        }
    }
    ngAfterContentInit() {
        this.handleOptionSubscriptions();
    }
    ngOnDestroy() {
        this.validationService.removeError(this);
        this.unsub$.next();
        this.unsub$.complete();
    }
    // #endregion
    // #region // ----------------------- Methods ----------------------- //
    handleOptionSubscriptions() {
        // Since the list of options may have change we need to
        // remove previous subscriptions then reset the array
        this.subscriptionArray.forEach(sub => {
            sub.unsubscribe();
        });
        this.subscriptionArray.length = 0;
        // set up new subscriptions
        if (this.options && this.options.length) {
            this.options.forEach(opt => {
                opt.initDropdownValue = this.value;
                const subResetStyles = opt.ddResetStyles
                    .pipe(takeUntil(this.unsub$))
                    .subscribe(groupChild => this.resetStyles(groupChild));
                const subClose = opt.ddClose
                    .pipe(takeUntil(this.unsub$))
                    .subscribe(kbEvent => this.close(kbEvent));
                const subDisplayText = opt.ddDisplayText
                    .pipe(takeUntil(this.unsub$))
                    .subscribe(text => (this.displayedText = text));
                const subValueName = opt.ddValueName
                    .pipe(takeUntil(this.unsub$))
                    .subscribe(name => (this.valueName = name));
                const subValue = opt.ddValue
                    .pipe(takeUntil(this.unsub$))
                    .subscribe(val => {
                    this.value = val;
                    this.displayedValueChange.emit(val);
                });
                const subHasOptionSelected = opt.ddHasOptionSelected
                    .pipe(takeUntil(this.unsub$))
                    .subscribe(bol => {
                    if (this.hasOptionSelected !== bol) {
                        this.hasOptionSelected = bol;
                    }
                });
                const subAnnounce = opt.ddAnnounce
                    .pipe(takeUntil(this.unsub$), debounceTime(400))
                    .subscribe(announceText => {
                    this.announcer.announce(announceText);
                });
                // Array of Subscriptions for this option
                const optSubs = [
                    subResetStyles,
                    subClose,
                    subDisplayText,
                    subValueName,
                    subValue,
                    subHasOptionSelected,
                    subAnnounce
                ];
                this.subscriptionArray.push(...optSubs);
            });
        }
    }
    getAriaLabelledBy() {
        if (!!this.ariaLabelledById)
            return this.ariaLabelledById;
        else if (!!this.labelId)
            return this.labelId;
        else
            return null;
    }
    sortOptions(optionGroupOptions) {
        this.setOrderOptionNum();
        return sort(optionGroupOptions);
    }
    setOrderOptionNum() {
        if (this.hasGroups) {
            const optionGroups = this.optionsGroups.toArray();
            let i = 0;
            optionGroups.forEach(optionGroup => {
                optionGroup.options.forEach(option => {
                    option.optionOrderNum = i;
                    i++;
                });
            });
        }
    }
    selectOption(value) {
        const opt = this.findOption(this.hasGroups, value);
        if (opt) {
            this.optionSelected(opt);
            this.hasOptionSelected = true;
        }
    }
    executeOnStable(func) {
        this._ngZone.runOutsideAngular(() => {
            this._ngZone.onStable
                .asObservable()
                .pipe(take(1))
                .subscribe(() => func());
        });
    }
    search(event) {
        // when there is a keyboard event inside the input box, this method will be fired to search through the options
        // if the key pressed is not another key we are listening for, then store the number of chars in the input value
        if ([
            'ArrowDown',
            'ArrowUp',
            'PageDown',
            'PageUp',
            'End',
            'Home',
            'Enter',
            ' '
        ].includes(event.key))
            return;
        const charNum = this.searchValue.length;
        this.searchValueChange.emit(this.searchValue);
        // allow time for search value to update list of options
        setTimeout(() => {
            // get array of all option components
            const optionArray = this.hasGroups
                ? this.sortOptions(flatMap(this.optionsGroups.toArray(), (group) => group.options.toArray()))
                : this.options.toArray();
            // gets options that can be selected
            this.filteredList = optionArray.filter(opt => !opt.disabled);
            const fListLength = this.filteredList.length;
            // shows 'no matching options' message
            this.noOps = fListLength === 0;
            // wait for filteredList to be updated
            setTimeout(() => {
                // resets the option subscriptions
                this.handleOptionSubscriptions();
                // hide disabled options when searching. otherwise, show all options
                if (charNum === 0) {
                    // show all groups
                    if (this.hasGroups) {
                        this.optionsGroups.forEach(og => {
                            og.isDisplayed = true;
                        });
                    }
                    // show all options
                    this.setOptionsDisplayAll(optionArray, true);
                }
                else {
                    // hide disabled options
                    this.setOptionsDisplayAll(optionArray.filter(opt => opt.disabled), false);
                    // hide groups with no available options
                    if (this.hasGroups) {
                        this.optionsGroups.forEach(og => {
                            const firstFoundAvailableOption = og.options.find(opt => !opt.disabled);
                            if (!firstFoundAvailableOption) {
                                og.isDisplayed = false;
                            }
                        });
                    }
                }
                const postSetValueAnnounce = this.hasSearchValue
                    ? `${fListLength} matching option${fListLength > 1 ? 's' : ''}`
                    : `${fListLength} total options`;
                // select first value out of the results given
                if (fListLength >= 1 && this.hasSearchValue)
                    this.filteredList[0].setValue(postSetValueAnnounce);
            }, 0);
        }, 0);
    }
    searchWithoutInputBox(event) {
        /*method that triggers when keyup event on dropdown button is fired. This will search through options and set the value for the indicated option
         *replication of native select tag functionality
         */
        if (this.isDisabled)
            return;
        this.handleOptionSubscriptions();
        // make sure the user entered a char a-z or A-Z
        if ((event.charCode >= 97 && event.charCode <= 122) ||
            (event.charCode <= 90 && event.charCode >= 65)) {
            this.searchingInput += String.fromCharCode(event.charCode).toUpperCase();
            const charNum = this.searchingInput.length;
            let currentSelection;
            // get the current selection
            if (this.optionsGroups.length === 0)
                currentSelection = this.options
                    .toArray()
                    .find(option => option.isSelected);
            if (this.optionsGroups.length > 1)
                currentSelection = flatMap(this.optionsGroups.toArray(), (group) => group.options.toArray()).find(option => option.isSelected);
            // if there is something selected we will use a switch based on if we have option groups and if our string is 1 or more chars
            if (currentSelection) {
                // we do not have option groups and there is only 1 character in our string
                if (this.optionsGroups.length === 0 && charNum === 1) {
                    // find the first value in the options array that matches the entered character
                    const match2 = this.options
                        .toArray()
                        .find(option => option.label.substr(0, 1).toUpperCase() === this.searchingInput);
                    // if we have a match, set the value
                    if (match2)
                        match2.setValue();
                }
                // we do not have option groups and we have more than 1 character in our string
                else if (this.optionsGroups.length === 0 && charNum > 1) {
                    const allEqual = allCharsEqual(this.searchingInput);
                    // if all the characters are the same, we will make an array of all options that begin with the character in the string
                    if (allEqual) {
                        // all options that begin with the entered character
                        const allMatchingOptions = this.options
                            .toArray()
                            .filter(option => option.label.substr(0, 1).toUpperCase() ===
                            this.searchingInput[0]);
                        // if there are options that begin with the entered character, we can get a reference to the index of the current selection and then next index
                        if (allMatchingOptions.length > 0) {
                            const currentIndex = allMatchingOptions.indexOf(currentSelection);
                            const nextIndex1 = currentIndex + 1;
                            // if the next index is out of bounds, set the value of the first option in the matching array
                            if (nextIndex1 >= allMatchingOptions.length) {
                                allMatchingOptions[0].setValue();
                                // if the index is in bounds, set the value of the index
                            }
                            else
                                allMatchingOptions[nextIndex1].setValue();
                        }
                    }
                    // if not all the characters in the string are equal, we will see if the string matches any of the options substrings
                    if (!allEqual) {
                        const matchingOption1 = this.options
                            .toArray()
                            .find(option => option.label.substr(0, charNum).toUpperCase() ===
                            this.searchingInput);
                        if (matchingOption1)
                            matchingOption1.setValue();
                    }
                }
                else if (this.hasGroups && charNum === 1) {
                    const optionsGroupsOptions = flatMap(this.optionsGroups.toArray(), (group) => group.options.toArray());
                    const matchingOptionGroupOptions1 = optionsGroupsOptions.filter(option => option.label.substr(0, 1).toUpperCase() === this.searchingInput);
                    // if there are options that match, find the option with the lowest order number since flatmap put the option in reverse order
                    if (matchingOptionGroupOptions1.length > 0) {
                        this.setOrderOptionNum();
                        const firstOption = findFirstOption(matchingOptionGroupOptions1);
                        // set the value of the first option
                        firstOption.setValue();
                    }
                }
                // if there are option groups and there is more than 1 character in the string
                else if (this.hasGroups && charNum > 1) {
                    const allLettersEqual = allCharsEqual(this.searchingInput);
                    const optionGroupOptions = flatMap(this.optionsGroups.toArray(), (group) => group.options.toArray());
                    // if all chars in the string are the same, get all options that match the letter that was entered
                    if (allLettersEqual) {
                        const allMatchingOptionGroupOptions = optionGroupOptions.filter(option => option.label.substr(0, 1).toUpperCase() ===
                            this.searchingInput[0]);
                        // if we have matching options, sort the option to be in the order they appear in the dropdown
                        if (allMatchingOptionGroupOptions.length > 0) {
                            const sortedOptionsArray = this.sortOptions(allMatchingOptionGroupOptions);
                            const currentIndex = sortedOptionsArray.indexOf(currentSelection);
                            const nextIndex2 = currentIndex + 1;
                            // if the next index is out of bounds, set it to the first value
                            if (nextIndex2 >= sortedOptionsArray.length) {
                                sortedOptionsArray[0].setValue();
                                // if the next index is in bounds of the array, set the next value
                            }
                            else
                                sortedOptionsArray[nextIndex2].setValue();
                        }
                    }
                    // if not all chars in the string are the same, find a matching option
                    if (!allLettersEqual) {
                        const matchingOption2 = optionGroupOptions.find(option => option.label.substr(0, charNum).toUpperCase() ===
                            this.searchingInput);
                        if (matchingOption2)
                            matchingOption2.setValue();
                    }
                }
            }
            // if there is not a selection yet, we will use a switch with a case for no option groups and a case with option groups
            if (!currentSelection) {
                // if we have no option groups, see if there is a match to the value entered
                if (this.optionsGroups.length === 0) {
                    const match1 = this.options
                        .toArray()
                        .find(option => option.label.substr(0, 1).toUpperCase() === this.searchingInput);
                    // if there is a match, set the value
                    if (match1)
                        match1.setValue();
                }
                // if we have option groups, put all options from each group in an array and filter out all options that match the first letter entered
                else if (this.hasGroups) {
                    const optionsGroupsOptions = flatMap(this.optionsGroups.toArray(), (group) => group.options.toArray());
                    const matchingOptionGroupOptions2 = optionsGroupsOptions.filter(option => option.label.substr(0, 1).toUpperCase() === this.searchingInput);
                    // if there are options that match, find the option with the lowest order number since flatmap put the option in reverse order
                    if (matchingOptionGroupOptions2.length > 0) {
                        this.setOrderOptionNum();
                        const firstOption = findFirstOption(matchingOptionGroupOptions2);
                        // set the value of the first option
                        firstOption.setValue();
                    }
                }
            }
        }
    }
    _setNextPrevWithPS(optionList, isPrev, isPageDownUp) {
        // check to see if we have a selected item in our options array
        const selectedFilteredOption = optionList.find(option => option.isSelected === true);
        const fll = optionList.length;
        const boundaryIndex = isPrev ? 0 : fll - 1;
        const oppositeBoundaryIndex = isPrev ? fll - 1 : 0;
        const moveNumber = isPageDownUp ? 6 : 1;
        // if we do not have a selected item, let's set the first option (but not on arrow/page up)
        if (!selectedFilteredOption) {
            optionList[oppositeBoundaryIndex].setValue();
        }
        // if there is a selected option, let's get the current index
        if (selectedFilteredOption) {
            const selectedIndex = optionList.indexOf(selectedFilteredOption);
            const nextIndex = isPrev
                ? selectedIndex - moveNumber
                : selectedIndex + moveNumber;
            const isWithinBounds = nextIndex >= 0 && nextIndex < fll;
            // check to make sure the index is in bounds of the array, and then find the option with the next index to be set
            if (isWithinBounds) {
                optionList[nextIndex].setValue();
            }
            // if the next index is not in bounds of the array, set it back to first/last option
            else {
                // if currently at boundary, go to opposite boundry
                if (selectedIndex === boundaryIndex) {
                    if (isPrev) {
                        this.scrollBottom = true;
                    }
                    else {
                        this.scrollTop = true;
                    }
                    optionList[oppositeBoundaryIndex].setValue();
                }
                // if not yet at boundary, but nextIndex is outside bounds, go to boundry
                else {
                    optionList[boundaryIndex].setValue();
                }
            }
        }
    }
    _setNextPrev(e, isPrev, isPageDownUp) {
        if (this.isDisabled)
            return;
        e.preventDefault();
        this.handleOptionSubscriptions();
        // we are not using filteredList
        if (this.filteredList.length === 0) {
            // make an array of all options
            const optionList = this.hasGroups
                ? this.sortOptions(this.flattenedOptions).filter(opt => !opt.disabled)
                : this.options.toArray().filter(opt => !opt.disabled);
            this._setNextPrevWithPS(optionList, isPrev, isPageDownUp);
        }
        // we are using filteredList
        else if (this.filteredList.length > 0) {
            if (this.hasGroups) {
                // sort the filtered list to be in order
                this.filteredList = this.sortOptions(this.filteredList);
            }
            this._setNextPrevWithPS(this.filteredList, isPrev, isPageDownUp);
        }
    }
    get flattenedOptions() {
        return flatMap(this.optionsGroups.toArray(), (group) => group.options.toArray());
    }
    setNextValue(e) {
        // sets next value in dropdown if down/right arrow is pressed
        this._setNextPrev(e);
    }
    setPreviousValue(e) {
        // sets previous value in dropdown if up/left arrow is pressed
        this._setNextPrev(e, true);
    }
    // page down
    goPagedownOnPagedown(e) {
        this._setNextPrev(e, false, true);
    }
    // page down ends
    // page up keyboard event
    goPageupOnPageup(e) {
        this._setNextPrev(e, true, true);
    }
    // page up ends
    _goEndHome(event, isPrev) {
        event.preventDefault();
        event.stopPropagation();
        const fll = this.filteredList.length;
        if (this.isDisabled)
            return;
        this.handleOptionSubscriptions();
        // we are not using filteredList
        if (fll === 0) {
            // make an array of all options in each group
            const optionList = this.hasGroups
                ? this.sortOptions(this.flattenedOptions).filter(opt => !opt.disabled)
                : this.options.toArray().filter(opt => !opt.disabled);
            // if none can be selected
            if (!optionList.find(opt => !opt.disabled))
                return;
            if (isPrev) {
                this.scrollTop = true;
                optionList[0].setValue();
            }
            else {
                this.scrollBottom = true;
                optionList[optionList.length - 1].setValue();
            }
        }
        // we are using filteredList
        else if (fll > 0) {
            if (this.hasGroups) {
                this.filteredList = this.sortOptions(this.filteredList);
            }
            // find the selected option in the filtered list
            const selectedFilteredOption = this.filteredList.find(option => option.isSelected === true);
            // if we have a selected option, get the current index and the next index
            if (selectedFilteredOption) {
                if (isPrev) {
                    this.scrollTop = true;
                    this.filteredList[0].setValue();
                }
                else {
                    this.scrollBottom = true;
                    this.filteredList[fll - 1].setValue();
                }
            }
        }
    }
    // go end on keyboard end
    goEndOnEnd(event) {
        this._goEndHome(event);
    }
    // go end ends
    // go home on keyboard home
    goHomeOnHome(event) {
        this._goEndHome(event, true);
    }
    // go home ends
    get hasGroups() {
        return !!this.optionsGroups && this.optionsGroups.length > 0;
    }
    get hasSearchValue() {
        return !!this.searchValue;
    }
    findOption(hasGroups, value) {
        if (hasGroups) {
            return flatMap(this.optionsGroups, (group) => group.options.toArray()).find(option => value === option.value);
        }
        else if (this.options) {
            return this.options.find(option => value === option.value);
        }
        else {
            return null;
        }
    }
    setOptionsDisplayAll(optionsArray, isDisplayed) {
        optionsArray.forEach(option => (option.displayed = isDisplayed));
    }
    handleOptionSelectForScrollable(optionComp) {
        // this will check to see if the selected option is out of view while the options box is scrollable and scroll up or down accordingly
        const optionsArray = this.options.toArray().filter(opt => !opt.disabled);
        if (this.scrollTop === true) {
            this.scrollableBox.nativeElement.scrollTop = 0;
            this.scrollTop = false;
            return;
        }
        if (this.scrollBottom === true) {
            this.scrollableBox.nativeElement.scrollTop =
                (optionsArray.length - 1) * 44;
            this.scrollBottom = false;
            return;
        }
        const optionsBoxPos = this.scrollableBox.nativeElement.getBoundingClientRect();
        const optionPos = optionComp.optionEl.nativeElement.getBoundingClientRect();
        const relativePosTop = optionPos.top - optionsBoxPos.top;
        const relativePosBottom = optionPos.bottom - optionsBoxPos.bottom;
        const offsetB = Math.ceil(relativePosBottom / 44) * 44;
        const offsetT = Math.ceil(relativePosBottom / 44) * 44;
        if (relativePosBottom >= 0)
            this.scrollableBox.nativeElement.scrollTop += offsetB; //44;
        if (relativePosTop <= 0)
            this.scrollableBox.nativeElement.scrollTop += offsetT; //-44;
    }
    resetStyles(groupChild) {
        /*if the option is a child of a group, we need to find the selected option in the group and set the resetOption property
          to true so that the selection styles will be removed on hover */
        if (groupChild) {
            const options = this.flattenedOptions;
            const optionGroupOption = options.find(option => option.isSelected);
            if (optionGroupOption)
                optionGroupOption.resetOption = true;
            // otherwise, we have a regular option -> so find the selected one and set restOption to true
        }
        else {
            const selectedOption = this.options.find(option => option.isSelected);
            if (selectedOption)
                selectedOption.resetOption = true;
        }
    }
    onClick(event) {
        // if we click outside the dropdown/options box, close the dropdown
        if (this.showOptionsBox === true &&
            !this.selectEl.nativeElement.contains(event.target) &&
            !this.optionsBox.nativeElement.contains(event.target)) {
            this.clickOutsideClose();
        }
    }
    close(event) {
        // when the dropdown is open, if the user presses tab or shift+tab, close the dropdown
        if (this.showOptionsBox) {
            event.preventDefault();
            this.valueChange.emit(this.value);
            this.showOptionsBox = false;
            if (this.predictiveSearch)
                this.inputHasFocus = false;
            if (this.isIOS || this.isAndroid || this.predictiveSearch) {
                this.announcer.announce('collapsed.');
            }
            setTimeout(() => this.selectEl.nativeElement.focus(), 200);
        }
    }
    clickOutsideClose() {
        if (this.showOptionsBox)
            this.valueChange.emit(this.value);
        this.showOptionsBox = false;
        this.validate();
    }
    onInputFocus() {
        this.inputHasFocus = true;
        if (this.isAndroid && this.showOptionsBox)
            this.announcer.announce(`expanded.\n ${this.required ? 'required ' : ''} ${this.currentText}`);
        else if (this.isIOS)
            this.announcer.announce(this.searchBoxPlaceholder);
    }
    onDropdownFocus() {
        setTimeout(() => {
            if (!this.showOptionsBox &&
                this.hasOptionSelected &&
                (this.isIOS || this.isAndroid)) {
                this.announcer.announce('collapsed. ' +
                    this.dropdownValueText.nativeElement.innerHTML +
                    ' selected');
            }
            else if (this.hasOptionSelected) {
                this.announcer.announce(this.dropdownValueText.nativeElement.innerHTML + ' selected');
            }
            else if (!!this.placeholder && !(this.isIOS || this.isAndroid))
                this.announcer.announce(this.placeholder);
        }, 100);
    }
    onBlur() {
        if (this.predictiveSearch && !this.inputHasFocus && !this.showOptionsBox)
            this.validate();
        if (!this.predictiveSearch && !this.showOptionsBox)
            this.validate();
    }
    open(event) {
        event.preventDefault();
        if (!this.showOptionsBox) {
            this.toggleDropdown();
        }
    }
    validate() {
        // check if dropdown is a required field/ if there is an option selected
        if (this.required &&
            (this.value === '' || this.value === 'null' || this.value === 'undefined')) {
            this.toggleErrorState(true, 'requiredError');
            this.errorFromSubmitCheck
                ? this.announcer.multiAnnounce(`${this.label} alert ${this.errorMessage}`)
                : this.announcer.announce(`${this.label} alert ${this.errorMessage}`);
            this.errorFromSubmitCheck = false;
        }
    }
    // Clears an error state on the input. Takes a type of error
    toggleErrorState(isErrored, errorType) {
        // Clear the error state
        this._error = isErrored;
        if (errorType === 'requiredError')
            this.errorMessage = this.requiredError;
        if (errorType === 'customError')
            this.errorMessage = this.customErrorMessage;
        // Emit the change to the error
        this.errorChange.emit(isErrored);
        // Add Rmove the error to the validation service
        this._error
            ? this.validationService.addError(this)
            : this.validationService.removeError(this);
    }
    closeOnEnter(event) {
        if (this.isDisabled)
            return;
        event.preventDefault();
        event.stopPropagation();
        if (this.showOptionsBox) {
            this.valueChange.emit(this.value);
        }
        else {
            this.handleOptionSubscriptions();
        }
        if (!this.showOptionsBox &&
            this.hasOptionSelected &&
            !this.predictiveSearch) {
            this.showOptionsBox = true;
            this.handleAnnouncementsOnOpen();
            setTimeout(() => {
                this.handleOptionSelectForScrollable(this.hasGroups
                    ? this.flattenedOptions.find(option => option.isSelected)
                    : this.options.find(option => option.isSelected));
            });
        }
        else if (!this.showOptionsBox &&
            !this.hasOptionSelected &&
            !this.predictiveSearch) {
            this.showOptionsBox = true;
            this.handleAnnouncementsOnOpen();
        }
        else {
            this.toggleDropdown();
        }
    }
    closeOnly(event) {
        // only close DD, will not emit value
        if (this.showOptionsBox) {
            this.showOptionsBox = false;
            if (this.predictiveSearch)
                this.inputHasFocus = false;
            setTimeout(() => this.selectEl.nativeElement.focus(), 100);
        }
    }
    toggleDropdownOnClick(event) {
        if (this.isDisabled)
            return;
        // redo option subsriptions when opened
        if (!this.showOptionsBox)
            this.handleOptionSubscriptions();
        if (this.showOptionsBox) {
            this.close(event);
        }
        else if (!this.showOptionsBox &&
            this.hasOptionSelected &&
            !this.predictiveSearch) {
            this.showOptionsBox = true;
            this.handleAnnouncementsOnOpen();
            setTimeout(() => {
                this.handleOptionSelectForScrollable(this.hasGroups
                    ? this.flattenedOptions.find(option => option.isSelected)
                    : this.options.find(option => option.isSelected));
            });
        }
        else if (!this.showOptionsBox &&
            !this.hasOptionSelected &&
            !this.predictiveSearch) {
            this.showOptionsBox = true;
            this.handleAnnouncementsOnOpen();
        }
        else {
            this.toggleDropdown();
        }
    }
    toggleDropdown() {
        // toggle dropdown modal content
        if (this.isDisabled)
            return;
        this.showOptionsBox = !this.showOptionsBox;
        setTimeout(_ => this.selectEl.nativeElement.focus(), 100);
        if (!this.predictiveSearch)
            this.onDropdownFocus();
        if (this.showOptionsBox) {
            setTimeout(() => {
                // if the options box is open, announce the total number of options
                this.handleAnnouncementsOnOpen();
                // wait till dropdown is open and then check if options box is off the screen
                isInViewPort(this.optionsBox);
            }, 0);
        }
        else if (this.predictiveSearch)
            this.inputHasFocus = false;
        if (this.predictiveSearch && this.showOptionsBox) {
            this.searchValue = '';
            this.searchValueChange.emit(this.searchValue);
            setTimeout(() => {
                this.handleDropdownOpenWithPS();
                this.handleOptionSubscriptions();
            }, 0);
        }
        /*if we open the dropdown and take the background away from the selected option on hover, and we also do not
          select a new option, we want to make sure the selected option has the blue background still, so let's set it back*/
        // we have option groups
        if (this.hasGroups) {
            const options = this.flattenedOptions;
            const optionGroupOption = options.find(option => option.isSelected && option.resetOption);
            if (optionGroupOption)
                optionGroupOption.resetOption = false;
            // otherwise -> we do not have any option groups
        }
        else {
            const selectedOption = this.options.find(option => option.isSelected && option.resetOption);
            if (selectedOption) {
                selectedOption.resetOption = false;
            }
        }
        this.setOptionsDisplay();
    }
    handleAnnouncementsOnOpen() {
        if (this.optionsGroups.length === 0) {
            this.isAndroid || this.isIOS
                ? this.announcer.announce('expanded. ' +
                    this.options.length +
                    ' total options. \nDouble tap to select an option')
                : this.announcer.announce(this.options.length + ' total options');
        }
        else {
            const totalOptions = flatMap(this.optionsGroups.toArray(), (group) => group.options.toArray());
            this.isAndroid || this.isIOS
                ? this.announcer.announce('expanded. ' +
                    totalOptions.length +
                    ' total options. \nDouble tap to select an option')
                : this.announcer.announce(totalOptions.length + ' total options.');
        }
    }
    handleDropdownOpenWithPS() {
        if (this.isAndroid || this.isIOS)
            this.announcer.announce('expanded');
        if (this.optionsGroups.length === 0) {
            // reassign the filtered list to the options in our dropdown so they will be displayed when we open it again
            this.filteredList = this.options.toArray();
        }
        else {
            // set all options to be displayed when dropdown is opened
            this.filteredList = flatMap(this.optionsGroups.toArray(), (group) => group.options.toArray());
            this.optionsGroups.forEach(group => (group.isDisplayed = true));
        }
        const disabledOps = this.filteredList.filter(option => option.disabled);
        if (disabledOps.length > 0)
            this.setOptionsDisplayAll(disabledOps, true);
        this.filteredList = this.filteredList.filter(option => !option.disabled);
        // set noOps back to false since we want the options box to display
        this.noOps = false;
        // set all options to be displayed when we open the dropdown
        this.setOptionsDisplayAll(this.filteredList, true);
        setTimeout(() => {
            // Focus on input unless on mobile Safari. Safari on iOS does not allow focus() after timeout
            if (!(this.isIOS && this.isSafari)) {
                this.input.nativeElement.focus();
            }
            if (this.isAndroid && this.isFirefox) {
                // Firefox on Android does not work with KeyboardEvent, using InputEvent instead
                fromEvent(this.input.nativeElement, 'input')
                    .pipe(takeUntil(this.unsub$))
                    .subscribe((ie) => {
                    // create empty keyboard event to pass to search
                    const ke = new KeyboardEvent('keyup', {});
                    this.search(ke);
                });
            }
            else {
                // searching with input box observable - dynamic searching/filtering on keyup
                fromEvent(this.input.nativeElement, 'keyup')
                    .pipe(takeUntil(this.unsub$))
                    .subscribe((e) => {
                    this.search(e);
                });
            }
        }, 100);
    }
    optionSelected(optionComp) {
        // if there are option groups -> set all options within the groups to false if any are true
        if (this.hasGroups) {
            const options = this.flattenedOptions;
            const optionGroupOption = options.find(option => option.isSelected);
            // set resetOption back to false so it can be highlighted on hover again
            if (optionGroupOption)
                optionGroupOption.isSelected = optionGroupOption.resetOption = false;
            // otherwise -> we do not have any option groups, so just set the options in the dropdown to false if they are true
        }
        else {
            const selectedOption = this.options.find(option => option.isSelected);
            // set resetOption back to false so it can be highlighted on hover again
            if (selectedOption)
                selectedOption.isSelected = selectedOption.resetOption = false;
        }
        optionComp.isSelected = true;
        this.valueName = optionComp.label;
        if (this._error)
            this.toggleErrorState(false);
        optionComp.resetOption = false;
        this.allowSetValueInWriteValue = false;
        this.writeValue(optionComp.value);
        if (this.showOptionsBox)
            this.handleOptionSelectForScrollable(optionComp);
        this.allowSetValueInWriteValue = true;
    }
    setOptionsDisplay() {
        if (this.predictiveSearch) {
            const selectedOption = this.options
                .toArray()
                .find(option => option.isSelected);
            if (selectedOption)
                this.setOptionsDisplayAll(this.options.toArray(), true);
        }
    }
    reset() {
        this.toggleErrorState(false);
        this.displayedText = this.placeholder;
        const selection = this.options.find(option => option.isSelected);
        if (selection)
            selection.isSelected = false;
        this.value = '';
        this.hasOptionSelected = false;
        this.valueName = '';
    }
    // Allows Angular to update the model.
    // Update the model and changes needed for the view here.
    writeValue(value) {
        if (this.value === value)
            return;
        if (this.allowSetValueInWriteValue) {
            setTimeout(() => {
                const opt = this.findOption(this.hasGroups, value);
                this.handleOptionSubscriptions();
                if (!!opt)
                    opt.setValue();
                this._value = value;
            }, 0);
        }
        //      !!!!    WARNING: DO NOT REMOVE    !!!!
        // this is actually necessary for ngModel to update
        this.onChange(value);
        // only change the displayed text when dropdown is open and using the arrow keys to navigate through it
        if (this.showOptionsBox) {
            this.displayedText = this.valueName; //this.value;
            return;
        }
        // infom the consumer
        this.valueChange.emit(value);
    }
    // Allows Angular to register a function to call when the model changes.
    // Save the function as a property to call later here.
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // Allows Angular to disable the input.
    setDisabledState(isDisabled) {
        this.isDisabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Dropdown2Component, deps: [{ token: ValidationService }, { token: CitiLiveAnnouncer }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: Dropdown2Component, isStandalone: false, selector: "citi-dropdown2", inputs: { CustomMasking: "CustomMasking", mask: "mask", placeholder: "placeholder", label: "label", idStr: "idStr", labelId: "labelId", ariaLabelledById: "ariaLabelledById", name: "name", size: "size", customErrorMessage: "customErrorMessage", error: "error", requiredError: "requiredError", required: "required", predictiveSearch: "predictiveSearch", isDisabled: "isDisabled", dropdownTheme: "dropdownTheme", additionalClasses: "additionalClasses", visibleLabel: "visibleLabel", value: "value", getScrollableMenuAriaLabelledby: "getScrollableMenuAriaLabelledby" }, outputs: { errorChange: "errorChange", valueChange: "valueChange", searchValueChange: "searchValueChange", displayedValueChange: "displayedValueChange" }, host: { listeners: { "window:touchstart": "onClick($event)", "window:click": "onClick($event)" }, properties: { "class.row": "this.hostRowClass" } }, providers: [DD2_VALUE_ACCESSOR], queries: [{ propertyName: "options", predicate: OptionsComponent, descendants: true }, { propertyName: "optionsGroups", predicate: OptionsGroupComponent }], viewQueries: [{ propertyName: "selectEl", first: true, predicate: ["selectEl"], descendants: true }, { propertyName: "dropdownValueText", first: true, predicate: ["dropdownValueText"], descendants: true, static: true }, { propertyName: "input", first: true, predicate: ["input"], descendants: true }, { propertyName: "optionsBox", first: true, predicate: ["optionsBox"], descendants: true }, { propertyName: "scrollableBox", first: true, predicate: ["scrollableBox"], descendants: true }], ngImport: i0, template: `
    <div [ngClass]="sizeClasses">
      <div class="dropdown2-wrapper">
        <label
          *ngIf="!ariaLabelledById"
          [ngClass]="{ disabledLabel: isDisabled }"
          [ngStyle]="{ opacity: labelOpacity }"
          class="dropdownLabel"
          [for]="idStr"
          [id]="labelId"
          [innerHtml]="label"
        ></label>

        <!-- Hidden Input for native <form> -->
        <input
          class="hidden-input"
          type="text"
          [attr.disabled]="isDisabled ? 'disabled' : null"
          [attr.name]="idStr + 'HiddenInput'"
          [(ngModel)]="value"
        />
        <!-- dropdown button -->
        <div
          [attr.aria-controls]="idStr + '-listbox'"
          #selectEl
          role="combobox"
          [attr.aria-labelledby]="getAriaLabelledBy()"
          [attr.aria-required]="required"
          [attr.aria-expanded]="showOptionsBox"
          [attr.aria-owns]="showOptionsBox ? idStr + '-listbox' : null"
          [attr.disabled]="isDisabled ? 'disabled' : null"
          [attr.aria-disabled]="isDisabled ? 'true' : null"
          aria-haspopup="listbox"
          [ngClass]="{
            open: predictiveSearch && showOptionsBox,
            disabledOptionSelect: isDisabled,
            errorState: error,
            dark: dropdownTheme === 'dark',
            light: dropdownTheme === 'light',
            valueColor: displayedText === value,
            placeholderColor: displayedText === placeholder
          }"
          [id]="idStr"
          [attr.tabIndex]="!isDisabled ? '0' : '-1'"
          class="dropdown {{ additionalClasses }}"
          (click)="toggleDropdownOnClick($event)"
          (keyup.enter)="closeOnEnter($event)"
          (keydown.ArrowDown)="setNextValue($event)"
          (keydown.alt.ArrowDown)="closeOnEnter($event)"
          (keydown.ArrowUp)="setPreviousValue($event)"
          (keydown.escape)="close($event)"
          (keydown.space)="closeOnEnter($event)"
          (keydown.home)="goHomeOnHome($event)"
          (keydown.end)="goEndOnEnd($event)"
          (keydown.pageup)="goPageupOnPageup($event)"
          (keydown.pagedown)="goPagedownOnPagedown($event)"
          (focus)="onDropdownFocus()"
          (blur)="onBlur()"
          (keydown.tab)="close($event)"
          (keydown.shift.tab)="close($event)"
        >
          <!-- current selected option text - placeholder before an option is selected/ value after option is selected -->
          <div
            #dropdownValueText
            aria-hidden="true"
            role="textbox"
            class="dropdownText"
            [innerHTML]="
              mask
                ? !!CustomMasking
                  ? (displayedText | customPipe: CustomMasking)
                  : (displayedText | masking)
                : displayedText
            "
          ></div>

          <!-- arrow on dropdown button -->
          <citi-icon2
            class="arrowIcon"
            name="02-down"
            [shade]="isDisabled ? 'g1' : 'b2'"
            ariaLabel="down arrow icon"
            ariaLabel="icon"
          >
          </citi-icon2>
        </div>

        <!-- the options box -->
        <div
          #optionsBox
          *ngIf="showOptionsBox"
          class="options"
          [ngClass]="[dropdownTheme]"
        >
          <!-- input box that is present if predictive search is on -->
          <div
            *ngIf="predictiveSearch && showOptionsBox"
            class="searchBoxWrapper"
          >
            <input
              #input
              [(ngModel)]="searchValue"
              role="textbox"
              class="searchBox"
              type="text"
              [id]="idStr + '-searchbox'"
              [placeholder]="searchBoxPlaceholder"
              (focus)="onInputFocus()"
              (keyup.enter)="closeOnEnter($event)"
              (blur)="inputHasFocus = false"
              (keydown.tab)="close($event)"
              (keydown.ArrowDown)="setNextValue($event)"
              (keydown.shift.tab)="close($event)"
              (keydown.ArrowUp)="setPreviousValue($event)"
              (keydown.home)="goHomeOnHome($event)"
              (keydown.end)="goEndOnEnd($event)"
              (keydown.pageup)="goPageupOnPageup($event)"
              (keydown.pagedown)="goPagedownOnPagedown($event)"
              (keydown.escape)="close($event)"
            />
          </div>

          <!--message that will display if the input field does not match any options-->
          <div *ngIf="noOps" class="noOpsLeft" role="alert">
            No matching options
          </div>

          <!--options content-->
          <div
            #scrollableBox
            role="listbox"
            [id]="idStr + '-listbox'"
            class="scrollable-menu"
            [attr.aria-labelledby]="getScrollableMenuAriaLabelledby"
          >
            <ng-content></ng-content>
          </div>
        </div>

        <!-- error message -->
        <span *ngIf="_error && !showOptionsBox" class="dropdownErrorMessage">{{
          errorMessage
        }}</span>
      </div>
    </div>
  `, isInline: true, styles: [":host{display:block}.dropdown2-wrapper{position:relative;margin-bottom:20px;margin-left:-5px;margin-right:-5px}.scrollable-menu>::ng-deep citi-options2:first-child .dd2-option,.scrollable-menu>::ng-deep citi-options-group2:first-child .optionsGroupText{padding-top:20px}.scrollable-menu>::ng-deep citi-options2:last-child .dd2-option{padding-bottom:20px}.scrollable-menu>::ng-deep citi-options-group2:last-child citi-options2:last-child .dd2-option{padding-bottom:20px}.dropdown{box-sizing:border-box;padding:12px 20px;cursor:pointer;height:50px;border-radius:6px;box-shadow:none;transition:border-color ease-in-out 225ms;font-size:16px;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;color:#6b6b6b}.dropdown:focus{border:1px solid #056DAE;outline:0}.dropdownLabel{font-size:.75rem;color:#666;width:100%;pointer-events:none;transition:opacity .3s ease-in-out,visibility 1ms ease 301ms}.hidden-input{visibility:hidden;height:0;width:0;position:absolute}.light{background-color:#fff;border:1px solid #eee}.dark{background-color:#eee;border:1px solid #fff}.options{width:100%;box-sizing:border-box;border-radius:6px;position:absolute;box-shadow:1px 1px 2px #0000003d;z-index:999}.arrowIcon{margin-top:17px;height:20px;position:absolute;right:25px;top:0;display:inline-block;pointer-events:none}.fullwidth{width:100%}.large{width:768px}.med{width:571px}.small{width:374px}.xsmall{width:172px}.dropdownText{position:absolute;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;padding-right:40px;width:85%}.valueColor{color:#333}.placeholderColor{color:#666}[role=combobox]{margin-top:3px}.open{border:1px solid #056DAE}.errorState{border:1px solid #D60000}.errorState:focus{border:2px solid #D60000}.dropdownErrorMessage{color:#d60000;font-weight:700;line-height:1rem;font-size:12px;font-size:.75rem;font-family:interstate_Bold,sans-serif}.searchBoxWrapper{padding-top:20px;padding-left:20px;padding-right:20px}.searchBox{background-color:#fff;padding-left:20px;padding-top:5px;padding-bottom:5px;border-radius:6px;border:1px solid #056DAE;width:100%}.searchBox:focus{outline:0}input::placeholder{color:#000;opacity:1}.scrollable-menu{max-height:50vh;overflow:auto}.noOpsLeft{padding:20px}@media (max-width: 991px){.large,.med,.small{width:100%}}.disabledOptionSelect{color:#333;font-size:16px;font-family:Interstate-Light;line-height:1.5;border:1px solid #666;background-color:transparent}.disabledOptionSelect:focus{border:1px solid #666}.disabledLabel{color:#333;font-size:12px;line-height:1.5;font-family:Interstate-Light}.disabledArrowIcon{fill:#666}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: Icon2Component, selector: "citi-icon2", inputs: ["role", "idStr", "ariaLabel", "ariaHidden", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "shade", "name"] }, { kind: "pipe", type: MaskingPipe, name: "masking" }, { kind: "pipe", type: CustomPipe, name: "customPipe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Dropdown2Component, decorators: [{
            type: Component,
            args: [{ selector: 'citi-dropdown2', standalone: false, template: `
    <div [ngClass]="sizeClasses">
      <div class="dropdown2-wrapper">
        <label
          *ngIf="!ariaLabelledById"
          [ngClass]="{ disabledLabel: isDisabled }"
          [ngStyle]="{ opacity: labelOpacity }"
          class="dropdownLabel"
          [for]="idStr"
          [id]="labelId"
          [innerHtml]="label"
        ></label>

        <!-- Hidden Input for native <form> -->
        <input
          class="hidden-input"
          type="text"
          [attr.disabled]="isDisabled ? 'disabled' : null"
          [attr.name]="idStr + 'HiddenInput'"
          [(ngModel)]="value"
        />
        <!-- dropdown button -->
        <div
          [attr.aria-controls]="idStr + '-listbox'"
          #selectEl
          role="combobox"
          [attr.aria-labelledby]="getAriaLabelledBy()"
          [attr.aria-required]="required"
          [attr.aria-expanded]="showOptionsBox"
          [attr.aria-owns]="showOptionsBox ? idStr + '-listbox' : null"
          [attr.disabled]="isDisabled ? 'disabled' : null"
          [attr.aria-disabled]="isDisabled ? 'true' : null"
          aria-haspopup="listbox"
          [ngClass]="{
            open: predictiveSearch && showOptionsBox,
            disabledOptionSelect: isDisabled,
            errorState: error,
            dark: dropdownTheme === 'dark',
            light: dropdownTheme === 'light',
            valueColor: displayedText === value,
            placeholderColor: displayedText === placeholder
          }"
          [id]="idStr"
          [attr.tabIndex]="!isDisabled ? '0' : '-1'"
          class="dropdown {{ additionalClasses }}"
          (click)="toggleDropdownOnClick($event)"
          (keyup.enter)="closeOnEnter($event)"
          (keydown.ArrowDown)="setNextValue($event)"
          (keydown.alt.ArrowDown)="closeOnEnter($event)"
          (keydown.ArrowUp)="setPreviousValue($event)"
          (keydown.escape)="close($event)"
          (keydown.space)="closeOnEnter($event)"
          (keydown.home)="goHomeOnHome($event)"
          (keydown.end)="goEndOnEnd($event)"
          (keydown.pageup)="goPageupOnPageup($event)"
          (keydown.pagedown)="goPagedownOnPagedown($event)"
          (focus)="onDropdownFocus()"
          (blur)="onBlur()"
          (keydown.tab)="close($event)"
          (keydown.shift.tab)="close($event)"
        >
          <!-- current selected option text - placeholder before an option is selected/ value after option is selected -->
          <div
            #dropdownValueText
            aria-hidden="true"
            role="textbox"
            class="dropdownText"
            [innerHTML]="
              mask
                ? !!CustomMasking
                  ? (displayedText | customPipe: CustomMasking)
                  : (displayedText | masking)
                : displayedText
            "
          ></div>

          <!-- arrow on dropdown button -->
          <citi-icon2
            class="arrowIcon"
            name="02-down"
            [shade]="isDisabled ? 'g1' : 'b2'"
            ariaLabel="down arrow icon"
            ariaLabel="icon"
          >
          </citi-icon2>
        </div>

        <!-- the options box -->
        <div
          #optionsBox
          *ngIf="showOptionsBox"
          class="options"
          [ngClass]="[dropdownTheme]"
        >
          <!-- input box that is present if predictive search is on -->
          <div
            *ngIf="predictiveSearch && showOptionsBox"
            class="searchBoxWrapper"
          >
            <input
              #input
              [(ngModel)]="searchValue"
              role="textbox"
              class="searchBox"
              type="text"
              [id]="idStr + '-searchbox'"
              [placeholder]="searchBoxPlaceholder"
              (focus)="onInputFocus()"
              (keyup.enter)="closeOnEnter($event)"
              (blur)="inputHasFocus = false"
              (keydown.tab)="close($event)"
              (keydown.ArrowDown)="setNextValue($event)"
              (keydown.shift.tab)="close($event)"
              (keydown.ArrowUp)="setPreviousValue($event)"
              (keydown.home)="goHomeOnHome($event)"
              (keydown.end)="goEndOnEnd($event)"
              (keydown.pageup)="goPageupOnPageup($event)"
              (keydown.pagedown)="goPagedownOnPagedown($event)"
              (keydown.escape)="close($event)"
            />
          </div>

          <!--message that will display if the input field does not match any options-->
          <div *ngIf="noOps" class="noOpsLeft" role="alert">
            No matching options
          </div>

          <!--options content-->
          <div
            #scrollableBox
            role="listbox"
            [id]="idStr + '-listbox'"
            class="scrollable-menu"
            [attr.aria-labelledby]="getScrollableMenuAriaLabelledby"
          >
            <ng-content></ng-content>
          </div>
        </div>

        <!-- error message -->
        <span *ngIf="_error && !showOptionsBox" class="dropdownErrorMessage">{{
          errorMessage
        }}</span>
      </div>
    </div>
  `, providers: [DD2_VALUE_ACCESSOR], styles: [":host{display:block}.dropdown2-wrapper{position:relative;margin-bottom:20px;margin-left:-5px;margin-right:-5px}.scrollable-menu>::ng-deep citi-options2:first-child .dd2-option,.scrollable-menu>::ng-deep citi-options-group2:first-child .optionsGroupText{padding-top:20px}.scrollable-menu>::ng-deep citi-options2:last-child .dd2-option{padding-bottom:20px}.scrollable-menu>::ng-deep citi-options-group2:last-child citi-options2:last-child .dd2-option{padding-bottom:20px}.dropdown{box-sizing:border-box;padding:12px 20px;cursor:pointer;height:50px;border-radius:6px;box-shadow:none;transition:border-color ease-in-out 225ms;font-size:16px;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;color:#6b6b6b}.dropdown:focus{border:1px solid #056DAE;outline:0}.dropdownLabel{font-size:.75rem;color:#666;width:100%;pointer-events:none;transition:opacity .3s ease-in-out,visibility 1ms ease 301ms}.hidden-input{visibility:hidden;height:0;width:0;position:absolute}.light{background-color:#fff;border:1px solid #eee}.dark{background-color:#eee;border:1px solid #fff}.options{width:100%;box-sizing:border-box;border-radius:6px;position:absolute;box-shadow:1px 1px 2px #0000003d;z-index:999}.arrowIcon{margin-top:17px;height:20px;position:absolute;right:25px;top:0;display:inline-block;pointer-events:none}.fullwidth{width:100%}.large{width:768px}.med{width:571px}.small{width:374px}.xsmall{width:172px}.dropdownText{position:absolute;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;padding-right:40px;width:85%}.valueColor{color:#333}.placeholderColor{color:#666}[role=combobox]{margin-top:3px}.open{border:1px solid #056DAE}.errorState{border:1px solid #D60000}.errorState:focus{border:2px solid #D60000}.dropdownErrorMessage{color:#d60000;font-weight:700;line-height:1rem;font-size:12px;font-size:.75rem;font-family:interstate_Bold,sans-serif}.searchBoxWrapper{padding-top:20px;padding-left:20px;padding-right:20px}.searchBox{background-color:#fff;padding-left:20px;padding-top:5px;padding-bottom:5px;border-radius:6px;border:1px solid #056DAE;width:100%}.searchBox:focus{outline:0}input::placeholder{color:#000;opacity:1}.scrollable-menu{max-height:50vh;overflow:auto}.noOpsLeft{padding:20px}@media (max-width: 991px){.large,.med,.small{width:100%}}.disabledOptionSelect{color:#333;font-size:16px;font-family:Interstate-Light;line-height:1.5;border:1px solid #666;background-color:transparent}.disabledOptionSelect:focus{border:1px solid #666}.disabledLabel{color:#333;font-size:12px;line-height:1.5;font-family:Interstate-Light}.disabledArrowIcon{fill:#666}\n"] }]
        }], ctorParameters: () => [{ type: ValidationService }, { type: CitiLiveAnnouncer }, { type: i0.NgZone }], propDecorators: { hostRowClass: [{
                type: HostBinding,
                args: ['class.row']
            }], options: [{
                type: ContentChildren,
                args: [OptionsComponent, { descendants: true }]
            }], optionsGroups: [{
                type: ContentChildren,
                args: [OptionsGroupComponent]
            }], selectEl: [{
                type: ViewChild,
                args: ['selectEl', { static: false }]
            }], dropdownValueText: [{
                type: ViewChild,
                args: ['dropdownValueText', { static: true }]
            }], input: [{
                type: ViewChild,
                args: ['input', { static: false }]
            }], optionsBox: [{
                type: ViewChild,
                args: ['optionsBox', { static: false }]
            }], scrollableBox: [{
                type: ViewChild,
                args: ['scrollableBox', { static: false }]
            }], CustomMasking: [{
                type: Input
            }], mask: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], label: [{
                type: Input
            }], idStr: [{
                type: Input
            }], labelId: [{
                type: Input
            }], ariaLabelledById: [{
                type: Input
            }], name: [{
                type: Input
            }], size: [{
                type: Input
            }], customErrorMessage: [{
                type: Input
            }], error: [{
                type: Input
            }], requiredError: [{
                type: Input
            }], required: [{
                type: Input
            }], predictiveSearch: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], dropdownTheme: [{
                type: Input
            }], additionalClasses: [{
                type: Input
            }], visibleLabel: [{
                type: Input
            }], errorChange: [{
                type: Output
            }], valueChange: [{
                type: Output
            }], value: [{
                type: Input,
                args: ['value']
            }], getScrollableMenuAriaLabelledby: [{
                type: Input
            }], searchValueChange: [{
                type: Output
            }], displayedValueChange: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['window:touchstart', ['$event']]
            }, {
                type: HostListener,
                args: ['window:click', ['$event']]
            }] } });
// #region // ----------------------- Functions ----------------------- //
function flatMap(array, mapFunc) {
    // this function will be used to put all option group options in one array
    return array.reduce((cumulus, next) => [...mapFunc(next), ...cumulus], []);
}
function allCharsEqual(stringToCompare) {
    // we will use this when searching when predictive search is off - returns true if all characters in a string are the same
    return stringToCompare.split('').every(char => char === stringToCompare[0]);
}
function findFirstOption(optionGroupOptions) {
    // this will find the first option when there are option groups. We will use this to put the options array in order
    let lowestOrderNum = optionGroupOptions[0];
    optionGroupOptions.forEach(option => {
        if (option.optionOrderNum < lowestOrderNum.optionOrderNum)
            lowestOrderNum = option;
    });
    return lowestOrderNum;
}
function sort(optionGroupOptions) {
    // this will sort the options when there are option groups (flat map does not put the options in the correct order)
    const newArray = [];
    let index = 0;
    // this.setOrderOptionNum();
    while (optionGroupOptions.length > 0) {
        const nextOption = findFirstOption(optionGroupOptions);
        optionGroupOptions.splice(optionGroupOptions.indexOf(nextOption), 1);
        newArray[index] = nextOption;
        index++;
    }
    return newArray;
}
function isInViewPort(selectEl) {
    const rect = selectEl.nativeElement.getBoundingClientRect();
    const html = document.documentElement;
    if (!(rect.bottom <= (window.innerHeight || html.clientHeight))) {
        const adjustment = rect.bottom - window.innerHeight;
        window.scrollBy(0, adjustment + 15);
    }
}

// Component Dependencies
const COMPONENTS$6 = [
    Dropdown2Component,
    OptionsComponent,
    OptionsGroupComponent,
    MaskingPipe,
    CustomPipe
];
const MODULES$7 = [CommonModule, FormsModule, Icons2];
// tslint:disable-next-line: class-name
class Dropdown2 {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Dropdown2, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: Dropdown2, declarations: [Dropdown2Component,
            OptionsComponent,
            OptionsGroupComponent,
            MaskingPipe,
            CustomPipe], imports: [CommonModule, FormsModule, Icons2], exports: [Dropdown2Component,
            OptionsComponent,
            OptionsGroupComponent,
            MaskingPipe,
            CustomPipe] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Dropdown2, imports: [MODULES$7] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Dropdown2, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$7],
                    declarations: [...COMPONENTS$6],
                    exports: [...COMPONENTS$6]
                }]
        }] });

/**
 * @Component: Column
 * @Section: GridLayouts
 * @description This is the column component and is used to leverage the grid
 * from Bootstrap.
*/
class ColumnComponent {
    constructor() {
        this.columnClasses = [];
    }
    /**
     * @property xs
     * @description This defines the grid behavior for the xs grid resolution
     * @return number (1-12)
    */
    set xs(xs) {
        this.resetSizeInput('col-xs-', xs);
    }
    /**
     * @property sm
     * @description This defines the grid behavior for the sm grid resolution
     * @return number (1-12)
    */
    set sm(sm) {
        this.resetSizeInput('col-sm-', sm);
    }
    /**
     * @property md
     * @description This defines the grid behavior for the md grid resolution
     * @return number (1-12)
    */
    set md(md) {
        this.resetSizeInput('col-md-', md);
    }
    /**
     * @property lg
     * @description This defines the grid behavior for the lg grid resolution
     * @return number (1-12)
    */
    set lg(lg) {
        this.resetSizeInput('col-lg-', lg);
    }
    /**
     * @property pullRight
     * @description This will force the column to float right
     * @Type: boolean
    */
    set pullRight(pullRight) {
        pullRight ? this.resetClass('pull-right', 'pull-right') : this.resetClass('pull-right', '');
    }
    /**
     * @property pullLeft
     * @description This will force the column to float left
     * @Type: boolean
    */
    set pullLeft(pullLeft) {
        pullLeft ? this.resetClass('pull-left', 'pull-left') : this.resetClass('pull-left', '');
    }
    /**
     * @property offXs
     * @description This defines the offset behavior for the xs grid resolution
     * @return number (1-12)
    */
    set offXs(offXs) {
        this.resetSizeInput('col-xs-offset-', offXs);
    }
    /**
     * @property offSm
     * @description This defines the offset behavior for the sm grid resolution
     * @return number (1-12)
    */
    set offSm(offSm) {
        this.resetSizeInput('col-sm-offset-', offSm);
    }
    /**
     * @property offMd
     * @description This defines the offset behavior for the md grid resolution
     * @return number (1-12)
    */
    set offMd(offMd) {
        this.resetSizeInput('col-md-offset-', offMd);
    }
    /**
     * @property offLg
     * @description This defines the offset behavior for the lg grid resolution
     * @return number (1-12)
    */
    set offLg(offLg) {
        this.resetSizeInput('col-lg-offset-', offLg);
    }
    ngOnInit() {
        if (this.classes) {
            this.columnClasses.push(this.classes);
        }
    }
    resetSizeInput(sizeSearch, newSize) {
        this.resetClass(sizeSearch, newSize ? sizeSearch + newSize : '');
    }
    resetClass(searchStr, newClass) {
        for (let i = this.columnClasses.length - 1; i >= 0; i--) {
            if (this.columnClasses[i].indexOf(searchStr) !== -1) {
                // Keeps offset class from being removed when not intended.
                if (!(searchStr.indexOf('offset') === -1 && this.columnClasses[i].indexOf('offset') !== -1)) {
                    this.columnClasses.splice(i, 1);
                }
            }
        }
        this.columnClasses.push(newClass);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ColumnComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: ColumnComponent, isStandalone: false, selector: "citi-column, [citi-column]", inputs: { xs: "xs", sm: "sm", md: "md", lg: "lg", pullRight: "pullRight", pullLeft: "pullLeft", offXs: "offXs", offSm: "offSm", offMd: "offMd", offLg: "offLg", classes: "classes", htmlContent: "htmlContent" }, ngImport: i0, template: `
    <div [ngClass]="columnClasses" [contentHtml]="htmlContent">
        <ng-content></ng-content>
    </div>`, isInline: true, dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: ContentHtmlDirective, selector: "[contentHtml]", inputs: ["contentHtml"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ColumnComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'citi-column, [citi-column]',
                    standalone: false,
                    template: `
    <div [ngClass]="columnClasses" [contentHtml]="htmlContent">
        <ng-content></ng-content>
    </div>`
                }]
        }], propDecorators: { xs: [{
                type: Input
            }], sm: [{
                type: Input
            }], md: [{
                type: Input
            }], lg: [{
                type: Input
            }], pullRight: [{
                type: Input
            }], pullLeft: [{
                type: Input
            }], offXs: [{
                type: Input
            }], offSm: [{
                type: Input
            }], offMd: [{
                type: Input
            }], offLg: [{
                type: Input
            }], classes: [{
                type: Input
            }], htmlContent: [{
                type: Input
            }] } });

/**
 * @Component: Row
 * @Section: GridLayouts
 * @description This is the column component and is used to leverage the grid
 * from Bootstrap.
*/
class RowComponent {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: RowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: RowComponent, isStandalone: false, selector: "citi-row, [citi-row]", inputs: { classes: "classes", htmlContent: "htmlContent" }, ngImport: i0, template: `
    <div class="row {{classes}}" [contentHtml]="htmlContent">
        <ng-content></ng-content>
    </div>`, isInline: true, dependencies: [{ kind: "directive", type: ContentHtmlDirective, selector: "[contentHtml]", inputs: ["contentHtml"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: RowComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'citi-row, [citi-row]',
                    standalone: false,
                    template: `
    <div class="row {{classes}}" [contentHtml]="htmlContent">
        <ng-content></ng-content>
    </div>`
                }]
        }], propDecorators: { classes: [{
                type: Input
            }], htmlContent: [{
                type: Input
            }] } });

/**
 * @Component: tileContainer Layout
 * @Section: GridLayouts
 * @description This is the tile layout component that includes panel wrappers based on the requirement.
*/
class TileContainerComponent {
    constructor() {
        /**
         * @property isRightPanelRequired
         * @description The isRightPanelRequired attribute will be set to enable/disable the right container.
         * @Type: boolean (true | false) -by default set to true.
         */
        this.isRightPanelRequired = true;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TileContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: TileContainerComponent, isStandalone: false, selector: "citi-tile-container", inputs: { tileContainer: "tileContainer", fullBodyContainer: "fullBodyContainer", isRightPanelRequired: "isRightPanelRequired" }, ngImport: i0, template: `
  <citi-row *ngIf="tileContainer" classes="tile-container">
    <citi-column md="9" xs="12" classes="left-container">
        <div class="left-content-body">
            <ng-content select="[leftcontent]"></ng-content>
        </div>
    </citi-column>
    <citi-column *ngIf="isRightPanelRequired" md="3" xs="12" classes="right-container">
        <div class="right-content-body">
            <ng-content select="[rightcontent]"></ng-content>
        </div>
    </citi-column>
  </citi-row>
  <citi-row *ngIf="fullBodyContainer" classes="full-container">
      <citi-column xs="12" classes="main-container">
          <div class="full-content-body">
              <ng-content select="[maincontent]"></ng-content>
          </div>
      </citi-column>
  </citi-row>
  `, isInline: true, styles: [":host-context(.theme-gray) ::ng-deep .tile-container .left-container .left-content-body,:host-context(.theme-gray) ::ng-deep .tile-container .right-container .right-content-body,:host-context(.theme-gray) ::ng-deep .full-container .main-container .full-content-body{background:#fff;padding:40px}:host-context(.theme-gray) ::ng-deep .tile-container .left-container .left-content-body .module-alert-box,:host-context(.theme-gray) ::ng-deep .tile-container .right-container .right-content-body .module-alert-box,:host-context(.theme-gray) ::ng-deep .full-container .main-container .full-content-body .module-alert-box{border:1px solid #eee}:host-context(.theme-light) ::ng-deep .tile-container .left-container .left-content-body,:host-context(.theme-light) ::ng-deep .tile-container .right-container .right-content-body,:host-context(.theme-light) ::ng-deep .full-container .main-container .full-content-body{background:#eee;padding:40px}:host-context(.theme-light) ::ng-deep .tile-container .left-container .left-content-body .module-alert-box,:host-context(.theme-light) ::ng-deep .tile-container .right-container .right-content-body .module-alert-box,:host-context(.theme-light) ::ng-deep .full-container .main-container .full-content-body .module-alert-box{border:1px solid #fff}:host-context(.cbolui-ddl.theme-gray) ::ng-deep .tile-container .left-container,:host-context(.cbolui-ddl.theme-gray) ::ng-deep .tile-container .right-container,:host-context(.cbolui-ddl.theme-gray) ::ng-deep .full-container .main-container{padding-top:20px;padding-bottom:20px}\n"], dependencies: [{ kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ColumnComponent, selector: "citi-column, [citi-column]", inputs: ["xs", "sm", "md", "lg", "pullRight", "pullLeft", "offXs", "offSm", "offMd", "offLg", "classes", "htmlContent"] }, { kind: "component", type: RowComponent, selector: "citi-row, [citi-row]", inputs: ["classes", "htmlContent"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TileContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-tile-container', standalone: false, template: `
  <citi-row *ngIf="tileContainer" classes="tile-container">
    <citi-column md="9" xs="12" classes="left-container">
        <div class="left-content-body">
            <ng-content select="[leftcontent]"></ng-content>
        </div>
    </citi-column>
    <citi-column *ngIf="isRightPanelRequired" md="3" xs="12" classes="right-container">
        <div class="right-content-body">
            <ng-content select="[rightcontent]"></ng-content>
        </div>
    </citi-column>
  </citi-row>
  <citi-row *ngIf="fullBodyContainer" classes="full-container">
      <citi-column xs="12" classes="main-container">
          <div class="full-content-body">
              <ng-content select="[maincontent]"></ng-content>
          </div>
      </citi-column>
  </citi-row>
  `, styles: [":host-context(.theme-gray) ::ng-deep .tile-container .left-container .left-content-body,:host-context(.theme-gray) ::ng-deep .tile-container .right-container .right-content-body,:host-context(.theme-gray) ::ng-deep .full-container .main-container .full-content-body{background:#fff;padding:40px}:host-context(.theme-gray) ::ng-deep .tile-container .left-container .left-content-body .module-alert-box,:host-context(.theme-gray) ::ng-deep .tile-container .right-container .right-content-body .module-alert-box,:host-context(.theme-gray) ::ng-deep .full-container .main-container .full-content-body .module-alert-box{border:1px solid #eee}:host-context(.theme-light) ::ng-deep .tile-container .left-container .left-content-body,:host-context(.theme-light) ::ng-deep .tile-container .right-container .right-content-body,:host-context(.theme-light) ::ng-deep .full-container .main-container .full-content-body{background:#eee;padding:40px}:host-context(.theme-light) ::ng-deep .tile-container .left-container .left-content-body .module-alert-box,:host-context(.theme-light) ::ng-deep .tile-container .right-container .right-content-body .module-alert-box,:host-context(.theme-light) ::ng-deep .full-container .main-container .full-content-body .module-alert-box{border:1px solid #fff}:host-context(.cbolui-ddl.theme-gray) ::ng-deep .tile-container .left-container,:host-context(.cbolui-ddl.theme-gray) ::ng-deep .tile-container .right-container,:host-context(.cbolui-ddl.theme-gray) ::ng-deep .full-container .main-container{padding-top:20px;padding-bottom:20px}\n"] }]
        }], propDecorators: { tileContainer: [{
                type: Input
            }], fullBodyContainer: [{
                type: Input
            }], isRightPanelRequired: [{
                type: Input
            }] } });

const SectionColorErrorMessage = 'CITI-SECTION: A valid color string was not provided to the sectionColor input.';
const ContentColorErrorMessage = 'CITI-SECTION: A valid color string was not provided to the contentColor input.';
const white = '#FFFFFF';
const black = '#333333';
const colorRegex = /^#([\dA-Fa-f]{6}|[\dA-Fa-f]{3})/;
;
;
function isValidString(string) {
    // Return if the type of the value provided is a string and its length is greater than 0 (has at least 1 character)
    return typeof string === 'string' && string.length > 0;
}
function isValidColor(color) {
    return isValidString(color) && color.match(colorRegex) !== null;
}
function getColorFromString(color, colors) {
    // If the string is a hex value return the string as the proper color
    if (color.match(colorRegex))
        return color;
    // If the string is a color name return the hex value of the named color from the colors object
    if (Object.keys(colors).map((key, value) => key).indexOf(color) !== -1)
        return colors[color];
    // Return undefined (fail) if neither of the above are true
    return undefined;
}
/**
 * @Component: Section
 * @Section: GridLayouts
 * @description This is the section component and is used to leverage the full-page theme
*/
class SectionComponent {
    constructor() {
        // List of colors by name that include a background color and font color
        this.colors = {
            'CitiBlue': { backgroundColor: '#056DAE', fontColor: white },
            'DarkBlue': { backgroundColor: '#002A54', fontColor: white },
            'NearBlack': { backgroundColor: '#333333', fontColor: white },
            'LightGray': { backgroundColor: '#EEEEEE', fontColor: black },
            'White': { backgroundColor: '#FFFFFF', fontColor: black },
            'DarkGray': { backgroundColor: '#666666', fontColor: white },
            'Seafoam': { backgroundColor: '#00B0B9', fontColor: black },
            'Teal': { backgroundColor: '#007377', fontColor: white },
            'Navy': { backgroundColor: '#002D72', fontColor: white },
            'Magenta': { backgroundColor: '#C6007E', fontColor: white },
            'DarkMagenta': { backgroundColor: '#890C58', fontColor: white },
            'BrightBlue': { backgroundColor: '#00BDF2', fontColor: black },
            'Citigold1': { backgroundColor: '#B5995C', fontColor: black },
            'Citigold2': { backgroundColor: '#8E6F32', fontColor: white },
            'Citigold3': { backgroundColor: '#745B2D', fontColor: white },
            'CitiPriority1': { backgroundColor: '#0E2A48', fontColor: white },
            'CitiPriority2': { backgroundColor: '#091022', fontColor: white },
            'Green': { backgroundColor: '#006E0A', fontColor: white },
            'Red': { backgroundColor: '#D60000', fontColor: white }
        };
    }
    // #endregion
    // #region -------------- Methods ------------- //
    setColorOrError(color, backgroundProperty, fontProperty, error) {
        // If we got all three values
        if (isValidString(color) && isValidString(backgroundProperty) && isValidString(fontProperty) && isValidString(error)) {
            // Get the color hex value using the string provided
            const colors = getColorFromString(color, this.colors);
            // If we got a valid color object
            if (typeof colors === 'object' && isValidColor(colors.backgroundColor) && isValidColor(colors.fontColor)) {
                // Set it on the section/content
                this[backgroundProperty] = colors.backgroundColor;
                // Set it on the font
                this[fontProperty] = colors.fontColor;
            }
            else if (isValidColor(colors)) {
                // Set the background color
                this[backgroundProperty] = colors;
            }
            else if (isDevMode) {
                // Otherwise error out
                console.error(error, this);
            }
        }
        else if (isDevMode) {
            // Otherwise error out
            console.error(error, this);
        }
    }
    // #endregion
    // #region ---------- Inputs/Outputs ---------- //
    /**
     * @property sectionColor
     * @description Sets the full-page color.  The full-page color spans the width of the entire page.
     * @return string: CamelCasedCitiColorName or #XXX or #XXXXXX
     * Colors names available at https://ddl-style-guide.cfapps-gt1-dev.nam.nsroot.net/color.html
     */
    set sectionColor(color) {
        // Set the color
        this.setColorOrError(color, '_sectionColor', '_fontColor', SectionColorErrorMessage);
    }
    /**
     * @property contentColor
     * @description Sets the content-area color. The content-area color spans the width of the content only.
     * @return string: CamelCasedCitiColorName or #XXX or #XXXXXX
     */
    set contentColor(color) {
        // Set the color
        this.setColorOrError(color, '_contentColor', '_fontColor', ContentColorErrorMessage);
    }
    ;
    /**
     * @property fontColor
     * @description Sets the font color for the section.
     * Font colors are set automatically when using a citi color name.
     * If setting font color manually ensure that sufficient contrast exists between background color and font color
     * A tool for checking this is available at https://webaim.org/resources/contrastchecker/
     * @return string: #XXX or #XXXXXX
     */
    set fontColor(color) {
        // Set the color
        if (isValidColor(color))
            this._fontColor = color;
    }
    ;
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SectionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: SectionComponent, isStandalone: false, selector: "citi-section", inputs: { sectionColor: "sectionColor", contentColor: "contentColor", fontColor: "fontColor" }, ngImport: i0, template: `
        <div class="section-wrapper" [ngStyle]="{ 'background-color': _sectionColor}">
            <div class="section-content-wrapper" [ngStyle]="{ 'background-color': _contentColor, 'color': _fontColor }">
                <ng-content></ng-content>
            </div>
        </div>
    `, isInline: true, styles: [".section-wrapper .section-content-wrapper{max-width:1440px;margin:0 auto;padding:0 20px}\n"], dependencies: [{ kind: "directive", type: i3$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SectionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-section', standalone: false, template: `
        <div class="section-wrapper" [ngStyle]="{ 'background-color': _sectionColor}">
            <div class="section-content-wrapper" [ngStyle]="{ 'background-color': _contentColor, 'color': _fontColor }">
                <ng-content></ng-content>
            </div>
        </div>
    `, styles: [".section-wrapper .section-content-wrapper{max-width:1440px;margin:0 auto;padding:0 20px}\n"] }]
        }], propDecorators: { sectionColor: [{
                type: Input
            }], contentColor: [{
                type: Input
            }], fontColor: [{
                type: Input
            }] } });

const COMPONENTS$5 = [
    ColumnComponent,
    RowComponent,
    SectionComponent,
];
const MODULES$6 = [
    CommonModule,
    ContentHtml
];
class Grid {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Grid, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: Grid, declarations: [ColumnComponent,
            RowComponent,
            SectionComponent], imports: [CommonModule,
            ContentHtml], exports: [ColumnComponent,
            RowComponent,
            SectionComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Grid, imports: [MODULES$6] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Grid, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$6],
                    declarations: [...COMPONENTS$5],
                    exports: [...COMPONENTS$5]
                }]
        }] });

const COMPONENTS$4 = [
    TileContainerComponent
];
const MODULES$5 = [
    CommonModule,
    Grid
];
class TileContainer {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TileContainer, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: TileContainer, declarations: [TileContainerComponent], imports: [CommonModule,
            Grid], exports: [TileContainerComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TileContainer, imports: [MODULES$5] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TileContainer, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$5],
                    declarations: [...COMPONENTS$4],
                    exports: [...COMPONENTS$4]
                }]
        }] });

let nextUniqueId$3 = 0;
const TEXT_AREA_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TextareaComponent),
    multi: true
};
class TextareaComponent {
    get isFocused() {
        return this._isFocused;
    }
    get hostClasses() {
        return `citi-textarea row ${this.textareavalue || !this.placeHolder ? 'has-value' : ''} ${this._isFocused ? 'is-focused' : ''}`;
    }
    /**
     * Reference to the bound FormControl
     */
    get control() {
        return this.reactiveMode ? this._control : null;
    }
    /**
     * Whether or not the component was set up with a form control
     */
    get reactiveMode() {
        return !!this._control;
    }
    get errorMessage() {
        return this._errorMessage;
    }
    set error(isError) {
        if (isError === this._error)
            return;
        this._error = isError;
        !this.reactiveMode && isError
            ? this.toggleError(true, 'custom')
            : this.toggleError(false);
    }
    get error() {
        return this._error;
    }
    /**
     * This defines the custom error state of the input. Takes precedence over other validation types.
     * @default false
     */
    set hasCustomError(useCustom) {
        if (isDevMode)
            console.warn('Deprecated: please trigger the error state using the [(error)] binding');
    }
    get idStr() {
        return this._id;
    }
    set idStr(value) {
        this._id = value || this._uid;
    }
    get sizeClass() {
        return ({
            fullwidth: 'col-xs-12',
            large: 'col-xs-10 col-md-8 col-lg-8',
            medium: 'col-xs-8 col-sm-9 col-md-6',
            small: 'col-xs-7 col-sm-6 col-md-4',
            xsmall: 'col-xs-6 col-sm-3 col-md-2',
            none: ''
        }[this.size] || 'col-xs-12');
    }
    // #endregion
    // #region // ---------- Lifecycle Hooks
    constructor(validationService, liveAnnouncer, inj) {
        this.validationService = validationService;
        this.liveAnnouncer = liveAnnouncer;
        this.inj = inj;
        // #region // ---------- Properties
        this.unsub$ = new Subject();
        this._errorMessage = null;
        // #endregion
        // #region // ---------- Input/Output
        this.textareavalue = null;
        this.textareavalueChange = new EventEmitter();
        this.customErrorMessage = 'Please provide a "customErrorMessage"';
        this._error = false;
        this.errorChange = new EventEmitter();
        this.readOnly = false;
        this.disabled = false;
        this.blur = new EventEmitter();
        this.focus = new EventEmitter();
        this.isRequired = false;
        this.maxLength = null;
        this.requiredError = 'This is a required field';
        this.validateOnBlur = true;
        this._hasCustomError = false;
        this._uid = `citi-textarea-${nextUniqueId$3++}`;
        this.name = null;
        this.size = 'fullwidth';
        // #endregion
        // #region - FORM BINDING METHODS
        // All methods below are required for the Value Control Accessor interface to work with [(ngModel)]
        this.onChange = (value) => { };
        this.onTouched = () => { };
        this.idStr = this.idStr;
    }
    ngOnInit() {
        /**
         * Will create a reference to the FormControl if a reactive form is setup, otherwise
         * it will assume that the consumer is using the FomContainer / Validation Service
         */
        try {
            this._control = this.inj.get(NgControl);
        }
        catch {
            this.validationService.validateNow
                .pipe(takeUntil(this.unsub$), tap(() => this.validate()))
                .subscribe();
        }
        // Double announcing on desktop so removing.
        // TODO: Check to see if this is required to annouce on mobile only.
        // this.errorChange
        //   .pipe(
        //     takeUntil(this.unsub$),
        //     debounceTime(1250),
        //     tap(error => this.announceError(error))
        //   )
        //   .subscribe();
    }
    ngOnDestroy() {
        this.validationService.removeError(this);
        this.unsub$.next();
        this.unsub$.complete();
    }
    // #endregion
    // #region // ---------- Methods
    get errorsInSpan() {
        return this.errorSpan.nativeElement.textContent;
    }
    /**
     * Manually triggers an error state
     * @param isErrorer - whether to turn the error on or off
     * @param errorType the nature of the error, which determines the error message
     * @example
     *  <citi-textarea #templateVar></citi-textarea>
     *
     *  @ViewChild('templateVar') textarea: ReactiveTextareaComponent;
     *
     *  textarea.toggleError(true, 'custom')
     */
    toggleError(isErrored, errorType = 'custom') {
        this._error = isErrored;
        this.errorChange.emit(isErrored);
        isErrored
            ? this.validationService.addError(this)
            : this.validationService.removeError(this);
        this._errorMessage = errorType.includes('required')
            ? this.requiredError
            : this.customErrorMessage;
    }
    announceError(error) {
        if (this.error)
            this.liveAnnouncer.announce(this.errorsInSpan);
    }
    /**
     * Triggers the baked-in, non-reactive validation
     */
    validate() {
        if (this.reactiveMode)
            return;
        if (this.isRequired && !this.textareavalue)
            this.toggleError(true, 'required');
        // TODO: evaluate when this is called and if it is needed
        else if (this.error && !Boolean(this.textareavalue))
            this.toggleError(true, 'custom');
        else
            this.toggleError(false);
        // TODO: Check if this call actually works and
        // see if this is required to annouce on mobile only.
        // this.announceError();
    }
    /**
     * Resets the apperance and state of the input to initial
     */
    reset() {
        this.toggleError(false);
        this.textareavalue = null;
        if (this.reactiveMode)
            this.control.reset();
    }
    castFocus() {
        this.textarea.nativeElement.focus();
    }
    onFocus(e) {
        this._isFocused = true;
        this.focus.emit(e);
    }
    onBlur(e) {
        this.onTouched();
        this._isFocused = false;
        this.blur.emit(e);
        this.reactiveValidate();
        if (this.validateOnBlur)
            this.validate();
    }
    onKeyup() {
        this.reactiveValidate();
    }
    textChange(value) {
        this.writeValue(value);
    }
    // #endregion
    // #region // ---------- Reactive Forms Methods
    /**
     * Helper method to determine if the bound FormControl's errors object contains an error
     * @param errorName key of the FormControl error object
     * @category reactive
    */
    hasError(errorName) {
        return this.reactiveMode ? this.control.hasError(errorName) : false;
    }
    /**
     * Whether the textarea has been blured. Only works when bound to a FormControl
     * @Type boolean
     * @category reactive
    */
    get touched() {
        return this.reactiveMode ? this.control.touched : false;
    }
    /**
     * Triggers the reactive validation
     * @category reactive
    */
    reactiveValidate() {
        if (!this.reactiveMode)
            return;
        const isError = (!this.validateOnBlur || this.touched) && this.control.invalid;
        if (this._error != isError || this.reactiveMode)
            this.errorChange.emit(isError);
        this._error = isError;
    }
    // Allows Angular to update the model.
    // Update the model and changes needed for the view here.
    writeValue(value) {
        this.textareavalue = value;
        this.textareavalueChange.emit(value);
        //      !!!!    WARNING: DO NOT REMOVE    !!!!
        // this is actually necessary for the (reactive) control to update
        this.onChange(value);
    }
    // Allows Angular to register a function to call when the model changes.
    // Save the function as a property to call later here.
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // Allows Angular to disable the input.
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TextareaComponent, deps: [{ token: ValidationService }, { token: CitiLiveAnnouncer }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: TextareaComponent, isStandalone: false, selector: "citi-textarea", inputs: { textareavalue: "textareavalue", customErrorMessage: "customErrorMessage", error: "error", placeHolder: "placeHolder", labelText: "labelText", describedby: "describedby", readOnly: "readOnly", disabled: "disabled", isRequired: "isRequired", maxLength: "maxLength", requiredError: "requiredError", validateOnBlur: "validateOnBlur", hasCustomError: "hasCustomError", idStr: "idStr", name: "name", size: "size" }, outputs: { textareavalueChange: "textareavalueChange", errorChange: "errorChange", blur: "blur", focus: "focus" }, host: { properties: { "class": "this.hostClasses" } }, providers: [TEXT_AREA_VALUE_ACCESSOR], viewQueries: [{ propertyName: "textarea", first: true, predicate: ["textarea"], descendants: true }, { propertyName: "errorSpan", first: true, predicate: ["errorSpan"], descendants: true }], ngImport: i0, template: `
    <div [attr.class]="sizeClass + ' form-group'">
      <label
        class="text-input-label"
        [attr.for]="idStr"
        [innerHtml]="labelText"
      ></label>

      <textarea
        #textarea
        class="form-control"
        [ngModel]="textareavalue"
        (ngModelChange)="textChange($event)"
        [ngClass]="{ 'validation-input-danger': error, 'readOnly': readOnly, 'disabled': disabled }"
        [attr.id]="idStr"
        [attr.name]="name || idStr"
        [readonly]="readOnly"
        [disabled]="disabled"
        [attr.maxlength]="maxLength"
        [attr.required]="isRequired ? true : null"
        [attr.aria-describedby]="
          error ? idStr + '-error' : describedby ? describedby : null
        "
        [attr.aria-invalid]="error ? true : null"
        [attr.placeholder]="placeHolder"
        (keyup)="onKeyup()"
        (focus)="onFocus($event)"
        (blur)="onBlur($event)"
      >
      </textarea>

      <span
        #errorSpan
        class="validation-message-danger"
        [attr.id]="idStr + '-error'"
      >
        <span *ngIf="error && errorMessage" [innerHtml]="errorMessage"></span>
        <ng-content></ng-content>
      </span>
    </div>
  `, isInline: true, styles: [":host{display:block}label{opacity:0}.is-focused label,.has-value label{opacity:1}.form-control.readOnly,.form-control.disabled{background:#fff!important;border:1px solid #666}:host .light .form-control{background:#fff}:host .light .form-control.readOnly{background-color:#eee!important}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TextareaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-textarea', standalone: false, providers: [TEXT_AREA_VALUE_ACCESSOR], template: `
    <div [attr.class]="sizeClass + ' form-group'">
      <label
        class="text-input-label"
        [attr.for]="idStr"
        [innerHtml]="labelText"
      ></label>

      <textarea
        #textarea
        class="form-control"
        [ngModel]="textareavalue"
        (ngModelChange)="textChange($event)"
        [ngClass]="{ 'validation-input-danger': error, 'readOnly': readOnly, 'disabled': disabled }"
        [attr.id]="idStr"
        [attr.name]="name || idStr"
        [readonly]="readOnly"
        [disabled]="disabled"
        [attr.maxlength]="maxLength"
        [attr.required]="isRequired ? true : null"
        [attr.aria-describedby]="
          error ? idStr + '-error' : describedby ? describedby : null
        "
        [attr.aria-invalid]="error ? true : null"
        [attr.placeholder]="placeHolder"
        (keyup)="onKeyup()"
        (focus)="onFocus($event)"
        (blur)="onBlur($event)"
      >
      </textarea>

      <span
        #errorSpan
        class="validation-message-danger"
        [attr.id]="idStr + '-error'"
      >
        <span *ngIf="error && errorMessage" [innerHtml]="errorMessage"></span>
        <ng-content></ng-content>
      </span>
    </div>
  `, styles: [":host{display:block}label{opacity:0}.is-focused label,.has-value label{opacity:1}.form-control.readOnly,.form-control.disabled{background:#fff!important;border:1px solid #666}:host .light .form-control{background:#fff}:host .light .form-control.readOnly{background-color:#eee!important}\n"] }]
        }], ctorParameters: () => [{ type: ValidationService }, { type: CitiLiveAnnouncer }, { type: i0.Injector }], propDecorators: { hostClasses: [{
                type: HostBinding,
                args: ['class']
            }], textarea: [{
                type: ViewChild,
                args: ['textarea']
            }], errorSpan: [{
                type: ViewChild,
                args: ['errorSpan']
            }], textareavalue: [{
                type: Input
            }], textareavalueChange: [{
                type: Output
            }], customErrorMessage: [{
                type: Input
            }], error: [{
                type: Input
            }], errorChange: [{
                type: Output
            }], placeHolder: [{
                type: Input
            }], labelText: [{
                type: Input
            }], describedby: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], disabled: [{
                type: Input
            }], blur: [{
                type: Output
            }], focus: [{
                type: Output
            }], isRequired: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], requiredError: [{
                type: Input
            }], validateOnBlur: [{
                type: Input
            }], hasCustomError: [{
                type: Input
            }], idStr: [{
                type: Input
            }], name: [{
                type: Input
            }], size: [{
                type: Input
            }] } });

class TextArea {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TextArea, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: TextArea, declarations: [TextareaComponent], imports: [CommonModule, FormsModule], exports: [TextareaComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TextArea, imports: [CommonModule, FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TextArea, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule],
                    declarations: [TextareaComponent],
                    exports: [TextareaComponent]
                }]
        }] });

class TextHeaderComponent {
    /**
     * What level the header should be.
     * This should be heirarchical to other headers in the document starting at h2.
     */
    set level(level) {
        const newLevel = (typeof level === 'string') ? +level : level;
        if (!newLevel || isNaN(+newLevel) || newLevel < 1 || newLevel > 6) {
            if (isDevMode)
                console.warn('TEXT_HEADER: The value passed to level should be a number between 1 and 6');
        }
        else
            this._headerLevel = newLevel;
    }
    get level() {
        return this._headerLevel;
    }
    /**
     * number corresponding to the level of header whose style you want to use. Not heirarchical.
     */
    set style(level) {
        const newLevel = (typeof level === 'string') ? +level : level;
        if (!newLevel || isNaN(+newLevel) || newLevel < 1 || newLevel > 6) {
            if (isDevMode)
                console.warn('TEXT_HEADER: The value passed to style should be a number between 1 and 6 and correspond to the header level whose style you want to use.');
            this._styleLevel = null;
        }
        else
            this._styleLevel = newLevel;
    }
    get style() {
        return this._styleLevel;
    }
    /**
     * If the header should be bold.
     */
    set bold(isBold) {
        this._isBold = isBold;
    }
    get bold() {
        return this._isBold;
    }
    set class(_class) {
        this._classString = _class;
    }
    get class() {
        return this._classString;
    }
    // #endregion
    // #region =========== Constructor / Life-cycle Hooks ========== //
    constructor() {
        // #endregion
        // #region =========== @Input ========== //
        /**
         * Sets aria-level value. This can be used to overwrite the automatic level calculation
         * of the user agent
         * @deprecated
         */
        this.ariaLevel = null;
        this.htmlContent = '';
    }
    ngAfterViewInit() {
        setTimeout(() => {
            if (this.html)
                this.htmlContent = this.html;
        }, 0);
    }
    ngOnChanges(changes) {
        for (const propName in changes) {
            switch (propName) {
                case 'htmlContent':
                    // When using htmlContent on outerHTML, it will not update correctly on
                    // subsequent changes, unless removed and re-added to DOM
                    this._hideHtmlContent = true;
                    setTimeout(() => {
                        this._hideHtmlContent = false;
                    }, 0);
                    break;
            }
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TextHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: TextHeaderComponent, isStandalone: false, selector: "citi-text-header", inputs: { ariaLevel: "ariaLevel", level: "level", style: "style", bold: "bold", class: "class", html: "html", ariaRole: "ariaRole", htmlContent: "htmlContent" }, usesOnChanges: true, ngImport: i0, template: `
    <ng-template #contentTemplate>
      <ng-container *ngIf="htmlContent; else projectedContent">
        <div [outerHTML]="htmlContent"></div>
      </ng-container>
      <ng-template #projectedContent>
        <ng-content></ng-content>
      </ng-template>
    </ng-template>

    <ng-container *ngIf="!_hideHtmlContent">
      <h1 *ngIf="level == 1"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h1>
      <h2 *ngIf="level == 2"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h2>
      <h3 *ngIf="level == 3"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h3>
      <h4 *ngIf="level == 4"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h4>
      <h5 *ngIf="level == 5"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h5>
      <h6 *ngIf="level == 6"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h6>
    </ng-container>
  `, isInline: true, styles: [".header-level-1,.header-level-2,.header-level-3,.header-level-4,.header-level-5,.header-level-6{margin-bottom:20px;line-height:normal}.header-level-1{font-size:4.25rem;line-height:4.75rem;text-transform:none;letter-spacing:initial}.header-level-2{font-size:2.625rem;line-height:3.125rem;text-transform:none;letter-spacing:initial}.header-level-3{font-size:1.625rem;line-height:2rem;text-transform:none;letter-spacing:initial}.header-level-4{font-size:1.313rem;line-height:1.688rem;text-transform:none;letter-spacing:initial}.header-level-5{font-size:1rem;letter-spacing:2px;line-height:1.4rem;text-transform:uppercase}.header-level-6{font-size:.75rem;letter-spacing:2px;line-height:1.125rem;text-transform:uppercase}.bold{font-family:Interstate_Bold,sans-serif}.html-content-hidden{position:absolute;width:1px;height:1px;opacity:.1;overflow:hidden;pointer-events:none}@media (max-width: 991px){.header-level-1{font-size:2.625rem!important}.header-level-2{font-size:1.625rem!important}.header-level-3{font-size:1.313rem!important}.header-level-4{font-size:1rem!important}.header-level-5{font-size:1rem!important;text-transform:uppercase;letter-spacing:2px}.header-level-6{font-size:.75rem!important;text-transform:uppercase;letter-spacing:2px}}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TextHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-text-header', standalone: false, template: `
    <ng-template #contentTemplate>
      <ng-container *ngIf="htmlContent; else projectedContent">
        <div [outerHTML]="htmlContent"></div>
      </ng-container>
      <ng-template #projectedContent>
        <ng-content></ng-content>
      </ng-template>
    </ng-template>

    <ng-container *ngIf="!_hideHtmlContent">
      <h1 *ngIf="level == 1"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h1>
      <h2 *ngIf="level == 2"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h2>
      <h3 *ngIf="level == 3"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h3>
      <h4 *ngIf="level == 4"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h4>
      <h5 *ngIf="level == 5"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h5>
      <h6 *ngIf="level == 6"
        [ngClass]="[style ? 'header-level-' + style : '', bold ? 'bold' : '', class ? class : '']"
        [attr.role]="(ariaRole? ariaRole : null)"
        [attr.aria-level]="(ariaLevel? ariaLevel : null)"
      ><ng-container *ngTemplateOutlet="contentTemplate"></ng-container></h6>
    </ng-container>
  `, styles: [".header-level-1,.header-level-2,.header-level-3,.header-level-4,.header-level-5,.header-level-6{margin-bottom:20px;line-height:normal}.header-level-1{font-size:4.25rem;line-height:4.75rem;text-transform:none;letter-spacing:initial}.header-level-2{font-size:2.625rem;line-height:3.125rem;text-transform:none;letter-spacing:initial}.header-level-3{font-size:1.625rem;line-height:2rem;text-transform:none;letter-spacing:initial}.header-level-4{font-size:1.313rem;line-height:1.688rem;text-transform:none;letter-spacing:initial}.header-level-5{font-size:1rem;letter-spacing:2px;line-height:1.4rem;text-transform:uppercase}.header-level-6{font-size:.75rem;letter-spacing:2px;line-height:1.125rem;text-transform:uppercase}.bold{font-family:Interstate_Bold,sans-serif}.html-content-hidden{position:absolute;width:1px;height:1px;opacity:.1;overflow:hidden;pointer-events:none}@media (max-width: 991px){.header-level-1{font-size:2.625rem!important}.header-level-2{font-size:1.625rem!important}.header-level-3{font-size:1.313rem!important}.header-level-4{font-size:1rem!important}.header-level-5{font-size:1rem!important;text-transform:uppercase;letter-spacing:2px}.header-level-6{font-size:.75rem!important;text-transform:uppercase;letter-spacing:2px}}\n"] }]
        }], ctorParameters: () => [], propDecorators: { ariaLevel: [{
                type: Input
            }], level: [{
                type: Input
            }], style: [{
                type: Input
            }], bold: [{
                type: Input
            }], class: [{
                type: Input
            }], html: [{
                type: Input
            }], ariaRole: [{
                type: Input
            }], htmlContent: [{
                type: Input
            }] } });

const MODULES$4 = [
    CommonModule,
    ContentHtml
];
class TextHeader {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TextHeader, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: TextHeader, declarations: [TextHeaderComponent], imports: [CommonModule,
            ContentHtml], exports: [TextHeaderComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TextHeader, imports: [MODULES$4] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: TextHeader, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$4],
                    declarations: [TextHeaderComponent],
                    exports: [TextHeaderComponent]
                }]
        }] });

const NAVIGATOR = new InjectionToken('NavigatorToken');
function navigatorFactory(platformId) {
    return isPlatformBrowser(platformId) ? navigator : null;
}
const NAVIGATOR_PROVIDER = {
    provide: NAVIGATOR,
    useFactory: navigatorFactory,
    deps: [PLATFORM_ID]
};

/**
 * @name executeOnStable
 * @description Waits until the microtask queue is empty and then executes code. Less hacky than setTimout and doesn't force another digest
 * @param NgZone ngZone the local NgZone context
 * @param Function func The code to execute
 * @example
 * executeOnStable(this.ngZone, () => this.input.nativeElement.focus())
 */
function executeOnStable(ngZone, func) {
    ngZone.runOutsideAngular(() => {
        ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => func());
    });
}

let nextUniqueId$2 = 0;
/**
 * @Component: Pagination
 * @description Automated generation of consecutive numbers to
 * identify the sequential order of pages
 */
class PaginationItem {
    constructor(number, isActive, isEllipsis, ellipsisPosition, hideEllipsis, id, ariaLabel) {
        this.ellipsisPosition = 'alter-front | alter-back | default';
        this.number = number;
        this.isActive = isActive;
        this.isEllipsis = isEllipsis;
        this.ellipsisPosition = ellipsisPosition;
        this.hideEllipsis = hideEllipsis;
        this.id = id;
        this.ariaLabel = ariaLabel;
    }
}
class Ellipses {
    constructor(front, back) {
        this.front = front;
        this.back = back;
    }
}
class PaginationComponent {
    get activePageID() {
        return this._id;
    }
    set activePageID(value) {
        this._id = value || this._uid;
    }
    constructor(_announcer, el, _ngZone, _navigator) {
        this._announcer = _announcer;
        this.el = el;
        this._ngZone = _ngZone;
        this._navigator = _navigator;
        this.visibleEllipsis = new PaginationItem(null, null, true, 'default', false, null, null);
        this.hiddenEllipsis = new PaginationItem(null, null, true, 'default', true, null, null);
        this.mobileFrontHiddenEllipsis = new PaginationItem(null, null, true, 'alter-front', true, null, null);
        this.mobileEndHiddenEllipsis = new PaginationItem(null, null, true, 'alter-back', true, null, null);
        this.pages = [];
        this.disablePrevButton = false;
        this.disableNextButton = false;
        this._isNavButtonClicked = false;
        this.class = 'citi-pagination';
        /**
         * @property ariaLabelPrefix
         * @description Pagination label prefix string for screen reader
         * @return string
         */
        this.ariaLabelPrefix = 'Page';
        /**
         * @property prevAriaLabel
         * @description Previous button label string for screen reader
         * @return string
         */
        this.prevAriaLabel = 'Previous';
        /**
         * @property nextAriaLabel
         * @description Next button label string for screen reader
         * @return string
         */
        this.nextAriaLabel = 'Next';
        this._uid = `citi-pagination-${nextUniqueId$2++}`;
        /**
         * @name activePageNumberChange
         * @description Emitted when a page in the pagination is active
         * @return Object
         * @Type: EventEmitter<number>
         */
        this.activePageNumberChange = new EventEmitter();
        /**
         * @property showNavigationButtons
         * @description Option to show previous/next button when page is less than 10
         * @Type: boolean
         */
        this.showNavigationButtons = false;
    }
    ngOnInit() {
        this.activePageID = this.activePageID;
    }
    ngOnChanges() {
        this.generatePages();
    }
    generatePages() {
        if (this.pageCount <= 0) {
            return new Error('Pagination configuration not accounted for.');
        }
        this.pages = this.sequentialPages;
        this.disablePrevButton = this.activePageNumber === 1;
        this.disableNextButton = this.activePageNumber === +this.pageCount;
        const ellipses = this.paginationEllipses;
        this.pages = [
            this.firstPageItem,
            ellipses.front,
            ...this.internalPagesItem(this.pages),
            ellipses.back,
            this.lastPageItem
        ].filter(page => page != null);
    }
    get sequentialPages() {
        return Array.apply(null, { length: this.pageCount }).map((val, index) => new PaginationItem(index + 1, // (display) number
        this.activePageNumber === index + 1, // isActive
        false, // isEllipsis
        'default', // ellipsisPosition
        false, // hideEllipsis
        `page-${index + 1}`, // id
        `${this.pageAriaLabel(this.activePageNumber === index + 1)} ${index +
            1} of ${this.pageCount}` // ariaLabel
        ));
    }
    get isButtonDisabledAndClicked() {
        return (this._isNavButtonClicked &&
            (this.activePageNumber === 1 || this.activePageNumber === +this.pageCount));
    }
    pageAriaLabel(isActive) {
        return isActive ? `current ${this.ariaLabelPrefix}` : this.ariaLabelPrefix;
    }
    hideNavButtons() {
        return this.pageCount < 10 && !this.showNavigationButtons;
    }
    get firstPageItem() {
        return this.pages[0];
    }
    get lastPageItem() {
        return this.pageCount > 1 ? this.pages[this.pageCount - 1] : null;
    }
    internalPagesItem(totalPages) {
        const internalPageItems = totalPages.slice(1, -1);
        console.log(internalPageItems);
        if (totalPages.length < 8)
            return internalPageItems;
        else if (this.activePageNumber <= 5)
            return internalPageItems.filter(page => page.number <= 5);
        else if (this.isCurrentLocationGreaterThanPage5 &&
            this.isCurrentLocationLessThan5PagesAway)
            return internalPageItems.splice(-4);
        else if (this.isCurrentLocationGreaterThanPage5)
            return internalPageItems.filter(page => page.number >= this.activePageNumber - 1 &&
                page.number <= this.activePageNumber + 1);
        else
            throw new Error('Pagination configuration not accounted for.');
    }
    get paginationEllipses() {
        if (this.pageCount < 8)
            return new Ellipses(this.mobileFrontHiddenEllipsis, this.mobileEndHiddenEllipsis);
        else if (this.activePageNumber <= 5)
            return new Ellipses(this.hiddenEllipsis, this.visibleEllipsis);
        else if (this.isCurrentLocationGreaterThanPage5 &&
            this.isCurrentLocationLessThan5PagesAway)
            return new Ellipses(this.visibleEllipsis, this.hiddenEllipsis);
        else if (this.isCurrentLocationGreaterThanPage5)
            return new Ellipses(this.visibleEllipsis, this.visibleEllipsis);
        else
            throw new Error('Pagination configuration not accounted for.');
    }
    get isCurrentLocationGreaterThanPage5() {
        return this.activePageNumber > 5;
    }
    get isCurrentLocationLessThan5PagesAway() {
        return this.pageCount - this.activePageNumber < 5;
    }
    get isCurrentLocationLessThan3PagesAway() {
        return this.pageCount - this.activePageNumber < 3;
    }
    get isCurrentLocationGreaterThanPage3() {
        return this.activePageNumber > 3;
    }
    arrowClick(index) {
        const currentPage = this.activePageNumber + index;
        let announceString;
        if (currentPage === 1) {
            announceString = `current ${this.ariaLabelPrefix} is ${currentPage} of ${this.pageCount} first page`;
        }
        else if (currentPage === this.pageCount) {
            announceString = `current ${this.ariaLabelPrefix} is ${currentPage} of ${this.pageCount} last page`;
        }
        else {
            announceString = `current ${this.ariaLabelPrefix} is ${currentPage} of ${this.pageCount}`;
        }
        if (currentPage < 1) {
            this._announcer.announce(`${this.prevAriaLabel} button unavailable`);
            return;
        }
        else if (currentPage > this.pageCount) {
            this._announcer.announce(`${this.nextAriaLabel} button unavailable`);
            return;
        }
        else {
            this._announcer.announce(announceString);
            this.setActivePage(currentPage, true);
            return;
        }
    }
    arrowFocus(index, pageLabel) {
        const currentPage = this.activePageNumber + index;
        if (currentPage >= 1 && currentPage <= this.pageCount) {
            setTimeout(() => {
                this._announcer.announce(pageLabel);
            }, 100);
        }
    }
    pageClick(currentPage) {
        this.setActivePage(currentPage);
        this.reCastFocus();
    }
    setActivePage(currentPage, isNavButtonClicked = false) {
        this.activePageNumber = currentPage;
        this.activePageNumberChange.emit(this.activePageNumber);
        this._isNavButtonClicked = isNavButtonClicked;
        if ((!this.isAndroidDevice && !this.isiOSDevice) ||
            this._isNavButtonClicked) {
            //prevent iOSDevice and Android device from announcing page twices
            this.announceNewCurrent();
        }
    }
    announceNewCurrent() {
        setTimeout(() => {
            this._announcer.announce(`current ${this.ariaLabelPrefix} is ${this.activePageNumber} of ${this.pageCount}`);
        }, 100);
    }
    /**
     * In Mobile, pagination only shows the first 3 pages or the last 3 pages (1,2,3...10 or 1...8,9,10)
     * If current page greater than 3 and less than the last 3 pages then pagination only shows the (1...5..10)
     * If there are more than 6 pages, show ellipses
     */
    hidePageInMobile(page) {
        if (this.pageCount <= 6)
            return false;
        return !this.visiblePages(page) && this.shouldHidePage(page);
    }
    displayHiddenEllipsis() {
        return (this.isCurrentLocationGreaterThanPage5 &&
            !this.isCurrentLocationLessThan5PagesAway);
    }
    visiblePages(page) {
        return (page === this.activePageNumber || page === 1 || page === +this.pageCount);
    }
    shouldHidePage(page) {
        return ((this.isCurrentLocationGreaterThanPage3 || page > 3) &&
            (!this.isCurrentLocationLessThan3PagesAway || this.pageCount - page >= 3));
    }
    hideNavButtonsMobile() {
        return this.pageCount < 7 && !this.showNavigationButtons;
    }
    hideEllipsis(isHiddenEllipsis, ellipsisPosition) {
        return this.pageCount < 10 && this.pageCount > 6
            ? this.hideEllipsIfUnder10Pages(isHiddenEllipsis, ellipsisPosition)
            : isHiddenEllipsis && !this.displayHiddenEllipsis();
    }
    hideEllipsIfUnder10Pages(isHiddenEllipsis, ellipsisPosition) {
        return (this.isCurrentLocationLessThan3PagesAway &&
            ellipsisPosition === 'alter-front') ||
            (!this.isCurrentLocationGreaterThanPage3 &&
                ellipsisPosition === 'alter-back')
            ? false
            : isHiddenEllipsis && !this.displayHiddenEllipsis();
    }
    get isAndroidDevice() {
        if (!this._navigator || !this._navigator.userAgent)
            return false;
        else
            return /(android)/i.test(this._navigator.userAgent);
    }
    get isiOSDevice() {
        if (!this._navigator || !this._navigator.userAgent)
            return false;
        else
            return (!!this._navigator.platform &&
                /(iPad|iPhone|iPod)/i.test(this._navigator.userAgent));
    }
    reCastFocus() {
        executeOnStable(this._ngZone, () => this.el.nativeElement.querySelector('.active').focus());
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PaginationComponent, deps: [{ token: CitiLiveAnnouncer }, { token: i0.ElementRef }, { token: i0.NgZone }, { token: NAVIGATOR }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: PaginationComponent, isStandalone: false, selector: "citi-pagination", inputs: { ariaLabelPrefix: "ariaLabelPrefix", prevAriaLabel: "prevAriaLabel", nextAriaLabel: "nextAriaLabel", activePageNumber: "activePageNumber", activePageID: "activePageID", showNavigationButtons: "showNavigationButtons", pageCount: "pageCount" }, outputs: { activePageNumberChange: "activePageNumberChange" }, host: { properties: { "class": "this.class" } }, usesOnChanges: true, ngImport: i0, template: `
    <nav aria-label="Pagination">
      <ul class="pagination">
        <li>
          <!-- Previous Button-->
          <button
            [attr.aria-label]="!disablePrevButton ? prevAriaLabel: prevAriaLabel + ' ' + 'button unavailable'"
            [attr.id]="_id + '-prev-button'"
            [attr.tabIndex]="!disablePrevButton ?0 : -1"
            [ngClass]="{
              previousButtonIcon: true,
              disabled: disablePrevButton,
              'hidden-lg': hideNavButtons(),
              'hidden-xs': hideNavButtonsMobile()
            }"
            (click)="arrowClick(-1)"
            (focus) = "arrowFocus(1)"
            >
            <citi-icon2
              name="255-chevron-left"
              [svgStyle]="{ 'width.px': 30, 'height.px': 30 }"
              [includeCircle]="true"
              shade="dark"
              ariaLabel="icon"
            >
            </citi-icon2>
          </button>
        </li>

        <ng-container *ngFor="let page of pages">
          <!-- Both Ellipses -->
          <li
            class="page-ellipsis"
            *ngIf="page.isEllipsis"
            [ngClass]="{
              'hidden-lg': page.hideEllipsis,
              'hidden-xs': hideEllipsis(
                page.hideEllipsis,
                page.ellipsisPosition
              )
            }"
          >
            &#8230;
          </li>

          <!-- All Page Items-->
          <li
            class="page-item"
            *ngIf="page.number"
            (click)="pageClick(page.number)"
          >
            <a
              href="javascript:;"
              [attr.aria-label]="(page.number) === 1? page.ariaLabel + ' first page' : (page.number) === pageCount? page.ariaLabel + ' last page' : page.ariaLabel"
              [attr.id]="page.isActive ? _id : null"
              [ngClass]="{
                active: page.isActive,
                'hidden-xs': hideEllipsis(
                page.hideEllipsis,
                page.ellipsisPosition
              )
              }"
            >
              {{ page.number | number: '2.0' }}
            </a>
          </li>
        </ng-container>

        <!-- Next Button-->
        <li>
          <button
            [attr.aria-label]="!disableNextButton ? nextAriaLabel: nextAriaLabel + ' ' + 'button unavailable'"
            [attr.id]="_id + '-next-button'"
            [attr.tabIndex]="!disableNextButton ?0 : -1"
            [ngClass]="{
              nextButtonIcon: true,
              disabled: disableNextButton,
              'hidden-lg': hideNavButtons(),
              'hidden-xs': hideNavButtonsMobile()
            }"
            (click)="arrowClick(1)"
            (focus) = "arrowFocus(1)"
          >
            <citi-icon2
              name="257-chevron-right"
              [svgStyle]="{ 'width.px': 30, 'height.px': 30 }"
              [includeCircle]="true"
              shade="dark"
              ariaLabel="icon"
            >
            </citi-icon2>
          </button>
        </li>
      </ul>
    </nav>
  `, isInline: true, styles: [":host{display:block}.previousButtonIcon ::ng-deep circle,.nextButtonIcon ::ng-deep circle{fill:#056dae!important}.previousButtonIcon ::ng-deep circle:hover,.previousButtonIcon ::ng-deep circle:focus,.nextButtonIcon ::ng-deep circle:hover,.nextButtonIcon ::ng-deep circle:focus{fill:#002a54!important}.previousButtonIcon.disabled ::ng-deep circle,.nextButtonIcon.disabled ::ng-deep circle{fill:#767676!important}.previousButtonIcon,.nextButtonIcon{border:none;margin:0;padding:0;height:30px;background-color:unset}.pagination{display:inline-block;padding-left:0;margin:24px 0;border-radius:6px}.pagination>li{display:inline}.pagination>li>a{min-height:30px;min-width:38px;position:relative;float:left;text-decoration:none;color:#056dae;margin-left:-1px;font-family:Interstate_Light,sans-serif}.pagination>li>a:focus,.pagination>li>a:hover{color:#002a54}.pagination>li>a.active{font-family:Interstate_Bold,sans-serif;color:#000;cursor:default;pointer-events:none}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: Icon2Component, selector: "citi-icon2", inputs: ["role", "idStr", "ariaLabel", "ariaHidden", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "shade", "name"] }, { kind: "pipe", type: i3$2.DecimalPipe, name: "number" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-pagination', standalone: false, template: `
    <nav aria-label="Pagination">
      <ul class="pagination">
        <li>
          <!-- Previous Button-->
          <button
            [attr.aria-label]="!disablePrevButton ? prevAriaLabel: prevAriaLabel + ' ' + 'button unavailable'"
            [attr.id]="_id + '-prev-button'"
            [attr.tabIndex]="!disablePrevButton ?0 : -1"
            [ngClass]="{
              previousButtonIcon: true,
              disabled: disablePrevButton,
              'hidden-lg': hideNavButtons(),
              'hidden-xs': hideNavButtonsMobile()
            }"
            (click)="arrowClick(-1)"
            (focus) = "arrowFocus(1)"
            >
            <citi-icon2
              name="255-chevron-left"
              [svgStyle]="{ 'width.px': 30, 'height.px': 30 }"
              [includeCircle]="true"
              shade="dark"
              ariaLabel="icon"
            >
            </citi-icon2>
          </button>
        </li>

        <ng-container *ngFor="let page of pages">
          <!-- Both Ellipses -->
          <li
            class="page-ellipsis"
            *ngIf="page.isEllipsis"
            [ngClass]="{
              'hidden-lg': page.hideEllipsis,
              'hidden-xs': hideEllipsis(
                page.hideEllipsis,
                page.ellipsisPosition
              )
            }"
          >
            &#8230;
          </li>

          <!-- All Page Items-->
          <li
            class="page-item"
            *ngIf="page.number"
            (click)="pageClick(page.number)"
          >
            <a
              href="javascript:;"
              [attr.aria-label]="(page.number) === 1? page.ariaLabel + ' first page' : (page.number) === pageCount? page.ariaLabel + ' last page' : page.ariaLabel"
              [attr.id]="page.isActive ? _id : null"
              [ngClass]="{
                active: page.isActive,
                'hidden-xs': hideEllipsis(
                page.hideEllipsis,
                page.ellipsisPosition
              )
              }"
            >
              {{ page.number | number: '2.0' }}
            </a>
          </li>
        </ng-container>

        <!-- Next Button-->
        <li>
          <button
            [attr.aria-label]="!disableNextButton ? nextAriaLabel: nextAriaLabel + ' ' + 'button unavailable'"
            [attr.id]="_id + '-next-button'"
            [attr.tabIndex]="!disableNextButton ?0 : -1"
            [ngClass]="{
              nextButtonIcon: true,
              disabled: disableNextButton,
              'hidden-lg': hideNavButtons(),
              'hidden-xs': hideNavButtonsMobile()
            }"
            (click)="arrowClick(1)"
            (focus) = "arrowFocus(1)"
          >
            <citi-icon2
              name="257-chevron-right"
              [svgStyle]="{ 'width.px': 30, 'height.px': 30 }"
              [includeCircle]="true"
              shade="dark"
              ariaLabel="icon"
            >
            </citi-icon2>
          </button>
        </li>
      </ul>
    </nav>
  `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:block}.previousButtonIcon ::ng-deep circle,.nextButtonIcon ::ng-deep circle{fill:#056dae!important}.previousButtonIcon ::ng-deep circle:hover,.previousButtonIcon ::ng-deep circle:focus,.nextButtonIcon ::ng-deep circle:hover,.nextButtonIcon ::ng-deep circle:focus{fill:#002a54!important}.previousButtonIcon.disabled ::ng-deep circle,.nextButtonIcon.disabled ::ng-deep circle{fill:#767676!important}.previousButtonIcon,.nextButtonIcon{border:none;margin:0;padding:0;height:30px;background-color:unset}.pagination{display:inline-block;padding-left:0;margin:24px 0;border-radius:6px}.pagination>li{display:inline}.pagination>li>a{min-height:30px;min-width:38px;position:relative;float:left;text-decoration:none;color:#056dae;margin-left:-1px;font-family:Interstate_Light,sans-serif}.pagination>li>a:focus,.pagination>li>a:hover{color:#002a54}.pagination>li>a.active{font-family:Interstate_Bold,sans-serif;color:#000;cursor:default;pointer-events:none}\n"] }]
        }], ctorParameters: () => [{ type: CitiLiveAnnouncer }, { type: i0.ElementRef }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NAVIGATOR]
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], ariaLabelPrefix: [{
                type: Input
            }], prevAriaLabel: [{
                type: Input
            }], nextAriaLabel: [{
                type: Input
            }], activePageNumber: [{
                type: Input
            }], activePageID: [{
                type: Input
            }], activePageNumberChange: [{
                type: Output
            }], showNavigationButtons: [{
                type: Input
            }], pageCount: [{
                type: Input
            }] } });

const COMPONENTS$3 = [PaginationComponent];
class Pagination {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Pagination, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: Pagination, declarations: [PaginationComponent], imports: [CommonModule, Icons2], exports: [PaginationComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Pagination, providers: [NAVIGATOR_PROVIDER], imports: [CommonModule, Icons2] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Pagination, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, Icons2],
                    providers: [NAVIGATOR_PROVIDER],
                    declarations: [...COMPONENTS$3],
                    exports: [...COMPONENTS$3]
                }]
        }] });

const numericValidator = char => /[0-9]{1}/.test(char);
const nonZeroNumericValidator = char => /[1-9]{1}/.test(char);
const lowerCaseValidator = char => /[a-z]{1}/.test(char);
const upperCaseValidator = char => /[A-Z]{1}/.test(char);
const anyValidator = char => true;
const numberRangeValidator = (maxValue, char) => numericValidator(char) && parseInt(char, 10) <= maxValue;
const neverValidator = char => false;
const MASK_DIGIT_VALIDATORS = {
    'a': lowerCaseValidator,
    'A': upperCaseValidator,
    '#': anyValidator,
    'Ü': nonZeroNumericValidator
    // numeric validators are added via the for loop below
};
for (let i = 0; i <= 9; i++) {
    MASK_DIGIT_VALIDATORS[i] = numberRangeValidator.bind(undefined, i);
}
const SPECIAL_CHARACTERS = [' ', '/', '(', ')', '+', '\/', '-'];
const INDENTIFIERS = ['Alt', 'Control', 'Shift', 'AltGraph', 'Meta', 'CapsLock', 'NumLock', 'Scroll'];
const hiddenModifier = '•';
function overWriteCharAtPosition(input, position, key) {
    const currentValue = input.value;
    input.value = currentValue.slice(0, position) + key + currentValue.slice(position + 1);
}
class MaskDigit {
    constructor(index, value, isPlaceholder, hidden, validator) {
        this.index = index;
        this.value = value;
        this.isPlaceholder = isPlaceholder;
        this.hidden = hidden;
        this.validator = validator;
    }
}

/**
 * This method is like `findIndex` except that it iterates over elements
 * of `collection` from right to left.
 *
 * @param {Array} array The array to inspect.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Number} Returns the index of the found element, else `-1`.
 * @example
 *
 * const users = [
  *   { 'user': 'barney',  'active': true },
  *   { 'user': 'pebbles', 'active': true }
  *   { 'user': 'fred',    'active': false },
  *   { 'user': 'pebbles', 'active': false }
  * ]
  *
  * findLastIndex(users, ({ user }) => user == 'pebbles')  => 3
  */
function findLastIndex(array, predicate) {
    const length = array == null ? 0 : array.length;
    if (!length)
        return -1;
    return baseFindIndex(array, predicate, length - 1, true);
}
function baseFindIndex(array, predicate, fromIndex = 0, fromRight = false) {
    const { length } = array;
    let index = fromIndex + (fromRight ? 1 : -1);
    while ((fromRight ? index-- : ++index < length)) {
        if (predicate(array[index], index, array))
            return index;
    }
    return -1;
}

/**
 * Creates an array of the own enumerable string keyed property values of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @param object The object to query.
 * @returns {Array} Returns the array of property values.
 * @example
 *
 * function Foo() {
  *   this.a = 1
  *   this.b = 2
  * }
  *
  * Foo.prototype.c = 3
  *
  * values(new Foo)
  * // => [1, 2] (iteration order is not guaranteed)
  *
  * values('hi')
  * // => ['h', 'i']
  */
function values(obj) {
    const result = [];
    for (const item in obj) {
        if (obj[item])
            result.push(obj[item]);
    }
    return result;
}

const BACKSPACE = 8;
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_SHIFT = 16;
const KEY_ESC = 27;
const KEY_SPACEBAR = 32;
const KEY_PAGE_UP = 33;
const KEY_PAGE_DOWN = 34;
const KEY_END = 35;
const KEY_HOME = 36;
const KEY_ARROW_LEFT = 37;
const KEY_ARROW_UP = 38;
const KEY_ARROW_RIGHT = 39;
const KEY_ARROW_DOWN = 40;
const DELETE = 46;
const KEY_0 = 48;
const KEY_1 = 49;
const KEY_2 = 50;
const KEY_3 = 51;
const KEY_4 = 52;
const KEY_5 = 53;
const KEY_6 = 54;
const KEY_7 = 55;
const KEY_8 = 56;
const KEY_9 = 57;
const KEY_NUMPAD_0 = 96;
const KEY_NUMPAD_1 = 97;
const KEY_NUMPAD_2 = 98;
const KEY_NUMPAD_3 = 99;
const KEY_NUMPAD_4 = 100;
const KEY_NUMPAD_5 = 101;
const KEY_NUMPAD_6 = 102;
const KEY_NUMPAD_7 = 103;
const KEY_NUMPAD_8 = 104;
const KEY_NUMPAD_9 = 105;
const PERIOD = 190;

const NumpadMap = {
    Numpad1: 1,
    Numpad2: 2,
    Numpad3: 3,
    Numpad4: 4,
    Numpad5: 5,
    Numpad6: 6,
    Numpad7: 7,
    Numpad8: 8,
    Numpad9: 9,
    Numpad0: 0,
};
class CustomInputMaskDirective {
    // @Input() preventPaste: boolean = false;
    set hideCharacters(hideCharacters) {
        this._hideCharacters = hideCharacters;
        if (this._unmaskedValue)
            this.initiateValue(this._unmaskedValue, 0);
        setTimeout(() => {
            const cursor = this.input.value.indexOf('_');
            this.setSelection(cursor, cursor);
        });
    }
    set mask(mask) {
        let maskArray = mask.split('');
        for (let index = 0; index < maskArray.length; index++) {
            const maskCharacter = maskArray[index];
            const isHidden = maskCharacter === hiddenModifier || maskCharacter === '*';
            const validatorIndex = isHidden ? index + 1 : index;
            const isPlacholder = SPECIAL_CHARACTERS.includes(maskArray[validatorIndex]);
            this.maskDigitMap[index] = new MaskDigit(index, isPlacholder ? maskArray[validatorIndex] : '_', isPlacholder, isHidden, this.getValidator(validatorIndex));
            if (isHidden)
                maskArray = maskArray.filter((char, i) => index !== i);
        }
        this.effectiveMask = maskArray.join('');
        this.buildPlaceHolder();
    }
    set unmaskedValue(value) {
        if (this._unmaskedValue === value)
            return;
        if (!value || value.length === 0)
            this.buildPlaceHolder();
        this._unmaskedValue = value;
        if (this.effectiveMask) {
            this.clearMaskdigitMap();
            this.buildPlaceHolder();
            this.initiateValue(this._unmaskedValue, this.input.value.indexOf('_'));
            this.getUnMaskedValue();
        }
    }
    constructor(el, liveAnnouncer, _document) {
        this.liveAnnouncer = liveAnnouncer;
        this._document = _document;
        // object holding all relevant data for a position in a mask
        this.maskDigitMap = {};
        // mask with hidden character identifiers removed
        this.effectiveMask = '';
        this._hideCharacters = true;
        this.unmaskedValueChange = new EventEmitter();
        this.input = el.nativeElement;
        this.specials = SPECIAL_CHARACTERS.slice(0);
        this.validators = Object.assign({}, MASK_DIGIT_VALIDATORS);
    }
    ngAfterViewInit() {
        if (!this._unmaskedValue)
            this.buildPlaceHolder();
    }
    blockPaste(e) {
        e.preventDefault();
        if (this.input.readOnly) {
            return;
        }
        this.liveAnnouncer.announce('cut, copy and paste are disabled due to formatting');
    }
    onSlectionChange(event) {
        event.preventDefault();
        this.setSelections();
        if (!this.rangeSelected())
            this.ensureValidPlacement();
    }
    onFocus(event) {
        event.preventDefault();
        this.setSelection(0, 0);
        this.ensureValidPlacement();
    }
    /*tslint:disable:triple-equals*/
    onSelect() {
        this.setSelections();
    }
    getindexOfDifference(prev, next) {
        const prevArr = prev.split('');
        const nextArr = next.split('');
        for (let i = 0; i < nextArr.length; i++) {
            if (prevArr[i] !== nextArr[i])
                return i;
        }
    }
    onKeyDown(event, keyCode) {
        if (keyCode == KEY_TAB || this.input.readOnly)
            return;
        this.valuestart = this.input.value;
        // Numpad keys
        const isNumpad = event.code && event.code.indexOf('Numpad') > -1;
        const char = isNumpad ? NumpadMap[event.code] : (event.key || event['char']);
        if (keyCode !== KEY_TAB || SPECIAL_CHARACTERS.includes(char))
            event.preventDefault();
        if (!this.effectiveMask || event.metaKey || event.ctrlKey || [...SPECIAL_CHARACTERS, ...INDENTIFIERS].includes(char))
            return;
        this.setSelections();
        if (this.rangeSelected() && !event.code.includes('hift'))
            this.cutSelection();
        else if (!isNumpad)
            switch (keyCode) {
                case KEY_ARROW_LEFT:
                    this.handleLeftArrow(this.cursorStart);
                    event.preventDefault();
                    return;
                case KEY_ARROW_RIGHT:
                    this.handleRightArrow(this.cursorStart);
                    event.preventDefault();
                    return;
                case BACKSPACE:
                    this.handleBackspace(this.cursorStart);
                    event.preventDefault();
                    return;
                case DELETE:
                    this.handleDelete(this.cursorStart);
                    event.preventDefault();
                    return;
            }
        const digitValidator = this.getValidator(this.cursorStart);
        if (digitValidator(char)) {
            if (this.rangeSelected())
                this.cutSelection();
            this.storeAndWriteCharacter(this.cursorStart, char);
        }
        // return the underlying value
        this.getUnMaskedValue();
    }
    // runs the unmaked value through the masking to set the initial masked state
    initiateValue(value, cursorPos) {
        if (!value || !value.length || value.length <= 0)
            return;
        const digitValidator = this.getValidator(cursorPos);
        const nextChar = value.charAt(0);
        // if char is valid for the mask
        if (digitValidator(nextChar)) {
            this.storeAndWriteCharacter(cursorPos, nextChar);
            // call recusively by chooping off the first character
            this.initiateValue(value.substr(1), ++cursorPos);
        }
        else {
            // skip placeholder spots
            this.handleRightArrow(cursorPos);
            // call recursively
            if (++cursorPos <= this.effectiveMask.length)
                this.initiateValue(value, cursorPos);
        }
    }
    storeAndWriteCharacter(cursorPos, char) {
        // store the underlying value
        this.maskDigitMap[cursorPos].value = char;
        // hide character if specified
        const shouldWrite = (this.maskDigitMap[cursorPos].hidden && this._hideCharacters) ? hiddenModifier : char;
        // write to input
        overWriteCharAtPosition(this.input, cursorPos, shouldWrite);
        // move cursor
        this.handleRightArrow(cursorPos);
    }
    setSelections() {
        this.cursorStart = this.input.selectionStart;
        this.cursorEnd = this.input.selectionEnd;
    }
    // moves the cursor to the next
    ensureValidPlacement() {
        this.setSelections();
        const cursor = this.cursorStart == this.effectiveMask.length
            ? 0 : this.cursorStart;
        const substringIndex = this.input.value
            .substring(cursor)
            .indexOf('_');
        const newPosition = substringIndex === -1
            ? this.firstWritableIndex()
            : cursor + substringIndex;
        this.setSelection(newPosition, newPosition);
    }
    firstWritableIndex() {
        let position = this.cursorStart;
        while (position < this.effectiveMask.length) {
            const digitValidator = this.getValidator(position);
            if (digitValidator(this.input.value.charAt(position)))
                return position;
            position++;
        }
        return this.effectiveMask.length;
    }
    isTypable(char) {
        return char !== '_' && this.cursorStart < this.effectiveMask.length;
    }
    // returns the digit validator at the given position
    getValidator(cursorPos) {
        return this.validators[this.effectiveMask.charAt(cursorPos)] || neverValidator;
    }
    handleBackspace(cursorPos) {
        // cut if range is selected
        if (this.rangeSelected())
            this.cutSelection();
        // otherwise just remove the previous cursor poistion
        else {
            const previousPos = this.calculatePreviousCursorPos(cursorPos);
            if (previousPos >= 0) {
                this.maskDigitMap[previousPos].value = '_';
                overWriteCharAtPosition(this.input, previousPos, '_');
                this.setSelection(previousPos, previousPos);
            }
        }
        this.getUnMaskedValue();
    }
    handleDelete(cursorPos) {
        // cut if range is selected
        if (this.rangeSelected())
            this.cutSelection();
        // otherwise just remove at the current cursor poistion
        else {
            this.maskDigitMap[cursorPos].value = '_';
            overWriteCharAtPosition(this.input, cursorPos, '_');
            this.setSelection(cursorPos, cursorPos);
        }
        this.getUnMaskedValue();
    }
    handleLeftArrow(cursorPos) {
        const previousPos = this.calculatePreviousCursorPos(cursorPos);
        if (previousPos >= 0)
            this.setSelection(previousPos, previousPos);
    }
    handleRightArrow(cursorPos) {
        const valueAfterCursor = this.input.value.slice(cursorPos + 1);
        const nextPos = valueAfterCursor.split('').findIndex(char => !SPECIAL_CHARACTERS.includes(char));
        if (nextPos >= 0) {
            const newCursorPos = cursorPos + nextPos + 1;
            this.setSelection(newCursorPos, newCursorPos);
        }
        else
            this.setSelection(this.input.value.length, this.input.value.length);
    }
    calculatePreviousCursorPos(cursorPos) {
        const valueBeforeCursor = this.input.value.slice(0, cursorPos);
        return findLastIndex(valueBeforeCursor, char => !SPECIAL_CHARACTERS.includes(char));
    }
    // build the placeholder i.e. (___) ___-____ for phone number
    buildPlaceHolder() {
        if (this.input)
            this.input.value = this.effectiveMask
                .split('')
                .reduce((result, char) => result += SPECIAL_CHARACTERS.includes(char) ? char : '_', '');
    }
    getUnMaskedValue() {
        let unmasked = '';
        let currIndex = 0;
        while (this.maskDigitMap[currIndex]) {
            const digit = this.maskDigitMap[currIndex];
            if (!digit.isPlaceholder && digit.value !== '_')
                unmasked += digit.value;
            currIndex++;
        }
        this._unmaskedValue = unmasked;
        this.unmaskedValueChange.emit(unmasked);
    }
    rangeSelected() {
        return this.cursorStart !== this.cursorEnd;
    }
    cutSelection() {
        // split the input value
        const currentValue = this.input.value.split('');
        // replace cursor selected characters that are not placeholders
        currentValue.forEach((char, index) => {
            if (this.shouldCut(index)) {
                this.maskDigitMap[index].value = currentValue[index] = '_';
            }
        });
        // rebuild the string for the input
        this.input.value = currentValue.join('');
        // reset the cursor position
        this.setSelection(this.cursorStart, this.cursorStart);
        this.ensureValidPlacement();
    }
    // whether or not a character should be replaced is a cursor range is selected
    shouldCut(index) {
        const modifiable = !!this.maskDigitMap[index] && !this.maskDigitMap[index].isPlaceholder;
        return (index >= this.cursorStart) && (index < this.cursorEnd) && modifiable;
    }
    showHiddenValues() {
        const digits = values(this.maskDigitMap);
        const hiddenDigits = digits.filter(digit => digit.value === hiddenModifier);
        hiddenDigits.forEach(digit => overWriteCharAtPosition(this.input, digit.index, digit.value));
    }
    hideValues() {
        const digits = values(this.maskDigitMap);
        const hiddenDigits = digits.filter(digit => digit.hidden);
        hiddenDigits.forEach(digit => overWriteCharAtPosition(this.input, digit.index, hiddenModifier));
    }
    addSpecial(char) {
        this.specials.push(char);
    }
    removeSpecial(char) {
        this.specials = this.specials.filter(specChar => char != specChar);
    }
    addValidator(char, validator) {
        this.validators[char] = validator;
    }
    removeValidator(char) {
        delete this.validators[char];
    }
    rebuild() {
        this.buildPlaceHolder();
    }
    clearMaskdigitMap() {
        let i = 0;
        while (!!this.maskDigitMap[i]) {
            this.maskDigitMap[i].value = '_';
            i++;
        }
    }
    setSelection(start, end) {
        if (this._document.activeElement === this.input)
            this.input.setSelectionRange(start, end || start);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CustomInputMaskDirective, deps: [{ token: i0.ElementRef }, { token: CitiLiveAnnouncer }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.14", type: CustomInputMaskDirective, isStandalone: false, selector: "[custom-mask]", inputs: { hideCharacters: "hideCharacters", mask: ["custom-mask", "mask"], unmaskedValue: "unmaskedValue" }, outputs: { unmaskedValueChange: "unmaskedValueChange" }, host: { attributes: { "autocapitalize": "none" }, listeners: { "cut": "blockPaste($event)", "paste": "blockPaste($event)", "click": "onSlectionChange($event)", "focus": "onFocus($event)", "select": "onSelect()", "keydown": "onKeyDown($event,$event.keyCode)" } }, exportAs: ["custom"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CustomInputMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[custom-mask]',
                    standalone: false,
                    exportAs: 'custom',
                    host: { 'autocapitalize': 'none' }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: CitiLiveAnnouncer }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { hideCharacters: [{
                type: Input
            }], mask: [{
                type: Input,
                args: ['custom-mask']
            }], unmaskedValue: [{
                type: Input
            }], unmaskedValueChange: [{
                type: Output
            }], blockPaste: [{
                type: HostListener,
                args: ['cut', ['$event']]
            }, {
                type: HostListener,
                args: ['paste', ['$event']]
            }], onSlectionChange: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], onSelect: [{
                type: HostListener,
                args: ['select']
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event', '$event.keyCode']]
            }] } });

/**
 * adds a character to a string at a specifed index
 *
 * @param String string The string to which a character should be added
 * @param String char The character to be added
 * @param Index index The index of the character to remove
 * @returns String Returns the string with the index removed.
 * @example
 *
 * const thur = 'Thursday'
 *
 * removeCharAt(2)
 * // => Thrsday
 */
function insertCharAt(string = '', char, index) {
    return string.substring(0, index) + char + string.substring(index);
}

/**
 * removes a character from a string at a specifed index
 *
 * @param String string The string from which to remove a character
 * @param Number index The index of the character to remove
 * @returns String Returns the string with the index removed.
 * @example
 *
 * const thur = 'Thursday'
 *
 * removeCharAt(2)
 * // => Thrsday
 */
function removeCharAt(string, index) {
    return string.substring(0, index) + string.substring(index + 1);
}

/**
 * reverses the order of the characters in a string
 *
 * @param Array string The string to reverse.
 * @returns Number Returns the reversed string.
 * @example
 *
 * const thur = 'Thursday'
 *
 * reverseString(thur)
 * // => yadsruht
 */
function reverseString(string) {
    return string.split('').reverse().join('');
}

/*tslint:disable:triple-equals*/
/*tslint:disable:no-input-rename*/
/*tslint:disable:directive-selector*/
class CurrencyInputMaskDirective {
    set unmaskedValue(value) {
        value = parseFloat(value);
        if (Number.isNaN(value)) {
            this._unmaskedValue = undefined;
            this.input.value = null;
            this.unmaskedValueChange.emit(this._unmaskedValue);
        }
        else if (this._unmaskedValue !== value)
            this.reCalculate(value.toString());
    }
    // #region - Life Cycle
    constructor(el, announcer, ngZone) {
        this.el = el;
        this.announcer = announcer;
        this.ngZone = ngZone;
        this.unmaskedValueChange = new EventEmitter();
        this.thousandsSeparator = ',';
        this.decimalSeparator = '.';
        // number of decimal places
        this.precision = 2;
        this.preventPaste = false;
        this.input = this.el.nativeElement;
    }
    ngOnInit() {
        this.allThousands = new RegExp(`${this.thousandsSeparator}`, 'g');
    }
    ngAfterViewInit() {
        // provide the initial value
        if (!this.input.value && !this._unmaskedValue)
            return;
        else if (this._unmaskedValue !== undefined)
            this.reCalculate(this._unmaskedValue.toString());
    }
    // #region - Host Listeners
    onPaste(e) {
        e.preventDefault();
        if (this.input.readOnly) {
            return;
        }
        if (this.preventPaste) {
            this.announcer.announce('paste is disabled');
            return;
        }
        // upfront work for storing the cursor positoin
        this.storeCountOfDigitsBeforeCursor();
        // chop it up
        const beforePaste = this.input.value.slice(0, this.input.selectionStart);
        const afterPaste = this.input.value.slice(this.input.selectionStart + Math.abs(this.input.selectionStart - this.input.selectionEnd));
        const pastedText = (beforePaste + afterPaste).indexOf(this.decimalSeparator) === -1
            ? e.clipboardData.getData('Text').replace(/[^0-9.]/g, '')
            : e.clipboardData.getData('Text').replace(/\D/g, '');
        this.digitsBefore += pastedText.length;
        // slice it together
        const newVal = `${beforePaste}${pastedText}${afterPaste}`;
        // update the value
        this.reCalculate(this.getUnMaskedValue(newVal).toString());
        // fix cursor
        this.reCalcCursor();
    }
    onCut() {
        if (this.input.readOnly) {
            return;
        }
        this.storeCountOfDigitsBeforeCursor();
        setTimeout(() => {
            const unmaksked = this.getUnMaskedValue(this.input.value).toString();
            this.reCalculate(unmaksked);
            const offset = this.wholeIsZeroish(unmaksked) ? 1 : 0;
            this.reCalcCursor(offset);
        });
    }
    onSelectionChange(event) {
        this.storeCountOfDigitsBeforeCursor();
        if (!this.rangeSelected())
            this.reCalcCursor();
    }
    onSelect(event) {
        this.storeCountOfDigitsBeforeCursor();
    }
    onKeyDown(event, keyCode) {
        if (this.input.readOnly) {
            return;
        }
        this.storeCountOfDigitsBeforeCursor();
        if (event.shiftKey) {
            if (keyCode !== KEY_TAB)
                event.preventDefault();
            switch (keyCode) {
                case KEY_ARROW_LEFT:
                    this.handleShiftSelectLeft(event);
                    return;
                case KEY_ARROW_RIGHT:
                    this.handleShiftSelectRight(event);
                    return;
            }
        }
        switch (keyCode) {
            case KEY_ARROW_LEFT:
                this.handleLeftArrow(this.input.selectionStart);
                event.preventDefault();
                return;
            case KEY_ARROW_RIGHT:
                this.handleRightArrow(this.input.selectionEnd);
                event.preventDefault();
                return;
            case BACKSPACE:
                this.handleBackspaceDelete(this.input.selectionStart);
                event.preventDefault();
                return;
            case DELETE:
                this.handleBackspaceDelete(this.input.selectionStart, true);
                event.preventDefault();
                return;
        }
    }
    onKeyPress(event, keyCode) {
        if (this.input.readOnly) {
            return;
        }
        if (event.metaKey || event.ctrlKey || keyCode == KEY_TAB)
            return;
        event.preventDefault();
        const cursorPosition = this.input.selectionStart;
        const char = event.key || event['char'];
        if (cursorPosition == 0 && char == '0') {
            this.input.value = '0';
            this.unmaskedValueChange.emit(0);
            this.input.setSelectionRange(1, 1);
            return;
        }
        ;
        this.storeCountOfDigitsBeforeCursor(); // upfront work for new cursor position
        if (this.rangeSelected())
            this.cutSelection(false, char);
        else if (char === this.decimalSeparator && this.precision > 0)
            this.addDecimal();
        else if (numericValidator(char))
            this.handleNumericCharacter(char, cursorPosition);
    }
    // #region - Helper Methods
    // https://www.w3schools.com/tags/tryit.asp?filename=tryhtml_input_maxlength
    // max length still announces the digit, but doesn't inform of maxlength
    shouldEnforceMaxFactor(charAdded) {
        if (!this.maxFactor)
            return false; // efficiency exit
        const whole = charAdded.split(this.decimalSeparator)[0].split('').filter(char => char !== this.thousandsSeparator);
        if (whole.length > this.maxFactor) {
            this.announcer.announce(this.maxFactorExceededMessage);
            return true;
        }
        else
            return false;
    }
    // whether or not the cursor selection is a range of characters
    rangeSelected() {
        return this.input.selectionStart !== this.input.selectionEnd;
    }
    // does the string have a decimal
    hasDecimal(value) {
        return value.indexOf(this.decimalSeparator) > -1;
    }
    // breaks a numeric string into its whole and decimal parts
    getParts(value) {
        const decimalSplit = value.split(this.decimalSeparator);
        return {
            whole: decimalSplit[0],
            decimal: decimalSplit[1]
        };
    }
    wholeIsZeroish(value) {
        const whole = this.getParts(value).whole;
        return whole === '0' || whole == '';
    }
    // whether or not a decimal should be added
    shouldAddDecimalSeperator() {
        return this.input.value.indexOf(this.decimalSeparator) !== -1
            && this.precision > 0;
    }
    // #region - Input Value Methods
    handleNumericCharacter(key, cursorPosition) {
        const charAdded = insertCharAt(this.input.value, key, cursorPosition);
        const offset = (this.input.value.charAt(0) === '0' && cursorPosition < 2) ? 0 : 1;
        if (this.shouldEnforceMaxFactor(charAdded))
            return;
        this.reCalculate(charAdded);
        // protects against infinite while loop while calculating the cursor position
        this.reCalcCursor(offset);
    }
    // applies the mask to the string
    reCalculate(value) {
        this.hasDecimal(value) ? this.addDigitWithDecimal(value) : this.addDigitNoDecimal(value);
    }
    // handle the undefined value state
    clear() {
        this.input.value = '';
        this.unmaskedValueChange.emit(undefined);
        this._unmaskedValue = undefined;
    }
    // adds a digit when no decimal is present
    addDigitNoDecimal(charAdded) {
        if (charAdded !== '') {
            this.input.value = this.maskWholeNumber(charAdded);
            this._unmaskedValue = this.getUnMaskedValue(this.input.value);
            this.unmaskedValueChange.emit(this._unmaskedValue);
        }
        else
            this.clear();
    }
    // adds a digit when a decial is present
    addDigitWithDecimal(value) {
        const parts = this.getParts(value);
        const decimal = this.maskDecimal(parts.decimal);
        // update the input
        this.input.value = `${this.maskWholeNumber(parts.whole)}${this.decimalSeparator}${decimal}`;
        // catch values less than 0
        const whole = parts.whole ? this.getUnMaskedValue(parts.whole) : '0';
        // get the new value
        this._unmaskedValue = parseFloat(`${whole}.${decimal}`);
        this.unmaskedValueChange.emit(this._unmaskedValue);
    }
    // adds the decimal character
    addDecimal() {
        // ignore duplicate decimals
        if (this.shouldAddDecimalSeperator())
            return;
        let newCursorPostion = 1;
        if (this.input.value.length === 0) {
            this.input.value = '0';
            newCursorPostion++;
        }
        // add the decimal seperator
        const newValue = insertCharAt(this.input.value, this.decimalSeparator, this.input.selectionStart).replace(this.allThousands, '');
        this.addDigitWithDecimal(newValue);
        newCursorPostion += this.input.value.indexOf(this.decimalSeparator);
        this.input.setSelectionRange(newCursorPostion, newCursorPostion);
    }
    handleBackspaceDelete(cursorPos, isDelete = false) {
        if (this.rangeSelected())
            this.cutSelection();
        else {
            const position = isDelete ? cursorPos : this.calculatePreviousCursorPos(cursorPos);
            if (position >= 0 && position < this.input.value.length) {
                const newValue = removeCharAt(this.input.value, position);
                if (newValue === '')
                    this.clear();
                this.reCalculate(newValue);
                this.reCalcCursor(isDelete ? 0 : -1); // backspace = -1
            }
        }
    }
    cutSelection(moveCursorToEnd = false, replaceWith = '') {
        // cut out the unwanted digits
        const spliceLength = Math.abs(this.input.selectionStart - this.input.selectionEnd);
        const beforePaste = this.input.value.slice(0, this.input.selectionStart);
        const afterPaste = this.input.value.slice(this.input.selectionStart + spliceLength);
        if (!numericValidator(replaceWith) && replaceWith !== this.decimalSeparator) {
            replaceWith = '';
            this.digitsBefore--;
        }
        // strip masking
        const newValue = `${beforePaste}${replaceWith}${afterPaste}`.replace(this.allThousands, '');
        let offset = (beforePaste.indexOf('.') > -1) ? 2 : 1;
        if (this.wholeIsZeroish(newValue))
            offset++;
        this.reCalculate(newValue);
        this.reCalcCursor(offset);
    }
    // #region - String Manipulation Methods
    // ruturns the dollar masked value of the whole number part of a value
    maskWholeNumber(wholeNumber) {
        if (!wholeNumber || wholeNumber === '0')
            return '0';
        // remove zeros at the beginnin if not right before the decimal.. only if length of whole number is greater then 1
        while (wholeNumber.charAt(0) === '0' && wholeNumber.length > 1)
            wholeNumber = wholeNumber.substr(1);
        // convert to string and remove thousands seperators
        wholeNumber = (wholeNumber + '').replace(this.allThousands, '');
        // add thousands separators in the correct positions
        return wholeNumber.length > 3 ? reverseString(reverseString(wholeNumber).match(/.{1,3}/g).join(this.thousandsSeparator)) : wholeNumber;
    }
    // only allow decimals up to the specified precision
    maskDecimal(decimal) {
        return (decimal + '').substring(0, this.precision);
    }
    getUnMaskedValue(value) {
        const newVal = parseFloat(value.split('').filter(char => char !== this.thousandsSeparator).join(''));
        return Number.isNaN(newVal) ? 0 : newVal;
    }
    // #region - Cursor Methods
    // handles range selection via shift key
    handleShiftSelectRight(event) {
        const end = event.ctrlKey
            ? this.input.value.length + 1
            : Math.min(this.input.selectionEnd + 1, this.input.value.length);
        executeOnStable(this.ngZone, () => this.input.setSelectionRange(this.input.selectionStart, end));
    }
    handleShiftSelectLeft(event) {
        const start = event.ctrlKey ? 0 : Math.max(this.input.selectionStart - 1, 0);
        executeOnStable(this.ngZone, () => this.input.setSelectionRange(start, this.input.selectionEnd));
    }
    // determines the next appropriate spot for the currsor - offset = -1 for backspace
    // at event start call storeCountOfDigitsBeforeCursor() to prepare for this method
    reCalcCursor(offSet = 0) {
        let newPosition = 0;
        // iterate through the string to find the next spot
        while (this.digitsBefore + offSet > 0) {
            const char = this.input.value.charAt(newPosition);
            if (numericValidator(char) || char === this.decimalSeparator)
                this.digitsBefore--;
            newPosition++;
            if (newPosition > this.input.value.length)
                break;
        }
        //skip it if it's a thousands seperator
        const nextChar = this.input.value.charAt(newPosition);
        newPosition = (nextChar === this.thousandsSeparator)
            ? newPosition + 1
            : newPosition;
        // never put the cursor pefore a leading 0
        if (newPosition == 0 && this.input.value.charAt(newPosition) === '0')
            newPosition++;
        // set the cursoposition
        this.input.setSelectionRange(newPosition, newPosition);
    }
    // stores the current indexes of the cursor selection
    // at the end of the input update call reCalcCursor()
    storeCountOfDigitsBeforeCursor() {
        // returns the number of numbers ( plus decimal ) to aid in recalculating the next cursor position
        const subString = this.input.value.substr(0, this.input.selectionStart);
        this.digitsBefore = subString.replace(/\D/g, '').length;
        if (subString.indexOf('.') !== -1)
            this.digitsBefore++;
    }
    handleLeftArrow(cursorPos) {
        if (!this.rangeSelected()) {
            let previousPos = this.calculatePreviousCursorPos(cursorPos);
            // never put the cursor pefore a leading 0
            if (previousPos == 0 && this.input.value.charAt(previousPos) === '0')
                previousPos++;
            // never go out of range
            if (previousPos >= 0)
                this.input.setSelectionRange(previousPos, previousPos);
        }
        else
            this.input.setSelectionRange(this.input.selectionStart, this.input.selectionStart);
    }
    calculatePreviousCursorPos(cursorPos) {
        const valueBeforeCursor = this.input.value.slice(0, cursorPos);
        return findLastIndex(valueBeforeCursor, char => numericValidator(char) || char === this.decimalSeparator);
    }
    handleRightArrow(cursorPos) {
        if (!this.rangeSelected()) {
            const valueAfterCursor = this.input.value.slice(cursorPos);
            const nextPos = valueAfterCursor.split('').findIndex(char => numericValidator(char) || char === this.decimalSeparator);
            if (nextPos >= 0) {
                const newCursorPos = cursorPos + nextPos + 1;
                this.input.setSelectionRange(newCursorPos, newCursorPos);
            }
        }
        else
            this.input.setSelectionRange(this.input.selectionEnd, this.input.selectionEnd);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CurrencyInputMaskDirective, deps: [{ token: i0.ElementRef }, { token: CitiLiveAnnouncer }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.14", type: CurrencyInputMaskDirective, isStandalone: false, selector: "[currency-mask]", inputs: { unmaskedValue: "unmaskedValue", thousandsSeparator: "thousandsSeparator", decimalSeparator: "decimalSeparator", precision: "precision", maxFactor: "maxFactor", maxFactorExceededMessage: "maxFactorExceededMessage", preventPaste: "preventPaste" }, outputs: { unmaskedValueChange: "unmaskedValueChange" }, host: { attributes: { "autocapitalize": "none" }, listeners: { "onpaste": "onPaste($event)", "paste": "onPaste($event)", "cut": "onCut()", "click": "onSelectionChange($event)", "select": "onSelect($event)", "keydown": "onKeyDown($event,$event.keyCode)", "keypress": "onKeyPress($event,$event.keyCode)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CurrencyInputMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[currency-mask]',
                    standalone: false,
                    host: { 'autocapitalize': 'none' }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: CitiLiveAnnouncer }, { type: i0.NgZone }], propDecorators: { unmaskedValue: [{
                type: Input
            }], unmaskedValueChange: [{
                type: Output
            }], thousandsSeparator: [{
                type: Input
            }], decimalSeparator: [{
                type: Input
            }], precision: [{
                type: Input
            }], maxFactor: [{
                type: Input
            }], maxFactorExceededMessage: [{
                type: Input
            }], preventPaste: [{
                type: Input
            }], onPaste: [{
                type: HostListener,
                args: ['onpaste', ['$event']]
            }, {
                type: HostListener,
                args: ['paste', ['$event']]
            }], onCut: [{
                type: HostListener,
                args: ['cut']
            }], onSelectionChange: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], onSelect: [{
                type: HostListener,
                args: ['select', ['$event']]
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event', '$event.keyCode']]
            }], onKeyPress: [{
                type: HostListener,
                args: ['keypress', ['$event', '$event.keyCode']]
            }] } });

class PartialInputMaskDirective {
    set hideCharacters(hideCharacters) {
        this._hideCharacters = hideCharacters;
        if (!!this._unmaskedValue && !!this._unmaskedValue.length && this._unmaskedValue.length > 0)
            this.applyMask(this._unmaskedValue);
    }
    set mask(mask) {
        switch (mask) {
            case 'partialEmailMasking':
                this.setPartialEmailMasking();
                break;
            case 'fullTextMasking':
                this.setFullTextMasking();
                break;
            case 'partialTextMasking':
                this.setPartialTextMasking();
                break;
        }
    }
    set unmaskedValue(value) {
        if (this._unmaskedValue !== value) {
            this._unmaskedValue = value;
            this.initiateValue(this._unmaskedValue);
        }
    }
    // #region - Life Cycle
    constructor(el, announcer) {
        this.announcer = announcer;
        // whether or not the whole field is selected
        this.fullFieldSelected = false;
        this.selectAdjustment = false;
        this.unmaskedLength = 4;
        this.numbersOnly = false;
        this.preventPaste = false;
        this._hideCharacters = true;
        this.unmaskedValueChange = new EventEmitter();
        this.input = el.nativeElement;
    }
    ngOnInit() {
        // this.initiateValue(this._unmaskedValue);
    }
    ngAfterViewInit() {
        // provide the initial value
        this.initiateValue(this._unmaskedValue);
    }
    // #region - Host Listeners
    onSlectionChange(event) {
        event.preventDefault();
    }
    handlePaste(e) {
        e.preventDefault();
        if (this.input.readOnly) {
            return;
        }
        if (this.preventPaste) {
            this.announcer.announce('paste is disabled');
            return;
        }
        this.setSelections();
        if (this.rangeSelected())
            this.cutSelection();
        const clip = e.clipboardData.getData('Text');
        const newValue = insertCharAt(this._unmaskedValue, clip, this.input.selectionStart);
        this.applyMask(newValue);
        const newPos = this.cursorStart + clip.length;
        this.input.setSelectionRange(newPos, newPos);
    }
    handleCut(e) {
        e.preventDefault();
        if (this.input.readOnly) {
            return;
        }
        this.setSelections();
        if (this.rangeSelected()) {
            const clipboard = this.cutSelection();
            e.clipboardData.setData('text/plain', clipboard);
        }
    }
    onKeyDown(event, keyCode) {
        if (this.input.readOnly) {
            return;
        }
        this.setSelections();
        if (event.metaKey || event.ctrlKey)
            return;
        switch (keyCode) {
            case BACKSPACE:
                this.handleBackspace(this.cursorStart);
                event.preventDefault();
                return;
            case DELETE:
                this.handleDelete(this.cursorStart);
                event.preventDefault();
                return;
        }
    }
    onKeyPress(event, keyCode) {
        if (event.metaKey || event.ctrlKey
            || this.input.readOnly
            || keyCode == KEY_TAB
            || [0, 8, 13, 27, 20].includes(event.which))
            return;
        event.preventDefault();
        if (this.rangeSelected())
            this.cutSelection();
        if ((this.numbersOnly && (event.which > 57 || event.which < 48))
            || this.input.value.length >= this.maxLength)
            return;
        this.addCharacter(String.fromCharCode(event.charCode));
    }
    addCharacter(char) {
        const cursorPos = this.input.selectionStart;
        this._unmaskedValue = insertCharAt(this._unmaskedValue, char, cursorPos);
        this.applyMask(this._unmaskedValue);
        this.input.setSelectionRange(cursorPos + 1, cursorPos + 1);
    }
    // #region - Methods
    // runs the unmaked value through the masking to set the initial masked state
    initiateValue(value) {
        if (!!value && !!value.length && value.length > 0) {
            this._unmaskedValue = value;
            this.applyMask(this._unmaskedValue);
        }
        else {
            this.input.value = '';
            this.unmaskedValueChange.emit('');
        }
        ;
    }
    rangeSelected() {
        return this.cursorStart !== this.cursorEnd;
    }
    setSelections() {
        this.cursorStart = this.input.selectionStart;
        this.cursorEnd = this.input.selectionEnd;
    }
    applyMask(value) {
        this._unmaskedValue = value;
        this.unmaskedValueChange.emit(value);
        // calculate when the hidden characters stop
        if (this._hideCharacters && value) {
            const stopMasking = (!!this.maskEndChartacter) ? value.indexOf(this.maskEndChartacter) - 1 : value.length - this.unmaskedLength;
            // apply to input
            this.input.value = value.split('').map((char, index) => (index >= this.maskStartPosition && index < stopMasking) ? '•' : char).join('');
        }
        else
            this.input.value = this._unmaskedValue;
    }
    handleBackspace(cursorPos) {
        if (this.rangeSelected()) {
            this.cutSelection();
            return;
        }
        if (cursorPos < 0)
            return;
        const newValue = removeCharAt(this._unmaskedValue, cursorPos - 1);
        this.applyMask(newValue);
        const newPostion = Math.max(cursorPos - 1, 0);
        this.input.setSelectionRange(newPostion, newPostion);
    }
    handleDelete(cursorPos) {
        if (this.rangeSelected()) {
            this.cutSelection();
            return;
        }
        if (cursorPos >= this._unmaskedValue.length)
            return;
        const newValue = removeCharAt(this._unmaskedValue, cursorPos);
        this.applyMask(newValue);
        const newPostion = Math.min(cursorPos, newValue.length);
        this.input.setSelectionRange(cursorPos, cursorPos);
    }
    cutSelection() {
        // split the input value
        const arrayString = this._unmaskedValue.split('');
        const clipboard = arrayString.splice(this.cursorStart, Math.abs(this.cursorStart - this.cursorEnd));
        this.applyMask(arrayString.join(''));
        this.input.setSelectionRange(this.cursorStart, this.cursorStart);
        return clipboard.join('');
    }
    setPartialEmailMasking() {
        this.maskStartPosition = 1;
        this.maskEndChartacter = '@';
        this.unmaskedLength = undefined;
    }
    setFullTextMasking() {
        this.maskStartPosition = 0;
        this.unmaskedLength = 0;
    }
    setPartialTextMasking() {
        this.maskStartPosition = 0;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PartialInputMaskDirective, deps: [{ token: i0.ElementRef }, { token: CitiLiveAnnouncer }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.14", type: PartialInputMaskDirective, isStandalone: false, selector: "[partial-mask]", inputs: { maxLength: "maxLength", unmaskedLength: "unmaskedLength", numbersOnly: "numbersOnly", preventPaste: "preventPaste", hideCharacters: "hideCharacters", mask: ["partial-mask", "mask"], unmaskedValue: "unmaskedValue" }, outputs: { unmaskedValueChange: "unmaskedValueChange" }, host: { attributes: { "autocapitalize": "none" }, listeners: { "click": "onSlectionChange($event)", "paste": "handlePaste($event)", "cut": "handleCut($event)", "keydown": "onKeyDown($event,$event.keyCode)", "keypress": "onKeyPress($event,$event.keyCode)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: PartialInputMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[partial-mask]',
                    standalone: false,
                    host: { 'autocapitalize': 'none' }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: CitiLiveAnnouncer }], propDecorators: { maxLength: [{
                type: Input
            }], unmaskedLength: [{
                type: Input
            }], numbersOnly: [{
                type: Input
            }], preventPaste: [{
                type: Input
            }], hideCharacters: [{
                type: Input
            }], mask: [{
                type: Input,
                args: ['partial-mask']
            }], unmaskedValue: [{
                type: Input
            }], unmaskedValueChange: [{
                type: Output
            }], onSlectionChange: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], handlePaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }], handleCut: [{
                type: HostListener,
                args: ['cut', ['$event']]
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event', '$event.keyCode']]
            }], onKeyPress: [{
                type: HostListener,
                args: ['keypress', ['$event', '$event.keyCode']]
            }] } });

// @dynamic
let nextUniqueId$1 = 0;
const DEFAULT_ERRORS$1 = {
    required: 'Input is required',
    positive: 'Value must be positive',
    integer: 'Value must be an integer',
    phone: 'Invalid phone number',
    nonZero: 'value cannot be zero',
    email: 'Invalid email',
    zip: 'Invalid Zip Code',
    ssn: 'Invalid social security number',
    areaCode: 'Please select an area code',
    countryCode: 'Please select a country code'
};
const INPUT2_VALUE_ACCESSOR$1 = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => Input2Component),
    multi: true
};
// @dynamic
class Input2Component {
    // #region - Properties: Static
    static { this.DEFAULT_MAXLENGTH = 524288; }
    static { this.VALIDATORS = {
        isEmail: (val) => /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(val),
        isNumber: (val) => !isNaN(val),
        isPositive: (val) => !isNaN(val) && parseFloat(val) > 0,
        isValue: (val) => !!val && val !== ''
    }; }
    static { this.VALID_TYPES = [
        'text',
        'password',
        'email',
        'number',
        'tel',
        'phone',
        'ssn',
        'zip'
    ]; }
    static { this.DEFAULT_MASKS = {
        phone: '(999) 999-9999',
        zipcode: '99999',
        ssn: '999-99-9999',
        partialSsnMasking: '*9*9*9-*9*9-9999',
        fullSsnMasking: '*9*9*9-*9*9-*9*9*9*9',
        partialPhoneMasking: '(*9*9*9)-*9*9*9-9999',
        fullZipcodeMasking: '*9*9*9*9*9',
        fullPhoneMasking: '(*9*9*9) *9*9*9-*9*9*9*9',
        creditCard: '9999 9999 9999 9999'
    }; }
    // Classes for the sizes of input, also applied to the wrapper
    static { this.SIZE_CLASSES = {
        fullwidth: 'col-xs-12',
        large: 'col-md-8 col-lg-8',
        medium: 'col-md-6 col-sm-9',
        small: 'col-md-4 col-sm-6',
        xsmall: 'col-md-2 col-sm-3 col-xs-6',
        none: ''
    }; }
    get idStr() { return this._id; }
    set idStr(value) { this._id = value || this._uid; }
    // #endregion
    // #region - Two-way Bound values
    get hideMask() {
        return this.hideCharacters;
    }
    set hideMask(value) {
        this.hideCharacters = value;
    }
    /**
     * This focuses the input if set to true
     */
    set focus(isFocused) {
        isFocused ? this.onFocus() : this.onBlur();
    }
    ;
    // #endregion
    // #region - Setters
    /**
     * Text to be displayed when the input doesn't meet the required length
     */
    set minLengthError(message) {
        if (message)
            this.errors.minLength = message;
    }
    /**
     * Text to be displayed when the value exceed the provided maximum
     */
    set maxValueError(message) {
        if (message)
            this.errors.maxValue = message;
    }
    /**
     * Text to be displayed when the value exceed the provided maximum
     */
    set minValueError(message) {
        if (message)
            this.errors.minValue = message;
    }
    /**
     * Text to be displayed when the input exceed the maximum length
     */
    set maxLengthError(message) {
        if (message)
            this.errors.maxLength = message;
    }
    /**
     * Text to be displayed when the input is required and no value was entered
     */
    set requiredError(message) {
        this.errors.required = (!!message) ? message : DEFAULT_ERRORS$1.required;
    }
    /**
     * Text to be displayed when the value fails non-zero validation
     */
    set messageOnlyNonZeroError(message) {
        this.errors.nonZero = (!!message) ? message : DEFAULT_ERRORS$1.nonZero;
    }
    /**
     * Text to be displayed when the value fails only integer validation
     */
    set messageOnlyIntegerError(message) {
        this.errors.integer = (!!message) ? message : DEFAULT_ERRORS$1.integer;
    }
    /**
     * Text to be displayed when the value fails only positive integer validation
     */
    set messageOnlyPositiveIntegerError(message) {
        this.errors.positive = (!!message) ? message : DEFAULT_ERRORS$1.positive;
    }
    /**
     * Text to be displayed when no phone is provided
     */
    set messageOnlyPhoneError(message) {
        this.errors.phone = (!!message) ? message : DEFAULT_ERRORS$1.phone;
    }
    /**
     * Text to be displayed when no zipcode is provided
     */
    set messageOnlyZipcodeError(message) {
        this.errors.zipcode = (!!message) ? message : DEFAULT_ERRORS$1.zip;
    }
    /**
     * Text to be displayed when no email is provided
     */
    set messageOnlyEmailError(message) {
        this.errors.email = (!!message) ? message : DEFAULT_ERRORS$1.email;
    }
    /**
     * Text to be displayed when no zip is provided
     */
    set messageOnlyZipError(message) {
        this.errors.zip = (!!message) ? message : DEFAULT_ERRORS$1.zip;
    }
    /**
     * Text to be displayed when no SSN is provided
     */
    set messageOnlySsnError(message) {
        this.errors.ssn = (!!message) ? message : DEFAULT_ERRORS$1.ssn;
    }
    /**
     * Text to be displayed when no areaCode is provided
     */
    set messageOnlyAreaCodeError(message) {
        this.errors.areaCode = (!!message) ? message : DEFAULT_ERRORS$1.areaCode;
    }
    /**
     * Text to be displayed when no countryCode is provided
     */
    set messageOnlyCountryCodeError(message) {
        this.errors.countryCode = (!!message) ? message : DEFAULT_ERRORS$1.countryCode;
    }
    /**
     * allows users to customize any of the error messages
     */
    set changeMessage([errorType, msg]) {
        if (errorType && msg)
            this.errors[errorType] = msg;
    }
    set hasCustomError(useCustom) {
        this._hasCustomError = coerceBool(useCustom);
        if (this.error) {
            if (this._hasCustomError)
                this.errorMessage = this.errors.custom;
            else
                this.validate();
        }
    }
    /**
     * Customized error message for custom error
     * @Note: *this field need to be above the error setter to trigger initial error correctly
     */
    set customErrorMessage(message) {
        this.errors.custom = (!!message) ? message : 'Your Custom Error Message';
        if (this.error && this._hasCustomError)
            this.errorMessage = this.errors.custom;
    }
    set error(err) {
        if (err === this._error)
            return;
        if (err) {
            this.validationService.addError(this);
            this.errorMessage = this.errors['custom'] || 'Default Error. You should provide your own customErrorMessage';
            this.announcer.announce(this.errorMessage);
        }
        this._error = err;
    }
    get error() {
        return this._error;
    }
    /**
     * Masking does not work on Android. This setting will set the input type to password on Android.
     */
    set usePasswordOnAndroid(usePass) {
        this._usePassOnAndroid = usePass;
        if (this.isAndroidDevice && usePass) {
            this.inputType = 'password';
        }
        else {
            this.inputType = this._inputType;
        }
    }
    ;
    get usePasswordOnAndroid() {
        return this._usePassOnAndroid;
    }
    /**
     * This defines the mask of the input, which changes the input text masking on character input.
     *  - 'fullZipcodeMasking'
     *  - 'fullPhoneMasking'
     *  - 'partialEmailMasking'
     *  - 'fullTextMasking'
     *  - 'partialTextMasking'
     * @defaults 'none'
     */
    set mask(mask) {
        this._storedMask = Input2Component.DEFAULT_MASKS[mask] || mask;
        // TODO: once Android 7 (Nougat) is no longer supported, we can fix this
        if (this.isAndroidDevice) {
            this.displayedInput = 'none';
            if (mask === 'dollar')
                this.inputType = 'number';
        }
        // selects the correct input and applied masking directive or lack there of
        else if (!mask || mask === 'none')
            this.displayedInput = 'none';
        else if (mask === 'dollar')
            this.displayedInput = 'dollar';
        else if (['partialEmailMasking', 'fullTextMasking', 'partialTextMasking'].includes(mask)) {
            this.displayedInput = 'partial';
            if (mask === 'fullTextMasking')
                this.unmaskedLength = 0;
        }
        else
            this.displayedInput = 'custom';
        if (this.isPhantom)
            this.displayedInput = 'none';
        // look up the stroed masks if available
        this._appliedMask = Input2Component.DEFAULT_MASKS[mask] || mask;
    }
    get showHideMaskText() {
        return this.hideCharacters ? this.showText : this.hideText;
    }
    /**
     * This defines the type of the input, which changes which kind of input is displayed.
     * Mask must be set separately.
     * defaults to text.
     */
    set type(type) {
        if (type && Input2Component.VALID_TYPES.includes(type)) {
            this.inputType = type.includes('hone') ? 'tel' : type;
            this._inputType = this.inputType;
            if (this.isAndroidDevice && this.usePasswordOnAndroid) {
                this.inputType = 'password';
            }
        }
        else if (isDevMode)
            console.error('CITI-INPUT - TYPE: Caught \'' + type + '\'. Valid types are: ' + Input2Component.VALID_TYPES);
    }
    get type() {
        return this._inputType;
    }
    /**
     * This defines the size of the input, which changes the bootstrap classes.
     */
    set size(size) {
        if (!!size && Input2Component.SIZE_CLASSES[size] !== undefined) {
            this.inputSize = size;
            this.classRow = size === 'none' ? false : true;
        }
        else if (isDevMode) {
            console.error('CITI-INPUT - SIZE: Caught \'' + size + '\'. Valid sizes are: ' + Object.keys(Input2Component.SIZE_CLASSES));
        }
    }
    ;
    // #endregion
    // #region - Getters
    get hasValue() {
        return Input2Component.VALIDATORS.isValue(this.value);
    }
    get size() {
        return Input2Component.SIZE_CLASSES[this.inputSize];
    }
    get isCountryCodes() {
        return !!this.countryCodes && this.countryCodes.length > 0;
    }
    get isAreaCodes() {
        return !!this.areaCodes && this.areaCodes.length > 0;
    }
    get hasCurrencySignBefore() {
        return !!this.currencySignBefore && this.currencySignBefore !== '';
    }
    get labelOpacity() {
        return this.visibleLabel ? 1 : (this.value || this.isFocused || this.isSelectFocused || this.displayedInput === 'custom') ? 1 : 0;
    }
    get isAndroidDevice() {
        if (!this._navigator || !this._navigator.userAgent)
            return false;
        else
            return /(android)/i.test(this._navigator.userAgent);
    }
    get isMobile() {
        return !!this._isMobile;
    }
    get isPhantom() {
        if (!this._navigator || !this._navigator.userAgent)
            return false;
        else
            return /(PhantomJS)/i.test(this._navigator.userAgent);
    }
    // #endregion
    // #region - Life Cycle
    constructor(validationService, announcer, el, // added for Duracell
    _navigator) {
        this.validationService = validationService;
        this.announcer = announcer;
        this.el = el;
        this._navigator = _navigator;
        // #endregion
        // #region - Properties: Public
        // Default errors for the different validation error states
        this.errors = {
            required: DEFAULT_ERRORS$1.required,
            positive: DEFAULT_ERRORS$1.positive,
            integer: DEFAULT_ERRORS$1.integer,
            phone: DEFAULT_ERRORS$1.phone,
            zipcode: DEFAULT_ERRORS$1.zip,
            nonZero: DEFAULT_ERRORS$1.nonZero,
            email: DEFAULT_ERRORS$1.email,
            zip: DEFAULT_ERRORS$1.zip,
            ssn: DEFAULT_ERRORS$1.ssn,
            areaCode: DEFAULT_ERRORS$1.areaCode,
            countryCode: DEFAULT_ERRORS$1.countryCode,
        };
        this.shouldValidate = {
            number: () => this.inputType === 'number' || this._appliedMask === 'dollar',
            url: () => this.inputType === 'url',
            zip: () => this.inputType === 'zip',
            ssn: () => this.inputType === 'ssn' || this._appliedMask.toLowerCase().includes('ssn'),
            phone: () => this.inputType === 'tel' || this._appliedMask.toLowerCase().includes('phone'),
            email: () => this.inputType === 'email' || this._appliedMask.toLowerCase().includes('email'),
            minLength: () => !!this.minLength && ['text', 'email', 'password', 'none'].includes(this.inputType)
        };
        // Id for the label for aria
        this.labelId = null;
        // applys class to remove excess padding if there is a pre addon
        this.hasPreAddon = false;
        // error message to be displayed when an error is triggered
        this.errorMessage = 'Error';
        // whether the input is focused
        this.isFocused = false;
        // whether the select of the input is focused
        this.isSelectFocused = false;
        this._calledFromSubmit = false;
        // #endregion
        // #region - Inputs: Error Related
        /**
         * Whether the input should valid on 'blur' and 'change' events (always validates on submit)
         */
        this.validateOn = [];
        /**
         * The input only accepts alphabetical or numeric value
         */
        this.allowIntegerOnly = false;
        /**
         * This defines the maximum character length of the input
         */
        this.maxLength = Input2Component.DEFAULT_MAXLENGTH;
        /**
         * readonly field, displays its value and focusable
         */
        this.readOnly = false;
        /**
         * disables the field and not focusable
         */
        this.disabled = false;
        /**
         * non-masked text fields this limits characters to numbers
         */
        this.typeNumbersOnly = false;
        /**
         * Enables/disables non zero validation
         */
        this.allowNonZeroValueOnly = false;
        // #endregion
        /**
         * Turns on/off the autocomplete feature of an input. Default value is 'off'.
         * @Two-way: false
         */
        this.autocomplete = 'off';
        /**
         * prevents all paste events
         */
        this.preventPaste = false;
        // #region - Inputs: Dollar mask
        /**
         * string to seperate the thousands position
         */
        this.thousandsSeparator = ',';
        /**
         * string to seperate the decimal position
         */
        this.decimalSeparator = '.';
        /**
         * number of digits allowed after the decimal
         */
        this.precision = 2;
        /**
         * max value allowed without throughing an error
         */
        this.maxValue = Number.MAX_SAFE_INTEGER;
        /**
         * Message to inform ADA users that the typed character surpassed the maxFactor and was not added to the input value
         */
        this.maxFactorExceededMessage = 'Maximum Factor Exceeded. Last number was not added';
        // #endregion
        // #region - Inputs: Global Properties
        /**
         * Label for aria-label tag if special aria label is needed
         */
        this.ariaLabel = false;
        this._uid = `citi-input2-${nextUniqueId$1++}`;
        /**
         * This defines the name of the input
         */
        this.name = null;
        /**
         * Show a search icon add-on in the input.
         */
        this.searchIcon = false;
        /**
         * Sets the tabindex attribute
         */
        this.tabIndex = 0;
        /**
         * Sets the isCbolPassword attribute
         */
        this.isCbolPassword = false;
        this.countryCodeChange = new EventEmitter();
        this.countryCodes = [];
        this.areaCodeChange = new EventEmitter();
        this.areaCodes = [];
        /**
         * How many character should not be masked at the end of the string, only applies to text masking
         */
        this.unmaskedLength = 4;
        // #endregion
        // #region - Inputs: Masking / Unmasking events
        /**
         * A list of HTML event to activate the masking on the content
         */
        this.applyMaskOnEvents = [];
        /**
         * A list of HTML event to remove the mask from the value
         */
        this.removeMaskOnEvents = [];
        // #endregion
        // #region - Inputs: Success related
        /**
         * This defines the success state on the input
         */
        this.isSuccess = false;
        /**
         * Customized success message for valid password input
         */
        this.successMessage = '';
        /**
        * Input for visibleLabel for the label
        */
        this.visibleLabel = false;
        /**
         * This defines the value of the input. Can be two-way bound through [(value)]='myValueVar'
         */
        this.value = undefined;
        this.valueChange = new EventEmitter();
        this.hideToggle = new EventEmitter();
        this.focusChange = new EventEmitter();
        // #endregion
        // #region - Outputs
        /**
         * emits when the focus is blured
         */
        this.blur = new EventEmitter();
        /*
        * This defines the custom error state of the input. Takes precedence over other validation types.
        */
        this._hasCustomError = false;
        /**
         * This defines the error state on the input
         */
        this._error = false;
        this.errorChange = new EventEmitter();
        this._appliedMask = 'none';
        this._storedMask = 'none';
        this.displayedInput = 'none';
        this.hideCharacters = true;
        /**
         * text of the show (unmask) button
         */
        this.showText = 'Show';
        /**
         * text of the hide (mask) button
         */
        this.hideText = 'Hide';
        this.inputType = 'text';
        this.inputSize = 'fullwidth';
        this.classRow = true;
        // #endregion
        // #region - FORM BINDING METHODS
        // All methods below are required for the Value Control Accessor interface to work with [(ngModel)]
        this.onChange = (value) => { };
        this.onTouched = () => { };
    }
    ngOnInit() {
        // Detects mobile device
        if (this._navigator)
            this._isMobile = this._navigator.userAgent.match(/(iPad)|(iPhone)|(iPod)|(android)|(webOS)/i);
        // ensures that an id is assigned
        this.idStr = this.idStr;
        // register and subscribe to validation service
        this.validationService.addInput(this);
        this.validateSub = this.validationService.validateNow.subscribe(() => {
            this._calledFromSubmit = true;
            this.validate();
        });
        this.labelId = Input2Component.VALIDATORS.isValue(this._id) ? this._id + '-label' : null;
        if (this.hasCurrencySignBefore || this.isCountryCodes || this.isAreaCodes)
            this.hasPreAddon = true;
        this.setDynamicMessages();
        if (this.countryCode) {
            const codeOption = this.countryCodes.find(code => code.value === this.countryCode);
            if (codeOption)
                this.mask = codeOption.mask || this._storedMask;
        }
    }
    ngOnDestroy() {
        this.validationService.removeError(this);
        if (this.validateSub)
            this.validateSub.unsubscribe();
    }
    // #endregion
    // #region - Event Listeners and handlers
    /**
     * Updates the ui to responsd to a focus event
     * This does not cast focus. Use "focusInput()" for that
     */
    onFocus() {
        if (this.isFocused)
            return;
        if (this.safeIncludes(this.removeMaskOnEvents, 'focus'))
            this.hideCharacters = false;
        this.isFocused = true;
        this.isSelectFocused = false;
        this.focusChange.emit(true);
    }
    /**
     * Casts focus to the native <input> element
     */
    focusInput() {
        this.input.nativeElement.focus();
    }
    ;
    onBlur() {
        if (!this.isFocused)
            return;
        if (this.safeIncludes(this.applyMaskOnEvents, 'blur'))
            this.hideCharacters = true;
        if (this.safeIncludes(this.validateOn, 'blur'))
            this.validate();
        this.isFocused = false;
        this.blur.emit();
        this.focusChange.emit(false);
        this.onTouched(); // registers
    }
    onPaste(e) {
        if (this.preventPaste)
            e.preventDefault();
    }
    onInputChange() {
        if (this.safeIncludes(this.validateOn, 'change'))
            this.validate();
    }
    onInput() {
        if (this.safeIncludes(this.validateOn, 'input'))
            this.validate();
    }
    onSelectFocus() {
        if (this.isSelectFocused === true)
            return;
        this.isSelectFocused = true;
    }
    preventCharacters(e) {
        if (!this.typeNumbersOnly)
            return;
        if (e.key === 'TAB' || e.code === 'TAB')
            return;
        if (e.shiftKey && e.keyCode === 9)
            return;
        if (e.shiftKey || e.which > 57 || ['.', ',', '/', '\'', '-'].includes(e.key))
            e.preventDefault();
    }
    // #endregion
    // #region - Methods: Masking
    // updates the mask from the template when the area or coutry code changes and requires a new mask
    updateMask(selected) {
        if (this.isAreaCodes)
            this.areaCodeChange.emit(selected);
        if (this.isCountryCodes)
            this.countryCodeChange.emit(selected);
        const options = this.countryCodes.length > 0 ? this.countryCodes : this.areaCodes;
        const mask = options.find(option => option.value === selected).mask;
        if (!!mask)
            this.mask = mask;
    }
    writeValueAndValidate(value) {
        this.writeValue(value);
        if (this.safeIncludes(this.validateOn, 'input'))
            this.validate();
    }
    // #endregion
    // #region - Methods: Validation
    setDynamicMessages() {
        const dynamicMessage = {
            minLength: this.minLengthError || `Input length must exceed ${this.minLength - 1} characters`,
            maxLength: this.maxLengthError || `Input length may not exceed ${this.maxLength} characters`,
            maxFactor: this.maxFactorExceededMessage || `Input value cannot exceed ${this.maxFactor} digits before the decimal`,
            custom: this.customErrorMessage
        };
        if (this.maxValue)
            dynamicMessage.maxValue = 'Value cannot exceed ' + this.formatDollar(this.maxValue);
        if (this.minValue)
            dynamicMessage.minValue = 'Value cannot subceed ' + this.formatDollar(this.minValue);
        this.errors = Object.assign(dynamicMessage, this.errors);
    }
    // only used for error messages, not for formatting the input field
    formatDollar(number) {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    // Clears an error state on the input. Takes a type of error
    toggleError(errorType, error) {
        // Clear the error state
        this._error = error;
        this.errorType = errorType;
        this.errorChange.emit(error);
        if (error) {
            // override message for customer errors
            if (this._hasCustomError)
                errorType = 'custom';
            // set the error message
            this.errorMessage = (!!errorType) ? this.errors[errorType] : '';
            this.validationService.addError(this);
        }
        else
            this.validationService.removeError(this);
    }
    validate() {
        if (this.required && !Input2Component.VALIDATORS.isValue(this.value) && this.value !== 0) {
            this.toggleError('required', true);
            return;
        }
        if (this.isCbolPassword) {
            this.toggleError('custom', this.error);
            return;
        }
        if (this.shouldValidate.minLength() && (!this.value || this.minLength > this.value.length)) {
            this.toggleError('minLength', true);
            return;
        }
        if (this.shouldValidate.number())
            this.validateNumber(); // and dollar & currency
        else if (this.shouldValidate.zip())
            this.validateZip();
        else if (this.shouldValidate.email())
            this.validateEmail();
        else if (this.shouldValidate.ssn())
            this.validateSSN();
        else if (this.shouldValidate.phone())
            this.validatePhone();
        else
            this.toggleError(null, false);
    }
    // and dollar-currency
    validateNumber() {
        const newValue = parseFloat(this.value);
        if (this.allowIntegerOnly && !Number.isInteger(newValue))
            this.toggleError('integer', true);
        else if (this.allowNonZeroValueOnly && newValue === 0)
            this.toggleError('nonZero', true);
        else if (this.allowPositiveIntegerOnly && (!Number.isInteger(newValue) || newValue <= 0))
            this.toggleError('positive', true);
        else if (!!this.maxValue && newValue > this.maxValue)
            this.toggleError('maxValue', true);
        else if (!!this.minValue && newValue < this.minValue)
            this.toggleError('minValue', true);
        else if (!!this.maxFactor && this.value > Math.pow(10, this.maxFactor + 1))
            this.toggleError('maxFactor', true);
        else
            this.toggleError(null, false);
    }
    validateEmail() {
        if (!Input2Component.VALIDATORS.isEmail(this.value))
            this.toggleError('email', true);
        else
            this.toggleError(null, false);
    }
    validateSSN() {
        const newValue = parseFloat(this.value);
        if (this.value.length !== 9 || !Number.isInteger(newValue))
            this.toggleError('ssn', true);
        else
            this.toggleError(null, false);
    }
    validateZip() {
        // strip off all non-numeric values from the mask to determine the number of characters
        const requiredLength = this._appliedMask.replace(/[^0-9]/g, '').length;
        if (!this.value || requiredLength !== this.value.length)
            this.toggleError('zip', true);
        else
            this.toggleError(null, false);
    }
    validatePhone() {
        // strip off all non-numeric values from the mask to determine the number of characters
        const hasBlanks = this._appliedMask.replace(/[^0-9]/g, '').length !== this.value.length;
        const isNull = !this.value || this.value.length === 0;
        if (this._appliedMask !== 'none' && (hasBlanks && this.required || !isNull && hasBlanks))
            this.toggleError('phone', true);
        else if (this.selectAddonIsValid(this.countryCodes, this.countryCode))
            this.toggleError('countryCode', true);
        else if (this.selectAddonIsValid(this.areaCodes, this.areaCode))
            this.toggleError('areaCode', true);
        else
            this.toggleError(null, false);
    }
    selectAddonIsValid(options, value) {
        return this.required && !value && options && options.length > 0;
    }
    safeIncludes(array, value) {
        return array && array.length && array.includes(value);
    }
    /**
     * @Function: reset
     * Resets the apperance and state of the input to initial
     * @Return: void
     */
    reset() {
        this.toggleError(null, false);
        this.value = null;
    }
    // #endregion
    // #region - Methods: Accessibility
    get describedByID() {
        if (this.describedby)
            return this.describedby;
        else if (this.error)
            return `${this._id}-error`;
        else if (this.isSuccess)
            return `${this._id}-success`;
    }
    getAriaLabelString() {
        if (this.hasValue && this.readOnly)
            return null;
        const resultString = this.label || this.placeholder;
        if (!this.ariaLabel || !Input2Component.VALIDATORS.isValue(resultString))
            return '';
        else
            return resultString;
    }
    getAriaLabelledBy() {
        if (!!this.ariaLabelledById)
            return this.ariaLabelledById;
        else if (!this.hasValue || !this.readOnly)
            return this.labelId;
        else
            return null;
    }
    get labeltext() {
        return this.label || this.placeholder;
    }
    get selectLabeltext() {
        return this.selectLabel || this.label || this.placeholder;
    }
    showhideMaskcheck(e) {
        this.hideCharacters = !this.hideCharacters;
        this.hideToggle.emit(this.hideCharacters);
    }
    /**
     * @Method: showhideMaskcheckKeyboard
     * This method gets called when enter key is pressed on the hide/show button
     */
    showhideMaskcheckKeyboard(e) {
        if (e.keyCode === 13)
            this.showhideMaskcheck(e);
    }
    // Allows Angular to update the model.
    // Update the model and changes needed for the view here.
    writeValue(value) {
        this.value = value;
        this.valueChange.emit(value);
        //      !!!!    WARNING: DO NOT REMOVE    !!!!
        // this is actually necessary for ngModel to update
        this.onChange(this.value);
    }
    // Allows Angular to register a function to call when the model changes.
    // Save the function as a property to call later here.
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // Allows Angular to disable the input.
    setDisabledState(isDisabled) {
        this.readOnly = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Input2Component, deps: [{ token: ValidationService }, { token: CitiLiveAnnouncer }, { token: i0.ElementRef }, { token: NAVIGATOR }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: Input2Component, isStandalone: false, selector: "citi-input2:not(.4Q21)", inputs: { validateOn: "validateOn", allowIntegerOnly: "allowIntegerOnly", maxLength: "maxLength", allowPositiveIntegerOnly: "allowPositiveIntegerOnly", minLength: "minLength", readOnly: "readOnly", disabled: "disabled", typeNumbersOnly: "typeNumbersOnly", allowNonZeroValueOnly: "allowNonZeroValueOnly", autocomplete: "autocomplete", ariaAutocomplete: "ariaAutocomplete", preventPaste: "preventPaste", thousandsSeparator: "thousandsSeparator", decimalSeparator: "decimalSeparator", precision: "precision", step: "step", maxValue: "maxValue", minValue: "minValue", maxFactor: "maxFactor", maxFactorExceededMessage: "maxFactorExceededMessage", currencySignAfter: "currencySignAfter", currencySignBefore: "currencySignBefore", ariaLabel: "ariaLabel", ariaLabelledById: "ariaLabelledById", idStr: "idStr", name: "name", label: "label", selectLabel: "selectLabel", placeholder: "placeholder", searchIcon: "searchIcon", required: "required", describedby: "describedby", tabIndex: "tabIndex", isCbolPassword: "isCbolPassword", countryCode: "countryCode", countryCodes: "countryCodes", areaCode: "areaCode", areaCodes: "areaCodes", unmaskedLength: "unmaskedLength", theme: "theme", applyMaskOnEvents: "applyMaskOnEvents", removeMaskOnEvents: "removeMaskOnEvents", isSuccess: "isSuccess", successMessage: "successMessage", visibleLabel: "visibleLabel", hideMask: "hideMask", value: "value", focus: "focus", minLengthError: "minLengthError", maxValueError: "maxValueError", minValueError: "minValueError", maxLengthError: "maxLengthError", requiredError: "requiredError", messageOnlyNonZeroError: "messageOnlyNonZeroError", messageOnlyIntegerError: "messageOnlyIntegerError", messageOnlyPositiveIntegerError: "messageOnlyPositiveIntegerError", messageOnlyPhoneError: "messageOnlyPhoneError", messageOnlyZipcodeError: "messageOnlyZipcodeError", messageOnlyEmailError: "messageOnlyEmailError", messageOnlyZipError: "messageOnlyZipError", messageOnlySsnError: "messageOnlySsnError", messageOnlyAreaCodeError: "messageOnlyAreaCodeError", messageOnlyCountryCodeError: "messageOnlyCountryCodeError", changeMessage: "changeMessage", hasCustomError: "hasCustomError", customErrorMessage: "customErrorMessage", error: "error", usePasswordOnAndroid: "usePasswordOnAndroid", mask: "mask", hideShowButtonWhenMasked: "hideShowButtonWhenMasked", showText: "showText", hideText: "hideText", type: "type", size: "size" }, outputs: { countryCodeChange: "countryCodeChange", areaCodeChange: "areaCodeChange", valueChange: "valueChange", hideToggle: "hideToggle", focusChange: "focusChange", blur: "blur", errorChange: "errorChange" }, host: { properties: { "class.row": "this.classRow" }, classAttribute: "citi-input2" }, providers: [INPUT2_VALUE_ACCESSOR$1], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true }, { propertyName: "custom", first: true, predicate: CustomInputMaskDirective, descendants: true }], ngImport: i0, template: `

    <div class="form-control-container {{theme}}" [ngClass]="size">
      <!-- label for the input, will switch text to select element's label when that element is focused -->
      <label [for]="_id" [id]="labelId" tabIndex="-1" [ngStyle]="{'opacity': labelOpacity}" [innerHtml]="(isSelectFocused ? selectLabeltext : labeltext)"></label>
      <!-- input wapper (all borders) -->
      <div class="input-container"
        [ngClass]="{
          'focused': isFocused,
          'errored': _error,
          'has-pre-addon': hasPreAddon,
          'readOnly': readOnly,
          'disabled': disabled,
          'success': isSuccess
        }">
        <!-- Add-ons Pre -->
        <div class="add-on-pre">
          <!-- Phone Area Code Selector -->
          <select *ngIf="isAreaCodes"
            [attr.name]="name"
            class="form-control"
            (change)="updateMask($event.target.value)"
            [attr.aria-label]="selectLabeltext"
            (focus)="onSelectFocus()">
            <option *ngFor="let code of areaCodes" [value]="code.value" [innerHtml]="code.label" [attr.selected]="(code.selected === true ? true : null)"></option>
          </select>
          <!-- Phone Country Prefix Selector -->
          <select *ngIf="isCountryCodes"
            [attr.name]="name"
            (change)="updateMask($event.target.value)"
            class="form-control"
            [attr.aria-label]="selectLabeltext"
            (focus)="onSelectFocus()">
            <option *ngFor="let code of countryCodes" [value]="code.value" [innerHtml]="code.label" [attr.selected]="(code.selected === true ? true : null)"></option>
          </select>
          <span *ngIf="currencySignBefore" (click)="focusInput()" class="input-group-addon before">{{currencySignBefore}}</span>
        </div>
        <!-- The Inputs managed by a switch statement -->
        <div [ngSwitch]="displayedInput" class="input-switch-wrapper">
          <!-- Custom Mask -->
          <input
            *ngSwitchCase="'custom'"
            #input
            #custom="custom"
            [attr.type]="(inputType == 'tel' ? 'tel' : 'text')"
            [custom-mask]="_appliedMask"
            [hideCharacters]="hideCharacters"
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValue($event)"
            [attr.id]="_id"
            [attr.name]="name"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            [attr.tabindex]="tabIndex"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.placeholder]="placeholder"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-describedby]="(isMobile ? describedByID : null)"
          />
          <!-- Dollar Mask -->
          <input
            *ngSwitchCase="'dollar'"
            #input
            type="text"
            currency-mask
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValueAndValidate($event)"
            [thousandsSeparator]="thousandsSeparator"
            [decimalSeparator]="decimalSeparator"
            [maxFactor]="maxFactor"
            [precision]="precision"
            [preventPaste]="preventPaste"
            [maxFactorExceededMessage]="maxFactorExceededMessage"
            [attr.id]="_id"
            [attr.name]="name"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-describedby]="(isMobile ? describedByID : null)"
          />
          <!-- Partial Mask -->
          <input
            *ngSwitchCase="'partial'"
            #input
            type="text"
            [partial-mask]="_appliedMask"
            [hideCharacters]="hideCharacters"
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValue($event)"
            [unmaskedLength]="unmaskedLength"
            [maxLength]="maxLength"
            [preventPaste]="preventPaste"
            [numbersOnly]="typeNumbersOnly"
            [attr.id]="_id"
            [attr.name]="name"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="(ariaAutocomplete ? ariaAutocomplete : null)"
            [attr.autocomplete]="(autocomplete ? autocomplete : null)"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-describedby]="(isMobile ? describedByID : null)"
          />
          <!-- No Mask -->
          <input
            *ngSwitchCase="'none'"
            #input
            [ngModel]="value"
            [attr.type]="inputType"
            (ngModelChange)="writeValue($event)"
            [attr.id]="_id"
            [attr.name]="name"
            [attr.step]="step"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            [attr.maxlength]="maxLength"
            autocapitalize="none"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            (keypress)="preventCharacters($event)"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            (paste)="onPaste($event)"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-describedby]="(isMobile ? describedByID : null)"
          />

        </div>
        <div class="add-on-post">
         <button
            #button1
            type=button
            *ngIf="hideShowButtonWhenMasked && value"
            class="input-group-addon showHideMask"
            (click)="showhideMaskcheck($event)"
            (keydown)="showhideMaskcheckKeyboard($event)"
            >
            {{showHideMaskText}}
      </button>
          <citi-icon2
            *ngIf="searchIcon"
            name="006-search"
            ariaLabel="icon"
            [idStr]="_id ? _id + '-search-icon' : null"
            shade="dark"
            [svgStyle]="{'width.px': 50, 'height.px': 50}">
          </citi-icon2>
          <span *ngIf="currencySignAfter" (click)="focusInput()" class="input-group-addon after">{{currencySignAfter}}</span>
        </div>
      </div>

      <div class="input-error" *ngIf="_error">
        <span [attr.id]="describedByID" class="validation-message-danger">{{errorMessage}}</span>
      </div>
      <div class="input-success" *ngIf="!_error && isSuccess">
        <span [attr.id]="describedByID" class="validation-message-success">{{successMessage}}</span>
      </div>
    </div>
  `, isInline: true, styles: [":host{display:block}label{font-size:.75rem;color:#666;width:100%;pointer-events:none;transition:opacity ease-in-out 225ms}.input-container{width:100%;margin:3px 0 5px;box-sizing:border-box;-moz-box-sizing:border-box;float:left;display:-webkit-inline-box;display:-webkit-inline-flex;display:-moz-inline-flex;display:-ms-inline-flexbox;display:inline-flex;-webkit-box-direction:normal;-webkit-box-orient:horizontal;-webkit-flex-direction:row;-moz-flex-direction:row;-ms-flex-direction:row;flex-direction:row;-webkit-flex-wrap:nowrap;-moz-flex-wrap:nowrap;-ms-flex-wrap:none;flex-wrap:nowrap;-webkit-justify-content:flex-start;-ms-flex-pack:start;justify-content:flex-start;-webkit-align-content:stretch;-ms-flex-line-pack:stretch;align-content:stretch;-webkit-align-items:flex-start;-ms-flex-align:start;align-items:flex-start;border-radius:8px;background:#eee;border:1px solid transparent;border:.9px solid transparent}.input-container.focused{border-color:#056dae}.input-container.focused input::placeholder{opacity:0}.input-container.errored{border-color:#d60000}.input-container.errored.focused{width:calc(100% + 2px);border-width:1.99px;margin:2px 0 4px -1px}.input-container.success{border-color:#006e0a}.input-container.success.focused{width:calc(100% + 2px);border-width:1.99px;margin:2px 0 4px -1px}.input-container.readOnly{background:#fff;border:1px solid #666}.input-container.readOnly input.readOnly:read-only,.input-container.readOnly select,.input-container.readOnly span{background-color:#fff}.input-container.disabled{background:#fff;border:1px solid #666}.input-container.disabled input.disabled:disabled,.input-container.disabled select,.input-container.disabled span{background-color:#fff}.input-container.has-pre-addon input{padding-left:0!important}.input-container .input-group-addon.before{border-top-left-radius:8px;border-bottom-left-radius:8px}.input-container .input-group-addon.before,.input-container .input-group-addon.after{width:48px;height:48px}.input-container .input-group-addon.after{border-top-right-radius:8px;border-bottom-right-radius:8px}.input-container .add-on-pre{-webkit-order:0;-ms-flex-order:0;order:0;-webkit-flex:0 1 auto;-ms-flex:0 1 auto;flex:0 1 auto;-webkit-align-self:auto;-ms-flex-item-align:auto;align-self:auto;box-sizing:border-box;-moz-box-sizing:border-box}.input-container .add-on-pre select{margin:0;width:105px;border:1px solid transparent;border-top-right-radius:0;border-bottom-right-radius:0}.input-container .add-on-pre select:focus{border-color:#056dae;outline:none}.input-container .input-switch-wrapper{flex:1 1 0%;box-sizing:border-box;-moz-box-sizing:border-box;margin:0 2px;border-radius:8px}.input-container .input-switch-wrapper input{width:100%;box-sizing:border-box;-moz-box-sizing:border-box;letter-spacing:.5px;height:48px;padding:10px 20px;background:#eee;border:none!important;box-shadow:none;border-radius:8px}.input-container .input-switch-wrapper input::-ms-clear{display:none}.input-container .input-switch-wrapper input:focus{outline:none}.input-container .add-on-post{flex:0 1 auto;box-sizing:border-box;-moz-box-sizing:border-box}.input-container .add-on-post .search{width:50px;padding-top:12px;border-radius:8px}.input-container .add-on-post .showHideMask{width:50px;border-radius:8px;padding:17px 20px 17px 10px;color:#056dae;font-family:Interstate_Bold,sans-serif;line-height:14px;cursor:pointer;font-size:12px}.input-error,.input-success{height:16px;width:100%;float:left}.input-success .validation-message-success{font-size:12px;font-family:Interstate_Bold,sans-serif;margin:0;color:#006e0a;font-weight:700;position:relative;display:block;line-height:1rem;font-size:.75rem}:host .light .input-container{background:#fff}:host .light .input-container input,:host .light .input-container select,:host .light .input-container span{background-color:#fff}:host .light .input-container.readOnly{background:#eee}:host .light .input-container.readOnly input,:host .light .input-container.readOnly select,:host .light .input-container.readOnly span{background-color:#eee}::ng-deep #inputSearchIconId path{fill:#056dae}::-webkit-input-placeholder{color:#6b6b6b}::-moz-placeholder{color:#6b6b6b}:-ms-input-placeholder{color:#6b6b6b}:-moz-placeholder{color:#6b6b6b}input::-ms-reveal,input::-ms-clear{display:none}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i3$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i3$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i4.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: Icon2Component, selector: "citi-icon2", inputs: ["role", "idStr", "ariaLabel", "ariaHidden", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "shade", "name"] }, { kind: "directive", type: CustomInputMaskDirective, selector: "[custom-mask]", inputs: ["hideCharacters", "custom-mask", "unmaskedValue"], outputs: ["unmaskedValueChange"], exportAs: ["custom"] }, { kind: "directive", type: CurrencyInputMaskDirective, selector: "[currency-mask]", inputs: ["unmaskedValue", "thousandsSeparator", "decimalSeparator", "precision", "maxFactor", "maxFactorExceededMessage", "preventPaste"], outputs: ["unmaskedValueChange"] }, { kind: "directive", type: PartialInputMaskDirective, selector: "[partial-mask]", inputs: ["maxLength", "unmaskedLength", "numbersOnly", "preventPaste", "hideCharacters", "partial-mask", "unmaskedValue"], outputs: ["unmaskedValueChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Input2Component, decorators: [{
            type: Component,
            args: [{ selector: 'citi-input2:not(.4Q21)', standalone: false, providers: [INPUT2_VALUE_ACCESSOR$1], host: { class: 'citi-input2' }, template: `

    <div class="form-control-container {{theme}}" [ngClass]="size">
      <!-- label for the input, will switch text to select element's label when that element is focused -->
      <label [for]="_id" [id]="labelId" tabIndex="-1" [ngStyle]="{'opacity': labelOpacity}" [innerHtml]="(isSelectFocused ? selectLabeltext : labeltext)"></label>
      <!-- input wapper (all borders) -->
      <div class="input-container"
        [ngClass]="{
          'focused': isFocused,
          'errored': _error,
          'has-pre-addon': hasPreAddon,
          'readOnly': readOnly,
          'disabled': disabled,
          'success': isSuccess
        }">
        <!-- Add-ons Pre -->
        <div class="add-on-pre">
          <!-- Phone Area Code Selector -->
          <select *ngIf="isAreaCodes"
            [attr.name]="name"
            class="form-control"
            (change)="updateMask($event.target.value)"
            [attr.aria-label]="selectLabeltext"
            (focus)="onSelectFocus()">
            <option *ngFor="let code of areaCodes" [value]="code.value" [innerHtml]="code.label" [attr.selected]="(code.selected === true ? true : null)"></option>
          </select>
          <!-- Phone Country Prefix Selector -->
          <select *ngIf="isCountryCodes"
            [attr.name]="name"
            (change)="updateMask($event.target.value)"
            class="form-control"
            [attr.aria-label]="selectLabeltext"
            (focus)="onSelectFocus()">
            <option *ngFor="let code of countryCodes" [value]="code.value" [innerHtml]="code.label" [attr.selected]="(code.selected === true ? true : null)"></option>
          </select>
          <span *ngIf="currencySignBefore" (click)="focusInput()" class="input-group-addon before">{{currencySignBefore}}</span>
        </div>
        <!-- The Inputs managed by a switch statement -->
        <div [ngSwitch]="displayedInput" class="input-switch-wrapper">
          <!-- Custom Mask -->
          <input
            *ngSwitchCase="'custom'"
            #input
            #custom="custom"
            [attr.type]="(inputType == 'tel' ? 'tel' : 'text')"
            [custom-mask]="_appliedMask"
            [hideCharacters]="hideCharacters"
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValue($event)"
            [attr.id]="_id"
            [attr.name]="name"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            [attr.tabindex]="tabIndex"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.placeholder]="placeholder"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-describedby]="(isMobile ? describedByID : null)"
          />
          <!-- Dollar Mask -->
          <input
            *ngSwitchCase="'dollar'"
            #input
            type="text"
            currency-mask
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValueAndValidate($event)"
            [thousandsSeparator]="thousandsSeparator"
            [decimalSeparator]="decimalSeparator"
            [maxFactor]="maxFactor"
            [precision]="precision"
            [preventPaste]="preventPaste"
            [maxFactorExceededMessage]="maxFactorExceededMessage"
            [attr.id]="_id"
            [attr.name]="name"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-describedby]="(isMobile ? describedByID : null)"
          />
          <!-- Partial Mask -->
          <input
            *ngSwitchCase="'partial'"
            #input
            type="text"
            [partial-mask]="_appliedMask"
            [hideCharacters]="hideCharacters"
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValue($event)"
            [unmaskedLength]="unmaskedLength"
            [maxLength]="maxLength"
            [preventPaste]="preventPaste"
            [numbersOnly]="typeNumbersOnly"
            [attr.id]="_id"
            [attr.name]="name"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="(ariaAutocomplete ? ariaAutocomplete : null)"
            [attr.autocomplete]="(autocomplete ? autocomplete : null)"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-describedby]="(isMobile ? describedByID : null)"
          />
          <!-- No Mask -->
          <input
            *ngSwitchCase="'none'"
            #input
            [ngModel]="value"
            [attr.type]="inputType"
            (ngModelChange)="writeValue($event)"
            [attr.id]="_id"
            [attr.name]="name"
            [attr.step]="step"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            [attr.maxlength]="maxLength"
            autocapitalize="none"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            (keypress)="preventCharacters($event)"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            (paste)="onPaste($event)"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-describedby]="(isMobile ? describedByID : null)"
          />

        </div>
        <div class="add-on-post">
         <button
            #button1
            type=button
            *ngIf="hideShowButtonWhenMasked && value"
            class="input-group-addon showHideMask"
            (click)="showhideMaskcheck($event)"
            (keydown)="showhideMaskcheckKeyboard($event)"
            >
            {{showHideMaskText}}
      </button>
          <citi-icon2
            *ngIf="searchIcon"
            name="006-search"
            ariaLabel="icon"
            [idStr]="_id ? _id + '-search-icon' : null"
            shade="dark"
            [svgStyle]="{'width.px': 50, 'height.px': 50}">
          </citi-icon2>
          <span *ngIf="currencySignAfter" (click)="focusInput()" class="input-group-addon after">{{currencySignAfter}}</span>
        </div>
      </div>

      <div class="input-error" *ngIf="_error">
        <span [attr.id]="describedByID" class="validation-message-danger">{{errorMessage}}</span>
      </div>
      <div class="input-success" *ngIf="!_error && isSuccess">
        <span [attr.id]="describedByID" class="validation-message-success">{{successMessage}}</span>
      </div>
    </div>
  `, styles: [":host{display:block}label{font-size:.75rem;color:#666;width:100%;pointer-events:none;transition:opacity ease-in-out 225ms}.input-container{width:100%;margin:3px 0 5px;box-sizing:border-box;-moz-box-sizing:border-box;float:left;display:-webkit-inline-box;display:-webkit-inline-flex;display:-moz-inline-flex;display:-ms-inline-flexbox;display:inline-flex;-webkit-box-direction:normal;-webkit-box-orient:horizontal;-webkit-flex-direction:row;-moz-flex-direction:row;-ms-flex-direction:row;flex-direction:row;-webkit-flex-wrap:nowrap;-moz-flex-wrap:nowrap;-ms-flex-wrap:none;flex-wrap:nowrap;-webkit-justify-content:flex-start;-ms-flex-pack:start;justify-content:flex-start;-webkit-align-content:stretch;-ms-flex-line-pack:stretch;align-content:stretch;-webkit-align-items:flex-start;-ms-flex-align:start;align-items:flex-start;border-radius:8px;background:#eee;border:1px solid transparent;border:.9px solid transparent}.input-container.focused{border-color:#056dae}.input-container.focused input::placeholder{opacity:0}.input-container.errored{border-color:#d60000}.input-container.errored.focused{width:calc(100% + 2px);border-width:1.99px;margin:2px 0 4px -1px}.input-container.success{border-color:#006e0a}.input-container.success.focused{width:calc(100% + 2px);border-width:1.99px;margin:2px 0 4px -1px}.input-container.readOnly{background:#fff;border:1px solid #666}.input-container.readOnly input.readOnly:read-only,.input-container.readOnly select,.input-container.readOnly span{background-color:#fff}.input-container.disabled{background:#fff;border:1px solid #666}.input-container.disabled input.disabled:disabled,.input-container.disabled select,.input-container.disabled span{background-color:#fff}.input-container.has-pre-addon input{padding-left:0!important}.input-container .input-group-addon.before{border-top-left-radius:8px;border-bottom-left-radius:8px}.input-container .input-group-addon.before,.input-container .input-group-addon.after{width:48px;height:48px}.input-container .input-group-addon.after{border-top-right-radius:8px;border-bottom-right-radius:8px}.input-container .add-on-pre{-webkit-order:0;-ms-flex-order:0;order:0;-webkit-flex:0 1 auto;-ms-flex:0 1 auto;flex:0 1 auto;-webkit-align-self:auto;-ms-flex-item-align:auto;align-self:auto;box-sizing:border-box;-moz-box-sizing:border-box}.input-container .add-on-pre select{margin:0;width:105px;border:1px solid transparent;border-top-right-radius:0;border-bottom-right-radius:0}.input-container .add-on-pre select:focus{border-color:#056dae;outline:none}.input-container .input-switch-wrapper{flex:1 1 0%;box-sizing:border-box;-moz-box-sizing:border-box;margin:0 2px;border-radius:8px}.input-container .input-switch-wrapper input{width:100%;box-sizing:border-box;-moz-box-sizing:border-box;letter-spacing:.5px;height:48px;padding:10px 20px;background:#eee;border:none!important;box-shadow:none;border-radius:8px}.input-container .input-switch-wrapper input::-ms-clear{display:none}.input-container .input-switch-wrapper input:focus{outline:none}.input-container .add-on-post{flex:0 1 auto;box-sizing:border-box;-moz-box-sizing:border-box}.input-container .add-on-post .search{width:50px;padding-top:12px;border-radius:8px}.input-container .add-on-post .showHideMask{width:50px;border-radius:8px;padding:17px 20px 17px 10px;color:#056dae;font-family:Interstate_Bold,sans-serif;line-height:14px;cursor:pointer;font-size:12px}.input-error,.input-success{height:16px;width:100%;float:left}.input-success .validation-message-success{font-size:12px;font-family:Interstate_Bold,sans-serif;margin:0;color:#006e0a;font-weight:700;position:relative;display:block;line-height:1rem;font-size:.75rem}:host .light .input-container{background:#fff}:host .light .input-container input,:host .light .input-container select,:host .light .input-container span{background-color:#fff}:host .light .input-container.readOnly{background:#eee}:host .light .input-container.readOnly input,:host .light .input-container.readOnly select,:host .light .input-container.readOnly span{background-color:#eee}::ng-deep #inputSearchIconId path{fill:#056dae}::-webkit-input-placeholder{color:#6b6b6b}::-moz-placeholder{color:#6b6b6b}:-ms-input-placeholder{color:#6b6b6b}:-moz-placeholder{color:#6b6b6b}input::-ms-reveal,input::-ms-clear{display:none}\n"] }]
        }], ctorParameters: () => [{ type: ValidationService }, { type: CitiLiveAnnouncer }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NAVIGATOR]
                }] }], propDecorators: { validateOn: [{
                type: Input
            }], allowIntegerOnly: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], allowPositiveIntegerOnly: [{
                type: Input
            }], minLength: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], disabled: [{
                type: Input
            }], typeNumbersOnly: [{
                type: Input
            }], allowNonZeroValueOnly: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], ariaAutocomplete: [{
                type: Input
            }], preventPaste: [{
                type: Input
            }], thousandsSeparator: [{
                type: Input
            }], decimalSeparator: [{
                type: Input
            }], precision: [{
                type: Input
            }], step: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], minValue: [{
                type: Input
            }], maxFactor: [{
                type: Input
            }], maxFactorExceededMessage: [{
                type: Input
            }], currencySignAfter: [{
                type: Input
            }], currencySignBefore: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledById: [{
                type: Input
            }], idStr: [{
                type: Input
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }], selectLabel: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], searchIcon: [{
                type: Input
            }], required: [{
                type: Input
            }], describedby: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], isCbolPassword: [{
                type: Input
            }], countryCode: [{
                type: Input
            }], countryCodeChange: [{
                type: Output
            }], countryCodes: [{
                type: Input
            }], areaCode: [{
                type: Input
            }], areaCodeChange: [{
                type: Output
            }], areaCodes: [{
                type: Input
            }], unmaskedLength: [{
                type: Input
            }], theme: [{
                type: Input
            }], applyMaskOnEvents: [{
                type: Input
            }], removeMaskOnEvents: [{
                type: Input
            }], isSuccess: [{
                type: Input
            }], successMessage: [{
                type: Input
            }], visibleLabel: [{
                type: Input
            }], hideMask: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], hideToggle: [{
                type: Output
            }], focus: [{
                type: Input
            }], focusChange: [{
                type: Output
            }], blur: [{
                type: Output
            }], minLengthError: [{
                type: Input
            }], maxValueError: [{
                type: Input
            }], minValueError: [{
                type: Input
            }], maxLengthError: [{
                type: Input
            }], requiredError: [{
                type: Input
            }], messageOnlyNonZeroError: [{
                type: Input
            }], messageOnlyIntegerError: [{
                type: Input
            }], messageOnlyPositiveIntegerError: [{
                type: Input
            }], messageOnlyPhoneError: [{
                type: Input
            }], messageOnlyZipcodeError: [{
                type: Input
            }], messageOnlyEmailError: [{
                type: Input
            }], messageOnlyZipError: [{
                type: Input
            }], messageOnlySsnError: [{
                type: Input
            }], messageOnlyAreaCodeError: [{
                type: Input
            }], messageOnlyCountryCodeError: [{
                type: Input
            }], changeMessage: [{
                type: Input
            }], hasCustomError: [{
                type: Input
            }], customErrorMessage: [{
                type: Input
            }], error: [{
                type: Input
            }], errorChange: [{
                type: Output
            }], usePasswordOnAndroid: [{
                type: Input
            }], mask: [{
                type: Input
            }], hideShowButtonWhenMasked: [{
                type: Input
            }], showText: [{
                type: Input
            }], hideText: [{
                type: Input
            }], type: [{
                type: Input
            }], size: [{
                type: Input
            }], input: [{
                type: ViewChild,
                args: ['input']
            }], custom: [{
                type: ViewChild,
                args: [CustomInputMaskDirective]
            }], classRow: [{
                type: HostBinding,
                args: ['class.row']
            }] } });

// @dynamic
let nextUniqueId = 0;
const DEFAULT_ERRORS = {
    required: 'Input is required',
    positive: 'Value must be positive',
    integer: 'Value must be an integer',
    phone: 'Invalid phone number',
    nonZero: 'value cannot be zero',
    email: 'Invalid email',
    zip: 'Invalid Zip Code',
    ssn: 'Invalid social security number',
    areaCode: 'Please select an area code',
    countryCode: 'Please select a country code'
};
const INPUT2_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => Input2Component4Q21),
    multi: true
};
// @dynamic
class Input2Component4Q21 {
    // #region - Properties: Static
    static { this.DEFAULT_MAXLENGTH = 524288; }
    static { this.VALIDATORS = {
        isEmail: (val) => /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(val),
        isNumber: (val) => !isNaN(val),
        isPositive: (val) => !isNaN(val) && parseFloat(val) > 0,
        isValue: (val) => !!val && val !== ''
    }; }
    static { this.VALID_TYPES = [
        'text',
        'password',
        'email',
        'number',
        'tel',
        'phone',
        'ssn',
        'zip'
    ]; }
    static { this.DEFAULT_MASKS = {
        phone: '(999) 999-9999',
        zipcode: '99999',
        ssn: '999-99-9999',
        partialSsnMasking: '*9*9*9-*9*9-9999',
        fullSsnMasking: '*9*9*9-*9*9-*9*9*9*9',
        partialPhoneMasking: '(*9*9*9)-*9*9*9-9999',
        fullZipcodeMasking: '*9*9*9*9*9',
        fullPhoneMasking: '(*9*9*9) *9*9*9-*9*9*9*9',
        creditCard: '9999 9999 9999 9999'
    }; }
    // Classes for the sizes of input, also applied to the wrapper
    static { this.SIZE_CLASSES = {
        fullwidth: 'col-xs-12',
        large: 'col-md-8 col-lg-8',
        medium: 'col-md-6 col-sm-9',
        small: 'col-md-4 col-sm-6',
        xsmall: 'col-md-2 col-sm-3 col-xs-6',
        none: ''
    }; }
    get idStr() { return this._id; }
    set idStr(value) { this._id = value || this._uid; }
    /**
     * How many character should not be masked at the end of the string, only applies to text masking
     */
    set unmaskedLength(value) {
        this._unmaskedLength = this._unmaskedViewLength = value;
    }
    get unmaskedLength() {
        return this._unmaskedViewLength;
    }
    // #endregion
    // #region - Two-way Bound values
    get hideMask() {
        return this.hideCharacters;
    }
    set hideMask(value) {
        this.hideCharacters = value;
    }
    /**
     * This focuses the input if set to true
     */
    set focus(isFocused) {
        isFocused ? this.onFocus() : this.onBlur();
    }
    ;
    // #endregion
    // #region - Setters
    /**
     * Text to be displayed when the input doesn't meet the required length
     */
    set minLengthError(message) {
        if (message)
            this.errors.minLength = message;
    }
    /**
     * Text to be displayed when the value exceed the provided maximum
     */
    set maxValueError(message) {
        if (message)
            this.errors.maxValue = message;
    }
    /**
     * Text to be displayed when the value exceed the provided maximum
     */
    set minValueError(message) {
        if (message)
            this.errors.minValue = message;
    }
    /**
     * Text to be displayed when the input exceed the maximum length
     */
    set maxLengthError(message) {
        if (message)
            this.errors.maxLength = message;
    }
    /**
     * Text to be displayed when the input is required and no value was entered
     */
    set requiredError(message) {
        this.errors.required = (!!message) ? message : DEFAULT_ERRORS.required;
    }
    /**
     * Text to be displayed when the value fails non-zero validation
     */
    set messageOnlyNonZeroError(message) {
        this.errors.nonZero = (!!message) ? message : DEFAULT_ERRORS.nonZero;
    }
    /**
     * Text to be displayed when the value fails only integer validation
     */
    set messageOnlyIntegerError(message) {
        this.errors.integer = (!!message) ? message : DEFAULT_ERRORS.integer;
    }
    /**
     * Text to be displayed when the value fails only positive integer validation
     */
    set messageOnlyPositiveIntegerError(message) {
        this.errors.positive = (!!message) ? message : DEFAULT_ERRORS.positive;
    }
    /**
     * Text to be displayed when no phone is provided
     */
    set messageOnlyPhoneError(message) {
        this.errors.phone = (!!message) ? message : DEFAULT_ERRORS.phone;
    }
    /**
     * Text to be displayed when no zipcode is provided
     */
    set messageOnlyZipcodeError(message) {
        this.errors.zipcode = (!!message) ? message : DEFAULT_ERRORS.zip;
    }
    /**
     * Text to be displayed when no email is provided
     */
    set messageOnlyEmailError(message) {
        this.errors.email = (!!message) ? message : DEFAULT_ERRORS.email;
    }
    /**
     * Text to be displayed when no zip is provided
     */
    set messageOnlyZipError(message) {
        this.errors.zip = (!!message) ? message : DEFAULT_ERRORS.zip;
    }
    /**
     * Text to be displayed when no SSN is provided
     */
    set messageOnlySsnError(message) {
        this.errors.ssn = (!!message) ? message : DEFAULT_ERRORS.ssn;
    }
    /**
     * Text to be displayed when no areaCode is provided
     */
    set messageOnlyAreaCodeError(message) {
        this.errors.areaCode = (!!message) ? message : DEFAULT_ERRORS.areaCode;
    }
    /**
     * Text to be displayed when no countryCode is provided
     */
    set messageOnlyCountryCodeError(message) {
        this.errors.countryCode = (!!message) ? message : DEFAULT_ERRORS.countryCode;
    }
    /**
     * allows users to customize any of the error messages
     */
    set changeMessage([errorType, msg]) {
        if (errorType && msg)
            this.errors[errorType] = msg;
    }
    set hasCustomError(useCustom) {
        this._hasCustomError = coerceBool(useCustom);
        if (this.error) {
            if (this._hasCustomError)
                this.errorMessage = this.errors.custom;
            else
                this.validate();
        }
    }
    /**
     * Customized error message for custom error
     * @Note: *this field need to be above the error setter to trigger initial error correctly
     */
    set customErrorMessage(message) {
        this.errors.custom = (!!message) ? message : 'Your Custom Error Message';
        if (this.error && this._hasCustomError)
            this.errorMessage = this.errors.custom;
    }
    set error(err) {
        if (err === this._error)
            return;
        if (err) {
            this.validationService.addError(this);
            this.errorMessage = this.errors['custom'] || 'Default Error. You should provide your own customErrorMessage';
        }
        this._error = err;
    }
    get error() {
        return this._error;
    }
    /**
     * Masking does not work on Android. This setting will set the input type to password on Android.
     */
    set usePasswordOnAndroid(usePass) {
        this._usePassOnAndroid = usePass;
        if (this.isAndroidDevice && usePass) {
            this.inputType = 'password';
        }
        else {
            this.inputType = this._inputType;
        }
    }
    ;
    get usePasswordOnAndroid() {
        return this._usePassOnAndroid;
    }
    /**
     * This defines the mask of the input, which changes the input text masking on character input.
     *  - 'fullZipcodeMasking'
     *  - 'fullPhoneMasking'
     *  - 'partialEmailMasking'
     *  - 'fullTextMasking'
     *  - 'partialTextMasking'
     * @defaults 'none'
     */
    set mask(mask) {
        this._storedMask = Input2Component4Q21.DEFAULT_MASKS[mask] || mask;
        // TODO: once Android 7 (Nougat) is no longer supported, we can fix this
        if (this.isAndroidDevice) {
            this.displayedInput = 'none';
            if (mask === 'dollar')
                this.inputType = 'number';
        }
        // selects the correct input and applied masking directive or lack there of
        else if (!mask || mask === 'none')
            this.displayedInput = 'none';
        else if (mask === 'dollar')
            this.displayedInput = 'dollar';
        else if (['partialEmailMasking', 'fullTextMasking', 'partialTextMasking'].includes(mask)) {
            this.displayedInput = 'partial';
            this._unmaskedViewLength = mask === 'fullTextMasking' ? 0 : this._unmaskedLength;
        }
        else
            this.displayedInput = 'custom';
        if (this.isPhantom)
            this.displayedInput = 'none';
        // look up the stroed masks if available
        this._appliedMask = Input2Component4Q21.DEFAULT_MASKS[mask] || mask;
    }
    get showHideMaskText() {
        return this.hideCharacters ? this.showText : this.hideText;
    }
    /**
     * This defines the type of the input, which changes which kind of input is displayed.
     * Mask must be set separately.
     * defaults to text.
     */
    set type(type) {
        if (type && Input2Component4Q21.VALID_TYPES.includes(type)) {
            this.inputType = type.includes('hone') ? 'tel' : type;
            this._inputType = this.inputType;
            if (this.isAndroidDevice && this.usePasswordOnAndroid) {
                this.inputType = 'password';
            }
        }
        else if (isDevMode)
            console.error('CITI-INPUT - TYPE: Caught \'' + type + '\'. Valid types are: ' + Input2Component4Q21.VALID_TYPES);
    }
    get type() {
        return this._inputType;
    }
    /**
     * This defines the size of the input, which changes the bootstrap classes.
     */
    set size(size) {
        if (!!size && Input2Component4Q21.SIZE_CLASSES[size] !== undefined) {
            this.inputSize = size;
            this.classRow = size === 'none' ? false : true;
        }
        else if (isDevMode) {
            console.error('CITI-INPUT - SIZE: Caught \'' + size + '\'. Valid sizes are: ' + Object.keys(Input2Component4Q21.SIZE_CLASSES));
        }
    }
    ;
    // #endregion
    // #region - Getters
    get hasValue() {
        return Input2Component4Q21.VALIDATORS.isValue(this.value);
    }
    get size() {
        return Input2Component4Q21.SIZE_CLASSES[this.inputSize];
    }
    get isCountryCodes() {
        return !!this.countryCodes && this.countryCodes.length > 0;
    }
    get isAreaCodes() {
        return !!this.areaCodes && this.areaCodes.length > 0;
    }
    get hasCurrencySignBefore() {
        return !!this.currencySignBefore && this.currencySignBefore !== '';
    }
    get labelOpacity() {
        return this.visibleLabel ? 1 : (this.value || this.isFocused || this.isSelectFocused || this.displayedInput === 'custom') ? 1 : 0;
    }
    get isAndroidDevice() {
        if (!this._navigator || !this._navigator.userAgent)
            return false;
        else
            return /(android)/i.test(this._navigator.userAgent);
    }
    get isMobile() {
        return !!this._isMobile;
    }
    get isPhantom() {
        if (!this._navigator || !this._navigator.userAgent)
            return false;
        else
            return /(PhantomJS)/i.test(this._navigator.userAgent);
    }
    // #endregion
    // #region - Life Cycle
    constructor(validationService, announcer, el, // added for Duracell
    _navigator) {
        this.validationService = validationService;
        this.announcer = announcer;
        this.el = el;
        this._navigator = _navigator;
        // #endregion
        // #region - Properties: Public
        // Default errors for the different validation error states
        this.errors = {
            required: DEFAULT_ERRORS.required,
            positive: DEFAULT_ERRORS.positive,
            integer: DEFAULT_ERRORS.integer,
            phone: DEFAULT_ERRORS.phone,
            zipcode: DEFAULT_ERRORS.zip,
            nonZero: DEFAULT_ERRORS.nonZero,
            email: DEFAULT_ERRORS.email,
            zip: DEFAULT_ERRORS.zip,
            ssn: DEFAULT_ERRORS.ssn,
            areaCode: DEFAULT_ERRORS.areaCode,
            countryCode: DEFAULT_ERRORS.countryCode,
        };
        this.shouldValidate = {
            number: () => this.inputType === 'number' || this._appliedMask === 'dollar',
            url: () => this.inputType === 'url',
            zip: () => this.inputType === 'zip',
            ssn: () => this.inputType === 'ssn' || this._appliedMask.toLowerCase().includes('ssn'),
            phone: () => this.inputType === 'tel' || this._appliedMask.toLowerCase().includes('phone'),
            email: () => this.inputType === 'email' || this._appliedMask.toLowerCase().includes('email'),
            minLength: () => !!this.minLength && ['text', 'email', 'password', 'none'].includes(this.inputType)
        };
        // Id for the label for aria
        this.labelId = null;
        // applys class to remove excess padding if there is a pre addon
        this.hasPreAddon = false;
        // error message to be displayed when an error is triggered
        this.errorMessage = 'Error';
        // whether the input is focused
        this.isFocused = false;
        // whether the select of the input is focused
        this.isSelectFocused = false;
        this._calledFromSubmit = false;
        // #endregion
        // #region - Inputs: Error Related
        /**
         * Whether the input should valid on 'blur' and 'change' events (always validates on submit)
         */
        this.validateOn = [];
        /**
         * The input only accepts alphabetical or numeric value
         */
        this.allowIntegerOnly = false;
        /**
         * This defines the maximum character length of the input
         */
        this.maxLength = Input2Component4Q21.DEFAULT_MAXLENGTH;
        /**
         * readonly field, displays its value and focusable
         */
        this.readOnly = false;
        /**
         * disables the field and not focusable
         */
        this.disabled = false;
        /**
         * non-masked text fields this limits characters to numbers
         */
        this.typeNumbersOnly = false;
        /**
         * Enables/disables non zero validation
         */
        this.allowNonZeroValueOnly = false;
        // #endregion
        /**
         * Turns on/off the autocomplete feature of an input. Default value is 'off'.
         * @Two-way: false
         */
        this.autocomplete = 'off';
        /**
         * prevents all paste events
         */
        this.preventPaste = false;
        // #region - Inputs: Dollar mask
        /**
         * string to seperate the thousands position
         */
        this.thousandsSeparator = ',';
        /**
         * string to seperate the decimal position
         */
        this.decimalSeparator = '.';
        /**
         * number of digits allowed after the decimal
         */
        this.precision = 2;
        /**
         * max value allowed without throughing an error
         */
        this.maxValue = Number.MAX_SAFE_INTEGER;
        /**
         * Message to inform ADA users that the typed character surpassed the maxFactor and was not added to the input value
         */
        this.maxFactorExceededMessage = 'Maximum Factor Exceeded. Last number was not added';
        // #endregion
        // #region - Inputs: Global Properties
        /**
         * Label for aria-label tag if special aria label is needed
         */
        this.ariaLabel = false;
        this._uid = `citi-input2-${nextUniqueId++}`;
        /**
         * This defines the name of the input
         */
        this.name = null;
        /**
         * Show a search icon add-on in the input.
         */
        this.searchIcon = false;
        /**
         * Sets the tabindex attribute
         */
        this.tabIndex = 0;
        /**
         * Sets the isCbolPassword attribute
         */
        this.isCbolPassword = false;
        this.countryCodeChange = new EventEmitter();
        this.countryCodes = [];
        this.areaCodeChange = new EventEmitter();
        this.areaCodes = [];
        this._unmaskedLength = 4;
        this._unmaskedViewLength = 4;
        // #endregion
        // #region - Inputs: Masking / Unmasking events
        /**
         * A list of HTML event to activate the masking on the content
         */
        this.applyMaskOnEvents = [];
        /**
         * A list of HTML event to remove the mask from the value
         */
        this.removeMaskOnEvents = [];
        // #endregion
        // #region - Inputs: Success related
        /**
         * This defines the success state on the input
         */
        this.isSuccess = false;
        /**
         * Customized success message for valid password input
         */
        this.successMessage = '';
        /**
        * Input for visibleLabel for the label
        */
        this.visibleLabel = false;
        /**
         * This defines the value of the input. Can be two-way bound through [(value)]='myValueVar'
         */
        this.value = undefined;
        this.valueChange = new EventEmitter();
        this.hideToggle = new EventEmitter();
        this.focusChange = new EventEmitter();
        // #endregion
        // #region - Outputs
        /**
         * emits when the focus is blured
         */
        this.blur = new EventEmitter();
        /*
        * This defines the custom error state of the input. Takes precedence over other validation types.
        */
        this._hasCustomError = false;
        /**
         * This defines the error state on the input
         */
        this._error = false;
        this.errorChange = new EventEmitter();
        this._appliedMask = 'none';
        this._storedMask = 'none';
        this.displayedInput = 'none';
        this.hideCharacters = true;
        /**
         * text of the show (unmask) button
         */
        this.showText = 'Show';
        /**
         * text of the hide (mask) button
         */
        this.hideText = 'Hide';
        this.inputType = 'text';
        this.inputSize = 'fullwidth';
        this.classRow = true;
        // #endregion
        // #region - FORM BINDING METHODS
        // All methods below are required for the Value Control Accessor interface to work with [(ngModel)]
        this.onChange = (value) => { };
        this.onTouched = () => { };
    }
    ngOnInit() {
        // Detects mobile device
        if (this._navigator)
            this._isMobile = this._navigator.userAgent.match(/(iPad)|(iPhone)|(iPod)|(android)|(webOS)/i);
        // ensures that an id is assigned
        this.idStr = this.idStr;
        // register and subscribe to validation service
        this.validationService.addInput(this);
        this.validateSub = this.validationService.validateNow.subscribe(() => {
            this._calledFromSubmit = true;
            this.validate();
        });
        this.labelId = Input2Component4Q21.VALIDATORS.isValue(this._id) ? this._id + '-label' : null;
        if (this.hasCurrencySignBefore || this.isCountryCodes || this.isAreaCodes)
            this.hasPreAddon = true;
        this.setDynamicMessages();
        if (this.countryCode) {
            const codeOption = this.countryCodes.find(code => code.value === this.countryCode);
            if (codeOption)
                this.mask = codeOption.mask || this._storedMask;
        }
    }
    ngOnDestroy() {
        this.validationService.removeError(this);
        if (this.validateSub)
            this.validateSub.unsubscribe();
    }
    // #endregion
    // #region - Event Listeners and handlers
    /**
     * Updates the ui to responsd to a focus event
     * This does not cast focus. Use "focusInput()" for that
     */
    onFocus() {
        if (this.isFocused)
            return;
        if (this.safeIncludes(this.removeMaskOnEvents, 'focus'))
            this.hideCharacters = false;
        this.isFocused = true;
        this.isSelectFocused = false;
        this.focusChange.emit(true);
    }
    /**
     * Casts focus to the native <input> element
     */
    focusInput() {
        this.input.nativeElement.focus();
    }
    ;
    onBlur() {
        if (!this.isFocused)
            return;
        if (this.safeIncludes(this.applyMaskOnEvents, 'blur'))
            this.hideCharacters = true;
        if (this.safeIncludes(this.validateOn, 'blur'))
            this.validate();
        this.isFocused = false;
        this.blur.emit();
        this.focusChange.emit(false);
        this.onTouched(); // registers
    }
    onPaste(e) {
        if (this.preventPaste)
            e.preventDefault();
    }
    onInputChange() {
        if (this.safeIncludes(this.validateOn, 'change'))
            this.validate();
    }
    onInput() {
        if (this.safeIncludes(this.validateOn, 'input'))
            this.validate();
    }
    onSelectFocus() {
        if (this.readOnly)
            this.announcer.announce(`${this.selectLabel} combo box read only`);
        if (this.isSelectFocused === true)
            return;
        this.isSelectFocused = true;
    }
    preventCharacters(e) {
        if (!this.typeNumbersOnly)
            return;
        if (e.key === 'TAB' || e.code === 'TAB')
            return;
        if (e.shiftKey && e.keyCode === 9)
            return;
        if (e.shiftKey || e.which > 57 || ['.', ',', '/', '\'', '-'].includes(e.key))
            e.preventDefault();
    }
    // #endregion
    // #region - Methods: Masking
    // updates the mask from the template when the area or coutry code changes and requires a new mask
    updateMask(selected) {
        if (this.isAreaCodes)
            this.areaCodeChange.emit(selected);
        if (this.isCountryCodes)
            this.countryCodeChange.emit(selected);
        const options = this.countryCodes.length > 0 ? this.countryCodes : this.areaCodes;
        const mask = options.find(option => option.value === selected).mask;
        if (!!mask)
            this.mask = mask;
    }
    writeValueAndValidate(value) {
        this.writeValue(value);
        if (this.safeIncludes(this.validateOn, 'input'))
            this.validate();
    }
    // #endregion
    // #region - Methods: Validation
    setDynamicMessages() {
        const dynamicMessage = {
            minLength: this.minLengthError || `Input length must exceed ${this.minLength - 1} characters`,
            maxLength: this.maxLengthError || `Input length may not exceed ${this.maxLength} characters`,
            maxFactor: this.maxFactorExceededMessage || `Input value cannot exceed ${this.maxFactor} digits before the decimal`,
            custom: this.customErrorMessage
        };
        if (this.maxValue)
            dynamicMessage.maxValue = 'Value cannot exceed ' + this.formatDollar(this.maxValue);
        if (this.minValue)
            dynamicMessage.minValue = 'Value cannot subceed ' + this.formatDollar(this.minValue);
        this.errors = Object.assign(dynamicMessage, this.errors);
    }
    // only used for error messages, not for formatting the input field
    formatDollar(number) {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    // Clears an error state on the input. Takes a type of error
    toggleError(errorType, error) {
        // Clear the error state
        this._error = error;
        this.errorType = errorType;
        this.errorChange.emit(error);
        if (error) {
            // override message for customer errors
            if (this._hasCustomError)
                errorType = 'custom';
            // set the error message
            this.errorMessage = (!!errorType) ? this.errors[errorType] : '';
            this.validationService.addError(this);
        }
        else
            this.validationService.removeError(this);
    }
    validate() {
        this._isErrorHidden = true;
        setTimeout(() => {
            this._isErrorHidden = false;
        }, 200);
        if (this.required && !Input2Component4Q21.VALIDATORS.isValue(this.value) && this.value !== 0) {
            this.toggleError('required', true);
            return;
        }
        if (this.isCbolPassword) {
            this.toggleError('custom', this.error);
            return;
        }
        if (this.shouldValidate.minLength() && (!this.value || this.minLength > this.value.length)) {
            this.toggleError('minLength', true);
            return;
        }
        if (this.shouldValidate.number())
            this.validateNumber(); // and dollar & currency
        else if (this.shouldValidate.zip())
            this.validateZip();
        else if (this.shouldValidate.email())
            this.validateEmail();
        else if (this.shouldValidate.ssn())
            this.validateSSN();
        else if (this.shouldValidate.phone())
            this.validatePhone();
        else
            this.toggleError(null, false);
    }
    // and dollar-currency
    validateNumber() {
        const newValue = parseFloat(this.value);
        if (this.allowIntegerOnly && !Number.isInteger(newValue))
            this.toggleError('integer', true);
        else if (this.allowNonZeroValueOnly && newValue === 0)
            this.toggleError('nonZero', true);
        else if (this.allowPositiveIntegerOnly && (!Number.isInteger(newValue) || newValue <= 0))
            this.toggleError('positive', true);
        else if (!!this.maxValue && newValue > this.maxValue)
            this.toggleError('maxValue', true);
        else if (!!this.minValue && newValue < this.minValue)
            this.toggleError('minValue', true);
        else if (!!this.maxFactor && this.value > Math.pow(10, this.maxFactor + 1))
            this.toggleError('maxFactor', true);
        else
            this.toggleError(null, false);
    }
    validateEmail() {
        if (this.value && this.value.length > 0 && !Input2Component4Q21.VALIDATORS.isEmail(this.value))
            this.toggleError('email', true);
        else
            this.toggleError(null, false);
    }
    validateSSN() {
        const newValue = parseFloat(this.value);
        if (this.value && this.value.length > 0 && (this.value.length !== 9 || !Number.isInteger(newValue)))
            this.toggleError('ssn', true);
        else
            this.toggleError(null, false);
    }
    validateZip() {
        // strip off all non-numeric values from the mask to determine the number of characters
        const requiredLength = this._appliedMask.replace(/[^0-9]/g, '').length;
        if (this.value && this.value.length > 0 && (!this.value || requiredLength !== this.value.length))
            this.toggleError('zip', true);
        else
            this.toggleError(null, false);
    }
    validatePhone() {
        // strip off all non-numeric values from the mask to determine the number of characters
        const hasBlanks = this._appliedMask.replace(/[^0-9]/g, '').length !== this.value.length;
        const isNull = !this.value || this.value.length === 0;
        if (this._appliedMask !== 'none' && (hasBlanks && this.required || !isNull && hasBlanks))
            this.toggleError('phone', true);
        else if (this.selectAddonIsValid(this.countryCodes, this.countryCode))
            this.toggleError('countryCode', true);
        else if (this.selectAddonIsValid(this.areaCodes, this.areaCode))
            this.toggleError('areaCode', true);
        else
            this.toggleError(null, false);
    }
    selectAddonIsValid(options, value) {
        return this.required && !value && options && options.length > 0;
    }
    safeIncludes(array, value) {
        return array && array.length && array.includes(value);
    }
    /**
     * @Function: reset
     * Resets the apperance and state of the input to initial
     * @Return: void
     */
    reset() {
        this.toggleError(null, false);
        this.value = null;
    }
    // #endregion
    // #region - Methods:
    get describedByID() {
        let describedBy = '';
        if (this.describedby) {
            describedBy = this.describedby;
        }
        if (this.error) {
            describedBy += ` ${this._id}-error`;
        }
        if (this.isSuccess) {
            describedBy += ` ${this._id}-success`;
        }
        return describedBy;
    }
    getAriaLabelString() {
        if (this.hasValue && this.readOnly)
            return null;
        const resultString = this.label || this.placeholder;
        if (!this.ariaLabel || !Input2Component4Q21.VALIDATORS.isValue(resultString))
            return '';
        else
            return resultString;
    }
    getAriaLabelledBy() {
        if (!!this.ariaLabelledById)
            return this.ariaLabelledById;
        else if ((!this.hasValue || !this.readOnly) && !(this.isAreaCodes || this.isCountryCodes))
            return this.labelId;
        else
            return null;
    }
    get labeltext() {
        return this.label || this.placeholder;
    }
    get selectLabeltext() {
        return this.selectLabel || this.label || this.placeholder;
    }
    showhideMaskcheck(e) {
        this.hideCharacters = !this.hideCharacters;
        this.announcer.announce(`${this.hideCharacters ? `${this.showText} Button` : `${this.hideText} Button`}`);
    }
    /**
     * @Method: showhideMaskcheckKeyboard
     * This method gets called when enter key is pressed on the hide/show button
     */
    showhideMaskcheckKeyboard(e) {
        if (e.keyCode === 13)
            this.showhideMaskcheck(e);
    }
    // Allows Angular to update the model.
    // Update the model and changes needed for the view here.
    writeValue(value) {
        this.value = value;
        this.valueChange.emit(value);
        //      !!!!    WARNING: DO NOT REMOVE    !!!!
        // this is actually necessary for ngModel to update
        this.onChange(this.value);
    }
    // Allows Angular to register a function to call when the model changes.
    // Save the function as a property to call later here.
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // Allows Angular to disable the input.
    setDisabledState(isDisabled) {
        this.readOnly = isDisabled;
    }
    // Disable keyboard click for select when readonly is true
    onSelectKeyDown(event) {
        if (!this.readOnly)
            return;
        let index = -1;
        const keyCodes = [32, 13, 33, 34, 38, 40, 37, 39];
        index = keyCodes.findIndex(item => item === event.keyCode);
        return (index !== -1 ? false : null);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Input2Component4Q21, deps: [{ token: ValidationService }, { token: CitiLiveAnnouncer }, { token: i0.ElementRef }, { token: NAVIGATOR }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: Input2Component4Q21, isStandalone: false, selector: "citi-input2.4Q21", inputs: { validateOn: "validateOn", allowIntegerOnly: "allowIntegerOnly", maxLength: "maxLength", allowPositiveIntegerOnly: "allowPositiveIntegerOnly", minLength: "minLength", readOnly: "readOnly", disabled: "disabled", typeNumbersOnly: "typeNumbersOnly", allowNonZeroValueOnly: "allowNonZeroValueOnly", autocomplete: "autocomplete", ariaAutocomplete: "ariaAutocomplete", preventPaste: "preventPaste", thousandsSeparator: "thousandsSeparator", decimalSeparator: "decimalSeparator", precision: "precision", step: "step", maxValue: "maxValue", minValue: "minValue", maxFactor: "maxFactor", maxFactorExceededMessage: "maxFactorExceededMessage", currencySignAfter: "currencySignAfter", currencySignBefore: "currencySignBefore", ariaLabel: "ariaLabel", ariaLabelledById: "ariaLabelledById", ariaGroupLabel: "ariaGroupLabel", idStr: "idStr", name: "name", label: "label", selectLabel: "selectLabel", placeholder: "placeholder", searchIcon: "searchIcon", required: "required", describedby: "describedby", tabIndex: "tabIndex", isCbolPassword: "isCbolPassword", countryCode: "countryCode", countryCodes: "countryCodes", areaCode: "areaCode", areaCodes: "areaCodes", unmaskedLength: "unmaskedLength", theme: "theme", applyMaskOnEvents: "applyMaskOnEvents", removeMaskOnEvents: "removeMaskOnEvents", isSuccess: "isSuccess", successMessage: "successMessage", visibleLabel: "visibleLabel", hideMask: "hideMask", value: "value", focus: "focus", minLengthError: "minLengthError", maxValueError: "maxValueError", minValueError: "minValueError", maxLengthError: "maxLengthError", requiredError: "requiredError", messageOnlyNonZeroError: "messageOnlyNonZeroError", messageOnlyIntegerError: "messageOnlyIntegerError", messageOnlyPositiveIntegerError: "messageOnlyPositiveIntegerError", messageOnlyPhoneError: "messageOnlyPhoneError", messageOnlyZipcodeError: "messageOnlyZipcodeError", messageOnlyEmailError: "messageOnlyEmailError", messageOnlyZipError: "messageOnlyZipError", messageOnlySsnError: "messageOnlySsnError", messageOnlyAreaCodeError: "messageOnlyAreaCodeError", messageOnlyCountryCodeError: "messageOnlyCountryCodeError", changeMessage: "changeMessage", hasCustomError: "hasCustomError", customErrorMessage: "customErrorMessage", error: "error", usePasswordOnAndroid: "usePasswordOnAndroid", mask: "mask", hideShowButtonWhenMasked: "hideShowButtonWhenMasked", showText: "showText", hideText: "hideText", type: "type", size: "size" }, outputs: { countryCodeChange: "countryCodeChange", areaCodeChange: "areaCodeChange", valueChange: "valueChange", hideToggle: "hideToggle", focusChange: "focusChange", blur: "blur", errorChange: "errorChange" }, host: { properties: { "class.row": "this.classRow" }, classAttribute: "citi-input2" }, providers: [INPUT2_VALUE_ACCESSOR], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true }, { propertyName: "custom", first: true, predicate: CustomInputMaskDirective, descendants: true }], ngImport: i0, template: `
    <div class="form-control-container {{theme}}" [ngClass]="size" [attr.role]="(isAreaCodes || isCountryCodes) ? 'group' : null"
      [attr.aria-labelledby]="((isAreaCodes || isCountryCodes) ? labelId : null)"  >
      <!-- label for the input with group -->
      <label *ngIf="(isAreaCodes || isCountryCodes)" [id]="labelId" tabIndex="-1"
        [ngStyle]="{'opacity': labelOpacity}"
          [innerHtml]="ariaGroupLabel"></label>
      <!-- label for the input, will switch text to select element's label when that element is focused -->
      <label *ngIf="!(isAreaCodes || isCountryCodes)" [for]="_id" [id]="labelId" tabIndex="-1"
        [ngStyle]="{'opacity': labelOpacity}"
          [innerHtml]="(isSelectFocused ? selectLabeltext : labeltext)"></label>
      <!-- input wapper (all borders) -->
      <div class="input-container"
        [ngClass]="{
          'focused': isFocused,
          'errored': _error,
          'has-pre-addon': hasPreAddon,
          'readOnly': readOnly,
          'disabled': disabled,
          'success': isSuccess
        }">
        <!-- Add-ons Pre -->
        <div class="add-on-pre">
          <!-- Phone Area Code Selector -->
          <select *ngIf="isAreaCodes"
            [attr.name]="name"
            class="form-control"
            (change)="updateMask($event.target.value)"
            [attr.aria-label]="selectLabeltext"
            (focus)="onSelectFocus()"
            [disabled]="disabled"
            (keydown)="onSelectKeyDown($event)"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }">
            <option *ngFor="let code of areaCodes" [value]="code.value" [innerHtml]="code.label" [attr.selected]="(code.selected === true ? true : null)"></option>
          </select>
          <!-- Phone Country Prefix Selector -->
          <select *ngIf="isCountryCodes"
            [attr.name]="name"
            (change)="updateMask($event.target.value)"
            class="form-control"
            [attr.aria-label]="selectLabeltext"
            (focus)="onSelectFocus()"
            [disabled]="disabled"
            (keydown)="onSelectKeyDown($event)"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }">
            <option *ngFor="let code of countryCodes" [value]="code.value" [innerHtml]="code.label" [attr.selected]="(code.selected === true ? true : null)"></option>
          </select>
          <span *ngIf="currencySignBefore" class="input-group-addon before">{{currencySignBefore}}</span>
        </div>
        <!-- The Inputs managed by a switch statement -->
        <div [ngSwitch]="displayedInput" class="input-switch-wrapper">
          <!-- Custom Mask -->
          <input
            *ngSwitchCase="'custom'"
            #input
            #custom="custom"
            [attr.type]="(inputType == 'tel' ? 'tel' : 'text')"
            [custom-mask]="_appliedMask"
            [hideCharacters]="hideCharacters"
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValue($event)"
            [attr.id]="_id"
            [attr.name]="name"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            [attr.tabindex]="tabIndex"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.placeholder]="placeholder"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-describedby]="describedByID"
          />
          <!-- Dollar Mask -->
          <input
            *ngSwitchCase="'dollar'"
            #input
            type="text"
            currency-mask
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValueAndValidate($event)"
            [thousandsSeparator]="thousandsSeparator"
            [decimalSeparator]="decimalSeparator"
            [maxFactor]="maxFactor"
            [precision]="precision"
            [preventPaste]="preventPaste"
            [maxFactorExceededMessage]="maxFactorExceededMessage"
            [attr.id]="_id"
            [attr.name]="name"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-describedby]="describedByID"
          />
          <!-- Partial Mask -->
          <input
            *ngSwitchCase="'partial'"
            #input
            type="text"
            [partial-mask]="_appliedMask"
            [hideCharacters]="hideCharacters"
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValue($event)"
            [unmaskedLength]="_unmaskedViewLength"
            [maxLength]="maxLength"
            [preventPaste]="preventPaste"
            [numbersOnly]="typeNumbersOnly"
            [attr.id]="_id"
            [attr.name]="name"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="(ariaAutocomplete ? ariaAutocomplete : null)"
            [attr.autocomplete]="(autocomplete ? autocomplete : null)"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-describedby]="describedByID"
          />
          <!-- No Mask -->
          <input
            *ngSwitchCase="'none'"
            #input
            [ngModel]="value"
            [attr.type]="inputType"
            (ngModelChange)="writeValue($event)"
            [attr.id]="_id"
            [attr.name]="name"
            [attr.step]="step"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            [attr.maxlength]="maxLength"
            autocapitalize="none"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            (keypress)="preventCharacters($event)"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            (paste)="onPaste($event)"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-describedby]="describedByID"
          />

        </div>
        <div class="add-on-post">
         <button
            #button1
            type=button
            *ngIf="hideShowButtonWhenMasked && value"
            class="input-group-addon showHideMask"
            (click)="showhideMaskcheck($event)"
            [disabled]="disabled"
            [ngClass]="{'disabled': disabled}"
            >
            {{showHideMaskText}}
      </button>
          <citi-icon2
            *ngIf="searchIcon"
            name="006-search"
            idStr="inputSearchIconId"
            shade="dark"
            [svgStyle]="{'width.px': 50, 'height.px': 50}"
            ariaLabel="icon">
          </citi-icon2>
          <span *ngIf="currencySignAfter" class="input-group-addon after">{{currencySignAfter}}</span>
        </div>
      </div>

      <div
        [attr.id]="_id+'-error'"
        class="validation-message-danger"
        *ngIf="_error && !_isErrorHidden"
        [innerHtml]="errorMessage"
        role="alert"
      ></div>
      <div class="input-success" *ngIf="!_error && isSuccess">
        <span [attr.id]="_id+'-success'" class="validation-message-success">{{successMessage}}</span>
      </div>
    </div>
  `, isInline: true, styles: [":host{display:block}label{font-size:.75rem;color:#666;width:100%;pointer-events:none;transition:opacity ease-in-out 225ms}.input-container{width:100%;margin:3px 0 5px;box-sizing:border-box;-moz-box-sizing:border-box;display:flex;-webkit-box-direction:normal;-webkit-box-orient:horizontal;-webkit-flex-direction:row;-moz-flex-direction:row;-ms-flex-direction:row;flex-direction:row;-webkit-flex-wrap:nowrap;-moz-flex-wrap:nowrap;-ms-flex-wrap:none;flex-wrap:nowrap;-webkit-justify-content:flex-start;-ms-flex-pack:start;justify-content:flex-start;-webkit-align-content:stretch;-ms-flex-line-pack:stretch;align-content:stretch;-webkit-align-items:flex-start;-ms-flex-align:start;align-items:flex-start;border-radius:8px;background:#eee;border:1px solid transparent;border:.9px solid transparent}.input-container.focused{border-color:#056dae}.input-container.focused input::placeholder{opacity:0}.input-container.errored{border-color:#d60000}.input-container.errored.focused{width:calc(100% + 2px);border-width:1.99px;margin:2px 0 4px -1px}.input-container.success{border-color:#006e0a}.input-container.success.focused{width:calc(100% + 2px);border-width:1.99px;margin:2px 0 4px -1px}.input-container.readOnly{background:#fff;border:1px solid #666}.input-container.readOnly input.readOnly:read-only,.input-container.readOnly select,.input-container.readOnly span{background-color:#fff}.input-container.readOnly button.input-group-addon{background-color:#eee}.input-container.readOnly.focused:not(.has-pre-addon){outline:1px solid #056DAE}.input-container.disabled{background-color:#ccc;border:1px solid #CCC}.input-container.disabled input.disabled:disabled,.input-container.disabled select,.input-container.disabled span{background-color:#ccc}.input-container.has-pre-addon .input-switch-wrapper{margin:0}.input-container.has-pre-addon.focused:not(.readOnly){border-color:transparent}.input-container.has-pre-addon.focused .input-switch-wrapper{border-radius:0 8px 8px 0;outline:1px solid #056DAE}.input-container.has-pre-addon input{padding-left:0!important}.input-container .input-group-addon{width:auto;background-color:#fff}.input-container .input-group-addon.before{border-top-left-radius:8px;border-bottom-left-radius:8px;border-right:1px solid #6B6B6B}.input-container .input-group-addon.before,.input-container .input-group-addon.after{width:48px;height:48px}.input-container .input-group-addon.after{border-top-right-radius:8px;border-bottom-right-radius:8px;border-left:1px solid #6B6B6B}.input-container .add-on-pre{-webkit-order:0;-ms-flex-order:0;order:0;-webkit-flex:0 1 auto;-ms-flex:0 1 auto;flex:0 1 auto;-webkit-align-self:auto;-ms-flex-item-align:auto;align-self:auto;box-sizing:border-box;-moz-box-sizing:border-box}.input-container .add-on-pre select{margin:0 2px 0 0;height:48px;width:105px;border:1px solid transparent;border-top-right-radius:0;border-bottom-right-radius:0}.input-container .add-on-pre select:focus{outline:1px solid #056DAE}.input-container .input-switch-wrapper{flex:1 1 0%;box-sizing:border-box;-moz-box-sizing:border-box;margin:0 2px;border-radius:8px}.input-container .input-switch-wrapper input{width:100%;box-sizing:border-box;-moz-box-sizing:border-box;letter-spacing:.5px;height:48px;padding:10px 20px;background:#eee;border:none!important;box-shadow:none;border-radius:8px}.input-container .input-switch-wrapper input::-ms-clear{display:none}.input-container .input-switch-wrapper input:focus{outline:none}.input-container .add-on-post{flex:0 1 auto;box-sizing:border-box;-moz-box-sizing:border-box}.input-container .add-on-post .search{width:50px;padding-top:12px;border-radius:8px}.input-container .add-on-post .showHideMask{min-width:50px;border-radius:8px;padding:17px 10px;color:#056dae;font-family:Interstate_Bold,sans-serif;line-height:14px;cursor:pointer;font-size:12px;margin-left:2px}.input-container .add-on-post .showHideMask.disabled{background-color:#ccc;border:1px solid #CCC;color:#333;cursor:context-menu}.input-container .add-on-post .showHideMask.disabled button.disabled:disabled{background-color:#ccc}.input-error,.input-success{height:16px;width:100%;float:left}.input-success .validation-message-success{font-size:12px;font-family:Interstate_Bold,sans-serif;margin:0;color:#006e0a;font-weight:700;position:relative;display:block;line-height:1rem;font-size:.75rem}:host .light .input-container{background:#fff}:host .light .input-container input,:host .light .input-container select,:host .light .input-container span{background-color:#fff}:host .light .input-container.readOnly{background:#eee}:host .light .input-container.readOnly input,:host .light .input-container.readOnly select,:host .light .input-container.readOnly span{background-color:#eee}:host .light .input-container.readOnly button.input-group-addon{background-color:#fff}:host .light .input-container .input-group-addon{background-color:#eee}:host ::ng-deep #inputSearchIconId path{fill:#056dae}.form-control.disabled{background-color:#ccc;border:1px solid #CCC;color:#333;cursor:context-menu}.form-control.disabled button.disabled:disabled{background-color:#ccc}select.form-control{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%20standalone%3D%22no%22%3F%3E%0A%3Csvg%20width%3D%2215px%22%20height%3D%228px%22%20viewBox%3D%220%200%2015%208%22%20version%3D%221.1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20xmlns%3Asketch%3D%22http%3A//www.bohemiancoding.com/sketch/ns%22%3E%0A%20%20%20%20%3C%21--%20Generator%3A%20Sketch%203.4.1%20%2815681%29%20-%20http%3A//www.bohemiancoding.com/sketch%20--%3E%0A%20%20%20%20%3Ctitle%3EChevron%3C/title%3E%0A%20%20%20%20%3Cdesc%3ECreated%20with%20Sketch.%3C/desc%3E%0A%20%20%20%20%3Cdefs%3E%3C/defs%3E%0A%20%20%20%20%3Cg%20id%3D%22Toolkit%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20sketch%3Atype%3D%22MSPage%22%3E%0A%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdowns%22%20sketch%3Atype%3D%22MSArtboardGroup%22%20transform%3D%22translate%28-878.000000%2C%20-122.000000%29%22%20fill%3D%22%23056DAE%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Grey%22%20sketch%3Atype%3D%22MSLayerGroup%22%20transform%3D%22translate%28570.000000%2C%20101.000000%29%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Default%22%20sketch%3Atype%3D%22MSShapeGroup%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M312%2C32%20L312%2C29.6917048%20L316.989689%2C25.0246591%20L312%2C20.3577556%20L312%2C18%20L318.461538%2C24.1555486%20L318.461538%2C25.8443093%20L312%2C32%22%20id%3D%22Chevron%22%20transform%3D%22translate%28315.230769%2C%2025.000000%29%20rotate%28-270.000000%29%20translate%28-315.230769%2C%20-25.000000%29%20%22%3E%3C/path%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%3C/g%3E%0A%3C/svg%3E);background-repeat:no-repeat}select.form-control.disabled{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%20standalone%3D%22no%22%3F%3E%0A%3Csvg%20width%3D%2215px%22%20height%3D%228px%22%20viewBox%3D%220%200%2015%208%22%20version%3D%221.1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20xmlns%3Asketch%3D%22http%3A//www.bohemiancoding.com/sketch/ns%22%3E%0A%20%20%20%20%3C%21--%20Generator%3A%20Sketch%203.4.1%20%2815681%29%20-%20http%3A//www.bohemiancoding.com/sketch%20--%3E%0A%20%20%20%20%3Ctitle%3EChevron%3C/title%3E%0A%20%20%20%20%3Cdesc%3ECreated%20with%20Sketch.%3C/desc%3E%0A%20%20%20%20%3Cdefs%3E%3C/defs%3E%0A%20%20%20%20%3Cg%20id%3D%22Toolkit%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20sketch%3Atype%3D%22MSPage%22%3E%0A%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdowns%22%20sketch%3Atype%3D%22MSArtboardGroup%22%20transform%3D%22translate%28-878.000000%2C%20-122.000000%29%22%20fill%3D%22%23333333%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Grey%22%20sketch%3Atype%3D%22MSLayerGroup%22%20transform%3D%22translate%28570.000000%2C%20101.000000%29%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Default%22%20sketch%3Atype%3D%22MSShapeGroup%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M312%2C32%20L312%2C29.6917048%20L316.989689%2C25.0246591%20L312%2C20.3577556%20L312%2C18%20L318.461538%2C24.1555486%20L318.461538%2C25.8443093%20L312%2C32%22%20id%3D%22Chevron%22%20transform%3D%22translate%28315.230769%2C%2025.000000%29%20rotate%28-270.000000%29%20translate%28-315.230769%2C%20-25.000000%29%20%22%3E%3C/path%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%3C/g%3E%0A%3C/svg%3E);background-repeat:no-repeat}select.form-control.readOnly{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%20standalone%3D%22no%22%3F%3E%0A%3Csvg%20width%3D%2215px%22%20height%3D%228px%22%20viewBox%3D%220%200%2015%208%22%20version%3D%221.1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20xmlns%3Asketch%3D%22http%3A//www.bohemiancoding.com/sketch/ns%22%3E%0A%20%20%20%20%3C%21--%20Generator%3A%20Sketch%203.4.1%20%2815681%29%20-%20http%3A//www.bohemiancoding.com/sketch%20--%3E%0A%20%20%20%20%3Ctitle%3EChevron%3C/title%3E%0A%20%20%20%20%3Cdesc%3ECreated%20with%20Sketch.%3C/desc%3E%0A%20%20%20%20%3Cdefs%3E%3C/defs%3E%0A%20%20%20%20%3Cg%20id%3D%22Toolkit%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20sketch%3Atype%3D%22MSPage%22%3E%0A%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdowns%22%20sketch%3Atype%3D%22MSArtboardGroup%22%20transform%3D%22translate%28-878.000000%2C%20-122.000000%29%22%20fill%3D%22%23CCC%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Grey%22%20sketch%3Atype%3D%22MSLayerGroup%22%20transform%3D%22translate%28570.000000%2C%20101.000000%29%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Default%22%20sketch%3Atype%3D%22MSShapeGroup%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M312%2C32%20L312%2C29.6917048%20L316.989689%2C25.0246591%20L312%2C20.3577556%20L312%2C18%20L318.461538%2C24.1555486%20L318.461538%2C25.8443093%20L312%2C32%22%20id%3D%22Chevron%22%20transform%3D%22translate%28315.230769%2C%2025.000000%29%20rotate%28-270.000000%29%20translate%28-315.230769%2C%20-25.000000%29%20%22%3E%3C/path%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%3C/g%3E%0A%3C/svg%3E);background-repeat:no-repeat}select.readOnly{pointer-events:none}::-webkit-input-placeholder{color:#6b6b6b}::-moz-placeholder{color:#6b6b6b}:-ms-input-placeholder{color:#6b6b6b}:-moz-placeholder{color:#6b6b6b}input::-ms-reveal,input::-ms-clear{display:none}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i3$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i3$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i4.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: Icon2Component, selector: "citi-icon2", inputs: ["role", "idStr", "ariaLabel", "ariaHidden", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "shade", "name"] }, { kind: "directive", type: CustomInputMaskDirective, selector: "[custom-mask]", inputs: ["hideCharacters", "custom-mask", "unmaskedValue"], outputs: ["unmaskedValueChange"], exportAs: ["custom"] }, { kind: "directive", type: CurrencyInputMaskDirective, selector: "[currency-mask]", inputs: ["unmaskedValue", "thousandsSeparator", "decimalSeparator", "precision", "maxFactor", "maxFactorExceededMessage", "preventPaste"], outputs: ["unmaskedValueChange"] }, { kind: "directive", type: PartialInputMaskDirective, selector: "[partial-mask]", inputs: ["maxLength", "unmaskedLength", "numbersOnly", "preventPaste", "hideCharacters", "partial-mask", "unmaskedValue"], outputs: ["unmaskedValueChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Input2Component4Q21, decorators: [{
            type: Component,
            args: [{ selector: 'citi-input2.4Q21', standalone: false, providers: [INPUT2_VALUE_ACCESSOR], host: { class: 'citi-input2' }, template: `
    <div class="form-control-container {{theme}}" [ngClass]="size" [attr.role]="(isAreaCodes || isCountryCodes) ? 'group' : null"
      [attr.aria-labelledby]="((isAreaCodes || isCountryCodes) ? labelId : null)"  >
      <!-- label for the input with group -->
      <label *ngIf="(isAreaCodes || isCountryCodes)" [id]="labelId" tabIndex="-1"
        [ngStyle]="{'opacity': labelOpacity}"
          [innerHtml]="ariaGroupLabel"></label>
      <!-- label for the input, will switch text to select element's label when that element is focused -->
      <label *ngIf="!(isAreaCodes || isCountryCodes)" [for]="_id" [id]="labelId" tabIndex="-1"
        [ngStyle]="{'opacity': labelOpacity}"
          [innerHtml]="(isSelectFocused ? selectLabeltext : labeltext)"></label>
      <!-- input wapper (all borders) -->
      <div class="input-container"
        [ngClass]="{
          'focused': isFocused,
          'errored': _error,
          'has-pre-addon': hasPreAddon,
          'readOnly': readOnly,
          'disabled': disabled,
          'success': isSuccess
        }">
        <!-- Add-ons Pre -->
        <div class="add-on-pre">
          <!-- Phone Area Code Selector -->
          <select *ngIf="isAreaCodes"
            [attr.name]="name"
            class="form-control"
            (change)="updateMask($event.target.value)"
            [attr.aria-label]="selectLabeltext"
            (focus)="onSelectFocus()"
            [disabled]="disabled"
            (keydown)="onSelectKeyDown($event)"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }">
            <option *ngFor="let code of areaCodes" [value]="code.value" [innerHtml]="code.label" [attr.selected]="(code.selected === true ? true : null)"></option>
          </select>
          <!-- Phone Country Prefix Selector -->
          <select *ngIf="isCountryCodes"
            [attr.name]="name"
            (change)="updateMask($event.target.value)"
            class="form-control"
            [attr.aria-label]="selectLabeltext"
            (focus)="onSelectFocus()"
            [disabled]="disabled"
            (keydown)="onSelectKeyDown($event)"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }">
            <option *ngFor="let code of countryCodes" [value]="code.value" [innerHtml]="code.label" [attr.selected]="(code.selected === true ? true : null)"></option>
          </select>
          <span *ngIf="currencySignBefore" class="input-group-addon before">{{currencySignBefore}}</span>
        </div>
        <!-- The Inputs managed by a switch statement -->
        <div [ngSwitch]="displayedInput" class="input-switch-wrapper">
          <!-- Custom Mask -->
          <input
            *ngSwitchCase="'custom'"
            #input
            #custom="custom"
            [attr.type]="(inputType == 'tel' ? 'tel' : 'text')"
            [custom-mask]="_appliedMask"
            [hideCharacters]="hideCharacters"
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValue($event)"
            [attr.id]="_id"
            [attr.name]="name"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            [attr.tabindex]="tabIndex"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.placeholder]="placeholder"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-describedby]="describedByID"
          />
          <!-- Dollar Mask -->
          <input
            *ngSwitchCase="'dollar'"
            #input
            type="text"
            currency-mask
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValueAndValidate($event)"
            [thousandsSeparator]="thousandsSeparator"
            [decimalSeparator]="decimalSeparator"
            [maxFactor]="maxFactor"
            [precision]="precision"
            [preventPaste]="preventPaste"
            [maxFactorExceededMessage]="maxFactorExceededMessage"
            [attr.id]="_id"
            [attr.name]="name"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-describedby]="describedByID"
          />
          <!-- Partial Mask -->
          <input
            *ngSwitchCase="'partial'"
            #input
            type="text"
            [partial-mask]="_appliedMask"
            [hideCharacters]="hideCharacters"
            [unmaskedValue]="value"
            (unmaskedValueChange)="writeValue($event)"
            [unmaskedLength]="_unmaskedViewLength"
            [maxLength]="maxLength"
            [preventPaste]="preventPaste"
            [numbersOnly]="typeNumbersOnly"
            [attr.id]="_id"
            [attr.name]="name"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            [attr.aria-autocomplete]="(ariaAutocomplete ? ariaAutocomplete : null)"
            [attr.autocomplete]="(autocomplete ? autocomplete : null)"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-describedby]="describedByID"
          />
          <!-- No Mask -->
          <input
            *ngSwitchCase="'none'"
            #input
            [ngModel]="value"
            [attr.type]="inputType"
            (ngModelChange)="writeValue($event)"
            [attr.id]="_id"
            [attr.name]="name"
            [attr.step]="step"
            [attr.tabindex]="tabIndex"
            [attr.placeholder]="placeholder"
            [attr.maxlength]="maxLength"
            autocapitalize="none"
            [readonly]="readOnly"
            [disabled]="disabled"
            [ngClass]="{
              'disabled': disabled,
              'readOnly': readOnly
            }"
            (keypress)="preventCharacters($event)"
            (focus)="onFocus()"
            (blur)="onBlur()"
            (change)="onInputChange()"
            (input)="onInput()"
            (paste)="onPaste($event)"
            [attr.aria-autocomplete]="ariaAutocomplete"
            [attr.autocomplete]="autocomplete"
            [attr.aria-required]="(required ? true : null)"
            [attr.aria-invalid]="(_error ? true : null)"
            [attr.aria-labelledby]="getAriaLabelledBy()"
            [attr.aria-label]="getAriaLabelString()"
            [attr.aria-describedby]="describedByID"
          />

        </div>
        <div class="add-on-post">
         <button
            #button1
            type=button
            *ngIf="hideShowButtonWhenMasked && value"
            class="input-group-addon showHideMask"
            (click)="showhideMaskcheck($event)"
            [disabled]="disabled"
            [ngClass]="{'disabled': disabled}"
            >
            {{showHideMaskText}}
      </button>
          <citi-icon2
            *ngIf="searchIcon"
            name="006-search"
            idStr="inputSearchIconId"
            shade="dark"
            [svgStyle]="{'width.px': 50, 'height.px': 50}"
            ariaLabel="icon">
          </citi-icon2>
          <span *ngIf="currencySignAfter" class="input-group-addon after">{{currencySignAfter}}</span>
        </div>
      </div>

      <div
        [attr.id]="_id+'-error'"
        class="validation-message-danger"
        *ngIf="_error && !_isErrorHidden"
        [innerHtml]="errorMessage"
        role="alert"
      ></div>
      <div class="input-success" *ngIf="!_error && isSuccess">
        <span [attr.id]="_id+'-success'" class="validation-message-success">{{successMessage}}</span>
      </div>
    </div>
  `, styles: [":host{display:block}label{font-size:.75rem;color:#666;width:100%;pointer-events:none;transition:opacity ease-in-out 225ms}.input-container{width:100%;margin:3px 0 5px;box-sizing:border-box;-moz-box-sizing:border-box;display:flex;-webkit-box-direction:normal;-webkit-box-orient:horizontal;-webkit-flex-direction:row;-moz-flex-direction:row;-ms-flex-direction:row;flex-direction:row;-webkit-flex-wrap:nowrap;-moz-flex-wrap:nowrap;-ms-flex-wrap:none;flex-wrap:nowrap;-webkit-justify-content:flex-start;-ms-flex-pack:start;justify-content:flex-start;-webkit-align-content:stretch;-ms-flex-line-pack:stretch;align-content:stretch;-webkit-align-items:flex-start;-ms-flex-align:start;align-items:flex-start;border-radius:8px;background:#eee;border:1px solid transparent;border:.9px solid transparent}.input-container.focused{border-color:#056dae}.input-container.focused input::placeholder{opacity:0}.input-container.errored{border-color:#d60000}.input-container.errored.focused{width:calc(100% + 2px);border-width:1.99px;margin:2px 0 4px -1px}.input-container.success{border-color:#006e0a}.input-container.success.focused{width:calc(100% + 2px);border-width:1.99px;margin:2px 0 4px -1px}.input-container.readOnly{background:#fff;border:1px solid #666}.input-container.readOnly input.readOnly:read-only,.input-container.readOnly select,.input-container.readOnly span{background-color:#fff}.input-container.readOnly button.input-group-addon{background-color:#eee}.input-container.readOnly.focused:not(.has-pre-addon){outline:1px solid #056DAE}.input-container.disabled{background-color:#ccc;border:1px solid #CCC}.input-container.disabled input.disabled:disabled,.input-container.disabled select,.input-container.disabled span{background-color:#ccc}.input-container.has-pre-addon .input-switch-wrapper{margin:0}.input-container.has-pre-addon.focused:not(.readOnly){border-color:transparent}.input-container.has-pre-addon.focused .input-switch-wrapper{border-radius:0 8px 8px 0;outline:1px solid #056DAE}.input-container.has-pre-addon input{padding-left:0!important}.input-container .input-group-addon{width:auto;background-color:#fff}.input-container .input-group-addon.before{border-top-left-radius:8px;border-bottom-left-radius:8px;border-right:1px solid #6B6B6B}.input-container .input-group-addon.before,.input-container .input-group-addon.after{width:48px;height:48px}.input-container .input-group-addon.after{border-top-right-radius:8px;border-bottom-right-radius:8px;border-left:1px solid #6B6B6B}.input-container .add-on-pre{-webkit-order:0;-ms-flex-order:0;order:0;-webkit-flex:0 1 auto;-ms-flex:0 1 auto;flex:0 1 auto;-webkit-align-self:auto;-ms-flex-item-align:auto;align-self:auto;box-sizing:border-box;-moz-box-sizing:border-box}.input-container .add-on-pre select{margin:0 2px 0 0;height:48px;width:105px;border:1px solid transparent;border-top-right-radius:0;border-bottom-right-radius:0}.input-container .add-on-pre select:focus{outline:1px solid #056DAE}.input-container .input-switch-wrapper{flex:1 1 0%;box-sizing:border-box;-moz-box-sizing:border-box;margin:0 2px;border-radius:8px}.input-container .input-switch-wrapper input{width:100%;box-sizing:border-box;-moz-box-sizing:border-box;letter-spacing:.5px;height:48px;padding:10px 20px;background:#eee;border:none!important;box-shadow:none;border-radius:8px}.input-container .input-switch-wrapper input::-ms-clear{display:none}.input-container .input-switch-wrapper input:focus{outline:none}.input-container .add-on-post{flex:0 1 auto;box-sizing:border-box;-moz-box-sizing:border-box}.input-container .add-on-post .search{width:50px;padding-top:12px;border-radius:8px}.input-container .add-on-post .showHideMask{min-width:50px;border-radius:8px;padding:17px 10px;color:#056dae;font-family:Interstate_Bold,sans-serif;line-height:14px;cursor:pointer;font-size:12px;margin-left:2px}.input-container .add-on-post .showHideMask.disabled{background-color:#ccc;border:1px solid #CCC;color:#333;cursor:context-menu}.input-container .add-on-post .showHideMask.disabled button.disabled:disabled{background-color:#ccc}.input-error,.input-success{height:16px;width:100%;float:left}.input-success .validation-message-success{font-size:12px;font-family:Interstate_Bold,sans-serif;margin:0;color:#006e0a;font-weight:700;position:relative;display:block;line-height:1rem;font-size:.75rem}:host .light .input-container{background:#fff}:host .light .input-container input,:host .light .input-container select,:host .light .input-container span{background-color:#fff}:host .light .input-container.readOnly{background:#eee}:host .light .input-container.readOnly input,:host .light .input-container.readOnly select,:host .light .input-container.readOnly span{background-color:#eee}:host .light .input-container.readOnly button.input-group-addon{background-color:#fff}:host .light .input-container .input-group-addon{background-color:#eee}:host ::ng-deep #inputSearchIconId path{fill:#056dae}.form-control.disabled{background-color:#ccc;border:1px solid #CCC;color:#333;cursor:context-menu}.form-control.disabled button.disabled:disabled{background-color:#ccc}select.form-control{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%20standalone%3D%22no%22%3F%3E%0A%3Csvg%20width%3D%2215px%22%20height%3D%228px%22%20viewBox%3D%220%200%2015%208%22%20version%3D%221.1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20xmlns%3Asketch%3D%22http%3A//www.bohemiancoding.com/sketch/ns%22%3E%0A%20%20%20%20%3C%21--%20Generator%3A%20Sketch%203.4.1%20%2815681%29%20-%20http%3A//www.bohemiancoding.com/sketch%20--%3E%0A%20%20%20%20%3Ctitle%3EChevron%3C/title%3E%0A%20%20%20%20%3Cdesc%3ECreated%20with%20Sketch.%3C/desc%3E%0A%20%20%20%20%3Cdefs%3E%3C/defs%3E%0A%20%20%20%20%3Cg%20id%3D%22Toolkit%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20sketch%3Atype%3D%22MSPage%22%3E%0A%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdowns%22%20sketch%3Atype%3D%22MSArtboardGroup%22%20transform%3D%22translate%28-878.000000%2C%20-122.000000%29%22%20fill%3D%22%23056DAE%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Grey%22%20sketch%3Atype%3D%22MSLayerGroup%22%20transform%3D%22translate%28570.000000%2C%20101.000000%29%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Default%22%20sketch%3Atype%3D%22MSShapeGroup%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M312%2C32%20L312%2C29.6917048%20L316.989689%2C25.0246591%20L312%2C20.3577556%20L312%2C18%20L318.461538%2C24.1555486%20L318.461538%2C25.8443093%20L312%2C32%22%20id%3D%22Chevron%22%20transform%3D%22translate%28315.230769%2C%2025.000000%29%20rotate%28-270.000000%29%20translate%28-315.230769%2C%20-25.000000%29%20%22%3E%3C/path%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%3C/g%3E%0A%3C/svg%3E);background-repeat:no-repeat}select.form-control.disabled{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%20standalone%3D%22no%22%3F%3E%0A%3Csvg%20width%3D%2215px%22%20height%3D%228px%22%20viewBox%3D%220%200%2015%208%22%20version%3D%221.1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20xmlns%3Asketch%3D%22http%3A//www.bohemiancoding.com/sketch/ns%22%3E%0A%20%20%20%20%3C%21--%20Generator%3A%20Sketch%203.4.1%20%2815681%29%20-%20http%3A//www.bohemiancoding.com/sketch%20--%3E%0A%20%20%20%20%3Ctitle%3EChevron%3C/title%3E%0A%20%20%20%20%3Cdesc%3ECreated%20with%20Sketch.%3C/desc%3E%0A%20%20%20%20%3Cdefs%3E%3C/defs%3E%0A%20%20%20%20%3Cg%20id%3D%22Toolkit%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20sketch%3Atype%3D%22MSPage%22%3E%0A%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdowns%22%20sketch%3Atype%3D%22MSArtboardGroup%22%20transform%3D%22translate%28-878.000000%2C%20-122.000000%29%22%20fill%3D%22%23333333%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Grey%22%20sketch%3Atype%3D%22MSLayerGroup%22%20transform%3D%22translate%28570.000000%2C%20101.000000%29%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Default%22%20sketch%3Atype%3D%22MSShapeGroup%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M312%2C32%20L312%2C29.6917048%20L316.989689%2C25.0246591%20L312%2C20.3577556%20L312%2C18%20L318.461538%2C24.1555486%20L318.461538%2C25.8443093%20L312%2C32%22%20id%3D%22Chevron%22%20transform%3D%22translate%28315.230769%2C%2025.000000%29%20rotate%28-270.000000%29%20translate%28-315.230769%2C%20-25.000000%29%20%22%3E%3C/path%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%3C/g%3E%0A%3C/svg%3E);background-repeat:no-repeat}select.form-control.readOnly{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%20standalone%3D%22no%22%3F%3E%0A%3Csvg%20width%3D%2215px%22%20height%3D%228px%22%20viewBox%3D%220%200%2015%208%22%20version%3D%221.1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20xmlns%3Asketch%3D%22http%3A//www.bohemiancoding.com/sketch/ns%22%3E%0A%20%20%20%20%3C%21--%20Generator%3A%20Sketch%203.4.1%20%2815681%29%20-%20http%3A//www.bohemiancoding.com/sketch%20--%3E%0A%20%20%20%20%3Ctitle%3EChevron%3C/title%3E%0A%20%20%20%20%3Cdesc%3ECreated%20with%20Sketch.%3C/desc%3E%0A%20%20%20%20%3Cdefs%3E%3C/defs%3E%0A%20%20%20%20%3Cg%20id%3D%22Toolkit%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20sketch%3Atype%3D%22MSPage%22%3E%0A%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdowns%22%20sketch%3Atype%3D%22MSArtboardGroup%22%20transform%3D%22translate%28-878.000000%2C%20-122.000000%29%22%20fill%3D%22%23CCC%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Grey%22%20sketch%3Atype%3D%22MSLayerGroup%22%20transform%3D%22translate%28570.000000%2C%20101.000000%29%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Dropdown-Default%22%20sketch%3Atype%3D%22MSShapeGroup%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M312%2C32%20L312%2C29.6917048%20L316.989689%2C25.0246591%20L312%2C20.3577556%20L312%2C18%20L318.461538%2C24.1555486%20L318.461538%2C25.8443093%20L312%2C32%22%20id%3D%22Chevron%22%20transform%3D%22translate%28315.230769%2C%2025.000000%29%20rotate%28-270.000000%29%20translate%28-315.230769%2C%20-25.000000%29%20%22%3E%3C/path%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%20%20%20%20%3C/g%3E%0A%20%20%20%20%3C/g%3E%0A%3C/svg%3E);background-repeat:no-repeat}select.readOnly{pointer-events:none}::-webkit-input-placeholder{color:#6b6b6b}::-moz-placeholder{color:#6b6b6b}:-ms-input-placeholder{color:#6b6b6b}:-moz-placeholder{color:#6b6b6b}input::-ms-reveal,input::-ms-clear{display:none}\n"] }]
        }], ctorParameters: () => [{ type: ValidationService }, { type: CitiLiveAnnouncer }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NAVIGATOR]
                }] }], propDecorators: { validateOn: [{
                type: Input
            }], allowIntegerOnly: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], allowPositiveIntegerOnly: [{
                type: Input
            }], minLength: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], disabled: [{
                type: Input
            }], typeNumbersOnly: [{
                type: Input
            }], allowNonZeroValueOnly: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], ariaAutocomplete: [{
                type: Input
            }], preventPaste: [{
                type: Input
            }], thousandsSeparator: [{
                type: Input
            }], decimalSeparator: [{
                type: Input
            }], precision: [{
                type: Input
            }], step: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], minValue: [{
                type: Input
            }], maxFactor: [{
                type: Input
            }], maxFactorExceededMessage: [{
                type: Input
            }], currencySignAfter: [{
                type: Input
            }], currencySignBefore: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledById: [{
                type: Input
            }], ariaGroupLabel: [{
                type: Input
            }], idStr: [{
                type: Input
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }], selectLabel: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], searchIcon: [{
                type: Input
            }], required: [{
                type: Input
            }], describedby: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], isCbolPassword: [{
                type: Input
            }], countryCode: [{
                type: Input
            }], countryCodeChange: [{
                type: Output
            }], countryCodes: [{
                type: Input
            }], areaCode: [{
                type: Input
            }], areaCodeChange: [{
                type: Output
            }], areaCodes: [{
                type: Input
            }], unmaskedLength: [{
                type: Input
            }], theme: [{
                type: Input
            }], applyMaskOnEvents: [{
                type: Input
            }], removeMaskOnEvents: [{
                type: Input
            }], isSuccess: [{
                type: Input
            }], successMessage: [{
                type: Input
            }], visibleLabel: [{
                type: Input
            }], hideMask: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], hideToggle: [{
                type: Output
            }], focus: [{
                type: Input
            }], focusChange: [{
                type: Output
            }], blur: [{
                type: Output
            }], minLengthError: [{
                type: Input
            }], maxValueError: [{
                type: Input
            }], minValueError: [{
                type: Input
            }], maxLengthError: [{
                type: Input
            }], requiredError: [{
                type: Input
            }], messageOnlyNonZeroError: [{
                type: Input
            }], messageOnlyIntegerError: [{
                type: Input
            }], messageOnlyPositiveIntegerError: [{
                type: Input
            }], messageOnlyPhoneError: [{
                type: Input
            }], messageOnlyZipcodeError: [{
                type: Input
            }], messageOnlyEmailError: [{
                type: Input
            }], messageOnlyZipError: [{
                type: Input
            }], messageOnlySsnError: [{
                type: Input
            }], messageOnlyAreaCodeError: [{
                type: Input
            }], messageOnlyCountryCodeError: [{
                type: Input
            }], changeMessage: [{
                type: Input
            }], hasCustomError: [{
                type: Input
            }], customErrorMessage: [{
                type: Input
            }], error: [{
                type: Input
            }], errorChange: [{
                type: Output
            }], usePasswordOnAndroid: [{
                type: Input
            }], mask: [{
                type: Input
            }], hideShowButtonWhenMasked: [{
                type: Input
            }], showText: [{
                type: Input
            }], hideText: [{
                type: Input
            }], type: [{
                type: Input
            }], size: [{
                type: Input
            }], input: [{
                type: ViewChild,
                args: ['input']
            }], custom: [{
                type: ViewChild,
                args: [CustomInputMaskDirective]
            }], classRow: [{
                type: HostBinding,
                args: ['class.row']
            }] } });

const COMPONENTS$2 = [
    Input2Component,
    CustomInputMaskDirective,
    CurrencyInputMaskDirective,
    PartialInputMaskDirective,
    Input2Component4Q21
];
const MODULES$3 = [CommonModule, FormsModule, Icons2];
class Input2 {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Input2, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: Input2, declarations: [Input2Component,
            CustomInputMaskDirective,
            CurrencyInputMaskDirective,
            PartialInputMaskDirective,
            Input2Component4Q21], imports: [CommonModule, FormsModule, Icons2], exports: [Input2Component,
            CustomInputMaskDirective,
            CurrencyInputMaskDirective,
            PartialInputMaskDirective,
            Input2Component4Q21] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Input2, imports: [MODULES$3] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: Input2, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$3],
                    declarations: [...COMPONENTS$2],
                    exports: [...COMPONENTS$2]
                }]
        }] });

/**
 * Created by ht08666 on 5/23/2017.
 */
class FormComponent {
    getOrderAppearance() {
        if (typeof this.DomEl !== 'undefined' && this.DomEl !== null) {
            const value = this.DomEl.getAttribute('data-order-appearance');
            if (value !== null) {
                return parseInt(value, 10);
            }
        }
        return -1;
    }
    updateDomEl() { }
}

class InputService {
    constructor() {
        this.errorAdded = new EventEmitter();
        this.errorRemoved = new EventEmitter();
        this.valueChanged = new EventEmitter();
        this.validateNow = new EventEmitter();
        this.errors = [];
        this.inputs = [];
        this.values = [];
    }
    addError(error) {
        if (this.errors.indexOf(error) === -1) {
            this.errors.push(error);
        }
        ;
    }
    ;
    removeError(error) {
        if (this.errors.indexOf(error) !== -1) {
            this.errors.splice(this.errors.indexOf(error), 1);
        }
    }
    ;
    addInput(input) {
        // protects against other types coming in later through content additions
        if (!this.type)
            this.type = input.type;
        if (this.type === input.type) {
            this.inputs.push(input);
            if (this.type === 'radio' && input.checked) {
                this.value = input.value;
                // avoids value changed after checked error
                setTimeout(_ => this.valueChanged.emit(this.value), 0);
            }
            else if (this.type === 'checkbox' && input.checked) {
                const alreadySelected = this.values.includes(input.value);
                if (!alreadySelected)
                    this.values.push(input.value);
                // avoids value changed after checked error
                setTimeout(_ => this.valueChanged.emit(this.values), 0);
            }
        }
    }
    removeInput(input) {
        // remove any selected values
        if (this.type === 'checkbox' && this.values.includes(input.value)) {
            this.values = this.values.filter(val => val !== input.value);
        }
        else if (this.type === 'radio' && this.value === input.value) {
            this.value = undefined;
        }
        // remove the input
        this.inputs = this.inputs.filter(inpt => inpt !== input);
    }
    validate() {
        this.validateNow.emit();
    }
    ;
    changeValue(value, checked = true) {
        if (this.type === 'radio') {
            if (value !== this.value) {
                this.value = value;
                this.valueChanged.emit(value);
            }
        }
        else if (this.type === 'checkbox') {
            const alreadySelected = this.values.includes(value);
            if (alreadySelected && !checked) {
                this.values = this.values.filter(val => val !== value);
                this.valueChanged.emit(this.values);
            }
            else if (!alreadySelected && checked) {
                this.values.push(value);
                this.valueChanged.emit(this.values);
            }
        }
    }
    hasBoxChecked() {
        if (this.type === 'radio')
            throw Error('This method should only be called on checkboxes');
        else {
            return this.inputs.map(comp => comp.checked).some(isChecked => isChecked === true);
        }
    }
    clearValue() {
        this.value = undefined;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InputService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InputService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InputService, decorators: [{
            type: Injectable
        }] });

/*tslint:disable*/
// ----------- Imports ------------- //
// ---------- Component ------------ //
/**
 * @Component: CheckboxRadioRootComponent
 * @Section: Forms
 * @Description: This component is used to generate either a checkbox or a radio button.  It should not be used directly and is extended by the checkbox and radio button components to provide core functionality to both.
 */
class CheckboxRadioRootComponent extends FormComponent {
    /**
     * @Property: contentAddition
     * @Description: Where the checkbox is a content addition or not.
     * @Type: String
     */
    set contentAddition(value) {
        value = true;
        this.isContentAddition = value;
    }
    // -------- Lifecycle Hooks --------- //
    constructor(inputService, validationService, liveAnnouncer, cdr) {
        super();
        this.inputService = inputService;
        this.validationService = validationService;
        this.liveAnnouncer = liveAnnouncer;
        this.cdr = cdr;
        /**
         * @Property: checked
         * @Description: Whether the checkbox is checked or not
         * @Type: String
         */
        this.checked = false;
        /**
         * @Event: checkedChange
         * @Description: Emitted when the checked property of the radio is changed
         */
        this.checkedChange = new EventEmitter;
        /**
         * @Event: errorChange
         * @Description: Emitted when the validation error is updated
         */
        this.errorChange = new EventEmitter();
        /**
         * @Property: errorMessageRequired
         * @Description: Set custom error message displayed when the value is required and the check box is not checked
         * @Type: Boolean
         */
        this.errorMessageRequired = 'Required';
        /**
         * @Property: customValidator
         * @Description: Sets up a custom validator for a check box
         * @Type: Boolean
         */
        this.customValidator = null;
        /**
         * @Event: blur
         * @Description: Emitted when a checkbox button loses focus
         */
        this.blur = new EventEmitter();
        /**
         * @Event: keyup
         * @Description: Emitted when keyup is fired on the checkbox button
         */
        this.keyup = new EventEmitter();
        /**
         * @Event: keydown
         * @Description: Emitted when keydown is fired on the checkbox button
         */
        this.keydown = new EventEmitter();
        /**
         * @Event: keypress
         * @Description: Emitted when key press is fired on the checkbox button
         */
        this.keypress = new EventEmitter();
        /**
         * @Event: onClickEvent
         * @Description: Emitted when a radio button is clicked
         */
        this.onClickEvent = new EventEmitter();
        this.classes = [];
        this.isContentAddition = false;
        this.customErrorMessage = null;
        this.displayedErrorMessage = 'Error';
        this.isInitRun = true;
        // Constants for LiveAnnouncer
        this.expandedMessage = "Additional information expanded";
        this.collapsedMessage = "Additional information collapsed";
    }
    ;
    markForCheck() {
        this.cdr.markForCheck();
    }
    ngOnInit() {
        // Tell the input service that a checkbox was added
        this.inputService.addInput(this);
        this.update();
        // set inputs as checked initially
        if (this.value) {
            if (this.type === 'radio' && this.inputService.value === this.value) {
                this.checked = true;
            }
            else if (this.type === 'checkbox' && this.inputService.values.includes(this.value)) {
                this.checked = true;
            }
        }
        this.inputService.valueChanged.subscribe(value => {
            if (this.value === value && !this.checked && !this.check) {
                this.checked = true;
                this.update();
            }
            else if (this.value !== value && this.type === "radio" && (this.checked || this.check)) {
                this.checked = false;
                // Use Live Announcer to simulate announcements from aria-expanded for closing radio buttons
                this.announceExpandOrCollapse();
            }
        });
    }
    ngAfterViewInit() {
        if (this.value && this.inputService.value === this.value) {
            this.input.nativeElement.checked = true;
            this.inputService.changeValue(this.value);
        }
        // Listen for a change on the radio or checkbox
        // this.input.nativeElement.addEventListener("change", () => {
        //   // If there are any tooltips in the content addition
        //   if (this.input.nativeElement.checked && this.tooltips.length > 0) {
        //     // For each tooltip in the content addition
        //     this.tooltips.forEach(tooltip => {
        //       // Make sure the tooltip is positioned correctly
        //       this.contentAdditionEl.nativeElement.style.display = "block";
        //       tooltip.setRelativeOffsets();
        //       tooltip.positionTooltip(tooltip.initialPosition);
        //       this.contentAdditionEl.nativeElement.style.display = "";
        //     })
        //   }
        // })
        this.isInitRun = false;
    }
    ngOnDestroy() {
        this.inputService.removeInput(this);
        this.validationService.removeError(this);
    }
    // ------------ Methods ------------- //
    // On Focus
    onFocus() {
        if (this.inputService.inputGroupError)
            this.liveAnnouncer.announce(this.inputService.inputGroupErrorMsg);
        // If the input is first or last in the group and there is an error state, announce the error
        // if (this.shouldReadError()) this.liveAnnouncer.announce(this.inputService.inputGroupErrorMsg);
    }
    // shouldReadError(): boolean{
    //   return this.inputService.inputGroupError
    //      && ( this.label === this.inputService.inputs[0].label
    //        || this.label === this.inputService.inputs[this.inputService.inputs.length-1].label
    //     );
    // }
    onChange() {
        if (!this.isInitRun)
            this.validate();
        this.update();
    }
    update() {
        // If there is an input created already (check if this function is called before onViewInit)
        if (typeof this.input !== "undefined") {
            // Get the checked state of the input
            const isChecked = this.input.nativeElement.checked;
            setTimeout(_ => this.input.nativeElement.checked = isChecked, 0);
            // Make sure the checked input is in sync with the checked state of the input
            this.checked = isChecked;
            // Emit the checked state of the input
            this.checkedChange.emit(isChecked);
            // protects against contentaddition checkboxes
            const isPossibleValue = this.inputService.inputs
                .map(component => component.value)
                .some(opt => this.value === opt);
            // If the input is checked
            if (isPossibleValue) {
                // Emit the new value to the input service
                this.inputService.changeValue(this.value, isChecked);
            }
            // Use Live Announcer to simulate announcements from aria-expanded
            this.announceExpandOrCollapse();
            // Otherwise see if the checked or check inputs are available
        }
        else if (typeof this.checked === "boolean" || typeof this.check === "boolean") {
            // Emit the value of the inputs
            this.checkedChange.emit(this.checked || this.check);
            // If the value is true
            if ((this.checked || this.check) && this.type === "radio") {
                // Emit the new value to the input service
                this.inputService.changeValue(this.value);
            }
            // Use Live Announcer to simulate announcements from aria-expanded
            this.announceExpandOrCollapse();
        }
    }
    getAriaLabelledBy() {
        if (!!this.ariaLabelledById)
            return this.ariaLabelledById;
        else if (this.idStr && this.label)
            return this.idStr + '-label';
        else
            return null;
    }
    getAriaLabel() {
        if (this.ariaLabelledById || this.label) {
            return null;
        }
        else if (this.ariaLabel) {
            return this.ariaLabel;
        }
        else
            return null;
    }
    validate() {
        if (typeof this.input !== 'undefined' && this.input !== null) {
            this.toggleError("required", false);
            this.toggleError("required", this.required && !this.input.nativeElement.checked);
            // If the value pass all built-in validation,
            if (!this.hasError) {
                // and we still have custom validation to perform
                if (typeof this.customValidator === 'function' && this.customValidator !== null) {
                    // Performs the custom validation call
                    const errorMessage = this.customValidator.call(this, this.input.nativeElement.checked);
                    // Toggles error when it is needed
                    this.customErrorMessage = errorMessage;
                    this.toggleError('custom', typeof errorMessage !== 'undefined' && errorMessage !== null && errorMessage !== '');
                }
            }
        }
    }
    toggleChecked() {
        this.checked = !this.checked;
        this.checkedChange.emit(this.checked);
        this.inputService.valueChanged.emit();
    }
    toggleError(errorType, isErrored) {
        if (this.errorType && this.errorType !== errorType)
            this.errorChange.emit();
        // Clear the error state
        this.error = isErrored;
        if (isErrored) {
            switch (errorType) {
                case 'required':
                    this.displayedErrorMessage = this.errorMessageRequired;
                    break;
                case 'custom':
                    this.displayedErrorMessage = this.customErrorMessage;
                    break;
            }
        }
        // Set the error on the validation service
        if (isErrored) {
            // Add the error type
            this.errorType = errorType;
            // Add the error to the validation service
            this.validationService.addError(this);
            // Add the error to the group
            this.inputService.addError(this);
        }
        else {
            // Remove the error type
            this.errorType = null;
            // Remove the error from the validation service
            this.validationService.removeError(this);
            // Remove the error from the group
            this.inputService.removeError(this);
        }
        // Emit the change to the error
    }
    onClick(event) {
        this.propagateClickEvent(event);
    }
    propagateClickEvent(event) {
        event.radioObject = this;
        this.onClickEvent.emit(!this.checked);
    }
    get hasValue() {
        return typeof this.value !== 'undefined' && this.value !== null;
    }
    get hasError() {
        return this.error;
    }
    // Use Live Announcer to simulate announcements from aria-expanded
    announceExpandOrCollapse() {
        // If the checkbox or radio item has a content addition, announce collapse
        // or expansion message if checked or not checked
        if (this.ngContent && this.ngContent.nativeElement.children.length > 0) {
            if (this.checked) {
                this.liveAnnouncer.announce(this.expandedMessage);
            }
            else {
                this.liveAnnouncer.announce(this.collapsedMessage);
            }
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CheckboxRadioRootComponent, deps: [{ token: InputService }, { token: ValidationService }, { token: CitiLiveAnnouncer }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: CheckboxRadioRootComponent, isStandalone: false, selector: "citi-checkbox-radio-root", inputs: { required: "required", ariaLabelledById: "ariaLabelledById", ariaLabel: "ariaLabel", contentAddition: "contentAddition", idStr: "idStr", name: "name", value: "value", label: "label", disabled: "disabled", checked: "checked", check: "check", error: "error", htmlContent: "htmlContent", errorMessageRequired: "errorMessageRequired", customValidator: "customValidator" }, outputs: { checkedChange: "checkedChange", errorChange: "errorChange", blur: "blur", keyup: "keyup", keydown: "keydown", keypress: "keypress", onClickEvent: "onClickEvent" }, providers: [{ provide: FormComponent, useExisting: forwardRef((() => CheckboxRadioRootComponent)) }], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true }, { propertyName: "contentAdditionEl", first: true, predicate: ["contentAdditionEl"], descendants: true }, { propertyName: "ngContent", first: true, predicate: ["ngContent"], descendants: true }], usesInheritance: true, ngImport: i0, template: `
  <div class="{{type}} col-xs-12 inline-addition" [class.validation-input-danger]="error ? true : null"
  [class.content-addition]="isContentAddition ? true : null" *ngIf="idStr && name && label && value">
  <input
    onkeypress="if (event.keyCode == 13) return false;"
    #input
    [attr.type]="type"
    [attr.id]="idStr"
    [attr.name]="name"
    [value]="value"
    [required]="required"
    (change)="onChange()"
    (blur)="blur.emit($event)"
    (keypress)="keypress.emit($event)"
    (keyup)="keyup.emit($event)"
    (keydown)="keydown.emit($event)"
    tabindex="0"
    [ngClass]="classes"
    [checked]="checked ? true : null"
    (click)="onClick($event)"
    [attr.disabled]="disabled ? true : null"
    [attr.aria-controls]="ngContent && ngContent.children.length ? idStr + '-addition' : null"
    (focus)="onFocus()"
    >
  <label
    [attr.for]="idStr"
    [attr.id]="idStr + '-label'"
    [innerHtml]="label"
    tabindex="-1"
  >
  </label>
  <!-- Error Messsage -->
  <span *ngIf="error" class="validation-message-danger" aria-live="assertive"
        [attr.id]="name !== null ? name + 'error' : null" [innerHtml]="displayedErrorMessage" role="alert">
  </span>
  <div [attr.id]="idStr + '-addition'" class="row" [hidden]="ngContent && !ngContent.children.length" #contentAdditionEl>
    <div class="col-xs-12">
      <div class="inline-content">
        <div #ngContent [contentHtml]="htmlContent">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
  </div>
</div>
`, isInline: true, styles: [".checkBoxAppear{-webkit-appearance:checkbox!important;-moz-appearance:checkbox!important;appearance:checkbox!important}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: ContentHtmlDirective, selector: "[contentHtml]", inputs: ["contentHtml"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CheckboxRadioRootComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-checkbox-radio-root', standalone: false, changeDetection: ChangeDetectionStrategy.OnPush, template: `
  <div class="{{type}} col-xs-12 inline-addition" [class.validation-input-danger]="error ? true : null"
  [class.content-addition]="isContentAddition ? true : null" *ngIf="idStr && name && label && value">
  <input
    onkeypress="if (event.keyCode == 13) return false;"
    #input
    [attr.type]="type"
    [attr.id]="idStr"
    [attr.name]="name"
    [value]="value"
    [required]="required"
    (change)="onChange()"
    (blur)="blur.emit($event)"
    (keypress)="keypress.emit($event)"
    (keyup)="keyup.emit($event)"
    (keydown)="keydown.emit($event)"
    tabindex="0"
    [ngClass]="classes"
    [checked]="checked ? true : null"
    (click)="onClick($event)"
    [attr.disabled]="disabled ? true : null"
    [attr.aria-controls]="ngContent && ngContent.children.length ? idStr + '-addition' : null"
    (focus)="onFocus()"
    >
  <label
    [attr.for]="idStr"
    [attr.id]="idStr + '-label'"
    [innerHtml]="label"
    tabindex="-1"
  >
  </label>
  <!-- Error Messsage -->
  <span *ngIf="error" class="validation-message-danger" aria-live="assertive"
        [attr.id]="name !== null ? name + 'error' : null" [innerHtml]="displayedErrorMessage" role="alert">
  </span>
  <div [attr.id]="idStr + '-addition'" class="row" [hidden]="ngContent && !ngContent.children.length" #contentAdditionEl>
    <div class="col-xs-12">
      <div class="inline-content">
        <div #ngContent [contentHtml]="htmlContent">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
  </div>
</div>
`, providers: [{ provide: FormComponent, useExisting: forwardRef((() => CheckboxRadioRootComponent)) }], styles: [".checkBoxAppear{-webkit-appearance:checkbox!important;-moz-appearance:checkbox!important;appearance:checkbox!important}\n"] }]
        }], ctorParameters: () => [{ type: InputService }, { type: ValidationService }, { type: CitiLiveAnnouncer }, { type: i0.ChangeDetectorRef }], propDecorators: { required: [{
                type: Input
            }], ariaLabelledById: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], contentAddition: [{
                type: Input
            }], idStr: [{
                type: Input
            }], name: [{
                type: Input
            }], value: [{
                type: Input
            }], label: [{
                type: Input
            }], disabled: [{
                type: Input
            }], checked: [{
                type: Input
            }], check: [{
                type: Input
            }], checkedChange: [{
                type: Output
            }], error: [{
                type: Input
            }], errorChange: [{
                type: Output
            }], htmlContent: [{
                type: Input
            }], errorMessageRequired: [{
                type: Input
            }], customValidator: [{
                type: Input
            }], blur: [{
                type: Output
            }], keyup: [{
                type: Output
            }], keydown: [{
                type: Output
            }], keypress: [{
                type: Output
            }], onClickEvent: [{
                type: Output
            }], input: [{
                type: ViewChild,
                args: ["input"]
            }], contentAdditionEl: [{
                type: ViewChild,
                args: ["contentAdditionEl"]
            }], ngContent: [{
                type: ViewChild,
                args: ["ngContent"]
            }] } });

/**
 * @Component: Checkbox
 * @Section: Forms
 * @Description: This component generates the checkbox
 */
class CheckboxComponent extends CheckboxRadioRootComponent {
    // ---------- Constructor ----------- //
    constructor(inputServiceCheckbox, validationService, liveAnnouncer, cdr) {
        super(inputServiceCheckbox, validationService, liveAnnouncer, cdr);
        this.inputServiceCheckbox = inputServiceCheckbox;
        if (isDevMode()) {
            console.error('%cThe Common UI DDL InputGroup <citi-checkbox> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
        this.type = 'checkbox';
        this.classes.push('checkboxAppear');
    }
    ;
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CheckboxComponent, deps: [{ token: InputService }, { token: ValidationService }, { token: CitiLiveAnnouncer }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: CheckboxComponent, isStandalone: false, selector: "citi-checkbox", usesInheritance: true, ngImport: i0, template: `
<div class="checkbox col-xs-12 inline-addition" [class.validation-input-danger]="error ? true : null"
  [class.content-addition]="isContentAddition ? true : null" *ngIf="idStr && name && label && value">
  <input
    onkeypress="if (event.keyCode == 13) return false;"
    #input
    type="checkbox"
    [attr.id]="idStr"
    [attr.name]="name"
    [value]="value"
    [required]="required"
    (change)="onChange()"
    (blur)="blur.emit($event)"
    (keypress)="keypress.emit($event)"
    (keyup)="keyup.emit($event)"
    (keydown)="keydown.emit($event)"
    class="checkBoxAppear"
    tabindex="0"
    [ngClass]="classes"
    [checked]="checked ? true : null"
    (click)="onClick($event)"
    (focus)="onFocus()"
    [attr.disabled]="disabled ? true : null"
    [attr.aria-controls]="ngContent && ngContent.children.length ? idStr + '-addition' : null"
    [attr.aria-expanded]="ngContent && checked && ngContent.children.length ? true : null"
    [attr.aria-labelledby]="getAriaLabelledBy()"
    [attr.aria-label]="getAriaLabel()"
    >
  <label
    *ngIf="!!label"
    [attr.for]="idStr"
    [attr.id]="idStr + '-label'"
    [innerHtml]="label"
    tabindex="-1"
  >
  </label>
  <!-- Error Messsage -->
  <span *ngIf="error" class="validation-message-danger" aria-live="assertive"
        [attr.id]="name !== null ? name + 'error' : null" [innerHtml]="displayedErrorMessage" role="alert">
  </span>
  <div [attr.id]="idStr + '-addition'" class="row" [hidden]="ngContent && !ngContent.children.length">
    <div class="col-xs-12">
      <div class="inline-content">
        <div #ngContent [contentHtml]="htmlContent">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
  </div>
</div>
`, isInline: true, dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: ContentHtmlDirective, selector: "[contentHtml]", inputs: ["contentHtml"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CheckboxComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'citi-checkbox',
                    standalone: false,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: `
<div class="checkbox col-xs-12 inline-addition" [class.validation-input-danger]="error ? true : null"
  [class.content-addition]="isContentAddition ? true : null" *ngIf="idStr && name && label && value">
  <input
    onkeypress="if (event.keyCode == 13) return false;"
    #input
    type="checkbox"
    [attr.id]="idStr"
    [attr.name]="name"
    [value]="value"
    [required]="required"
    (change)="onChange()"
    (blur)="blur.emit($event)"
    (keypress)="keypress.emit($event)"
    (keyup)="keyup.emit($event)"
    (keydown)="keydown.emit($event)"
    class="checkBoxAppear"
    tabindex="0"
    [ngClass]="classes"
    [checked]="checked ? true : null"
    (click)="onClick($event)"
    (focus)="onFocus()"
    [attr.disabled]="disabled ? true : null"
    [attr.aria-controls]="ngContent && ngContent.children.length ? idStr + '-addition' : null"
    [attr.aria-expanded]="ngContent && checked && ngContent.children.length ? true : null"
    [attr.aria-labelledby]="getAriaLabelledBy()"
    [attr.aria-label]="getAriaLabel()"
    >
  <label
    *ngIf="!!label"
    [attr.for]="idStr"
    [attr.id]="idStr + '-label'"
    [innerHtml]="label"
    tabindex="-1"
  >
  </label>
  <!-- Error Messsage -->
  <span *ngIf="error" class="validation-message-danger" aria-live="assertive"
        [attr.id]="name !== null ? name + 'error' : null" [innerHtml]="displayedErrorMessage" role="alert">
  </span>
  <div [attr.id]="idStr + '-addition'" class="row" [hidden]="ngContent && !ngContent.children.length">
    <div class="col-xs-12">
      <div class="inline-content">
        <div #ngContent [contentHtml]="htmlContent">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
  </div>
</div>
`
                }]
        }], ctorParameters: () => [{ type: InputService }, { type: ValidationService }, { type: CitiLiveAnnouncer }, { type: i0.ChangeDetectorRef }] });

/**
 * @Component: Checkbox
 * @Section: Forms
 * @Description: This componentt generates the checkbox
 */
class RadioComponent extends CheckboxRadioRootComponent {
    // ---------- Constructor ----------- //
    constructor(inputServiceRadio, validationService, liveAnnouncer, cdr) {
        super(inputServiceRadio, validationService, liveAnnouncer, cdr);
        this.inputServiceRadio = inputServiceRadio;
        if (isDevMode()) {
            console.error('%cThe Common UI DDL InputGroup <citi-radio> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
        this.type = 'radio';
    }
    ;
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: RadioComponent, deps: [{ token: InputService }, { token: ValidationService }, { token: CitiLiveAnnouncer }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: RadioComponent, isStandalone: false, selector: "citi-radio", usesInheritance: true, ngImport: i0, template: `
<div class="radio col-xs-12 inline-addition" [class.validation-input-danger]="error ? true : null"
  [class.content-addition]="isContentAddition ? true : null" *ngIf="idStr && name && label && value">
  <input
    onkeypress="if (event.keyCode == 13) return false;"
    #input
    type="radio"
    [attr.id]="idStr"
    [attr.name]="name"
    [value]="value"
    [required]="required"
    (change)="onChange()"
    (blur)="blur.emit($event)"
    (keypress)="keypress.emit($event)"
    (keyup)="keyup.emit($event)"
    (keydown)="keydown.emit($event)"
    tabindex="0"
    [ngClass]="classes"
    [checked]="checked ? true : null"
    (click)="onClick($event)"
    (focus)="onFocus()"
    [attr.disabled]="disabled ? true : null"
    [attr.aria-controls]="ngContent && ngContent.children.length ? idStr + '-addition' : null"
    [attr.aria-expanded]="ngContent && checked && ngContent.children.length ? true : null"
    [attr.aria-labelledby]="getAriaLabelledBy()"
    [attr.aria-label]="getAriaLabel()"
    >
  <label
    *ngIf="!!label"
    [attr.for]="idStr"
    [attr.id]="idStr + '-label'"
    [innerHtml]="label"
    tabindex="-1"
  >
  </label>
  <!-- Error Messsage -->
  <span *ngIf="error" class="validation-message-danger" aria-live="assertive"
        [attr.id]="name !== null ? name + 'error' : null" [innerHtml]="displayedErrorMessage" role="alert">
  </span>
  <div [attr.id]="idStr + '-addition'" class="row" [hidden]="ngContent && !ngContent.children.length">
    <div class="col-xs-12">
      <div class="inline-content">
        <div #ngContent [contentHtml]="htmlContent">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
  </div>
</div>
`, isInline: true, dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: ContentHtmlDirective, selector: "[contentHtml]", inputs: ["contentHtml"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: RadioComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'citi-radio',
                    standalone: false,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: `
<div class="radio col-xs-12 inline-addition" [class.validation-input-danger]="error ? true : null"
  [class.content-addition]="isContentAddition ? true : null" *ngIf="idStr && name && label && value">
  <input
    onkeypress="if (event.keyCode == 13) return false;"
    #input
    type="radio"
    [attr.id]="idStr"
    [attr.name]="name"
    [value]="value"
    [required]="required"
    (change)="onChange()"
    (blur)="blur.emit($event)"
    (keypress)="keypress.emit($event)"
    (keyup)="keyup.emit($event)"
    (keydown)="keydown.emit($event)"
    tabindex="0"
    [ngClass]="classes"
    [checked]="checked ? true : null"
    (click)="onClick($event)"
    (focus)="onFocus()"
    [attr.disabled]="disabled ? true : null"
    [attr.aria-controls]="ngContent && ngContent.children.length ? idStr + '-addition' : null"
    [attr.aria-expanded]="ngContent && checked && ngContent.children.length ? true : null"
    [attr.aria-labelledby]="getAriaLabelledBy()"
    [attr.aria-label]="getAriaLabel()"
    >
  <label
    *ngIf="!!label"
    [attr.for]="idStr"
    [attr.id]="idStr + '-label'"
    [innerHtml]="label"
    tabindex="-1"
  >
  </label>
  <!-- Error Messsage -->
  <span *ngIf="error" class="validation-message-danger" aria-live="assertive"
        [attr.id]="name !== null ? name + 'error' : null" [innerHtml]="displayedErrorMessage" role="alert">
  </span>
  <div [attr.id]="idStr + '-addition'" class="row" [hidden]="ngContent && !ngContent.children.length">
    <div class="col-xs-12">
      <div class="inline-content">
        <div #ngContent [contentHtml]="htmlContent">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
  </div>
</div>
`
                }]
        }], ctorParameters: () => [{ type: InputService }, { type: ValidationService }, { type: CitiLiveAnnouncer }, { type: i0.ChangeDetectorRef }] });

/**
 * @Component: Input Group
 * @Section: Forms
 * @Description: This component generates a group of checkboxes or radio boxes
 */
class InputGroupComponent extends FormComponent {
    set errorMessage(errorMessage) {
        this.inputService.inputGroupErrorMsg = this._errorMessage = errorMessage;
    }
    set value(value) {
        // If the value is not the current value on the input service
        if (value !== this.inputService.value) {
            // If this is the initialization run of the group
            // this.inputService.changeValue(value);
            if (typeof value === 'string')
                this.inputService.value = value;
            else if (Array.isArray(value))
                this.inputService.values = value;
        }
    }
    constructor(inputService, validationService, host, cdr) {
        super();
        this.inputService = inputService;
        this.validationService = validationService;
        this.host = host;
        this.cdr = cdr;
        /**
         * @Property: title
         * @Description: The title for the input group
         * @Type: String
         */
        this.title = '';
        /**
         * @Property: required
         * @Description: If the input group is required
         * @Type: Boolean
         */
        this.required = false;
        /**
         * @Property: error
         * @Description: If the input group is in an error state
         * @Type: Boolean
         */
        this.error = false;
        this.errorChange = new EventEmitter();
        /**
         * @Property: errorMessage
         * @Description: Error message to display while the input group is in an error state
         * @Type: String
         */
        this._errorMessage = 'Error';
        this.valueChange = new EventEmitter();
        if (isDevMode()) {
            console.error('%cThe Common UI DDL InputGroup <citi-input-group> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    markForCheck() {
        this.cdr.markForCheck();
    }
    // Clears an error state on the input. Takes a type of error
    toggleError(errorType, isErrored) {
        // Clear the error state
        this.error = isErrored;
        // Set the error on the validation service
        if (isErrored) {
            // Add the error to the validation service
            this.validationService.addError(this);
        }
        else {
            // Remove the error from the validation service
            this.validationService.removeError(this);
        }
        // Emit the change to the error
        this.errorChange.emit(isErrored);
        this.inputService.inputGroupError = isErrored;
    }
    // Function to validate the input group
    validate() {
        // If this input group is required
        if (this.required) {
            // If the input group is valid or not
            let valid = false;
            // Loop over all the inputs that are a part of this input group
            for (let i = 0, j = this.inputService.inputs.length; i < j; i++) {
                // Get a  reference to the current input
                const input = this.inputService.inputs[i];
                if (input.checked) {
                    valid = input.checked;
                }
                if (input.isChecked) {
                    valid = input.isChecked;
                }
                // fix for first click on an required radio group
                if (!valid && this.inputService.value) {
                    valid = true;
                }
                if (valid) {
                    break;
                }
            }
            // Set the error type if needed
            this.toggleError('required', !valid);
        }
    }
    // On init
    ngOnInit() {
        this.validationService.addInput(this);
        // Subscribe to validateNow on the validation service
        this.validateSub = this.validationService.validateNow.subscribe(validate => {
            this.validate();
        });
        this.valueChangedSub = this.inputService.valueChanged.subscribe(value => {
            this.valueChange.emit(value);
            this.validate();
        });
        // Catch any initial errors in the error service and set the error state if needed
        setTimeout(() => {
            if (this.inputService.errors.length) {
                this.toggleError('input', true);
            }
        });
    }
    ngAfterViewInit() {
        this.updateDomEl();
    }
    ngOnDestroy() {
        this.validationService.removeError(this);
        if (this.validateSub)
            this.validateSub.unsubscribe();
    }
    updateDomEl() {
        this.DomEl = this.host.nativeElement.querySelectorAll('input')[0];
    }
    /**
     * @Function: clearSelection
     * @Description: Function that clears the group input's selections
     * @Return: void
     */
    clearSelection() {
        for (let i = 0; i < this.inputService.inputs.length; i++) {
            this.inputService.inputs[i].checked = false;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InputGroupComponent, deps: [{ token: InputService }, { token: ValidationService }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: InputGroupComponent, isStandalone: false, selector: "citi-input-group", inputs: { title: "title", required: "required", error: "error", errorMessage: "errorMessage", htmlContent: "htmlContent", value: "value" }, outputs: { errorChange: "errorChange", valueChange: "valueChange" }, providers: [InputService], usesInheritance: true, ngImport: i0, template: `
  <fieldset>
  <legend *ngIf="title !== null && title !== ''">
      <span [innerHtml]="title"></span>
  </legend>
  <div class="validation-message-danger" *ngIf="error && _errorMessage" [innerHtml]="_errorMessage" role="alert"></div>
  <div class="row" [ngClass]= "error ? 'validation-fieldset-danger' : null">
      <ng-content></ng-content>
  </div>
</fieldset>
`, isInline: true, styles: [""], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InputGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-input-group', standalone: false, changeDetection: ChangeDetectionStrategy.OnPush, template: `
  <fieldset>
  <legend *ngIf="title !== null && title !== ''">
      <span [innerHtml]="title"></span>
  </legend>
  <div class="validation-message-danger" *ngIf="error && _errorMessage" [innerHtml]="_errorMessage" role="alert"></div>
  <div class="row" [ngClass]= "error ? 'validation-fieldset-danger' : null">
      <ng-content></ng-content>
  </div>
</fieldset>
`, providers: [InputService] }]
        }], ctorParameters: () => [{ type: InputService }, { type: ValidationService }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }], propDecorators: { title: [{
                type: Input
            }], required: [{
                type: Input
            }], error: [{
                type: Input
            }], errorChange: [{
                type: Output
            }], errorMessage: [{
                type: Input
            }], htmlContent: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

const COMPONENTS$1 = [
    CheckboxComponent,
    RadioComponent,
    InputGroupComponent,
    CheckboxRadioRootComponent
];
const MODULES$2 = [
    CommonModule,
    ContentHtml
    // Directives,
    // CitiTooltip
];
const SERVICES = [
    InputService
];
class InputGroup {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InputGroup, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: InputGroup, declarations: [CheckboxComponent,
            RadioComponent,
            InputGroupComponent,
            CheckboxRadioRootComponent], imports: [CommonModule,
            ContentHtml
            // Directives,
            // CitiTooltip
        ], exports: [CheckboxComponent,
            RadioComponent,
            InputGroupComponent,
            CheckboxRadioRootComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InputGroup, providers: [InputService], imports: [MODULES$2] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InputGroup, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$2],
                    declarations: [...COMPONENTS$1],
                    exports: [...COMPONENTS$1],
                    providers: [InputService]
                }]
        }] });

/**
 * @Component: Nav Group
 * @Section: Navigation
 * @description This is in page navigation component to create
 * from Bootstrap.
*/
class NavGroupComponent {
    set activeTab(tabNum) {
        this._activeTab = tabNum;
        this.changeTab(tabNum);
    }
    addTab(tab) {
        if (this.tabContent) {
            if (this.tabContent.nativeElement.children.length === 0) {
                this.tabs = [];
            }
        }
        this.tabs.push(tab);
        return this.tabs.length - 1;
    }
    countTabs() {
        return this.tabs.length;
    }
    constructor(el) {
        this.el = el;
        /**
         * @property activeTab
         * @description This defines the active panel of in page navigation
         * @return number
        */
        this._activeTab = 0;
        this.activeTabChange = new EventEmitter();
        this.tabs = [];
        this.tabsFocused = false;
        this.shiftOn = false;
        /**
         * @Event: tabChanged
         * @description This event that is fired when a tab activates; returns the index of the active tab (first tab is 1)
         * @return Number
         */
        this.tabChanged = new EventEmitter();
    }
    ngOnInit() {
        this.tabLength = this.tabs.length;
    }
    ngAfterViewInit() {
        this.selectTab(this.tabs[this._activeTab]);
        // Attaches the trigger button to the tab pages
        if (this.tabs.length > 0) {
            const triggerButtons = this.el.nativeElement.querySelectorAll('ul.nav > li > a');
            this.tabs.forEach((tab, index) => {
                tab.triggerButton = triggerButtons[index];
                tab.tabFocusTrigger.subscribe(function (isActivated) {
                    if (isActivated) {
                        this.triggerButton.focus();
                    }
                }.bind(tab));
            });
        }
    }
    selectTab(tab) {
        if (!!tab) {
            this.tabs.forEach((tab2) => {
                tab2.isExpanded = false;
                tab2.tabIndexAttr = -1;
                tab2.tabFocusTrigger.emit(false);
            });
            tab.isExpanded = true;
            tab.tabIndexAttr = 0;
            tab.tabFocusTrigger.emit(true);
            // Event emitter
            this.tabChanged.emit(tab.tabIndex);
            this.activeTabChange.emit(tab.tabIndex);
        }
    }
    changeTab(tabNum) {
        if (!this.tabs[0])
            return;
        this.tabs.forEach((tab) => tab.isExpanded = false);
        this.tabs[tabNum].isExpanded = true;
        this.tabChanged.emit(tabNum);
        this.activeTabChange.emit(tabNum);
    }
    tablistFocus() {
        this.tabsFocused = true;
    }
    tabToContent() {
        this.tabsFocused = false;
        this.tabContent.nativeElement.focus();
    }
    currentTab() {
        return this.tabs.findIndex(tab => tab.isExpanded);
    }
    prevTab() {
        const curIndex = this.currentTab();
        const tab = (curIndex === 0)
            ? this.tabs[this.tabs.length]
            : this.tabs[curIndex - 1];
        this.selectTab(tab);
    }
    nextTab() {
        const curIndex = this.currentTab();
        let tab;
        if (curIndex === (this.tabs.length)) {
            tab = this.tabs[0];
        }
        else {
            tab = this.tabs[curIndex + 1];
        }
        this.selectTab(tab);
    }
    stopBubble(event) {
        event.preventDefault();
        event.stopPropagation();
    }
    handleKeyPress(event) {
        if (this.tabsFocused) {
            const keycode = event.which;
            switch (keycode) {
                case KEY_HOME:
                    this.stopBubble(event);
                    this.selectTab(this.tabs[0]);
                    break;
                case KEY_END:
                    this.stopBubble(event);
                    this.selectTab(this.tabs[this.tabs.length]);
                    break;
                case KEY_ARROW_LEFT:
                case KEY_ARROW_UP:
                    this.stopBubble(event);
                    this.prevTab();
                    break;
                case KEY_ARROW_RIGHT:
                case KEY_ARROW_DOWN:
                    this.stopBubble(event);
                    this.nextTab();
                    break;
            }
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: NavGroupComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: NavGroupComponent, isStandalone: false, selector: "citi-nav-group", inputs: { idStr: "idStr", activeTab: "activeTab", htmlContent: "htmlContent" }, outputs: { activeTabChange: "activeTabChange", tabChanged: "tabChanged" }, host: { listeners: { ":keydown": "handleKeyPress($event)" } }, viewQueries: [{ propertyName: "tabContent", first: true, predicate: ["tabContent"], descendants: true }], ngImport: i0, template: `
  <div class [attr.id]="idStr">
    <section [class]="'in-page-nav tabs-'+tabLength">

        <div class="container-fluid">
            <ul class="nav nav-pills nav-justified" role="tablist">
                <li *ngFor="let tab of tabs; let idx = index" role="presentation" [class]="(tab.isExpanded ? 'active' : '')" (click)="selectTab(tab); $event.preventDefault()">
                    <a href="#" id="{{idStr}}-{{idx}}-Tab" attr.aria-controls="{{idStr}}-panel-{{idx}}" role="tab" [attr.aria-selected]="(tab.isExpanded ? 'true' : 'false')" data-toggle="tab"
                        (focus)="tablistFocus()" [tabindex]="(tab.isExpanded ? 0 : -1)">
                        <span class="nav-pill-tab-content" [innerHtml]="tab.tabTitle"></span>
                    </a>
                </li>
            </ul>
          </div>

          <div class="tab-content noPadding" #tabContent [contentHtml]="htmlContent">
              <ng-content></ng-content>
          </div>

      </section>
  </div>

  `, isInline: true, styles: [".noPadding{padding:0}\n"], dependencies: [{ kind: "directive", type: i3$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: ContentHtmlDirective, selector: "[contentHtml]", inputs: ["contentHtml"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: NavGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-nav-group', standalone: false, template: `
  <div class [attr.id]="idStr">
    <section [class]="'in-page-nav tabs-'+tabLength">

        <div class="container-fluid">
            <ul class="nav nav-pills nav-justified" role="tablist">
                <li *ngFor="let tab of tabs; let idx = index" role="presentation" [class]="(tab.isExpanded ? 'active' : '')" (click)="selectTab(tab); $event.preventDefault()">
                    <a href="#" id="{{idStr}}-{{idx}}-Tab" attr.aria-controls="{{idStr}}-panel-{{idx}}" role="tab" [attr.aria-selected]="(tab.isExpanded ? 'true' : 'false')" data-toggle="tab"
                        (focus)="tablistFocus()" [tabindex]="(tab.isExpanded ? 0 : -1)">
                        <span class="nav-pill-tab-content" [innerHtml]="tab.tabTitle"></span>
                    </a>
                </li>
            </ul>
          </div>

          <div class="tab-content noPadding" #tabContent [contentHtml]="htmlContent">
              <ng-content></ng-content>
          </div>

      </section>
  </div>

  `, host: { '(:keydown)': 'handleKeyPress($event)' }, styles: [".noPadding{padding:0}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { tabContent: [{
                type: ViewChild,
                args: ['tabContent']
            }], idStr: [{
                type: Input
            }], activeTab: [{
                type: Input
            }], activeTabChange: [{
                type: Output
            }], htmlContent: [{
                type: Input
            }], tabChanged: [{
                type: Output
            }] } });

/**
 * @Component: Nav Panel
 * @Section: Navigation
 * @description This is the nav panel component and is used to create panel in the nav group
 * from Bootstrap.
*/
class NavPanelComponent {
    constructor(parent, el) {
        this.parent = parent;
        this.el = el;
        // Set classes on host <citi-tab> tag
        this.classTabPane = true;
        this.classLevel1 = true;
        this.classParent = true;
        this.isExpanded = false;
        this.tabFocusTrigger = new EventEmitter();
        this.triggerButton = null;
        this.tabgroup = parent;
        this.tabIndex = this.tabgroup.addTab(this);
        this.tabIndexAttr = -1;
    }
    ngOnInit() {
        let totalTabs = this.tabgroup.countTabs();
        this.lastTab = this.tabIndex === totalTabs;
        this.isExpanded = this.tabIndex === this.tabgroup.defaultTab;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: NavPanelComponent, deps: [{ token: NavGroupComponent }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: NavPanelComponent, isStandalone: false, selector: "citi-nav-panel", inputs: { tabTitle: ["title", "tabTitle"], htmlContent: "htmlContent" }, host: { properties: { "class.tab-pane": "this.classTabPane", "class.level1": "this.classLevel1", "class.parent": "this.classParent", "class.active": "this.isExpanded" } }, ngImport: i0, template: `
  <div role="tabpanel"
    id="{{tabgroup.idStr}}-panel-{{tabIndex}}"
    attr.aria-labelledby="{{tabgroup.idStr}}-panel-{{tabIndex}}"
    class="tab-pane active transcript container-fluid"
    [contentHtml]="htmlContent">
    <ng-content></ng-content>
  </div>

  `, isInline: true, styles: [""], dependencies: [{ kind: "directive", type: ContentHtmlDirective, selector: "[contentHtml]", inputs: ["contentHtml"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: NavPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-nav-panel', standalone: false, template: `
  <div role="tabpanel"
    id="{{tabgroup.idStr}}-panel-{{tabIndex}}"
    attr.aria-labelledby="{{tabgroup.idStr}}-panel-{{tabIndex}}"
    class="tab-pane active transcript container-fluid"
    [contentHtml]="htmlContent">
    <ng-content></ng-content>
  </div>

  ` }]
        }], ctorParameters: () => [{ type: NavGroupComponent }, { type: i0.ElementRef }], propDecorators: { classTabPane: [{
                type: HostBinding,
                args: ['class.tab-pane']
            }], classLevel1: [{
                type: HostBinding,
                args: ['class.level1']
            }], classParent: [{
                type: HostBinding,
                args: ['class.parent']
            }], isExpanded: [{
                type: HostBinding,
                args: ['class.active']
            }], tabTitle: [{
                type: Input,
                args: ['title']
            }], htmlContent: [{
                type: Input
            }] } });

const COMPONENTS = [
    NavGroupComponent,
    NavPanelComponent
];
const MODULES$1 = [
    CommonModule,
    ContentHtml
];
class InPageNavigation {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InPageNavigation, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: InPageNavigation, declarations: [NavGroupComponent,
            NavPanelComponent], imports: [CommonModule,
            ContentHtml], exports: [NavGroupComponent,
            NavPanelComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InPageNavigation, imports: [MODULES$1] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: InPageNavigation, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$1],
                    declarations: [...COMPONENTS],
                    exports: [...COMPONENTS]
                }]
        }] });

function getFocableAbleElements(root) {
    return root.querySelectorAll(`
    a[href]:not([tabindex='-1']),
    area[href]:not([tabindex='-1']),
    input:not([disabled]):not([tabindex='-1']),
    select:not([disabled]):not([tabindex='-1']),
    textarea:not([disabled]):not([tabindex='-1']),
    button:not([disabled]):not([tabindex='-1']),
    iframe:not([tabindex='-1']),
    [tabindex]:not([tabindex='-1']),
    [contentEditable=true]:not([tabindex='-1']
  `);
}

const BANNER_ALERT_ICON2_MEDIUM_STYLES = {
    'width.px': '30',
    'height.px': '30',
    'margin-right.px': '2'
};
const BANNER_ALERT_ICON2_SMALL_STYLES = {
    'width.px': '20',
    'height.px': '20',
    'margin.px': '6'
};
/**
 * @Component: Banner Alert
 * @Section: uicomponent
 * @description This is the Banner Alert component and is used to display different types of Banner Alert
 * from Bootstrap.
 */
class BannerAlertComponent {
    constructor(elRef, announcer) {
        this.elRef = elRef;
        this.announcer = announcer;
        this.open = true;
        /**
         * @property bannerType
         * @description This property helps to define the type of alert to be displayed
         * @return string (page | module) default page
         */
        this.bannerType = 'page';
        /**
         * @property hasCloseButton
         * @description This property helps to show/hide the close icon.
         * @return string
         */
        this.hasCloseButton = true;
        this.bold = true;
        /**
         * @Event: closeButtonClick
         * @description Emitted when close icon is clicked
         * @return Object
         */
        this.closeButtonClick = new EventEmitter();
    }
    generateCssClassString() {
        switch (this.alertType) {
            case 'success':
                this.alertTypeClass = 'ct-banner-green-alert';
                break;
            case 'critical':
                this.alertTypeClass = 'ct-banner-red-alert';
                break;
            case 'marketing':
                this.alertTypeClass = 'ct-banner-blue-alert';
                break;
            case 'technical':
                this.alertTypeClass = 'ct-banner-gray-alert';
                break;
            default:
                this.alertTypeClass = 'ct-banner-gray-alert';
        }
    }
    ngOnInit() {
        this.generateCssClassString();
    }
    /** close alert on click X icon **/
    closeAlert() {
        setTimeout(() => {
            this.announcer.announce(this.alertType + ' banner is closed');
            setTimeout(() => {
                this.open = false;
                // Emit the change to the close
                this.closeButtonClick.emit();
                const focusableElements = Array.from(getFocableAbleElements(document));
                if (focusableElements)
                    focusableElements.forEach((el, index) => {
                        if (el == this.close.nativeElement)
                            focusableElements[index + 1].focus();
                    });
            }, 200);
        }, 300);
    }
    button2GetAriaLabel() {
        return `${this.alertType} Alert Close`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BannerAlertComponent, deps: [{ token: i0.ElementRef }, { token: CitiLiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.14", type: BannerAlertComponent, isStandalone: false, selector: "citi-banner-alert", inputs: { alertType: "alertType", bannerType: "bannerType", idStr: "idStr", hasCloseButton: "hasCloseButton", alertMessage: "alertMessage", bold: "bold" }, outputs: { closeButtonClick: "closeButtonClick" }, viewQueries: [{ propertyName: "wrapper", first: true, predicate: ["alertWrapper"], descendants: true }, { propertyName: "close", first: true, predicate: ["close"], descendants: true }], ngImport: i0, template: `
    <div
      #alertWrapper
      class="ct-cbolui-page-banner {{ open ? 'alertOpen' : 'alertClose' }}"
      *ngIf="bannerType == 'page'"
    >
      <div
        class="ct-banner-alert-box"
        [ngClass]="alertTypeClass"
        [attr.id]="idStr"
      >
        <div class="row">
          <div class="col-xs-10">
            <strong>
              <ng-content select="[alertContent]"></ng-content>
            </strong>
          </div>
          <div class="col-xs-2">
            <button
              #close
              *ngIf="hasCloseButton"
              type="button"
              class="close"
              [attr.aria-label]="button2GetAriaLabel()"
              (click)="closeAlert()"
            ></button>
          </div>
        </div>
      </div>
    </div>
    <div
      #alertWrapper
      class="ct-cbolui-module-banner {{ open ? 'alertOpen' : 'alertClose' }}"
      *ngIf="bannerType == 'module'"
    >
      <div
        class="ct-module-alert-box"
        [ngClass]="alertTypeClass"
        [attr.id]="idStr"
      >
        <div class="row">
          <div class="col-xs-10">
            <strong>
              <ng-content select="[alertmoduleContent]"></ng-content>
            </strong>
          </div>
          <div class="col-xs-2">
            <button
              #close
              *ngIf="hasCloseButton"
              type="button"
              class="close"
              [attr.aria-label]="button2GetAriaLabel()"
              (click)="closeAlert()"
            ></button>
          </div>
        </div>
      </div>
    </div>
  `, isInline: true, styles: [".alertOpen{display:block}.alertClose{display:none}.ct-cbolui-page-banner ::ng-deep .cbolui-icon-text,.ct-cbolui-module-banner ::ng-deep .cbolui-icon-text{width:calc(100% - 30px)}.ct-cbolui-page-banner .ct-banner-alert-box{padding:20px 20px 20px 30px;margin:20px 0}.ct-cbolui-module-banner .ct-module-alert-box{padding:10px 20px 10px 30px;margin:20px 0}.ct-banner-red-alert{border-left:4px solid #d60000}.ct-banner-blue-alert{border-left:4px solid #056dae}.ct-banner-green-alert{border-left:4px solid #006e0a}.ct-banner-gray-alert{border-left:4px solid #666666}::ng-deep .theme-light .ct-cbolui-page-banner .ct-banner-alert-box{background-color:#eee}::ng-deep .theme-gray .ct-cbolui-page-banner .ct-banner-alert-box{background-color:#fff}.ct-cbolui-page-banner .ct-banner-alert-box ::ng-deep .message-text{padding-right:40px}.ct-banner-alert-box ::ng-deep .cbolui-icon-wrapper,.ct-module-alert-box ::ng-deep .cbolui-icon-wrapper{display:flex;padding:0;margin-bottom:0;border:1px solid transparent;border-radius:0}::ng-deep .theme-gray .ct-module-alert-box{border-right:1px solid #fff;border-top:1px solid #fff;border-bottom:1px solid #fff}::ng-deep .theme-light .ct-module-alert-box{border-right:1px solid #eee;border-top:1px solid #eee;border-bottom:1px solid #eee}.close.close{height:12px;width:12px;margin-top:5px;margin-right:4px;float:right;background-position-x:0px;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-white%3C/title%3E%3Cpath d='M21.41,20l3.8-3.79a1,1,0,0,0-1.42-1.42L20,18.59l-3.79-3.8a1,1,0,0,0-1.42,1.42L18.59,20l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L20,21.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-14.5 -14.5)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.close.close:hover,.close.close:focus{margin-top:0;margin-right:0;height:20px;width:20px;background-position:unset;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-white-hover%3C/title%3E%3Cpath d='M21.41,20.15l3.8-3.79a1,1,0,0,0,0-1.41,1,1,0,0,0-1.42,0L20,18.74,16.21,15a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l3.8,3.79L14.79,24a1,1,0,0,0,0,1.41,1,1,0,0,0,1.42,0L20,21.57l3.79,3.79a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.41ZM20,9A11,11,0,1,0,31,20,11,11,0,0,0,20,9' transform='translate(-9 -9)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}:host ::ng-deep citi-icon2.success path{fill:#015a09}:host ::ng-deep citi-icon2.critical g{fill:#b50404;width:34px;height:34px}:host ::ng-deep citi-icon2.marketing path{fill:#004e7f}:host ::ng-deep citi-icon2.technical g{fill:#2a2a2a;width:34px;height:34px}\n"], dependencies: [{ kind: "directive", type: i3$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BannerAlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'citi-banner-alert', standalone: false, template: `
    <div
      #alertWrapper
      class="ct-cbolui-page-banner {{ open ? 'alertOpen' : 'alertClose' }}"
      *ngIf="bannerType == 'page'"
    >
      <div
        class="ct-banner-alert-box"
        [ngClass]="alertTypeClass"
        [attr.id]="idStr"
      >
        <div class="row">
          <div class="col-xs-10">
            <strong>
              <ng-content select="[alertContent]"></ng-content>
            </strong>
          </div>
          <div class="col-xs-2">
            <button
              #close
              *ngIf="hasCloseButton"
              type="button"
              class="close"
              [attr.aria-label]="button2GetAriaLabel()"
              (click)="closeAlert()"
            ></button>
          </div>
        </div>
      </div>
    </div>
    <div
      #alertWrapper
      class="ct-cbolui-module-banner {{ open ? 'alertOpen' : 'alertClose' }}"
      *ngIf="bannerType == 'module'"
    >
      <div
        class="ct-module-alert-box"
        [ngClass]="alertTypeClass"
        [attr.id]="idStr"
      >
        <div class="row">
          <div class="col-xs-10">
            <strong>
              <ng-content select="[alertmoduleContent]"></ng-content>
            </strong>
          </div>
          <div class="col-xs-2">
            <button
              #close
              *ngIf="hasCloseButton"
              type="button"
              class="close"
              [attr.aria-label]="button2GetAriaLabel()"
              (click)="closeAlert()"
            ></button>
          </div>
        </div>
      </div>
    </div>
  `, styles: [".alertOpen{display:block}.alertClose{display:none}.ct-cbolui-page-banner ::ng-deep .cbolui-icon-text,.ct-cbolui-module-banner ::ng-deep .cbolui-icon-text{width:calc(100% - 30px)}.ct-cbolui-page-banner .ct-banner-alert-box{padding:20px 20px 20px 30px;margin:20px 0}.ct-cbolui-module-banner .ct-module-alert-box{padding:10px 20px 10px 30px;margin:20px 0}.ct-banner-red-alert{border-left:4px solid #d60000}.ct-banner-blue-alert{border-left:4px solid #056dae}.ct-banner-green-alert{border-left:4px solid #006e0a}.ct-banner-gray-alert{border-left:4px solid #666666}::ng-deep .theme-light .ct-cbolui-page-banner .ct-banner-alert-box{background-color:#eee}::ng-deep .theme-gray .ct-cbolui-page-banner .ct-banner-alert-box{background-color:#fff}.ct-cbolui-page-banner .ct-banner-alert-box ::ng-deep .message-text{padding-right:40px}.ct-banner-alert-box ::ng-deep .cbolui-icon-wrapper,.ct-module-alert-box ::ng-deep .cbolui-icon-wrapper{display:flex;padding:0;margin-bottom:0;border:1px solid transparent;border-radius:0}::ng-deep .theme-gray .ct-module-alert-box{border-right:1px solid #fff;border-top:1px solid #fff;border-bottom:1px solid #fff}::ng-deep .theme-light .ct-module-alert-box{border-right:1px solid #eee;border-top:1px solid #eee;border-bottom:1px solid #eee}.close.close{height:12px;width:12px;margin-top:5px;margin-right:4px;float:right;background-position-x:0px;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-white%3C/title%3E%3Cpath d='M21.41,20l3.8-3.79a1,1,0,0,0-1.42-1.42L20,18.59l-3.79-3.8a1,1,0,0,0-1.42,1.42L18.59,20l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L20,21.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-14.5 -14.5)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.close.close:hover,.close.close:focus{margin-top:0;margin-right:0;height:20px;width:20px;background-position:unset;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-white-hover%3C/title%3E%3Cpath d='M21.41,20.15l3.8-3.79a1,1,0,0,0,0-1.41,1,1,0,0,0-1.42,0L20,18.74,16.21,15a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l3.8,3.79L14.79,24a1,1,0,0,0,0,1.41,1,1,0,0,0,1.42,0L20,21.57l3.79,3.79a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.41ZM20,9A11,11,0,1,0,31,20,11,11,0,0,0,20,9' transform='translate(-9 -9)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}:host ::ng-deep citi-icon2.success path{fill:#015a09}:host ::ng-deep citi-icon2.critical g{fill:#b50404;width:34px;height:34px}:host ::ng-deep citi-icon2.marketing path{fill:#004e7f}:host ::ng-deep citi-icon2.technical g{fill:#2a2a2a;width:34px;height:34px}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: CitiLiveAnnouncer }], propDecorators: { wrapper: [{
                type: ViewChild,
                args: ['alertWrapper']
            }], close: [{
                type: ViewChild,
                args: ['close']
            }], alertType: [{
                type: Input
            }], bannerType: [{
                type: Input
            }], idStr: [{
                type: Input
            }], hasCloseButton: [{
                type: Input
            }], alertMessage: [{
                type: Input
            }], bold: [{
                type: Input
            }], closeButtonClick: [{
                type: Output
            }] } });

const MODULES = [
    CommonModule,
    RouterModule,
    Icons2
];
class BannerAlert {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BannerAlert, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: BannerAlert, declarations: [BannerAlertComponent], imports: [CommonModule,
            RouterModule,
            Icons2], exports: [BannerAlertComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BannerAlert, imports: [MODULES] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: BannerAlert, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES],
                    declarations: [BannerAlertComponent],
                    exports: [BannerAlertComponent]
                }]
        }] });

class StripHtmlPipe {
    transform(message) {
        return !message ? '' : message
            .replace(/<(?:.|\n)*?>/gm, '')
            .replace(/&amp|;/g, '&')
            .replace(/&lt|;/g, '<')
            .replace(/&gt|;/g, '>');
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: StripHtmlPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: StripHtmlPipe, isStandalone: false, name: "stripHtml" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: StripHtmlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'stripHtml',
                    standalone: false
                }]
        }] });

class StripHtml {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: StripHtml, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: StripHtml, declarations: [StripHtmlPipe], imports: [CommonModule], exports: [StripHtmlPipe] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: StripHtml, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: StripHtml, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [StripHtmlPipe],
                    exports: [StripHtmlPipe]
                }]
        }] });

/*
 * Public API Surface of citi-utils
 */
// export * from './lib/citi-utils.module';

/**
 * Generated bundle index. Do not edit.
 */

export { AppConfigService, BANNER_ALERT_ICON2_MEDIUM_STYLES, BANNER_ALERT_ICON2_SMALL_STYLES, BannerAlert, BannerAlertComponent, BrowserNavigatorRef, BrowserWindowRef, CTA, CTAComponent, CTA_CLASSES, CheckboxComponent, CheckboxRadioRootComponent, CitiAppConfig, CitiAppConfigService, CitiLiveAnnouncer, ColumnComponent, ContentHtml, ContentHtmlDirective, CookieService, CurrencyInputMaskDirective, CustomInputMaskDirective, CustomPipe, DD2_VALUE_ACCESSOR, DataCollectorService, Dropdown2, Dropdown2Component, EcmCookieService, Ellipses, FeatureDetectionService, GlobalErrorHandlerService, Grid, ICON2_REGISTRY_PROVIDER, ICON2_REGISTRY_PROVIDER_FACTORY, INPUT2_VALUE_ACCESSOR$1 as INPUT2_VALUE_ACCESSOR, IS_SSRENDERED_APP, Icon2Component, Icon2RegistryService, Icon2WrapperComponent, Icons2, IdleTimeRouterComponent, IdleTimeRouterModule, InPageNavigation, Input2, Input2Component, Input2Component4Q21, InputGroup, InputGroupComponent, InputService, JAMP, JSLoaderService, JampComponent, LIVE_ANNOUNCER_ELEMENT_TOKEN, LIVE_ANNOUNCER_PROVIDER, LIVE_ANNOUNCER_PROVIDER_FACTORY, LoggingService, MaskingPipe, MessagingService, Modal, ModalComponent, ModalSharedService, NAVIGATOR$1 as NAVIGATOR, NAVIGATOR_PROVIDER$1 as NAVIGATOR_PROVIDER, NavGroupComponent, NavPanelComponent, NavigationHistoryService, NavigatorRef, OptionsComponent, OptionsGroupComponent, Pagination, PaginationComponent, PaginationItem, PartialInputMaskDirective, PostSessionService, PostSignoffService, QaCitiMobileModal, QaCitiMobileModalModule, RadioComponent, RouterRedirectGuard, RowComponent, SERVER_URL, ScriptLoadService, SectionComponent, SessionService, SpeedBumpComponent, StorageService, StripHtml, StripHtmlPipe, TAGGING_CONSTANT, TEXT_AREA_VALUE_ACCESSOR, TaggingService, TextArea, TextHeader, TextHeaderComponent, TextareaComponent, TileContainer, TileContainerComponent, UserPersonalizationService, ValidationService, WINDOW, WINDOW_PROVIDER, WindowRef, addJfpToken, boolCheck, navigatorFactory$1 as navigatorFactory, windowFactory };
